import { catalogueContributionsCopy } from './catalogue-contributions-copy';
import { communityCopy } from '../community/community-copy';
import { catalogueSyncCopy } from './catalogue-sync-copy';
import { textLanguageCopy } from '../text-language/text-language-copy';
import { boardAdminCopy } from './board-admin-copy';
import { achievementCelebrationTranslations } from './achievement-celebration-translations';
import { boardCopy } from '../board/board-copy';
import { bookShareCopy } from '../book-sharing/book-share-copy';
import { bookFinishCopy } from '../chamber/reading-completion-copy';
import { shareImageCopy } from '../sharing/share-copy';
import { countFormatCopy } from './count-format-copy';
import { readingGoalCopy } from '../reading-goals/reading-goal-copy';
import {
  readingCalendarEn,
  readingCalendarSk,
  readingCalendarCs,
} from '../reading-goals/reading-calendar-i18n';
import { progressCelebrationTranslations } from '../reading-goals/progress-celebration-i18n';
import { APP_NAME } from '@living-library/contracts';
import { modularLibraryCopy } from '../library/prototype/modular-library-copy';
import { modularLibraryScreenCopy } from '../library/prototype/modular-library-screen-copy';
import { modularLibraryBookPreviewCopy } from '../library/prototype/modular-library-book-preview-copy';
import { modularLibraryCollectibleCopy } from '../library/prototype/modular-library-collectible-copy';
import { modularLibrarySceneItemCopy } from '../library/prototype/modular-library-scene-item-copy';
import { modularLibraryArrangementCopy } from '../library/prototype/modular-library-arrangement-copy';
import { modularLibraryEditorCopy } from '../library/prototype/modular-library-editor-copy';
import { perspectiveLibraryCopy } from '../library/perspective/perspective-library-copy';

export const defaultLanguage = 'en';

export const supportedLanguages = ['en', 'sk', 'cs'] as const;

export type SupportedLanguage = (typeof supportedLanguages)[number];

export const resources = {
  en: {
    translation: {
      catalogueContributions: catalogueContributionsCopy.en,
      community: communityCopy.en,
      textLanguage: textLanguageCopy.en,
      countFormat: countFormatCopy.en,
      perspectiveLibrary: perspectiveLibraryCopy.en,
      noteEditor: {
        fullScreen: 'Full screen',
        quickHint: 'Longer thoughts? Open it full screen and keep writing.',
        wholeBook: 'About the whole book',
        wholeBookHint: 'No page — it shows first on the book’s page.',
        titlePlaceholder: 'Title (optional)',
        titleLabel: 'Note title',
        placeholder: 'Keep writing…',
        bodyLabel: 'Your note',
        format: {
          bold: 'Bold',
          italic: 'Italic',
          underline: 'Underline',
          strikeThrough: 'Strikethrough',
          unorderedList: 'Bulleted list',
          orderedList: 'Numbered list',
        },
        insertQuote: 'Insert a quote',
        insert: 'Insert',
        scanNew: 'Scan a new one',
        fromBook: 'From {{book}}',
        noQuotes: 'Your saved quotes from this book will appear here.',
        selectQuote: 'Select quote: {{body}}',
        noPage: 'No page',
        quoteRemoved: 'Quote removed',
        openQuote: 'Open quote',
        removeLink: 'Remove link',
        hideKeyboard: 'Hide keyboard',
        formatting: 'Formatting',
        newPlaceholder: 'Write your note…',
        page: 'p. {{page}}',
        privateShort: 'Private',
        keepWriting: 'Keep writing',
        yourNotes: 'Your notes',
        yourQuotes: 'Your quotes',
        openJournal: 'Open in Journal',
        journalTitle: 'Journal',
        openJournalShort: 'Open',
        notesCount_one: '{{count}} note',
        notesCount_other: '{{count}} notes',
        quotesCount_one: '{{count}} quote',
        quotesCount_other: '{{count}} quotes',
        words_one: '{{count}} word',
        words_other: '{{count}} words',
        edited: 'edited {{date}}',
        minutes: '{{count}} min',
        private: 'Private note',
        untitled: 'Untitled note',
        conflictTitle: 'This note changed elsewhere',
        conflictBody:
          'Your draft and the saved version are both here. Keep both as separate notes, or use the saved version.',
        yourDraft: 'Your draft',
        savedVersion: 'Saved version',
        keepBoth: 'Keep both notes',
        useSaved: 'Use saved version',
        reviewVersions: 'Review versions',
        drafts: 'Notes waiting to save',
        resumeDraft: 'Continue {{title}}',
        draftHint: 'Kept on this device',
        status: {
          saved: 'Saved',
          dirty: 'Unsaved changes',
          saving: 'Saving…',
          failed: 'Not saved · Retry',
          storageFailed: 'Not saved',
          conflict: 'Choose a version',
          invalid: 'Check your note',
        },
        message: {
          captureFailed:
            'The editor could not finish reading your changes. Keep this note open and try again.',
          failed: 'Not saved. Your draft is kept on this device. Try again when you’re connected.',
          storageFailed:
            'This device could not keep your latest changes. Keep this note open and try again.',
          conflict:
            'A newer version was saved elsewhere. Your draft is safe; choose which version to keep.',
          invalid:
            'Add some text, a title or a quote. Notes can contain up to 20,000 characters, with a title up to 200 characters.',
        },
      },
      capture: {
        scan: 'Scan the page',
        cameraPermission: 'Allow Belore to scan book barcodes and capture passages.',
        fromBook: 'From the book',
        typePassage: 'Or type the quote here…',
        scanAgain: 'Scan again',
        checkWords: 'Scanned from the page · check the words',
        fromProgress: 'From your progress',
        foundOnPage: 'Found on the page',
        pageOptional: 'Optional page',
        optional: 'Optional',
        thoughtPlaceholder: 'Why did it stay with you?',
        save: 'Save quote',
        retrySave: 'Retry save',
        saveUncertain:
          'The save could not be confirmed. Your draft is kept here. Retry the same save to avoid a duplicate.',
        choose: 'Choose the lines',
        takePhoto: 'Take photo',
        retake: 'Retake',
        typeInstead: 'Type quote',
        retryPhoto: 'Read it again',
        usePassage: 'Use this passage',
        useAll: 'Use all text',
        reading: 'Reading the page…',
        selectIOS: 'Long-press a word, then drag the handles. Or use all text and edit it next.',
        selectAndroid: 'Tap a word, double-tap a sentence, or drag the handles.',
        pageFound: 'Page {{page}} found',
        photoPrivacy: 'The text is read on your phone. The photo isn’t kept.',
        unavailable:
          'Scanning is unavailable in this build or on this device. You can type or paste your quote.',
        cameraReason: 'Allow camera access to scan a book page.',
        allowCamera: 'Allow camera',
        openSettings: 'Open settings',
        scanFailed: 'Could not read this photo. Retake it in better light, or type your quote.',
        noText: 'No text found. Retake the page or type your quote.',
        typeQuoteInstead: 'Type the quote instead',
        headerSave: 'Save',
        privacyShow: 'Shows how your quotes are counted',
        privacyHide: 'Hides the explanation',
        liveTextTitle: 'No text found',
        liveTextBody:
          'If the page looks sharp, Live Text is probably off. Belore uses it to read the page, right on your phone.',
        liveTextSteps: 'Turn on Live Text',
        liveTextSettings: 'Settings',
        liveTextGeneral: 'General',
        liveTextRegion: 'Language & Region',
        liveTextToggle: 'Live Text',
        liveTextReturn: 'Then come back here. We’ll read the photo again.',
        cameraFailed: 'The camera could not start. Close the scanner and try again.',
        flashOn: 'Flash on',
        flashOff: 'Flash off',
        pagePhoto: 'Photographed book page',
        selectionStart: 'Selection start',
        selectionEnd: 'Selection end',
      },
      journalReturn: {
        latestNote: 'Your latest note',
        edited: 'edited {{date}}',
        keepWriting: 'Keep writing',
        allNotes: 'All notes ({{count}}) ›',
        allNotesAccessibility: 'All notes for this book, {{count}}',

        lastSaved: 'Last time you saved from this book',
        page: 'p. {{page}}',
        kept: 'What you kept',
        step: '{{current}} of {{total}}',
        summary: '{{quotes}} and {{notes}} while you read it.',
        quoteCount_one: '{{count}} quote',
        quoteCount_few: '{{count}} quotes',
        quoteCount_other: '{{count}} quotes',
        noteCount_one: '{{count}} note',
        noteCount_few: '{{count}} notes',
        noteCount_other: '{{count}} notes',
        empty: 'Nothing kept yet. Add a quote or a note before you move on.',
        loadFailed: 'Your saved entries could not be loaded. You can retry or continue.',
        shareFavourite: 'Share a favourite',
        chooseQuote: 'Choose a quote to share.',
        addOne: 'Add one more',
        continue: 'Continue',
        next: 'Next',
        more: 'Load more entries',
      },
      libraryReaders: {
        title: 'Readers of this book',
        accessible: 'Readers of this book. Saves: {{readers}}. Line likes: {{likes}}.',
        thoughtAccessible: '{{name}}: {{thought}}. Thought likes: {{likes}}.',
      },
      lines: {
        title: 'Lines from this book',
        intro:
          'Quotes approved by moderators, with approved public thoughts. Quotes past your page stay hidden.',
        mostSaved: 'Most saved',
        mostLiked: 'Most liked',
        save: 'Save',
        saved: 'Saved',
        thoughts_one: '{{formattedCount}} thought',
        thoughts_other: '{{formattedCount}} thoughts',
        allThoughts: 'All {{formattedCount}} thoughts',
        empty: 'No public lines yet. Keep a quote that stays with you.',
        error: 'These lines could not be loaded.',
        mutationError: 'Your change could not be saved. Try again.',
        more: 'Show more',
        viewAll: 'View all quotes',
        allTitle: 'Book quotes',
        previous: 'Previous',
        next: 'Next',
        position: 'Quote {{number}}',
        signIn: 'Sign in to see quotes from this book.',
        beyond: 'Beyond your page',
        laterThought: 'Talks about later parts',
        page: 'p. {{page}}',
        approximatePage: 'about p. {{page}}',
        yourPage: 'you’re on p. {{page}}',
        unknownPage: 'Page unknown',
        show: 'Tap to show',
        reader: 'Reader',
        you: 'You',
        addThought: 'Add your thought',
        thoughtPrompt: 'Add your thought…',
        write: 'Write',
        makePublic: 'Make my thought public',
        publicHint: 'Shown with your name next to this line, after a quick review.',
        privateHint: 'Only you can see this thought.',
        spoilerHint: 'Hide this thought until the reader finishes the book or chooses to show it.',
        keepsJournal: 'Saving also keeps this line and your thought in your Journal.',
        editInReview: 'Edit in review',
        editNotApproved: 'Edit not approved',
        approvedStays: 'Your approved thought stays public while this edit is reviewed.',
        duplicateThought:
          'You already have a public thought on this line. Open it in your Journal to edit it.',
        viewJournal: 'Open in Journal',
        openLine: 'Open this line and its thoughts',
        likeThought: 'Like this thought, {{formattedCount}} likes',
        unlikeThought: 'Unlike this thought, {{formattedCount}} likes',
      },
      quoteModeration: {
        declineEditHint: 'Declining this edit keeps the currently public version.',
        pair: 'Quote & thought',
        approveBoth: 'Approve quote & thought',
        approveThought: 'Approve thought',
        approveQuoteDeclineThought: 'Approve quote · decline thought',
        keepQuoteDeclineThought: 'Keep quote · decline thought',
        declineThoughtOnly: 'Decline thought · leave quote unchanged',
        quoteAlreadyApproved: 'Quote already approved',
        skip: 'Skip for now →',
        returnSkipped: 'Return to skipped',
        skippedNotice: 'Skipped for this visit. No decision was saved.',
        allSkipped: 'The loaded items have been set aside. Load more or return to skipped items.',
        quoteOnlyNotice: 'Quote approved; thought declined.',

        nav: 'Quotes & thoughts',
        moderation: 'Moderation',
        intro: 'Shared quotes, catalogue quotes, and reader thoughts in one place.',
        shared: 'Shared quote request',
        wording: 'Catalogue quote',
        thought: 'Public thought',
        refresh: 'Refresh',
        loading: 'Loading…',
        loadingItems: 'Loading moderation items…',
        waiting: 'waiting',
        matching: 'matching',
        content: 'Content',
        all: 'All content',
        sharedFilter: 'Shared quotes',
        wordingFilter: 'Catalogue quotes',
        thoughtFilter: 'Thoughts',
        status: 'Status',
        pending: 'Waiting for approval',
        approvedFilter: 'Approved catalogue quotes',
        hiddenFilter: 'Hidden catalogue quotes',
        search: 'Search',
        searchPlaceholder: 'Book title or text',
        clear: 'Clear all filters',
        readerHint:
          'Reader filters apply to shared quotes and thoughts. Catalogue contributors remain anonymous.',
        empty: 'Nothing is waiting for approval',
        noMatch: 'No items match these filters',
        emptyHint: 'All pending quote and thought approvals are included here.',
        filterHint: 'Change or clear the filters to see more items.',
        more: 'Load more',
        of: 'of',
        loadError: 'The moderation queue could not be loaded. Try refreshing.',
        approvedNotice: 'Approved. The queue has been refreshed.',
        hiddenNotice: 'Quote hidden. The queue has been refreshed.',
        declinedNotice: 'Declined. The queue has been refreshed.',
        conflict: 'This item changed while you were reviewing it. Refresh before deciding again.',
        decisionError: 'The decision could not be saved. Please try again.',
        saves: 'reader saves',
        awaiting: 'Awaiting approval',
        approved: 'Approved',
        hidden: 'Hidden',
        published: 'Currently public',
        edit: 'Submitted edit',
        spoilers: 'Contains spoilers',
        hide: 'Hide quote',
        decline: 'Decline',
        approve: 'Approve',
        thoughtLabel: 'Thought',
        quoteLabel: 'Quote',
      },
      lineModeration: {
        nav: 'Lines & thoughts',
        title: 'Quotes & thought review',
        wordings: 'Catalogue quotes',
        thoughts: 'Public thoughts',
        intro:
          'Every public quote requires moderator approval of its wording. During the closed beta a single reader save establishes eligibility, so approving a catalogue quote publishes it. Popular approved quotes lead book previews. Public thoughts and their edits require separate approval.',
        loading: 'Loading…',
        empty: 'Nothing is waiting for review.',
        refresh: 'Refresh',
        more: 'Load more',
        approve: 'Approve',
        allowPublication: 'Allow publication',
        hide: 'Hide quote',
        notPublic: 'Not public',
        reject: 'Decline',
        spoilers: 'Talks about later parts',
        published: 'Currently public',
        draft: 'Submitted text',
        error: 'The queue could not be loaded or updated. Try again.',
        conflict: 'This entry changed. Refresh and review the latest version.',
      },
      quoteSharing: {
        title: 'Share quote',
        ink: 'Ink',
        paper: 'Paper',
        teal: 'Teal',
        includeThought: 'Include my thought',
        showCover: 'Show the cover',
        showName: 'Include my first name: {{name}}',
        myThought: 'My thought',
        kept: 'Kept in Belore',
        keptBy: 'Kept by {{name}} in Belore',
        save: 'Save image',
        saved: 'Image saved.',
        unavailable: 'Sharing is unavailable on this device. You can save the image instead.',
        failed: 'The image could not be exported. Please try again.',
        tooLong:
          'This passage is too long for one image. Share a shorter quote, or turn off your thought.',
        preview: 'Quote image preview: {{text}}',
      },
      journal: {
        deleteErrorTitle: 'Couldn’t delete this entry',
        editInReview: 'Edit in review',
        editNotApproved: 'Edit not approved',
        wholeBook: 'Whole book',
        public: 'Public',
        inReview: 'In review',
        notPublished: 'Not published',
        entryAccessibility: '{{kind}}, {{metadata}}: {{text}}',
        wordCount_one: '{{formattedCount}} word',
        wordCount_few: '{{formattedCount}} words',
        wordCount_other: '{{formattedCount}} words',
        edited: 'edited {{date}}',
        thoughtLikes: '{{formattedCount}} likes on your thought',
        likeLine: 'Like this line. Likes: {{formattedCount}}',
        unlikeLine: 'Unlike this line. Likes: {{formattedCount}}',

        shelves: {
          all: 'All',
          currently_reading: 'Reading',
          to_be_read: 'TBR',
          already_read: 'Read',
          did_not_finish: 'DNF',
        },

        customPrivacy:
          'Quotes and thoughts for your custom books stay private and are not counted in the catalogue.',

        removeThoughtConfirm: 'Remove this thought? Your saved quote will stay in your Journal.',
        saveDraft: 'Save my draft',
        savedThought: 'Saved version',
        noSavedThought: 'The thought was removed elsewhere.',

        title: 'Journal',
        books: 'Books',
        all: 'All',
        quotes: 'Quotes',
        notes: 'Notes',
        reviews: 'Reviews',
        filter: 'Filter',
        search: 'Search my journal',
        clearSearch: 'Clear journal search',
        add: 'Add a quote or a note',
        saveQuote: 'Save a quote',
        writeNote: 'Write a note',
        emptyTitle: 'A place for your thoughts',
        emptyBody: 'Save a quote or write a note about a book. Your reviews will appear here too.',
        noBooksTitle: 'Start with a book',
        noBooksBody: 'Add your first book, then collect quotes, notes and reviews here.',
        findBook: 'Find a book',
        checkingBooks: 'Checking your books…',
        booksError: 'Your books could not be loaded.',
        clearFilters: 'Clear filters',
        filterEmptyTitle: 'Nothing found',
        filterEmptyBody: 'Try another search or change your filters.',
        thought: 'Your thought',
        private: 'Private',
        addThought: '+ Add a thought',
        editThought: 'Edit thought',
        saveThought: 'Save thought',
        removeThought: 'Remove thought',
        thoughtPlaceholder: 'What did this line make you think?',
        saved: '{{formattedCount}} saved',
        openQuote: 'Open quote: {{text}}',
        order: 'Change journal order',
        error: 'Your journal could not be loaded.',
        thoughtError: 'Your thought was not saved. Your draft is still here.',
        conflict: 'This thought changed elsewhere. Reload the saved version before trying again.',
        reload: 'Reload saved version',
        privacy:
          'Quotes you save from catalogue books count anonymously. When a moderator approves the wording of a line you save, the book’s words may appear on its page, without your name or thought. Your thoughts stay private unless you choose to make them public. Notes are always private.',
        privacyTitle: 'Your saved quotes',
        privacyShort: 'Saved quotes count anonymously.',
        customPrivacyShort: 'Quotes from your own books stay private.',
        loading: 'Loading your journal…',
        retry: 'Try again',
        filterTitle: 'Filter journal',
        bookFilterGroups: {
          currently_reading: 'Currently reading',
          already_read: 'Read',
          other: 'Other books',
        },
        added: 'Added',
        clear: 'Clear filters',
        newestShort: 'Newest',
        oldestShort: 'Oldest',
        orderToggle: 'Order: {{order}}. Tap to change',
        kindSelected: '{{kind}}, selected. Tap to show everything',
        removeFilter: 'Remove filter: {{label}}',
        openBookAction: 'Open book',
        reviewAccessibility: 'Review, {{rating}}, {{status}}, {{date}}: {{text}}',
        reviewSpoilers: 'Contains spoilers · others see it hidden',
        yourReview: 'YOUR REVIEW',
        yourRating: 'RATING',
        addReview: '+ Add a review',
        moreFilters: '{{count}} more filters. Open the filter',
        moderatorNote: 'Moderator note',
      },

      achievementContentLanguage: 'en',
      modularLibrary: {
        ...modularLibraryCopy.en,
        ...modularLibraryScreenCopy.en,
        illustratedTitle: 'Illustrated library',
      },
      modularLibraryBookPreview: modularLibraryBookPreviewCopy.en,
      modularLibraryCollectible: modularLibraryCollectibleCopy.en,
      modularLibrarySceneItems: modularLibrarySceneItemCopy.en,
      modularLibraryArrange: modularLibraryArrangementCopy.en,
      modularLibraryEditor: modularLibraryEditorCopy.en,
      readingGoal: readingGoalCopy.en,
      board: boardCopy.en,
      bookShare: bookShareCopy.en,
      bookFinish: bookFinishCopy.en,
      shareImage: shareImageCopy.en,
      boardAdmin: boardAdminCopy.en,
      catalogueSync: catalogueSyncCopy.en,
      progressCelebration: progressCelebrationTranslations.en,
      libraryResources: {
        catalogueFailed: 'Artwork availability could not be checked. Please try again.',
        checkingAvailability: 'Checking artwork availability…',
        downloading: 'Downloading artwork for offline use…',
        downloadFailed: 'Your collectible is safe. Its artwork could not download.',
        unavailable: 'You own this collectible. Its artwork is not available to download yet.',
        retry: 'Retry download',
      },
      productionLibrary: {
        loadFailed:
          'Your library could not be loaded. Try again to keep your saved arrangement safe.',
        starterCollection: 'Starter collection',
        testingCollection: 'Test collection',
        included: 'Included with your library',
        emptyTitle: 'A place for your books',
        emptyBody:
          'Add a book you’re reading, have finished, or want to read. Make this library yours.',
        emptyAddBook: 'Add your first book',
      },
      selection: {
        choose: 'Choose an option',
        openHint: 'Opens the options in a bottom sheet.',
        search: 'Search options',
        clearSearch: 'Clear option search',
        noResults: 'No matching options. Try another search.',
        selectedCount_one: '{{count}} selected',
        selectedCount_other: '{{count}} selected',
        done: 'Done',
        limit: 'Selection limit reached. Deselect an option to choose another.',
      },
      bookLanguages: {
        en: 'English',
        sk: 'Slovak',
        cs: 'Czech',
        de: 'German',
        pl: 'Polish',
        hu: 'Hungarian',
        fr: 'French',
        es: 'Spanish',
        sv: 'Swedish',
      },
      readingCompletion: {
        save: 'Mark as read',
        unknown: 'I don’t remember',
        title: 'When did you finish this book?',
        now: 'Now',
        today: 'Today',
        earlier: 'Earlier',
        earlierHint: 'A year is enough.',
        fieldsHint: 'Fill in only what you remember.',
        dateEntry: 'Or enter a date',
        optionalFieldsHint: 'Year is enough. Month and day are optional.',
        yearPlaceholder: 'YYYY',
        dayLabel: 'Day',
        monthOptionalLabel: 'Month (optional)',
        dayOptionalLabel: 'Day (optional)',
        optionalValue: 'Not sure',
        unknownYear: 'I don’t remember the year',
        calendar: 'Choose from calendar',
        dateNotSelected: 'Choose “Today”, enter a year, or select “I don’t remember”.',
        restoreDate: 'Enter a remembered date',
        monthRequiresYear: 'Enter the year first.',
        dayRequiresMonth: 'Choose a month first.',
        year: 'Year only',
        month: 'Year and month',
        date: 'Exact date',
        precision: 'How much do you remember?',
        choiceHint: 'Choose what you remember. You don’t need an exact date.',
        nowHint:
          'Save today as the finish date. For a book in progress, the remaining pages count toward today’s goal.',
        pastHint: 'This records past reading without adding pages to today’s goal.',
        unknownHint:
          'Save as read without a date. It won’t count toward a particular year or today’s pages.',
        yearLabel: 'Year',
        monthLabel: 'Month',
        yearInvalid: 'Enter a four-digit year, no later than this year.',
        monthInvalid: 'Choose a year and month, no later than this month.',
        dateInvalid: 'Choose a valid date, no later than today.',
        summary: 'Read: {{period}}',
        yearOnlyTitle: 'Month not recorded',
        yearOnlyBody: 'These books count toward this year, without assigning them a month.',
      },
      customBooks: {
        title: 'Add a custom book',
        addBook: 'Add book',
        pageTitle: 'Add a book',
        pagePrivacy: 'Only in your library',
        privacyDisclaimer:
          'Private. Catalogue details are shared only if you submit them and a moderator approves.',
        authorsSearch: 'Search authors or enter a new name',
        authorsPlaceholder: 'Search or add an author',
        subtitlePlaceholder: 'Add a subtitle',
        yearPlaceholder: 'Year',
        libraryCategoriesHint: 'Your labels. They don’t affect genre achievements.',
        authorsIntro: 'Search the catalogue, or add an author by name.',
        authorsEmpty: 'No matching authors. You can add the name below.',
        chooseDate: 'Choose a date',
        datePickerHint: 'Choose the day you finished, no later than today.',
        titleRequired: 'Book title is required.',
        authorLabel: 'Author',
        addAnotherAuthor: 'Add another author',
        enterAuthor: 'Enter author',
        optional: 'Optional',
        chooseCategories: 'Choose categories',
        isbnPlaceholder: 'ISBN-10 or ISBN-13',
        categoriesIntro: 'Choose all that fit.',
        categoriesPrivacy: 'Private labels · not used for genre achievements',
        categoriesSearch: 'Search categories',
        categoriesSelected: '{{count}} selected',
        categoriesClear: 'Clear',
        categoriesDone: 'Done',
        categoriesEmpty: 'No matching categories. Try another search.',
        missingTitle: 'Missing from the catalogue?',
        entryPrivacy:
          'Add it privately. You can later choose to submit its catalogue details for review.',
        entryHint: 'Can’t find your book or the right edition?',
        privacy:
          'Add a book to your personal library. Its catalogue details stay private unless you submit them and a moderator approves.',
        signInRequired: 'Sign in to save a custom book to your library.',
        signIn: 'Go to sign in',
        bookTitle: 'Book title',
        authors: 'Authors',
        authorHint:
          'Choose a catalogue suggestion or add a name. Authors appear in the order you add them.',
        authorName: 'Author name',
        addAuthor: 'Add “{{name}}”',
        removeAuthor: 'Remove {{name}}',
        moveAuthorEarlier: 'Move {{name}} earlier',
        searchingAuthors: 'Searching catalogue authors…',
        authorSearchFailed: 'Author suggestions are unavailable. You can still add a name.',
        edition: 'Edition details',
        isbn: 'ISBN',
        isbnHint: 'Optional. Enter the ISBN printed in this edition, if it has one.',
        language: 'Language',
        unknown: 'Unknown',
        otherLanguage: 'Another language',
        languageCode: 'Language code',
        languageCodeHint: 'Enter its two-letter language code, for example “uk” for Ukrainian.',
        pages: 'Number of pages',
        pagesHint:
          'Optional. Without a total, you can log pages and mark the book finished yourself.',
        binding: 'Format',
        bindings: {
          unknown: 'Not specified',
          paperback: 'Paperback',
          hardcover: 'Hardcover',
          ebook: 'Ebook',
          other: 'Other',
        },
        genres: 'Personal categories',
        genresHint:
          'Choose any that fit. These labels are personal and do not count toward genre achievements.',
        moreDetails: 'More edition details',
        subtitle: 'Subtitle',
        publisher: 'Publisher',
        publishedYear: 'Publication year',
        reading: 'In your library',
        status: 'Reading shelf',
        statuses: {
          to_be_read: 'To be read',
          currently_reading: 'Currently reading',
          already_read: 'Already read',
          did_not_finish: 'Did not finish',
        },
        startsAtBeginning:
          'Reading starts at page 0. You can update your page after adding the book.',
        knowFinishDate: 'I know when I finished it',
        unknownFinishDateHint:
          'Leave this off if the date is unknown. We won’t count an undated book toward an annual goal.',
        finishedOn: 'Date finished',
        dateHint: 'Use year-month-day. Choose a real date on or before today.',
        datePlaceholder: 'YYYY-MM-DD',
        save: 'Add to my library',
        retrySave: 'Retry the same save',
        optionalHint:
          'ISBN and edition details are optional. Add an author or choose “Unknown author”.',
        openMatch: 'Open the catalogue book',
        editIsbn: 'Edit the ISBN',
        shelfDestination: 'Private · adds to {{shelf}}',
        validation: {
          title: 'Enter a title of up to 1,000 characters.',
          subtitle: 'Use a subtitle of up to 2,000 characters.',
          publisher: 'Use a publisher name of up to 1,000 characters.',
          authors:
            'Add at least one author or choose “Unknown author”. You can add up to 16 authors.',
          authorsPending: 'Add the name in the author field, or clear it before saving.',
          isbn: 'Check the ISBN. Use a valid 10- or 13-character book ISBN, or leave it blank.',
          language: 'Enter a two-letter language code, or choose “Unknown”.',
          pages: 'Enter a whole number of pages from 1 to 10,000, or leave it blank.',
          year: 'Enter a whole publication year from 1 to 9999, or leave it blank.',
          date: 'Choose a finish date no later than today, or mark the date unknown.',
        },
        errors: {
          catalogueMatchShort: 'This edition is already in the catalogue.',
          catalogueMatch:
            'This ISBN is already in the catalogue. Open that edition to check it and add it to your library.',
          conflict: 'We couldn’t save these details together. Review them and try again.',
          invalid: 'Some book details could not be accepted. Review the fields and try again.',
          unavailable:
            'Saving is temporarily unavailable. Your details are kept here; retry the same save when the service returns.',
          uncertain:
            'We couldn’t confirm whether your book was saved. Retry the same save to avoid adding it twice.',
          signIn: 'Sign in again to save this book to your library.',
        },
      },
      libraryBookPreview: {
        title: 'Book preview',
        inMyBooks: 'In My Books',
        openDetails: 'Open book details',
        currentPage: 'Page {{currentPage}}',
        percent: '{{progress}}%',
        accessibility: {
          open: 'Preview {{title}}',
          close: 'Close book preview',
          details: 'Open book details for {{title}}',
        },
      },
      accessibility: {
        addBookToShelf: 'Add {{title}} to a reading shelf',
        archiveFilter: '{{label}}, {{count}} books',
        archiveFilter_one: '{{label}}, {{count}} book',
        adjustProgress: 'Adjust progress by {{amount}}',
        backToMyBooks: 'Back to My Books',
        changeBookShelf: 'Change the reading shelf for {{title}}, currently {{shelf}}',
        clearBookSearch: 'Clear book search',
        closeBookBarcodeScanner: 'Close book barcode scanner',
        closeChamberActions: 'Close chamber actions',
        closeCompletion: 'Close completion celebration',
        closeProgressEditor: 'Close progress editor',
        categorySheetHandle: 'Reading shelf panel handle',
        categorySheetHandleHint: 'Swipe down or activate to close the reading shelf choices',
        emptyFirstReward: 'First reward: Page Keeper. Choose a book to begin.',
        emptyWeeklyGoal: 'Weekly goal. Starts with a book.',
        filterSheetHandle: 'Search filters panel handle',
        filterSheetHandleHint: 'Swipe down or activate to close the search filters',
        progressSheetHandle: 'Progress panel handle',
        progressSheetHandleHint: 'Swipe down or activate to close the progress panel',
        openBookByAuthor: 'Open {{title}} by {{authors}}',
        openBookProgress: 'Open {{title}}, page {{currentPage}}',
        openBookProgressWithTotal: 'Open {{title}}, page {{currentPage}} of {{pageCount}}',
        progressEditorHint: 'Opens controls for changing the current page',
        pageRuler: 'Current page {{currentPage}} of {{pageCount}}',
        percentSlider: 'Reading progress, {{progress}} percent',
        progressValueInputPages: 'Current page',
        progressValueInputPercent: 'Progress percentage',
        scanBookBarcode: 'Scan a book barcode',
        prototypeGoal:
          '{{completed}} of {{target}} reading sessions completed for the prototype weekly goal',
        prototypeReward: 'Prototype next reward: Page Keeper, {{count}} sessions away',
        prototypeReward_one: 'Prototype next reward: Page Keeper, {{count}} session away',
        searchByTitleOrAuthor: 'Search title, author or ISBN',
        setProgressPercent: 'Set progress to {{value}} percent',
        searchMyBooks: 'Search my books',
        showChamberActions: 'Show chamber actions',
        showTbrList: 'Show To Be Read list',
        showTbrListHint: 'Opens your To Be Read books',
        startNextBook: 'Start reading {{title}}',
        updateProgress: 'Update progress for {{title}}, {{progress}}% read',
        updateProgressUnknownTotal: 'Update progress for {{title}}, page total unknown',
      },
      actions: {
        add: 'Add',
        addBook: 'Add Book',
        addToTbr: 'Add to TBR',
        adding: 'Adding...',
        back: 'Back',
        browseBooks: 'Browse books',
        cancel: 'Cancel',
        chooseLater: 'Choose later',
        close: 'Close',
        continueApple: 'Continue with Apple',
        continueAuthKit: 'Continue with AuthKit',
        continueGoogle: 'Continue with Google',
        details: 'Details',
        find: 'Find',
        findBooks: 'Find books',
        finishBook: 'Finish book',
        more: 'More actions',
        retry: 'Retry',
        remove: 'Remove',
        saveProgress: 'Save progress',
        saving: 'Saving...',
        search: 'Search',
        share: 'Share',
        signOut: 'Sign out',
        signInEmail: 'Sign in with email',
        signingOut: 'Signing out...',
        startReading: 'Start reading',
        trackProgress: 'Track progress',
      },
      auth: {
        callbackFailed: 'Sign-in could not be completed',
        callbackReturning: 'Completing secure sign-in...',
        callbackTryAgain: 'Return and try again',
        errors: {
          authorizationGrantInvalid:
            'The identity provider did not complete sign-in. Return and start a fresh attempt.',
          callbackMissingCode: 'The AuthKit callback is missing its code or PKCE state',
          callbackStateInvalid: 'The AuthKit callback state is invalid or expired',
          remoteSignOutUnconfirmed: 'Remote WorkOS sign-out was not confirmed',
          sessionExpired: 'The WorkOS session has expired',
          signInRequired: `Sign in before calling the ${APP_NAME} API`,
        },
        proof: {
          apiUserCreated: 'Created after API verification',
          appOwnedUser: 'App-owned user',
          authenticated: 'Authenticated',
          body: 'This bounded screen proves the native WorkOS PKCE flow, persisted session, and an authenticated call through the Nest API.',
          eyebrow: 'READ TRACKER · AUTH PROOF',
          nestMe: 'Nest /me',
          notAuthenticated: 'Not authenticated',
          redirect: 'Redirect: {{uri}}',
          title: 'A trustworthy door into the library.',
          verified: 'Verified',
          verifyNestApi: 'Verify Nest API',
          waiting: 'Waiting',
          workosSession: 'WorkOS session',
        },
      },
      dev: {
        typographyStandoff: {
          body: 'The same product roles, sizes and palette in every specimen. Compare the title rhythm, large numerals, progress suffix and Central European diacritics.',
          candidateLabel: '{{name}} typography specimen',
          current: 'Current',
          currentPage: 'Current page',
          derived: 'Page 146 of 416',
          eyebrow: 'TYPOGRAPHY LAB · SIX-WAY STANDOFF',
          languageProof: 'A quiet library keeps every story.',
          loadError: 'The comparison fonts could not be loaded.',
          loading: 'Preparing the comparison fonts...',
          modalTitle: 'Edit reading progress',
          pageSuffix: 'of 416 pages',
          title: 'Six serifs enter the library.',
        },
      },
      catalogue: {
        authorSuggestions: 'Authors',
        authorSuggestionsLoading: 'Finding authors…',
        authorSuggestionsFailed: 'Suggestions are unavailable. You can still search by name.',
        authorSuggestionsEmpty: 'No suggestions yet. You can still search by name.',
        aboutBook: 'About the book',
        obalkyFeedback: {
          title: 'Ratings and reviews from Obálky knih',
          averageAccessibility: 'Obálky knih rating: {{value}} out of 5',
          ratingInfo:
            'This rating comes from Obálky knih readers, on a scale of 5 stars. Available review excerpts appear below the book description. Belore ratings and reviews are shown separately. Provider data is cached and may take up to 24 hours to refresh.',
          votes_one: '{{count}} rating',
          votes_few: '{{count}} ratings',
          votes_many: '{{count}} ratings',
          votes_other: '{{count}} ratings',
          excerpts: 'Review excerpts',
          openSource: 'View on obalkyknih.cz',
        },
        coverCredit: {
          prefix: 'Cover from',
          accessibilityHint: 'Opens this book on obalkyknih.cz.',
        },
        allGenres: 'All genres',
        account: 'ACCOUNT CATALOGUE',
        addToShelf: 'Add to a shelf',
        addedToTbr: '{{title}} was added to your TBR.',
        allResults: 'All',
        anyLanguage: 'Any',
        assignmentFailed: 'This book could not be moved. Please try again.',
        assignedToShelf: '{{title}} is now in {{shelf}}.',
        availableResults: 'Not added',
        bookLanguages: 'Book languages',
        detailEyebrow: 'BOOK DETAIL',
        detailLoading: 'Loading book details…',
        detailUnavailable: 'Book details could not be loaded. Please try again.',
        detailUnavailableTitle: 'We couldn’t open this book',
        detailUnavailableBody:
          'Book details are unavailable right now. Please try again in a moment.',
        detailRefreshFailedTitle: 'We couldn’t update this book',
        detailRefreshFailedBody:
          'You can keep reading the details already loaded. Try again to update them.',
        partialDetail: {
          body: 'We couldn’t load this edition right now. The catalogue information above may be incomplete.',
          retry: 'Try again',
          source: 'View catalogue source',
          title: 'Full details unavailable',
        },
        publicPreview: {
          addAction: 'Add to my library',
          addBody: 'Choose where this book belongs.',
          addTitle: 'Keep this book close',
          footerBody: 'Save books, track reading, and share reviews.',
          footerTitle: 'Continue with Belore',
          openInAppAction: 'Open in Belore',
          openInAppFailed: 'Belore could not be opened. You can keep reading here.',
          openInAppTitle: 'Already have Belore on this device?',
          signInAction: 'Sign in to Belore',
          signInBody: 'Choose how you’d like to continue.',
          signInTitle: 'Sign in to Belore',
          shareNotAvailable: 'This edition is not available for public sharing yet.',
        },
        editionsLoadError: 'Other editions could not be loaded. Try again.',
        isbnCopy: {
          action: 'Copy ISBN',
          copied: 'Copied to clipboard',
          failed: 'Couldn’t copy. Touch and hold the ISBN to copy it.',
        },
        metadataPageCount: 'Pages',
        editionLanguage: 'Edition language: {{language}}',
        editionLanguageUnknown: 'Edition language unknown',
        editionUnavailable: 'This edition could not be opened in the app yet.',
        genreAndSubjectLabel: 'Genres & catalogue topics',
        loadMoreEditions: 'Load more editions',
        loadingEditions: 'Loading…',
        retryEditions: 'Try again',
        noMatchingEditionsLoaded: 'No matching editions are loaded yet.',
        noOtherEditions: 'No other editions are known for this book yet.',
        noOtherEditionsInLanguages: 'No other editions match the selected book languages.',
        openInOpenLibrary: 'Open in Open Library',
        otherEditions: 'Other editions',
        moveReadBookTitle: 'Move this finished book?',
        moveReadBookBody: 'This book is already marked as read. Move it to “{{shelf}}”?',
        moveReadBookAction: 'Move book',
        rereadProgressHint: 'Reading progress will restart from the beginning.',
        removeFromLibrary: 'Remove from my library',
        removeFromLibraryBody:
          'Your reading progress, personal rating, review, notes and quotes for this edition will be deleted. The book stays in the shared catalogue.',
        removePrivateBookFromLibraryBody:
          'Your reading progress, personal rating, review, notes and quotes for this private book will be deleted.',
        removeFromLibraryFailed:
          'The book could not be removed from your library. Please try again.',
        removeFromLibraryTitle: 'Remove {{title}} from your library?',
        showMoreEditions: 'Show all {{count}}',
        genres: {
          adventure: 'Adventure',
          biography_memoir: 'Biography & memoir',
          childrens: 'Children’s',
          comics_graphic_novels: 'Comics & graphic novels',
          fantasy: 'Fantasy',
          historical_fiction: 'Historical fiction',
          history: 'History',
          horror: 'Horror',
          literary_fiction: 'Literary fiction',
          mystery_thriller: 'Mystery & thriller',
          nonfiction: 'Nonfiction',
          poetry: 'Poetry',
          romance: 'Romance',
          science_fiction: 'Science fiction',
          young_adult: 'Young adult',
        },
        markdownAccessibility: {
          blockquote: 'Blockquote',
          bulletPoint: 'Bullet point',
          headings: 'Headings',
          images: 'Images',
          links: 'Links',
          math: 'Math: {latex}',
          nestedBlockquote: 'Nested blockquote',
          nestedBulletPoint: 'Nested bullet point',
          nestedOrderedItem: 'Nested list item {n}',
          orderedItem: 'List item {n}',
          tableRow: 'Row {n}: {content}',
        },
        externalCandidateUnavailable:
          'This external book could not be saved yet. Please try again.',
        applyFilters: 'Show results',
        activeFilterCount: '{{count}} active filters',
        activeFilterCount_one: '{{count}} active filter',
        filterPanel: 'Refine search',
        filters: 'Filters',
        filtersAccessibilityLabel: 'Filters, {{count}} active',
        findTitle: 'Find books',
        recentSearches: 'Recent searches',
        clearRecentSearches: 'Clear',
        clearRecentSearchesLabel: 'Clear recent searches',
        repeatRecentSearch: 'Search again for {{query}}',
        scanTip: 'Holding the book? Scan the barcode on the back cover to open that exact edition.',
        idleHint: 'Search the catalogue by title, author or ISBN.',
        findInvitation: 'Find a book by title,\nauthor or ISBN.',
        findFocusedTitle: 'Which book are\nyou looking for?',
        findFocusedHint: 'A title, author\nor ISBN is enough.',
        findSearchPlaceholder: 'Title, author or ISBN',
        idleTitle: 'Find your book',
        idleBody: 'Search by title, author or ISBN, or add a book yourself.',
        startSearch: 'Search books',
        resultsTitle: 'Results',
        searchingResults: 'Searching…',
        bookLanguagesLabel: 'Book languages',
        changeBookLanguages: 'Book languages: {{languages}}. Change',
        searchAllFields: 'Search all fields',
        keepOneLanguage: 'Choose another language before removing this one.',
        languages: 'Languages',
        languageSelectorAccessibilityLabel: '{{languages}}, {{count}} selected',
        resetFilters: 'Reset',
        searchIn: 'Search in',
        searchScope: {
          all: 'All',
          author: 'Author',
          isbn: 'ISBN',
          title: 'Title',
        },
        linkCopied: 'Book link copied.',
        moveToShelf: 'Move to another shelf',
        noResults: 'No matching books. Try another title, author or ISBN.',
        emptyTitle: 'No books found',
        emptyHintAll: 'Check the spelling, or try another title, author or ISBN.',
        emptyHintTitle:
          'Nothing matched this title. Check the spelling, or try the author or ISBN.',
        emptyHintAuthor:
          'Nothing matched this author. Check the spelling, or try the title or ISBN.',
        emptyHintIsbn: 'No edition has this ISBN. Check the number, or search by title or author.',
        emptySearchedLanguages: 'Searched in: {{languages}}.',
        changeLanguages: 'Change languages',
        nowOnReadingDesk: '{{title}} is now on your reading desk.',
        pageCount: '{{count}} pages',
        pageCount_one: '{{count}} page',
        pageCountUnknown: 'Page count unknown',
        preview: 'PREVIEW CATALOGUE',
        resultCount: '{{count}} books loaded',
        resultCount_one: '{{count}} book loaded',
        returnToFind: 'Return to Find and choose another result.',
        savedResults: 'My books',
        showLess: 'Show less',
        showMore: 'Show more',
        shareFailed: 'The book link could not be shared.',
        shareMessage: '{{title}}\n{{url}}',
        shareText: `Open {{title}} in ${APP_NAME}.`,
        shareUnavailable: 'Set EXPO_PUBLIC_WEB_URL before sharing from a native build.',
        scanner: {
          allowCamera: 'Allow camera access',
          errors: {
            camera: 'The camera could not start. Close the scanner and try again.',
            invalid:
              'That barcode is not a valid book ISBN. Try the original barcode on the back cover.',
            lookup:
              'The book catalogue could not be reached. Check your connection and scan again.',
            not_found:
              'No edition was found for that ISBN. Try another barcode or enter the ISBN manually.',
          },
          instructions: 'Place the ISBN barcode on the back of the book inside the frame.',
          lookingUp: 'Looking up this edition...',
          manualHint: 'If the barcode will not scan, close this view and enter the ISBN manually.',
          openSettings: 'Open settings',
          permissionBody: 'Camera access is used only to read the ISBN barcode. No photo is saved.',
          permissionTitle: 'Allow camera access',
          title: 'Scan a book',
        },
        pagination: {
          filtered: 'No matching editions on this page. Load more to continue searching.',
          more: 'Load more books',
          moreWider: 'Load more from the wider catalogue',
          loading: 'Loading…',
          retry: 'Retry loading more',
          failed: 'Could not load more books. Your results are still here.',
          refine:
            'Looking for a specific edition? Refine the title, author or language, or search by ISBN.',
          limit:
            'This catalogue’s search limit was reached. Refine your search to find other matches.',
        },
        searchWider: 'Search a wider catalogue',
        titleOrAuthor: 'Search title, author or ISBN',
        scopedSearchPlaceholder: {
          title: 'Search by title',
          author: 'Search by author',
          isbn: 'Search by ISBN',
        },
        widerSearchHint: 'Not seeing the right edition? Search our extended book catalogue.',
        widerSearchIncluded: 'The wider catalogue was searched too.',
      },
      chamber: {
        allChallengesComplete: 'ALL CHALLENGES COMPLETE',
        allChallengesCompleteShort: 'COMPLETE',
        badgeReward: 'Badge',
        chooseBookToBegin: 'Choose a book to begin',
        chooseCurrentRead: 'Choose your current read',
        currentReading: 'CURRENTLY READING',
        earnedRewards: 'EARNED REWARDS',
        earnedRewardsShort: 'COLLECTION',
        emptyCurrentBody: 'Start a book from My Books to place it on the reading desk.',
        firstReward: 'FIRST REWARD',
        followedChallenge: 'FOLLOWED CHALLENGE',
        followedChallengeShort: 'FOLLOWED CHALLENGE',
        journeyLoading: 'Loading your Journey',
        journeyUnavailable: 'JOURNEY UNAVAILABLE',
        nextReward: 'NEXT REWARD',
        nextRewardShort: 'REWARD',
        noCurrentBook: 'NO CURRENT BOOK',
        openChallengeHint: "Opens this challenge's detail.",
        openCollection: 'View earned rewards',
        openCollectionHint: 'Opens your reward collection.',
        openJourney: 'Explore challenges',
        openJourneyHint: 'Opens Journey.',
        openTbr: 'Open TBR',
        pageKeeper: 'Page Keeper',
        readingChamber: 'Reading Chamber',
        readingRhythmCompact:
          '{{daysCurrent}}/{{daysTarget}} days · {{weeksCurrent}}/{{weeksTarget}} wks',
        recommendedChallenge: 'CLOSEST CHALLENGE',
        recommendedChallengeShort: 'CLOSEST CHALLENGE',
        rewardDistance: '{{count}} sessions away',
        rewardDistance_one: '{{count}} session away',
        retryJourney: 'Tap to try loading it again',
        retryJourneyHint: 'Retries loading your Journey progress.',
        sourceAccountSynced: 'ACCOUNT SYNCED',
        sourcePreviewCollection: 'PREVIEW COLLECTION',
        sourceSyncingLibrary: 'SYNCING LIBRARY',
        syncPausedPreview: 'Sync paused - preview data is available.',
        syncPausedSaved: 'Sync paused - saved progress is available.',
        themePackReward: 'Library appearance',
        yourCollection: 'Your collection',
        weeklyGoal: 'WEEKLY GOAL',
        weeklyGoalEmpty: 'Starts with a book',
      },
      libraryThemes: {
        active: 'ACTIVE',
        assetPending: 'The production room asset is still being prepared.',
        previewUnavailable: 'Preview unavailable',
        change: 'Change',
        classic: {
          description: 'The original Belore walnut cabinet, warm display light and brass details.',
          title: 'Classic Cabinet',
          unlockHint: 'Included with every library.',
        },
        downloadAndUse: 'Download & use',
        downloadRequired: 'Downloads securely when you choose it.',
        remoteDownloadSavedChoice: 'SELECTED',
        remoteDownloadInProgress: 'Preparing this theme…',
        remoteDownloadFailed:
          'The theme could not be downloaded. Your current appearance has not changed.',
        remoteDownloadUnavailable:
          'This theme is in your collection. Its files are not available yet.',
        remoteDownloadRetry: 'Retry download',
        editorLabel: 'THEME PACK',
        errors: {
          'asset-missing': 'This theme is not available in this app build yet.',
          conflict: 'The active theme changed on another device. Try again.',
          invalid: 'The theme response was not valid. Your previous theme remains active.',
          unavailable: 'The theme could not be changed. Please try again.',
          unowned: 'This theme pack has not been unlocked for your account.',
        },
        eyebrow: 'COLLECTIBLE ROOM THEMES',
        intro:
          'Theme packs change the cabinet materials and authored light without moving your books, collectibles or shelf arrangement.',
        loadFailed: 'Your theme collection could not be refreshed. Please try again.',
        locked: 'LOCKED',
        menuLabel: 'Library themes',
        openHint: 'Opens your collected library theme packs',
        minimalWarmGrey: {
          description:
            'Painted warm-grey shelves with softly rounded edges in Belore’s illustrated style.',
          title: 'Warm Grey Minimal',
          unlockHint: 'Unlocked with five approved public reviews of different books.',
        },
        minimalBlack: {
          description:
            'Polished black lacquer shelves with subtle reflections that let your books stand out.',
          title: 'Black Minimal',
          unlockHint: 'Unlocked by rating five different books. Private ratings count too.',
        },
        orbitalArchive: {
          description: 'Graphite shelves, cool light and a tablet frame around your current read.',
          title: 'Orbital Archive',
          unlockHint: 'Awarded for completing a reading challenge.',
        },
        verdantScriptorium: {
          description:
            'Smoked oak, forest-green alcoves and a bronze reading ward beneath your book.',
          title: 'Verdant Scriptorium',
          unlockHint: 'Awarded for completing a reading challenge.',
        },
        owned: 'OWNED',
        title: 'Library themes',
        useTheme: 'Use this theme',
        webUnsupported: 'Themes aren’t supported in web browsers yet.',
      },
      cabinetSyncChoice: {
        body: 'This device is arranged differently from the library already saved in your account. Choose which layout should become shared across your devices.',
        chooseLater: 'Decide later',
        cloud: 'Cloud layout',
        cloudHint: 'Currently shared with your account',
        consequence:
          'Using this device replaces the cloud arrangement. Using cloud replaces the saved arrangement on this device.',
        deviceHint: 'Saved only on this device',
        error: 'That layout could not be selected. Nothing was replaced; please try again.',
        eyebrow: 'FIRST SYNC',
        itemCount: '{{count}} items arranged',
        itemCount_one: '{{count}} item arranged',
        laterNote:
          'Editing stays paused until you choose. Neither layout is deleted when you decide later.',
        saveConflictBody:
          'The cloud library changed after you opened the editor. Choose whether to save this device’s arrangement or load the latest cloud layout.',
        saveConflictEyebrow: 'SAVE CONFLICT',
        saveConflictLaterNote:
          'Your unsaved changes stay open. Nothing is replaced until you choose.',
        saveConflictTitle: 'Library changed on another device',
        thisDevice: 'This device',
        title: 'Two library layouts found',
        useCloud: 'Use cloud layout',
        useDevice: 'Use this device’s layout',
        unsavedDeviceHint: 'Your current unsaved arrangement',
      },
      cabinetEditor: {
        applyDisplay: 'Apply to board',
        applyPlatformSpot: 'Apply to platform',
        alreadyPlaced: 'On another shelf',
        applyShelf: 'Apply to shelf',
        arrange: 'Arrange',
        bookPlacementNotice:
          'Unavailable books are already displayed, reserved as the current read, or marked DNF. Books from every other library category can be placed here.',
        chooseEveryPosition: 'Choose an item for every position.',
        chooseLayout: 'Choose a layout',
        chooseShelf: 'CHOOSE A SHELF, PLATFORM SPOT OR BADGE BOARD',
        clearDisplay: 'Clear board',
        clearPlatformSpot: 'Clear spot',
        clearShelf: 'Clear shelf',
        collectibles: 'Collectibles',
        currentBook: 'Reading',
        currentReserved: 'Currently Reading — reserved',
        didNotFinishUnavailable: 'DNF — unavailable',
        discard: 'Discard changes',
        discardBody: 'Your unsaved shelf arrangement will be lost.',
        discardTitle: 'Leave without saving?',
        errors: {
          conflict: 'This library changed elsewhere. Close the editor, reopen it, and try again.',
          saveFailed: 'The shelf arrangement could not be saved. Please try again.',
        },
        keepEditing: 'Keep editing',
        layoutDoesNotUseTab: 'This layout does not use items from this collection.',
        loadFailed: 'The shelf editor could not load your library.',
        loading: 'Preparing your shelves…',
        myBooks: 'My books',
        movesFromAnotherDisplay: 'Moves from another display',
        noBooks: 'No matching available books.',
        noCollectibles: 'No collectible rewards are available for your shelves yet.',
        open: 'Edit library',
        openHint: 'Opens customization for your library theme, displayed books and collectibles',
        recipes: {
          bookObject: 'Book + object',
          compactAccessory: 'Compact accessory',
          collectible: 'Collectible',
          empty: 'Empty',
          fiveBadges: 'Five badges',
          faceOut: 'Face-out',
          longAccessory: 'Long accessory',
          objectBook: 'Object + book',
          oneBadge: 'One badge',
          threeBadges: 'Three badges',
          threeSpines: 'Three spines',
          twoCollectibles: 'Two objects',
          twoCovers: 'Two covers',
          twoSpines: 'Two spines',
        },
        redo: 'Redo',
        reloadLatest: 'Reload latest',
        save: 'Save',
        searchPlaceholder: 'Search your library',
        selectedDisplay: 'SELECTED DISPLAY',
        selectedPlatformSpot: 'SELECTED PLATFORM SPOT',
        selectedShelf: 'SELECTED SHELF',
        shelves: {
          badgeBoard: 'Badge board',
          centerMiddle: 'Current reading shelf',
          centerUpper: 'Centre upper shelf',
          leftLower: 'Left lower shelf',
          leftMiddle: 'Left middle shelf',
          leftUpper: 'Left upper shelf',
          platformLeft: 'Left platform spot',
          platformRight: 'Right platform spot',
          rightLower: 'Right lower shelf',
          rightMiddle: 'Right middle shelf',
          rightUpper: 'Right upper shelf',
        },
        title: 'Arrange Library',
        undo: 'Undo',
        badges: {
          bookExplorer: 'Book explorer',
          firstFinish: 'First finish',
          hundredPages: 'Hundred pages',
          nightReader: 'Night reader',
          sevenDayStreak: 'Seven-day streak',
        },
      },
      completion: {
        dnf: {
          eyebrow: 'DID NOT FINISH',
          stoppedAt: 'Stopped at page {{currentPage}} of {{pageCount}} · {{progress}}',
          stoppedAtPage: 'Stopped at page {{currentPage}}',
          returnToReading: 'Back to Reading',
          returnFailed: 'The book could not be moved back to Reading. Please try again.',
          body: 'This book is now on your DNF shelf.',
          reflectionTitle: 'A note, if you like',
          reflectionBody:
            'If you’d like, you can let other readers know why you stopped reading. A rating and review are both optional.',
          save: 'Save & close',
          skip: 'Close for now',
          discardBody:
            'Your unsaved rating or review will be lost. The book will stay on your DNF shelf.',
          discard: 'Discard & close',
        },
        body: 'Another book finished. Take a moment to enjoy it.',
        chooseNext: 'What will you read next?',
        chooseNextBody: 'Choose from your TBR to place a new book on the reading desk.',
        continueToNext: 'Continue to the next book',
        discardAndContinue: 'Discard draft & continue',
        discardBody: 'The unsaved rating or review draft will be lost. The book will stay Read.',
        discardTitle: 'Leave this reflection unfinished?',
        eyebrow: 'BOOK COMPLETED',
        keepEditing: 'Keep editing',
        milestoneLabel: 'READING MILESTONE',
        milestoneValue: '+1 finished book',
        noTbr: 'Your TBR is empty. Browse the catalogue when you are ready for another book.',
        pagesRead: '{{count}} pages read',
        reflectionBody:
          'How was the book? Add your rating and share what stayed with you in a review. Both are optional, and you can always come back later.',
        reflectionTitle: 'Share your thoughts',
        saveAndContinue: 'Save reflection & continue',
        saveAndClose: 'Save & close',
        closeForNow: 'Close for now',
        discardAndClose: 'Discard & close',
        skipReflection: 'Skip for now · choose the next book',
        startFailed: 'The next book could not be started. Please try again.',
        title: 'You finished {{title}}',
      },
      annotations: {
        editor: {
          add: {
            note: 'Add a note',
            quote: 'Save a quote',
          },
          bodyAccessibilityLabel: {
            note: 'Note text',
            quote: 'Quote text',
          },
          bodyLabel: {
            note: 'Your note',
            quote: 'Quoted passage',
          },
          characterCount: '{{count}} / {{limit}} characters',
          close: 'Close the quote or note editor',
          confirmDelete: {
            note: 'Delete note',
            quote: 'Delete quote',
          },
          delete: {
            note: 'Delete note',
            quote: 'Delete quote',
          },
          deleteBody: {
            note: 'This private note cannot be recovered after deletion.',
            quote: 'This saved quote cannot be recovered after deletion.',
          },
          deleteTitle: {
            note: 'Delete this note?',
            quote: 'Delete this quote?',
          },
          discard: 'Discard changes',
          discardBody: 'Your unsaved changes will be lost.',
          discardTitle: 'Leave this entry unfinished?',
          edit: {
            note: 'Edit note',
            quote: 'Edit quote',
          },
          handle: 'Quote and note editor handle',
          handleHint: 'Swipe down or activate to close the editor',
          keepEditing: 'Keep editing',
          open: {
            notes: 'All notes',
            quotes: 'All quotes',
          },
          pageAccessibilityLabel: 'Page number',
          pageHint: 'Optional',
          pageLabel: 'Page',
          placeholder: {
            note: 'What do you want to remember?',
            quote: 'Type or paste the passage here…',
          },
          save: 'Save entry',
        },
        errors: {
          conflict: 'This entry changed elsewhere. Close it, reopen it, and try again.',
          delete: 'This entry could not be deleted. Please try again.',
          save: 'This entry could not be saved. Please try again.',
        },
        journal: {
          createLabel: { quote: 'Add quote', note: 'Add note' },
          add: {
            note: '+ Note',
            quote: '+ Quote',
          },
          addAccessibilityLabel: {
            note: 'Add a private note',
            quote: 'Save a quote',
          },
          open: {
            notes: 'All notes',
            quotes: 'All quotes',
          },
          edit: 'Edit',
          editAccessibilityLabel: {
            note: 'Edit note {{position}}: {{details}}',
            quote: 'Edit quote {{position}}: {{details}}',
          },
          empty: 'No saved quotes or notes for this book yet.',
          emptyBook: 'Save a line from the page or write down what the book brought to mind.',
          emptyCurrent: 'Save a passage or thought while it is fresh.',
          eyebrow: 'READING JOURNAL',
          intro: 'Keep passages worth returning to and private notes beside the book.',
          loadError: 'Your quotes and notes could not be loaded.',
          loading: 'Opening your reading journal…',
          loadMore: 'Load older entries',
          loadMoreError: 'Older entries could not be loaded.',
          page: 'Page {{pageNumber}}',
          retryLoadMore: 'Try loading older entries again',
          title: 'Quotes & notes',
        },
        kind: {
          note: 'NOTE',
          quote: 'QUOTE',
          review: 'REVIEW',
        },
        quick: {
          sectionTitle: 'Capture from this book',
          addAccessibilityLabel: {
            note: 'Add a private note for {{title}}',
            quote: 'Save a quote from {{title}}',
          },
          label: {
            note: 'Note',
            quote: 'Quote',
          },
        },
        validation: {
          body_required: 'Enter some text before saving.',
          body_too_long: 'Keep this entry under {{limit}} characters.',
          page_invalid: 'Enter a whole page number from 1 to 10,000,000.',
        },
      },
      quotes: {
        title: 'My Quotes',
        shortTitle: 'Quotes',
        passageCount: '{{formattedCount}} passages',
        passageCount_one: '{{formattedCount}} passage',
        passageCount_other: '{{formattedCount}} passages',
        searchAccessibility: 'Search quotes, books or authors',
        searchPlaceholder: 'Search quotes, books or authors',
        capturedOn: 'Captured {{date}}',
        pageSuffix: '  ·  p. {{pageNumber}}',
        readerCount: '{{formattedCount}} readers captured this',
        readerCount_one: '{{formattedCount}} reader captured this',
        readerCount_other: '{{formattedCount}} readers captured this',
        privateHint: 'Only you can see this quote',
        pendingHint: 'Publication review is in progress',
        rejectedHint: 'This quote was not approved for publication',
        loading: 'Loading your quotes',
        loadingMore: 'Loading older quotes…',
        actions: {
          add: 'Add',
          addAccessibility: 'Add a quote',
          clearSearch: 'Clear quote search',
          like: 'Like this quote',
          makePrivateAccessibility: 'Make this quote private',
          openAccessibility: 'Open My Quotes',
          openBook: 'Open {{title}}',
          publish: 'Publish',
          publishAccessibility: 'Publish this quote',
          unlike: 'Remove your like from this quote',
        },
        status: {
          private: 'PRIVATE',
          pending: 'PENDING',
          rejected: 'NOT PUBLISHED',
          published: 'PUBLISHED',
        },
        filters: {
          date: {
            label: 'Added',
            choose: 'Date captured',
            any: 'Any time',
            '30_days': 'Last 30 days',
            year: 'This year',
          },
          book: {
            label: 'Book',
            choose: 'Choose books',
            all: 'All',
          },
          author: {
            label: 'Author',
            choose: 'Choose authors',
            all: 'All',
          },
        },
        sort: {
          choose: 'Sort quotes',
          newest: 'Newest first',
          oldest: 'Oldest first',
          changeAccessibility: 'Change quote order',
        },
        bookPicker: {
          title: 'Which book is it from?',
          intro: 'Choose a book before saving the passage.',
          selectAccessibility: 'Save a quote from {{title}}',
        },
        errors: {
          update: 'The quote could not be updated. Please try again.',
        },
        states: {
          signedOut: {
            title: 'Your quotes stay with your account',
            body: 'Sign in to see and save passages from your books.',
          },
          error: {
            title: 'Your quotes could not be loaded',
            body: 'Your saved passages are still safe. Try opening them again.',
            action: 'Try again',
          },
          noResults: {
            title: 'No passages match',
            body: 'Try another phrase or clear the current filters.',
            action: 'Clear filters',
          },
          empty: {
            title: 'A passage worth returning to?',
            body: 'Save your first quote and keep it beside its book and author.',
            action: 'Add a quote',
          },
        },
      },
      notes: {
        title: 'My Notes',
        shortTitle: 'Notes',
        noteCount: '{{formattedCount}} notes',
        noteCount_one: '{{formattedCount}} note',
        noteCount_other: '{{formattedCount}} notes',
        searchAccessibility: 'Search notes, books or authors',
        searchPlaceholder: 'Search notes, books or authors',
        addedOn: 'Added {{date}}',
        pageSuffix: '  ·  p. {{pageNumber}}',
        loading: 'Loading your notes',
        loadingMore: 'Loading older notes…',
        actions: {
          add: 'Add',
          addAccessibility: 'Add a note',
          clearSearch: 'Clear note search',
          openAccessibility: 'Open My Notes',
          openBook: 'Open {{title}}',
        },
        status: {
          private: 'PRIVATE NOTE',
        },
        filters: {
          date: {
            label: 'Added',
            choose: 'Date added',
            any: 'Any time',
            '30_days': 'Last 30 days',
            year: 'This year',
          },
          book: {
            label: 'Book',
            choose: 'Choose books',
            all: 'All',
          },
          author: {
            label: 'Author',
            choose: 'Choose authors',
            all: 'All',
          },
        },
        sort: {
          choose: 'Sort notes',
          newest: 'Newest first',
          oldest: 'Oldest first',
          changeAccessibility: 'Change note order',
        },
        bookPicker: {
          title: 'Which book is it for?',
          intro: 'Choose a book before saving the note.',
          selectAccessibility: 'Add a note to {{title}}',
        },
        states: {
          signedOut: {
            title: 'Your notes stay with your account',
            body: 'Sign in to see and save private notes beside your books.',
          },
          error: {
            title: 'Your notes could not be loaded',
            body: 'Your private notes are still safe. Try opening them again.',
            action: 'Try again',
          },
          versionMismatch: {
            title: 'Notes are temporarily unavailable',
            body: 'Your notes are safe. Please try again in a moment.',
            action: 'Try again',
          },
          noResults: {
            title: 'No notes match',
            body: 'Try another phrase or clear the current filters.',
            action: 'Clear filters',
          },
          empty: {
            title: 'Something worth remembering?',
            body: 'Save your first private note beside its book and author.',
            action: 'Add a note',
          },
        },
      },
      feedback: {
        community: {
          averageAccessibility: 'Average rating {{value}} out of 5',
          beloreSource: 'BELORE COMMUNITY',
          empty: 'No approved public reviews yet.',
          eyebrow: 'READER REVIEWS',
          loadError: 'Reader reviews could not be loaded. Your private reflection is unaffected.',
          loading: 'Opening reader reviews…',
          ownReviewAbove: 'Your published review is shown in Your reflection above.',
          openLibraryAccessibility: 'Open this book on Open Library',
          openLibraryAverageAccessibility: 'Open Library average {{value}} out of 5',
          openLibraryEmpty: 'No Open Library ratings yet.',
          openLibraryEmptyShort: 'No ratings yet',
          openLibraryLoading: 'Loading Open Library ratings…',
          totalRatingCount: 'Total of {{count}} ratings',
          totalRatingCount_one: 'Total of {{count}} rating',
          ratingInfoTitle: 'About this rating',
          ratingInfoOpenLibrary:
            'This average is out of 5 stars and comes only from Open Library readers. Belore reader ratings and reviews are kept separate and are not included in this score. The Open Library data is cached, so recent ratings may not appear yet.',
          openLibraryNote: 'Cached separately from Belore reader ratings and reviews.',
          openLibraryRatingCount: '{{count}} Open Library ratings',
          openLibraryRatingCount_one: '{{count}} Open Library rating',
          openLibrarySource: 'OPEN LIBRARY RATING',
          publishedOn: 'Published {{date}}',
          ratingCount: '{{count}} ratings',
          ratingCount_one: '{{count}} rating',
          readerReview: 'Reader review',
          revealSpoiler: 'Show review',
          reviewCount: '{{count}} public reviews',
          reviewCount_one: '{{count}} public review',
          spoilerHidden: 'This review contains spoilers.',
          title: 'What readers say',
        },
        discardChanges: 'Discard changes',
        eyebrow: 'PERSONAL REFLECTION',
        intro: 'Your rating and review save separately. Add either one, or both.',
        loadErrorBody:
          'We did not open an empty editor because it could overwrite feedback already saved for this book.',
        loadErrorTitle: 'Your reflection could not be loaded',
        notSaved: 'Not saved',
        saveChanges: 'Save reflection',
        readPrompt: {
          addAction: 'Add to Read & continue',
          eyebrow: 'BEFORE YOU REFLECT',
          finishDateNote: 'Moving it marks it finished today. You can edit the date later.',
          keepAction: 'Keep in {{shelf}} & continue',
          moveAction: 'Move to Read & continue',
          trackedBody:
            'You opened rating and review while this book is still in {{shelf}}. Choose where it belongs before continuing.',
          title: 'Move this book to Read?',
          untrackedBody:
            'Ratings and reviews need a place in your library. Add this book to Read before continuing.',
        },
        retryLoad: 'Try loading again',
        summary: {
          edit: 'Edit reflection',
          emptyBody: 'Keep a private rating, write a review, or publish it for other readers.',
          private: 'Private',
          ratingLabel: 'YOUR RATING',
          rejectedBadge: 'NEEDS CHANGES',
          rejectedBody:
            'Edit your review and save it again to send the revised version back for review.',
          rejectedTitle: 'A moderator left a note about your public review',
          reviewLabel: 'YOUR REVIEW',
          spoilers: 'Contains spoilers',
          start: 'Rate or review',
          value: '{{value}} / 5',
        },
        rating: {
          clear: 'Clear rating',
          confirmRemove: 'Remove rating',
          controlAccessibilityLabel: 'Choose your rating',
          hint: 'Tap a star to rate. Tap the selected star again for a half point.',
          keep: 'Keep rating',
          compactLabel: 'Rating',
          label: 'Your rating',
          notRated: 'Not rated',
          remove: 'Remove rating',
          removeConfirmationBody: 'Your written review will stay exactly as it is.',
          removeConfirmationTitle: 'Remove this rating?',
          save: 'Save rating',
          sectionAccessibilityLabel: 'Personal rating',
          update: 'Update rating',
          value: '{{value}} out of 5',
          zeroAccessibilityLabel: 'Rate this book zero out of five stars',
          pickerLabel: 'Choose an exact rating',
          confirmPicker: 'Done',
          compactValue: '{{value}} / 5',
        },
        review: {
          confirmDelete: 'Delete review',
          containsSpoilers: 'This review contains spoilers',
          delete: 'Delete review',
          deleteConfirmationBody:
            'Your rating will remain. The review cannot be recovered after deletion.',
          deleteConfirmationTitle: 'Delete this review?',
          inputAccessibilityLabel: 'Your written review',
          keep: 'Keep review',
          compactLabel: 'Review',
          label: 'Your review',
          optional: 'optional',
          placeholder: 'What stayed with you after the final page?',
          privateNote: 'Only you can see this review.',
          publish: 'Publish this review for other readers',
          publishBody:
            'On by default. Approved reviews show your public reader name. Uncheck to keep it private.',
          save: 'Save review',
          sectionAccessibilityLabel: 'Personal written review',
          update: 'Update review',
        },
        title: 'Your reflection',
        unsavedClose: 'Save your changes or choose Discard changes before closing.',
        unavailable: 'Sign in to save a rating or review for this book.',
      },
      recovery: {
        title: 'Something went wrong',
        body: 'We cleared what Belore had stored on this phone. Your books, quotes and notes are safe in your account.',
        reload: 'Reload Belore',
      },
      common: {
        retry: 'Try again',
        all: 'All',
        allBooks: 'All Books',
        bookCount: '{{count}} books',
        bookCount_one: '{{count}} book',
        brand: APP_NAME.toUpperCase(),
        currentlyReading: 'Currently reading',
        currentlyReadingShort: 'Reading',
        currentlyReadingTitle: 'Currently Reading',
        didNotFinish: 'Did not finish',
        didNotFinishTitle: 'Did Not Finish',
        dnf: 'DNF',
        finishedBooks: 'Finished Books',
        inTbr: 'In TBR',
        ofCount: 'of {{count}}',
        pageCurrent: 'Page {{currentPage}}',
        pageCurrentOfTotal: 'Page {{currentPage}} of {{pageCount}}',
        pageCurrentUnknownTotal: 'Page {{currentPage}} of unknown total',
        read: 'Read',
        searchResults: 'Search Results',
        tbr: 'TBR',
        toBeRead: 'To Be Read',
      },
      achievements: {
        categories: {
          journey: {
            body: 'Reading the app observed over time.',
            title: 'Reading journey',
          },
          library: {
            body: 'Your lifetime library, including books read before joining.',
            title: 'Personal library',
          },
          reflection: {
            body: 'The ratings and reviews that capture your reading impressions.',
            title: 'Reflection',
          },
        },
        eyebrow: 'YOUR READING JOURNEY',
        intro:
          'Explore independent reading challenges, see your confirmed progress, and preview the badges and library appearances each one offers.',
        items: {
          afterword: {
            body: 'Finish a tracked book, then add your rating.',
            title: 'Afterword',
          },
          another_story: {
            body: 'Complete three different works tracked from reading to finish.',
            title: 'Another Story',
          },
          between_bookmarks: {
            body: 'Log progress for the same book on three different days.',
            title: 'Between Bookmarks',
          },
          cover_to_cover: {
            body: 'Finish a book that was started and progressed in the app.',
            title: 'Cover to Cover',
          },
          first_bookmark: {
            body: 'Log your first forward reading progress.',
            title: 'First Bookmark',
          },
          first_impression: {
            body: 'Rate your first finished book, including one from your history.',
            title: 'First Impression',
          },
          first_read: {
            body: 'Record your first finished book, including past reading.',
            title: 'First Story',
          },
          first_shelf: {
            body: 'Add your first book to the library.',
            title: 'First Shelf',
          },
          five_worlds: {
            body: 'Finish books from five different genres.',
            title: 'Five Worlds',
          },
          five_public_reviews: {
            body: 'Publish reviews of five different books. Only approved public reviews count.',
            title: 'Five Reviews',
          },
          five_ratings: {
            body: 'Rate five different books. Private ratings count too; the books do not need to be finished.',
            title: 'Five Ratings',
          },
          personal_archive: {
            body: 'Record ten different finished works.',
            title: 'Personal Archive',
          },
          reading_rhythm: {
            body: 'Read on seven different days spread across at least three weeks.',
            title: 'Reading Rhythm',
          },
          second_genre: {
            body: 'Finish books from two different genres and discover new territory.',
            title: 'New Territory',
          },
        },
        loadFailed: 'Your achievements could not be loaded.',
        locked: 'Badge',
        milestone: 'Milestone',
        ritual: {
          body: 'Return to one book on three days, finish it, and add your rating. All three steps must belong to the same reading attempt.',
          eyebrow: 'LIBRARY APPEARANCE REWARD',
          progress: '{{current}} of {{target}} ritual steps complete',
          title: 'The Reading Ritual',
          unlocked: 'Challenge complete · Appearance available',
        },
        title: 'Achievements',
        unlocked: 'Complete',
      },
      journey: {
        readingCalendar: readingCalendarEn,
        loading: 'Loading your Journey',
        refreshFailed: 'Showing your last saved Journey. Fresh progress could not be loaded.',
        versionMismatch: {
          body: 'The app and server are finishing an update. Reload Belore in a moment.',
          cachedBody:
            'Showing saved progress while the app and server finish updating. Reload Belore in a moment.',
          title: 'Belore is being updated',
        },
        accessibility: {
          instrumentHint: "Opens this achievement's details.",
          nextGoal: 'Next achievement: {{title}}',
          pager: 'Focused achievements',
          pagerItem: 'Show achievement {{position}} of {{count}}',
          pagerPosition: 'Achievement {{position}} of {{count}}',
          previousGoal: 'Previous achievement: {{title}}',
        },
        hero: {
          betweenBookmarksRemaining_one: 'One more day with the same book.',
          betweenBookmarksRemaining_other: '{{count}} more days with the same book.',
          readingRhythm: '7 days · at least 3 weeks',
          readingRitual: 'Three steps within one reading attempt.',
        },
        actions: {
          collection: 'Collection',
          details: 'Details',
          discover: 'Discover achievements',
        },
        focused: {
          newAttemptRequired: 'Start a new reading attempt to complete all three steps together.',
          rateBook: 'Rate book',
          ritualSteps: {
            post_finish_rating: 'Rate after finishing',
            three_activity_days: 'Read on three days',
            tracked_finish: 'Finish the book',
          },
        },
        allComplete: {
          body: 'Explore your collection or return when new achievements arrive.',
          title: 'All introductory achievements complete',
        },
        annualGoal: {
          accessibilityProgress: '{{year}} reading goal, {{current}} of {{target}} books',
          bookAccessibility: '{{title}}, completed {{date}}',
          booksCounted: 'Books counted',
          booksCountedBody_one: '{{count}} distinct work completed in this calendar year.',
          booksCountedBody_other: '{{count}} distinct works completed in this calendar year.',
          completed: 'Complete',
          completedBody:
            "You reached this year's target. Every additional finished book still stays in the record below.",
          completedOn: 'Completed {{date}}',
          completedTitle: 'Goal reached',
          compactProgress: '{{current}} / {{target}}',
          compactSet: 'Set goal',
          create: 'Set annual goal',
          detailProgress: '{{current}} of {{target}} books',
          detailRemaining_one: 'One book remains to reach your target.',
          detailRemaining_other: '{{count}} books remain to reach your target.',
          detailTitle: '{{year}} reading goal',
          edit: 'Edit goal',
          editor: {
            body: 'Choose a personal target. It is a planning tool, not an achievement or collectible requirement.',
            create: 'Set goal',
            createTitle: 'Choose your target',
            decrement: 'Decrease annual book target',
            editTitle: 'Change your target',
            eyebrow: '{{year}} · PERSONAL GOAL',
            hint: 'Enter a whole number from 1 to 999.',
            increment: 'Increase annual book target',
            invalid: 'Choose a whole number from 1 to 999.',
            label: 'Books to finish',
            preciseEntry: 'Tap to enter an exact number',
            preciseEntryHint: 'Opens the numeric keyboard for direct entry.',
            preciseEntryLabel: 'Enter an exact annual target, currently {{count}}',
            rulerAccessibility_one: 'Annual reading target, {{count}} book',
            rulerAccessibility_other: 'Annual reading target, {{count}} books',
            rulerHint:
              'Swipe left or right to adjust one book at a time. Tap the value for direct entry.',
            save: 'Save goal',
            value_one: '{{count}} book',
            value_other: '{{count}} books',
          },
          emptyBody:
            'Choose how many distinct books you would like to finish this calendar year. You can change it later.',
          emptyTitle: 'Set your {{year}} reading goal',
          eyebrow: '{{year}} · PERSONAL GOAL',
          historyEmptyBody: 'There is no saved personal target for this calendar year.',
          historyEmptyTitle: 'No goal was set for {{year}}',
          keep: 'Keep goal',
          loadFailed: 'Annual goal could not be loaded. Try again.',
          loadFailedTitle: 'Your annual goal is unavailable',
          loading: 'Loading annual goal',
          noBooksBody: 'Finish a book in {{year}} and it will appear here automatically.',
          noBooksTitle: 'No completed books yet',
          openBookHint: "Opens this book's details.",
          openHint: 'Opens your annual goal and the books counted toward it.',
          pace: {
            complete: 'The target is already reached, so no remaining pace is needed.',
            label: 'Pace snapshot',
            remaining_one:
              'With {{count}} day left in {{year}}, the remaining target averages about {{rate}} books per month.',
            remaining_other:
              'With {{count}} days left in {{year}}, the remaining target averages about {{rate}} books per month.',
          },
          progress: '{{current}} / {{target}} books',
          refreshFailed: 'Showing the last saved goal. Fresh progress could not be loaded.',
          remaining_one: '{{count}} left',
          remaining_other: '{{count}} left',
          remove: 'Remove annual goal',
          removeBody: 'Your books and reading history will stay exactly as they are.',
          removeConfirm: 'Remove goal',
          removeFailed: 'The annual goal could not be removed. Try again.',
          removeTitle: 'Remove your {{year}} goal?',
          retryHint: 'Reloads your annual reading goal.',
          saveFailed: 'The annual goal could not be saved. Try again.',
          set: 'Set a {{year}} goal',
          title: 'Annual goal',
        },
        catalogue: {
          filters: {
            all: 'All',
            library: 'Library',
            reading: 'Reading',
            reflection: 'Reflection',
          },
          status: {
            completed: 'Complete',
            inProgress: 'In progress',
            notStarted: 'Not started',
            unavailable: 'Status unavailable',
          },
          intro:
            'Challenges you complete by reading. Most earn a badge or a new look for your library.',
          filtersLabel: 'Show achievements',
          stillOpen: 'Still open',
          left: '{{count}} left',
          complete: 'Complete',
          completeCount: '{{completed}} of {{total}}',
          earned: 'Earned {{date}}',
          earnedUnknown: 'Earned date unavailable',
          noReward: 'No reward',
          everyAchievement: 'Every achievement',
          allComplete: 'All {{count}} complete',
          dateRange: 'Earned between {{first}} and {{last}}',
          dateRangeUnknown: 'Every reading challenge complete',
          rewardGrid: 'Your completed achievements',
          share: 'Share',
          shareTitle: '{{name}} earned all {{count}}',
          shareAnonymous: 'All {{count}} earned',
          shareEyebrow: 'Reading achievements',
          shareFooter: 'A reading life, kept in Belore',
          shareFailed: 'The image could not be shared. Please try again.',
          shareUnavailable: 'Image sharing is unavailable on this device.',
          shareNeedsBuild: 'Image sharing needs an updated development build.',
          shareNeedsUpdate: 'Update the app to share achievement images.',
          title: 'Discover achievements',
        },
        celebration: achievementCelebrationTranslations.en,
        categories: {
          library: 'Library',
          reading: 'Reading',
          reflection: 'Reflection',
        },
        collection: {
          inUse: 'In use',
          preview: 'Preview',
          inUseCount: '{{count}} in use',
          onShelfCount: '{{count}} on a shelf',
          onBoardCount: '{{count}} on the badge board',
          onShelf: 'On a shelf',
          notPlaced: 'Not placed yet',
          onBoard: 'On your badge board',
          notOnBoard: 'Not on your badge board yet',
          moreToEarn: 'More to earn',
          putOnBoard: 'Put on badge board',
          editBoard: 'Edit badge board',
          boardFull: 'Your badge board is full. Edit it to make room.',
          seeAchievement: 'See how you earned it',
          earnedDate: 'Badge · earned {{date}}',
          useAppearance: 'Use this appearance',
          appearanceBody:
            'Changes the bookcases and the reading stand. Your books and shelf pieces stay where they are.',
          currentAppearance: 'In use now',
          replacesAppearance: 'Replaces {{name}} · you can switch back any time',
          placePiece: 'Place on a shelf',
          movePiece: 'Move to another shelf',
          usePot: 'Use with a plant',
          placementUnknown: 'Placement could not be checked',
          saveFailed: 'Could not save this change. Please try again.',

          badge: 'Badge',
          badges: 'Badges',
          discoverAction: 'Discover achievements',
          emptyBody:
            'Complete achievements and your earned badges, shelf pieces and library appearances will show up here.',
          emptyTitle: 'Your collection is waiting',
          filterEmptyBody:
            'Nothing of this type has been added yet. Your other rewards are still in the collection.',
          filterEmptyTitle: 'No rewards of this type yet',
          filters: {
            all: 'All',
            badges: 'Badges',
            objects: 'Shelf pieces',
            themes: 'Appearances',
          },
          filtersLabel: 'Filter collection by reward type',
          inspectTheme: 'View appearance',
          intro: 'Everything here was earned by reading. Tap one to use it in your library.',
          items: {
            artDecoBookmark: 'Art Deco bookmark',
            artDecoReadingLamp: 'Art Deco reading lamp',
            oxbloodVessel: 'Oxblood vessel',
            readingSocietyMedallion: 'Reading Society medallion',
            soapstoneOwl: 'Soapstone owl',
          },
          shelfPiece: 'Shelf piece',
          shelfPieces: 'Shelf pieces',
          shelfPiecesBody:
            'Larger pieces keep their real visual importance; smaller badges remain a separate collection.',
          loading: 'Loading your collection',
          showAll: 'Show all',
          source: {
            achievementsCached:
              'Showing saved badges. New achievement rewards could not be checked.',
            achievementsUnavailable:
              'Badges could not be checked. Other reward types may still be available below.',
            objectsInvalid:
              'Shelf-piece ownership could not be verified, so those items are not being treated as an empty collection.',
            objectsUnavailable:
              'Shelf pieces could not be loaded. Other verified rewards are still shown below.',
            partialTitle: 'Collection may be incomplete',
            themesCached:
              'Showing saved library appearances while the current entitlement source is offline.',
            themesUnavailable:
              'Library appearances could not be loaded. Other verified rewards are still shown below.',
            unavailableBody:
              'None of the reward sources could be checked. Try again to load your verified collection.',
            unavailableTitle: 'Collection unavailable',
          },
          themePack: 'Library appearance',
          themes: 'Appearances',
          title: 'Collection',
        },
        context: {
          followed: 'Followed achievement',
          near: 'Within reach',
          recommended: 'Recommended',
        },
        detail: {
          challenge: 'Challenge',
          followShort: 'Follow',
          followingShort: 'Following',
          yourReading: 'Your reading',
          steps: 'Steps',
          chooseBook: 'Choose a book',
          changeBook: 'Change book',
          chooseBookBody: 'Choose the book for this achievement.',
          selectionFailed: 'The book could not be changed. Try again.',
          noBooks: 'Add a book to your library to start.',
          sameBook1: 'This step belongs to this book',
          sameBook2: 'Both steps belong to this book',
          sameBook3: 'All three steps belong to this book',
          sameBook4: 'All four steps belong to this book',
          sameBookMany: 'All {{count}} steps belong to this book',
          starsEnough: 'just the stars are enough',
          ratingValue: '{{rating}} out of 5 stars',
          rewardUnlocked: 'Reward · unlocked',
          unlocksByRating: 'Unlocks with your rating',
          unlocksWhenComplete: 'Unlocks when the steps are complete',
          yoursNow: 'Now it’s yours',
          preview: 'Preview',
          lastStep: 'The last step to your reward',
          nextStep: 'Your next step',
          rateBook: 'Rate the book',
          readBook: 'Continue reading',
          finishBook: 'Finish the book',
          startBook: 'Start reading',
          applyTheme: 'Apply appearance',
          themeApplied: 'Appearance applied',
          themeCaption: 'You can change it anytime in Edit library',
          actionFailed: 'The reading action could not be saved. Try again.',
          themeFailed: 'The appearance could not be applied. Try again.',
          stepAccessibility: '{{step}}, {{status}}',
          dateListEnd: '{{start}} and {{end}}',
          finishedOn: '{{weekday}} {{date}}',
          weekdays: {
            '0': 'on Sunday',
            '1': 'on Monday',
            '2': 'on Tuesday',
            '3': 'on Wednesday',
            '4': 'on Thursday',
            '5': 'on Friday',
            '6': 'on Saturday',
          },
          earnedOn: 'Completed {{date}}',
          follow: 'Follow achievement',
          followOutcomes: {
            followed: 'This achievement is now in your focused set.',
            limit_reached: 'You can follow up to three achievements. Stop following one first.',
            unavailable: 'This achievement cannot be followed right now.',
            unfollowed: 'This achievement is no longer in your focused set.',
          },
          noReward: 'Milestone · No collectible reward',
          notFound: 'This achievement could not be found.',
          progress: 'Progress',
          reward: 'Reward',
          rewardUnavailable: 'Reward details unavailable',
          title: 'Achievement',
          unfollow: 'Stop following',
        },
        feedback: {
          actions: {
            continue: 'Continue',
            done: 'Done',
            revealReward: 'Reveal reward',
            viewCollection: 'View collection',
            viewThemes: 'View appearances',
          },
          confirmation: {
            archive: 'ACHIEVEMENT COMPLETE',
            tracked: 'ACHIEVEMENT COMPLETE',
          },
          result: {
            alreadyOwned: 'Already in your collection',
            badgeAdded: 'Added to your collection',
            milestone: 'MILESTONE COMPLETE',
            themeAdded: 'Appearance added',
            unavailable: 'The achievement is complete. Its reward is temporarily unavailable.',
          },
          reward: {
            badge: 'NEW BADGE',
            theme: 'NEW LIBRARY APPEARANCE',
          },
          progress: {
            compactValue: '{{current}} / {{target}}',
            dismissAccessibility: 'Dismiss progress update',
            eyebrow: 'JOURNEY PROGRESS',
            headline: 'One step closer',
            multiple: '{{count}} achievements moved forward',
            multiple_one: '{{count}} achievement is moving forward',
            multiple_other: '{{count}} achievements are moving forward',
            multipleBody: 'Open Journey to review the confirmed progress.',
            more_one: '+{{count}} more achievement',
            more_other: '+{{count}} more achievements',
            motivationMany: 'Keep going — the reward is within reach',
            motivationOne: 'Just one more step to the reward',
            openMultiple: 'View Journey',
            openSingle: 'View challenge',
            single: '+{{delta}} {{unit}} · now {{current}} of {{target}}',
          },
          summary: {
            archiveBody: 'Your saved history advanced these achievements.',
            archiveTitle: 'Your journey moved forward',
            importBody:
              'Your reading history was added without turning it into a chain of reveals.',
            importTitle: 'History added',
            remainingBody: 'These achievements were completed by the same action.',
            remainingTitle: 'And there is more',
          },
        },
        groups: {
          library_discovery: 'Discovery',
          library_foundations: 'Library foundations',
          reading_completion: 'Finishing',
          reading_habits: 'Habits',
          reading_ritual: 'Reading Ritual',
          reflection_notes: 'Notes',
        },
        progress: {
          betweenBookmarks: {
            active: 'Active reading attempt',
            activeWithBest: 'Active attempt · previous best {{best}} of {{target}} {{unit}}',
            bestValue: 'Best attempt · {{current}} of {{target}} {{unit}}',
            newAttemptRequired: 'Start a new reading attempt to continue this challenge.',
          },
          dimensions: {
            days: 'Reading days',
            weeks: 'Active weeks',
          },
          readingRhythm:
            '{{daysCurrent}}/{{daysTarget}} days · {{weeksCurrent}}/{{weeksTarget}} weeks',
          ritualStepState: '{{step}}: {{state}}',
          ritualSteps: {
            post_finish_rating: {
              long: 'Rating after finishing',
              short: 'Rating',
            },
            three_activity_days: {
              long: 'Three activity days',
              short: '3 days',
            },
            tracked_finish: {
              long: 'Finish the book',
              short: 'Finish',
            },
          },
          stepComplete: 'Complete',
          stepIncomplete: 'Still to do',
          unavailable: 'Progress unavailable',
          units: {
            books: 'books',
            count: 'items',
            weeks: 'weeks',
            days: 'days',
            genres: 'genres',
            ratings: 'ratings',
            reviews: 'reviews',
            sessions: 'sessions',
            steps: 'steps',
            works: 'works',
          },
          value: '{{current}} of {{target}} {{unit}}',
        },
        rewards: {
          badge: 'Reward · Badge',
          themePack: 'Reward · Library appearance',
        },
        summary: {
          completed: '{{completed}} of {{total}} complete',
        },
        unavailable: {
          body: 'Open Discover to review the achievement catalogue while progress refreshes.',
          title: 'Journey progress is unavailable',
        },
      },
      dataErrors: {
        bookNotFound: 'Book not found',
        bookRelationNotFound: 'Book relation not found',
        noCurrentReading: 'There is no currently reading book',
        requestFailed: 'The library could not complete that request.',
      },
      legal: {
        title: 'Legal information',
        terms: 'Terms & Conditions',
        privacy: 'Privacy Policy',
        opensBrowser: 'Opens the Belore website in your browser.',
        openError: 'Could not open the page. Try again or visit {{url}} in your browser.',
      },
      accountDeletion: {
        action: 'Delete account',
        hint: 'Permanently delete your Belore account and personal reading data.',
        title: 'Delete your account?',
        body: 'Your profile, library, reading history, notes, saved quotes and reviews will be permanently removed. This cannot be undone.',
        catalogueNote:
          'Approved contributions to the shared book catalogue may remain without your account attribution.',
        acknowledgement: 'I understand that deletion is permanent.',
        confirm: 'Permanently delete account',
        deleting: 'Deleting your account… Keep this screen open.',
        retry: 'Retry deletion',
        error:
          'We could not confirm that deletion finished. Try again to check and complete the request.',
        unavailable: 'Account deletion is temporarily unavailable. Please try again later.',
        retryRequired:
          'Account deletion has started and access is blocked. Removal is still finishing automatically. Retry to check its status; closing this screen does not cancel deletion.',
      },
      profileRedesign: {
        photoLibraryPermission: 'Belore uses your photo library to choose a profile photo.',
        usePhoto: 'Use photo',
        account: 'Account',
        save: 'Save',
        username: 'Username',
        name: 'Name',
        email: 'Email',
        library: 'Books',
        app: 'App',
        privacyTerms: 'Privacy and terms',
        languages: 'Languages',
        quotesPrivacy: 'Quotes and privacy',
        developer: 'Developer · dev builds only',
        changePhoto: 'Change photo',
        emailHint:
          'Your username is yours alone on Belore. It is shown above your name and on thoughts you make public. Your email is never shown.',
        usernameHint:
          '3 to 30 characters: uppercase and lowercase letters (A–Z), numbers, dots and underscores. Shown above your name and on thoughts you make public.',
        available: '@{{username}} is available',
        taken: 'This username is already taken',
        invalid: 'Use 3–30 uppercase and lowercase letters (A–Z), numbers, dots or underscores',
        reserved: 'This username is reserved',
        checking: 'Checking availability…',
        checkError: 'Could not check availability. Try again.',
        clear: 'Clear username',
        saveError: 'Could not save your changes. Please try again.',
        appLanguage: 'App language',
        readingCount: 'Languages I read · {{count}}',
        contentCount: 'Content & recommendations · {{count}}',
        addLanguages: 'Add or remove languages',
        readingHint: 'Default language filters in Find only. Choose 1 to 12.',
        contentHint:
          'Languages for quotes, reviews and Board recommendations. Book pages also include the edition’s language. Choose 1 to 12.',
        selected: 'Selected · {{count}} of 12',
        allLanguages: 'All languages',
        takePhoto: 'Take a photo',
        choosePhoto: 'Choose from library',
        providerPhoto: 'Use photo from sign-in',
        removePhoto: 'Remove photo',
        initials: 'Initials instead',
        photoHint: 'Square, shown small. Only your newest photo is kept.',
        photoError: 'Could not update your photo. Please try again.',
        photoPermission: 'Allow camera access in your device settings to take a photo.',
        photoHidden:
          'Your previous photo was hidden by a moderator. You can choose a different photo.',
        completionBody: 'Choose your name and a unique username to finish your profile.',
        privacyIntro: 'What other readers can see of what you keep in Belore.',
        privacySave: 'Saves are counted without your name',
        privacySaveBody: 'Each quote you save from a catalogue book adds one reader to that line.',
        privacyPopular: 'Saved lines can appear on the book’s page',
        privacyPopularBody:
          'Once a moderator approves their wording. Only the book’s words, never your name or your thought.',
        privacyThought: 'Your thoughts stay private',
        privacyThoughtBody:
          'Until you choose to make one public. Public thoughts are reviewed before they appear.',
        privacyNotes: 'Notes are always private',
        privacyCustom: 'Books you added yourself never count',
        you: 'You',
        searchLanguages: 'Search languages',
        privacyMore: 'More in the <privacy>Privacy policy</privacy>.',
        nameHint: 'The name shown on your profile.',
        version: 'Belore {{version}}',
      },
      profile: {
        readingLanguages: 'Languages I read',
        readingLanguagesHint: 'Default language filters in Find only. Choose 1 to 12.',
        contentLanguages: 'Content languages',
        contentLanguagesError: 'Could not save or load content languages. Tap to retry.',
        readingLanguagesError: 'Could not save or load reading languages. Tap to retry.',

        blenderLibrary: 'Living cabinet',
        blenderLibraryAccessibilityHint:
          'Switches between the live layered walnut cabinet and the original library artwork.',
        blenderLibraryAccessibilityLabel: 'Living cabinet',
        blenderLibraryBody:
          'Use the live walnut chamber with books, relics and authored light. Turn it off to return to the original library artwork.',
        body: 'Manage your account and bring your reading history into one library.',
        completion: {
          body: 'Choose the name readers will see beside your public reviews. Save it before entering your library.',
          continue: 'Continue to my library',
          errors: {
            required: 'Enter a name to continue.',
            too_long: 'Use 120 characters or fewer.',
          },
          eyebrow: 'ONE LAST DETAIL',
          loadErrorBody:
            'We can’t reach your reader profile right now. Give it another try in a moment.',
          loadErrorEyebrow: 'A LITTLE INK MISHAP',
          retrying: 'Trying again…',
          loadErrorTitle: 'We could not load your profile',
          loadingBody: 'Making sure your reader profile is ready.',
          loadingTitle: 'Checking your profile…',
          nameHint:
            'Use your real name or a pen name. This is the name shown publicly with approved reviews.',
          nameLabel: 'Public reader name',
          namePlaceholder: 'Your name or pen name',
          saveError: 'We could not save your profile. Check your connection and try again.',
          signedInAs: 'Signed in as {{email}}',
          signOutError: 'We could not finish signing out. Try again.',
          title: 'Complete your profile',
          useAnotherAccount: 'Use another account',
        },
        goodreadsImportAccessibilityHint:
          'Opens guided instructions and a Goodreads CSV file preview.',
        goodreadsImportBody: 'Books, shelves, ratings and reviews.',
        goodreadsImportTitle: 'Import from Goodreads',
        dataExport: {
          title: 'Download my data',
          body: 'Your library for other apps, quotes and notes as text, and a full backup.',
          chooseFormat: 'Choose what you want to download.',
          formats: {
            backup: {
              title: 'Full Belore backup',
              body: 'Everything you have saved: a full backup, library CSV, quotes and notes in one ZIP.',
            },
            goodreads: {
              title: 'Library for other apps',
              body: 'Books, shelves, ratings and reviews as a CSV in Goodreads format.',
            },
            notebook: {
              title: 'Quotes and notes',
              body: 'Your writing as Markdown text, organized by book in one ZIP.',
            },
          },
          preparing: 'Preparing your data…',
          retry: 'Try downloading again',
          successNative: 'Your export is ready to save or share.',
          successWeb: 'Your download has started.',
          errors: {
            download: 'We could not download your data. Check your connection and try again.',
            rateLimit:
              'You can download your data once a minute. Please wait a moment and try again.',
            sharingUnavailable: 'Sharing is unavailable on this device. Please try again.',
          },
        },
        illustratedLibraryPrototype: 'Illustrated library prototype',
        illustratedLibraryPrototypeHint: 'Opens the separate illustrated library preview.',
        language: 'App language',
        languageBody: `Use your device language or choose one for ${APP_NAME}.`,
        languageSelectorAccessibilityHint: 'Opens the list of supported languages.',
        languageSelectorAccessibilityLabel: 'App language, {{language}}',
        developerLabs: {
          textRecognition: 'Text recognition lab',
          textRecognitionAccessibilityHint:
            'Compares Apple Vision and Google ML Kit on a photographed page.',
          title: 'Developer labs',
        },
        journeyTesting: {
          body: 'Preview Journey feedback without changing your library or rewards.',
          completionAccessibilityHint: 'Shows the animated achievement completion ceremony.',
          completionAction: 'Test achievement completion',
          title: 'Journey testing',
        },
        languages: {
          czech: 'Čeština',
          english: 'English',
          slovak: 'Slovenčina',
        },
        libraryTools: 'Library tools',
        libraryToolsBody: `Move an existing reading history into ${APP_NAME}.`,
        readerFallback: `${APP_NAME} reader`,
        settings: 'App settings',
        settingsBody: `Choose how ${APP_NAME} looks and responds on this device.`,
        soundEffectsBody: 'Sounds for progress on your Journey.',
        soundEffectsTitle: 'Sound effects',
        hapticsTitle: 'Haptic feedback',
        hapticsBody: 'Gentle feedback when selecting and saving.',
        hapticsSaveFailed: 'Applied for now. Could not save this preference for your next visit.',
        systemDefault: 'System default',
        openHint: 'Opens your books and settings.',
        title: 'Your profile',
        settingsTitle: 'Settings',
        settingsHint: 'Opens app and account settings.',
        booksPrivate: 'Only you can see your books and shelves',
      },
      goodreadsImport: {
        unmatched: {
          title: 'Books without a catalogue match',
          body: 'Choose which books to keep as custom books in your library. We will use the details and reading history from your Goodreads export. These books and their reviews stay private.',
          selected: '{{selected}} of {{total}} selected',
          selectAll: 'Select all',
          deselectAll: 'Deselect all',
          summary:
            'Books to import: {{count}} · Custom books: {{selected}} · Deselected: {{skipped}} · Conflicts: {{conflicts}}',
          skipped: 'Skipped',
          conflicts: 'Conflicts',
        },
        backToProfile: 'Back to profile',
        body: `Goodreads prepares the catalogue. ${APP_NAME} reads it on this device first, shows a summary, then lets you map custom reading-status shelves.`,
        changeFile: 'Change',
        chooseCsv: 'I already have the CSV',
        chooseDestination: `Choose its ${APP_NAME} destination`,
        completed: {
          body: 'The import finished safely. {{changed}} of {{count}} books changed your library.',
          created: 'New books added',
          openLibrary: 'Open My Books',
          ratings: 'Ratings imported',
          reviews: 'Reviews imported',
          summaryTitle: 'Import result',
          title: 'Your Goodreads library is imported',
          unchanged: 'Books already up to date',
          updated: 'Existing books updated',
        },
        errors: {
          empty_file: 'The selected file is empty.',
          file_too_large: 'Choose a Goodreads CSV smaller than 10 MB.',
          malformed_csv: 'The CSV is damaged or has an unfinished quoted value.',
          missing_headers: 'This does not look like a Goodreads library export.',
          no_books: 'The Goodreads export does not contain any books.',
          not_csv: 'Choose the .csv file downloaded from Goodreads.',
          open_failed: 'The Goodreads export page could not be opened.',
          read_failed: 'The file could not be read. Try downloading it again.',
          title: 'We could not preview this file',
        },
        importErrors: {
          apply_failed: 'Nothing was partially imported. Try the import again.',
          community_terms_required:
            'Public reviews need the updated Terms of Use. Import them as private for now; Belore will ask you to accept the Terms when you next open it.',
          public_posting_restricted:
            'Public posting isn’t available for your account right now. Import your reviews as private instead.',
          feature_unavailable:
            'Goodreads importing is temporarily unavailable on the server. Your library has not changed.',
          plan_changed:
            'Your library changed or this plan expired. Return to the review and prepare a fresh plan.',
          prepare_failed:
            'The server could not prepare this import. The CSV remains only on this device; try again.',
          server_shelves_unresolved:
            'The server found a custom reading-status shelf that still needs a destination. Review the shelf mapping and try again.',
          title: 'The import could not continue',
        },
        fileSizeKb: '{{count}} KB',
        finishReview: 'Finish review',
        handoffBody:
          'This screen prepared a verified plan only. No books, ratings, reviews or choices were saved or applied.',
        handoffTitle: 'Import is not active yet',
        kicker: 'An outside catalogue',
        instructions: {
          chooseBody: 'Return here and select the downloaded goodreads_library_export.csv file.',
          chooseTitle: 'Choose the downloaded CSV',
          exportBody:
            'Go directly to https://goodreads.com/review/import, or open My Books → Import/Export in Goodreads. Select Export Library and wait for Goodreads to prepare the file.',
          exportTitle: 'Export your library',
          openBody: `Sign in directly on Goodreads. ${APP_NAME} never asks for your Goodreads password.`,
          openTitle: 'Open Goodreads',
        },
        mappingBody:
          'Known Goodreads reading statuses are mapped automatically. Custom categories stay separate from reading status.',
        mappingTitle: 'Check your shelves',
        metrics: {
          books: 'Books found',
          ratings: 'Ratings',
          reviews: 'Reviews',
        },
        noExclusiveShelf: 'No exclusive shelf',
        openGoodreads: 'Open Goodreads export',
        privateBody:
          'The file is read on this device first. You see a summary and representative samples before anything can change.',
        privateTitle: 'Your password stays with Goodreads',
        progress: {
          accessibility: 'Stage {{current}} of {{total}}',
          export: 'Export',
          ready: 'Ready',
          review: 'Review',
        },
        readyPlan: {
          body: 'The server verified {{count}} books: {{matched}} match the catalogue and {{create}} will be created.',
          body_one:
            'The server verified {{count}} book: {{matched}} matches the catalogue and {{create}} will be created.',
          importBooks: 'Import {{count}} books',
          importBooks_one: 'Import {{count}} book',
          noEffect:
            'Nothing has changed in your library yet. Import Books applies the whole plan atomically—if it fails, no partial import is kept.',
          ratingsExcluded: 'Ratings: {{count}} ratings stay outside community recommendation use.',
          ratingsIncluded:
            'Ratings: permission covers {{count}} ratings for future community recommendations.',
          reviewsExcluded: 'Reviews: {{count}} reviews stay unpublished.',
          reviewsIncluded:
            'Reviews: permission covers {{count}} reviews for future publication on public book pages.',
          title: 'Choices in this plan',
        },
        readyBody:
          'The review for {{count}} books is complete. This is a plan only; no library data has changed.',
        readyBody_one:
          'The review for {{count}} book is complete. This is a plan only; no library data has changed.',
        readyTitle: 'Your import plan is ready',
        reviewPlan: {
          allShelvesBody:
            'Known reading statuses are mapped automatically, and every custom status shelf now has a destination. Other Goodreads categories stay separate below.',
          allShelvesTitle: 'Reading statuses are mapped',
          booksAndHistory: 'Books & reading history',
          bookDetails: 'Book details',
          bookSamplesShown: 'Showing {{shown}} of {{total}} books as a sample.',
          booksSummary: '{{reading}} Reading · {{tbr}} TBR · {{read}} Read · {{dnf}} DNF',
          changeFileHint: 'Choose a different Goodreads CSV and replace this preview.',
          choose: 'Choose',
          continue: 'Review details & permissions',
          categorySamplesShown: 'Showing {{shown}} of {{total}} category labels.',
          customCategories: 'Goodreads categories',
          customCategoryNone: 'No custom Goodreads categories were found.',
          customCategoryPolicy:
            'Categories stay separate from reading status; they are not flattened into Read. This plan records them for future category support.',
          customCategorySummary: '{{count}} labels across {{assignments}} book placements',
          customShelvesBody:
            'Choose the closest reading status for each custom exclusive shelf. Its original Goodreads name stays visible in this preview.',
          dateAdded: 'Date added',
          dateRead: 'Date read',
          detailsBody:
            'Open any section for a sample and decide which permissions to include in the plan.',
          detailsTitle: 'Review your import plan',
          fileChecked: 'CSV checked on this device',
          formats: 'Book formats',
          groupedBody:
            'Large libraries stay folded into summaries. The detailed review shows samples and exact counts.',
          included: 'Included',
          invalidRatings: '{{count}} invalid ratings were treated as unrated.',
          invalidRatings_one: '{{count}} invalid rating was treated as unrated.',
          invalidReadCounts: '{{count}} invalid read counts were ignored.',
          invalidReadCounts_one: '{{count}} invalid read count was ignored.',
          duplicateBookIds: '{{count}} duplicate Goodreads book IDs were ignored.',
          duplicateBookIds_one: '{{count}} duplicate Goodreads book ID was ignored.',
          isbn10: 'ISBN-10',
          isbn13: 'ISBN-13',
          metadataSummary: 'ISBNs, editions, publishers, dates and read counts',
          noRatingsConsent: 'This file has no valid ratings to include.',
          noReviewsConsent: 'This file has no written reviews to include.',
          nothingChanged: 'This is a local preview. No library data has changed.',
          noWrittenReviews: 'This file does not contain any written reviews.',
          overviewBody:
            'We grouped a large library into a few clear sections. Nothing has changed in your library.',
          overviewTitle: 'Here is what we found',
          planOnly:
            'This uploads the CSV to prepare a secure server-side plan. Your library changes only after you confirm Import Books on the next screen.',
          preparePlan: 'Prepare import plan',
          publishers: 'Publishers',
          qualityTitle: 'Some rows need attention',
          ratingsConsentBody: `Allow these {{count}} ratings to improve recommendations across ${APP_NAME}. Ratings will not be published as reviews.`,
          ratingsConsentTitle: 'Use my ratings for community recommendations',
          ratingsSummary: '{{rated}} rated · {{unrated}} without a valid rating',
          readCountSummary: 'Recorded reads: {{reads}} · Books read more than once: {{rereads}}',
          resolveToContinue: 'Map {{count}} custom status shelves to continue',
          resolveToContinue_one: 'Map this custom status shelf to continue',
          reviewSamplesShown: 'Showing {{shown}} of {{total}} reviews as a sample.',
          reviewsConsentBody: `Allow these {{count}} reviews to appear on public book pages with my ${APP_NAME} profile.`,
          reviewsConsentTitle: 'Publish my written reviews on book pages',
          reviewsSummary: '{{count}} written reviews',
          reviewsSummary_one: '{{count}} written review',
          sharingBody:
            'Choose how imported ratings and reviews may be used. You can turn either permission off before importing.',
          sharingFootnote: 'These choices are recorded with the import and can be changed later.',
          sharingTitle: 'Permissions to include',
          shelvesNeedChoice: '{{count}} custom status shelves need a destination',
          shelvesNeedChoice_one: 'One custom status shelf needs a destination',
          statusDestination: `${APP_NAME} reading status`,
          skippedRows: '{{count}} unusable rows will be left out of the plan.',
          skippedRows_one: '{{count}} unusable row will be left out of the plan.',
          starRating: '{{count}} stars',
          starRating_one: '{{count}} star',
          unrated: 'Unrated',
          unratedExplanation: '{{count}} books have no valid Goodreads rating.',
          unratedExplanation_one: '{{count}} book has no valid Goodreads rating.',
          whatWillImport: 'What this plan contains',
          writtenReviews: 'Written reviews',
        },
        reviewAgain: 'Review shelf mapping',
        reviewBody: 'Check what we found and decide where custom Goodreads shelves belong.',
        reviewTitle: 'Review before importing',
        status: {
          currently_reading: 'Reading',
          did_not_finish: 'DNF',
          read: 'Read',
          to_be_read: 'TBR',
          unclassified: 'Choose a destination',
        },
        title: 'Bring your books through the threshold',
        toolbarTitle: 'Goodreads import',
        unresolved: 'Choose a destination for {{count}} custom shelves to continue.',
        unresolved_one: 'Choose a destination for this custom shelf to continue.',
      },
      author: {
        biography: 'Biography',
        accessibility: {
          openProfile: 'Open author profile for {{name}}',
          openSource: 'Open the profile source',
          photo: 'Portrait of {{name}}',
        },
        booksBy: 'Books by the author',
        booksRating: "{{value}} · {{count}} ratings across this author's books on Open Library",
        booksRating_one: "{{value}} · {{count}} rating across this author's books on Open Library",
        booksRatingAccessibility: 'Books by this author average {{value}} out of 5 on Open Library',
        emptyBooks: 'No ISBN-backed books are available for this author yet.',
        emptyQuotes: 'You have not saved a quote from this author yet.',
        loadError: 'This author profile could not be loaded. Please try again.',
        loading: 'Finding {{name}}…',
        loadingQuotes: 'Finding your quotes…',
        openLibraryBorn: 'Author · Born {{date}}',
        openLibraryProfile: 'Author profile',
        seeMoreInOpenLibrary: 'See more in Open Library',
        shareFailed: 'The author profile could not be shared.',
        shareMessage: '{{name}}\n{{url}}',
        title: 'Author',
        unknownName: 'Unknown author',
        viewAll: 'View all',
        website: 'Website',
        yourQuotes: 'Your quotes',
      },
      launch: {
        illustrated: {
          tagline: 'Build a library of your own.',
          preparing: 'Preparing your library…',
          restoring: 'Restoring your library…',
          signingIn: 'Signing you in…',
          slow: 'This is taking a little longer.',
          loadError: 'We couldn’t load your library.',
          retry: 'Try again',
          otherAccount: 'Use another account',
        },
        authenticated: {
          body: 'Your session is ready. The Reading Thread is unlocking the doors.',
          title: 'The latch recognizes you',
        },
        authorizing: {
          body: 'Finish signing in securely. The doors will open when you return.',
          title: 'Secure sign-in is open',
        },
        booting: {
          body: 'The Reading Thread is checking for a saved session.',
          title: 'Restoring your library...',
        },
        entered: {
          body: 'Your Reading Chamber is ready.',
          title: 'Library entered',
        },
        entering: {
          body: 'Moving into your current reading space.',
          title: 'Entering your library...',
        },
        error: {
          fallbackBody: 'Please try the secure sign-in again.',
          title: 'The threshold could not open',
        },
        opening: {
          body: 'The Reading Chamber is ready behind the threshold.',
          title: 'Opening your library...',
        },
        sceneLoading: 'Preparing your complete library scene…',
        signOutRemoteUnconfirmed:
          'You are signed out on this device, but remote sign-out could not be confirmed.',
      },
      menu: {
        allBooks: 'All books',
        findBook: 'Find a book',
      },
      tabs: {
        library: 'Library',
        myBooks: 'My Books',
        find: 'Find',
        myJourney: 'Journey',
        journal: 'Journal',
        board: 'Board',
        profile: 'Profile',
      },
      myBooks: {
        loading: 'Loading your books…',
        loadErrorTitle: 'We couldn’t load your books',
        loadErrorBody: 'Check your connection, then try again in a moment.',
        refreshErrorTitle: 'Your books couldn’t be updated',
        refreshErrorBody: 'You can still browse the books shown here. Try again in a moment.',
        chooseNextRead: 'Choose your next read',
        currentReading: 'Currently Reading',
        emptyNoMatches: 'No matching books',
        emptyShelf: 'This shelf is empty',
        findPlace: 'Find a book and give it a place in your library.',
        finishedEmpty: 'Finished books will become part of this shelf.',
        finishedOn: 'Finished {{date}}',
        openTbrToBegin: 'Open your TBR shelf to begin.',
        readShelf: 'Recently Finished',
        searchCatalogue: 'Search the catalogue',
        searchPlaceholder: 'Search my books',
        shelfWaiting: 'Books waiting for you will gather here.',
        title: 'My Books',
        tryAnother: 'Try another title or author.',
        viewAll: 'View all {{count}}',
      },
      personalPageCount: {
        sheetRange: 'Enter a whole number from {{minimum}} to {{maximum}}.',
        add: 'Add page total',
        belowCurrentPage: 'The total cannot be lower than your current page ({{currentPage}}).',
        change: 'Change page total',
        inputLabel: "Your copy's total pages",
        invalid: 'Enter a whole number from 1 to {{maximum}}.',
        placeholder: 'Total pages',
        privateExplanation:
          'This is saved only to your library entry and does not change the shared book record.',
        required: 'Enter the total number of pages.',
        save: 'Save total',
        saveFailed: 'Your page total could not be saved. Please try again.',
        title: 'Your page total',
        update: 'Update total',
      },
      progress: {
        approximatePages: '≈ {{currentPage}} pages',
        completeLabel: 'complete',
        percentValue: '{{progress}}%',
        percentModeCompact: '%',
        completionNote: 'Saving 100% will finish this book and move it to Read.',
        editorBody: 'Enter the page you reached or the percentage you have read.',
        invalidPage: 'Enter a whole page from 0 to {{pageCount}}.',
        invalidPageWithoutTotal: 'Enter a whole page number of 0 or more.',
        invalidPercent: 'Enter a whole percentage from 0 to 100.',
        ofPages: 'of {{pageCount}} pages',
        pageInputLabel: 'Current page',
        pageUnit: 'page',
        pageOfTotal: 'Page {{currentPage}} of {{pageCount}}',
        pageTotalPrefix: 'of',
        pageTotalValue: '{{pageCount}} pages',
        pageRulerHint: 'Tap the number to edit.',
        pagesMode: 'Pages',
        pagesUnit: 'pages',
        percentComplete: '{{progress}}% complete',
        percentInputLabel: 'Current progress',
        percentMode: 'Percent',
        percentSliderHint: 'Tap the number to edit.',
        percentUnit: 'percent',
        resolvedPage: 'Page {{currentPage}} of {{pageCount}} - {{progress}}%',
        saveFailed: 'Your progress could not be saved. Please try again.',
        beingSaved: 'Being saved…',
        savedOffline: 'Saved on this phone · syncs when online',
        syncingOffline: 'Syncing saved progress…',
        saveFailedRestored:
          'Your progress could not be saved. Your previous progress has been restored.',
        updateTitle: 'Update progress',
        moreActions: 'More options for this book',
        openBookDetails: 'Open book details',
        share: 'Share',
        openBookDetailsFor: 'Open book details for {{title}}',
        markDidNotFinish: 'Mark as did not finish',
        markDidNotFinishFailed: 'The book could not be moved to DNF. Please try again.',
        unsaved: {
          title: 'Save your new progress?',
          body: 'Your progress change isn’t saved yet.',
          invalidBody: 'Your progress change isn’t valid, so it can’t be saved.',
          save: 'Save',
          discard: 'Discard',
          cancel: 'Cancel',
          webSave: 'OK saves it. Cancel lets you discard it or keep editing.',
          webDiscard: 'Discard the unsaved progress change?',
          returnToEditing: 'Back to editing',
          webReturn: 'Go back to editing? Cancel discards the change.',
        },
        unknownTotalHint:
          "This edition has no known page total. Enter the current page directly, or add your copy's total below.",
        valueRequired: 'Enter your reading progress.',
      },
    },
  },
  sk: {
    translation: {
      catalogueContributions: catalogueContributionsCopy.sk,
      community: communityCopy.sk,
      textLanguage: textLanguageCopy.sk,
      countFormat: countFormatCopy.sk,
      perspectiveLibrary: perspectiveLibraryCopy.sk,
      noteEditor: {
        fullScreen: 'Celá obrazovka',
        quickHint: 'Dlhšie myšlienky? Otvor celú obrazovku a píš ďalej.',
        wholeBook: 'O celej knihe',
        wholeBookHint: 'Bez strany — na stránke knihy sa zobrazí ako prvá.',
        titlePlaceholder: 'Názov (nepovinné)',
        titleLabel: 'Názov poznámky',
        placeholder: 'Píš ďalej…',
        bodyLabel: 'Tvoja poznámka',
        format: {
          bold: 'Tučné',
          italic: 'Kurzíva',
          underline: 'Podčiarknuté',
          strikeThrough: 'Prečiarknuté',
          unorderedList: 'Odrážkový zoznam',
          orderedList: 'Číslovaný zoznam',
        },
        insertQuote: 'Vložiť citát',
        insert: 'Vložiť',
        scanNew: 'Skenovať nový',
        fromBook: 'Z knihy {{book}}',
        noQuotes: 'Tu sa zobrazia tvoje uložené citáty z tejto knihy.',
        selectQuote: 'Vybrať citát: {{body}}',
        noPage: 'Bez strany',
        quoteRemoved: 'Citát bol odstránený',
        openQuote: 'Otvoriť citát',
        removeLink: 'Odstrániť odkaz',
        hideKeyboard: 'Skryť klávesnicu',
        formatting: 'Formátovanie',
        newPlaceholder: 'Napíš poznámku…',
        page: 's. {{page}}',
        privateShort: 'Súkromné',
        keepWriting: 'Pokračovať v písaní',
        yourNotes: 'Tvoje poznámky',
        yourQuotes: 'Tvoje citáty',
        openJournal: 'Otvoriť v zápisníku',
        journalTitle: 'Zápisník',
        openJournalShort: 'Otvoriť',
        notesCount_one: '{{count}} poznámka',
        notesCount_few: '{{count}} poznámky',
        notesCount_many: '{{count}} poznámky',
        notesCount_other: '{{count}} poznámok',
        quotesCount_one: '{{count}} citát',
        quotesCount_few: '{{count}} citáty',
        quotesCount_many: '{{count}} citátu',
        quotesCount_other: '{{count}} citátov',
        words_one: '{{count}} slovo',
        words_few: '{{count}} slová',
        words_many: '{{count}} slova',
        words_other: '{{count}} slov',
        edited: 'upravené {{date}}',
        minutes: '{{count}} min',
        private: 'Súkromná poznámka',
        untitled: 'Poznámka bez názvu',
        conflictTitle: 'Táto poznámka sa zmenila inde',
        conflictBody:
          'Tvoj koncept aj uložená verzia sú tu. Ponechaj obe ako samostatné poznámky alebo použi uloženú verziu.',
        yourDraft: 'Tvoj koncept',
        savedVersion: 'Uložená verzia',
        keepBoth: 'Ponechať obe poznámky',
        useSaved: 'Použiť uloženú verziu',
        reviewVersions: 'Porovnať verzie',
        drafts: 'Poznámky čakajúce na uloženie',
        resumeDraft: 'Pokračovať: {{title}}',
        draftHint: 'Uložené v tomto zariadení',
        status: {
          saved: 'Uložené',
          dirty: 'Neuložené zmeny',
          saving: 'Ukladá sa…',
          failed: 'Neuložené · Skúsiť znova',
          storageFailed: 'Neuložené',
          conflict: 'Vyber verziu',
          invalid: 'Skontroluj poznámku',
        },
        message: {
          captureFailed:
            'Editor nedokázal načítať posledné zmeny. Nechaj poznámku otvorenú a skús to znova.',
          failed: 'Neuložené. Tvoj koncept je v tomto zariadení. Po pripojení to skús znova.',
          storageFailed:
            'Zariadenie nedokázalo uchovať posledné zmeny. Nechaj poznámku otvorenú a skús to znova.',
          conflict:
            'Inde bola uložená novšia verzia. Tvoj koncept je v bezpečí; vyber verziu, ktorú chceš ponechať.',
          invalid:
            'Pridaj text, názov alebo citát. Poznámka môže mať najviac 20 000 znakov a názov 200 znakov.',
        },
      },
      capture: {
        scan: 'Naskenovať stranu',
        cameraPermission: 'Povoľ Belore skenovať čiarové kódy kníh a fotiť úryvky.',
        fromBook: 'Z knihy',
        typePassage: 'Alebo sem napíš citát…',
        scanAgain: 'Skenovať znova',
        checkWords: 'Naskenované zo strany · skontroluj text',
        fromProgress: 'Z tvojho pokroku',
        foundOnPage: 'Nájdené na strane',
        pageOptional: 'Nepovinná strana',
        optional: 'Nepovinné',
        thoughtPlaceholder: 'Prečo ti utkvel v pamäti?',
        save: 'Uložiť citát',
        retrySave: 'Skúsiť uložiť znova',
        saveUncertain:
          'Uloženie sa nepodarilo potvrdiť. Návrh zostáva tu. Zopakuj rovnaké uloženie, aby nevznikla kópia.',
        choose: 'Vyber text',
        takePhoto: 'Odfotiť',
        retake: 'Odfotiť znova',
        typeInstead: 'Napísať citát',
        retryPhoto: 'Prečítať znova',
        usePassage: 'Použiť tento text',
        useAll: 'Použiť celý text',
        reading: 'Čítam stranu…',
        selectIOS: 'Podrž slovo a potiahni úchyty. Alebo použi celý text a potom ho uprav.',
        selectAndroid: 'Ťukni na slovo, dvakrát na vetu alebo potiahni úchyty.',
        pageFound: 'Nájdená strana {{page}}',
        photoPrivacy: 'Text sa číta v telefóne. Fotka sa neuchováva.',
        unavailable:
          'Skenovanie v tejto verzii alebo na tomto zariadení nie je dostupné. Citát môžeš napísať alebo vložiť.',
        cameraReason: 'Povoľ fotoaparát na skenovanie strany knihy.',
        allowCamera: 'Povoliť fotoaparát',
        openSettings: 'Otvoriť nastavenia',
        scanFailed: 'Fotku sa nepodarilo prečítať. Skús lepšie svetlo alebo citát napíš.',
        noText: 'Text sa nenašiel. Odfotiť stranu môžeš znova alebo citát napíš.',
        typeQuoteInstead: 'Radšej citát napíšem',
        headerSave: 'Uložiť',
        privacyShow: 'Ukáže, ako sa tvoje citáty počítajú',
        privacyHide: 'Skryje vysvetlenie',
        liveTextTitle: 'Text sa nenašiel',
        liveTextBody:
          'Ak je strana na fotke ostrá, je asi vypnutý Text naživo. Belore ním číta stranu priamo v telefóne.',
        liveTextSteps: 'Zapni Text naživo',
        liveTextSettings: 'Nastavenia',
        liveTextGeneral: 'Všeobecné',
        liveTextRegion: 'Jazyk a región',
        liveTextToggle: 'Text naživo',
        liveTextReturn: 'Potom sa vráť sem a fotku prečítame znova.',
        cameraFailed: 'Fotoaparát sa nespustil. Zavri skener a skús to znova.',
        flashOn: 'Zapnúť blesk',
        flashOff: 'Vypnúť blesk',
        pagePhoto: 'Odfotená strana knihy',
        selectionStart: 'Začiatok výberu',
        selectionEnd: 'Koniec výberu',
      },
      journalReturn: {
        latestNote: 'Tvoja posledná poznámka',
        edited: 'upravené {{date}}',
        keepWriting: 'Písať ďalej',
        allNotes: 'Všetky ({{count}}) ›',
        allNotesAccessibility: 'Všetky poznámky k tejto knihe, {{count}}',

        lastSaved: 'Naposledy si si z tejto knihy uložil/a',
        page: 's. {{page}}',
        kept: 'Čo ti zostalo',
        step: '{{current}} z {{total}}',
        summary: '{{quotes}} a {{notes}} počas čítania.',
        quoteCount_one: '{{count}} citát',
        quoteCount_few: '{{count}} citáty',
        quoteCount_other: '{{count}} citátov',
        noteCount_one: '{{count}} poznámka',
        noteCount_few: '{{count}} poznámky',
        noteCount_other: '{{count}} poznámok',
        empty: 'Zatiaľ nič uložené. Pred ďalšou knihou si pridaj citát alebo poznámku.',
        loadFailed: 'Uložené záznamy sa nepodarilo načítať. Skús to znova alebo pokračuj.',
        shareFavourite: 'Zdieľať obľúbený citát',
        chooseQuote: 'Vyber citát na zdieľanie.',
        addOne: 'Pridať ešte jeden',
        continue: 'Pokračovať',
        next: 'Ďalej',
        more: 'Načítať ďalšie záznamy',
      },
      libraryReaders: {
        title: 'Čitatelia tejto knihy',
        accessible: 'Čitatelia tejto knihy. Uloženia: {{readers}}. Srdiečka citátu: {{likes}}.',
        thoughtAccessible: '{{name}}: {{thought}}. Srdiečka myšlienky: {{likes}}.',
      },
      lines: {
        title: 'Riadky z tejto knihy',
        intro:
          'Citáty schválené moderátormi so schválenými verejnými myšlienkami. Citáty za tvojou stranou zostávajú skryté.',
        mostSaved: 'Najviac uložené',
        mostLiked: 'Najviac srdiečok',
        save: 'Uložiť',
        saved: 'Uložené',
        thoughts_one: '{{formattedCount}} myšlienka',
        thoughts_few: '{{formattedCount}} myšlienky',
        thoughts_other: '{{formattedCount}} myšlienok',
        allThoughts: 'Všetky myšlienky ({{formattedCount}})',
        empty: 'Zatiaľ tu nie sú verejné riadky. Ulož si citát, ktorý ti utkvel v pamäti.',
        error: 'Tieto riadky sa nepodarilo načítať.',
        mutationError: 'Zmenu sa nepodarilo uložiť. Skús to znova.',
        more: 'Zobraziť ďalšie',
        viewAll: 'Zobraziť všetky citáty',
        allTitle: 'Citáty z knihy',
        previous: 'Predošlý',
        next: 'Ďalší',
        position: 'Citát {{number}}',
        signIn: 'Prihlás sa a zobraz citáty z tejto knihy.',
        beyond: 'Za tvojou stranou',
        laterThought: 'Hovorí o neskorších častiach',
        page: 's. {{page}}',
        approximatePage: 'približne s. {{page}}',
        yourPage: 'si na s. {{page}}',
        unknownPage: 'Neznáma strana',
        show: 'Ťuknutím zobrazíš',
        reader: 'Čitateľ',
        you: 'Ty',
        addThought: 'Pridať svoju myšlienku',
        thoughtPrompt: 'Pridaj svoju myšlienku…',
        write: 'Napísať',
        makePublic: 'Zverejniť moju myšlienku',
        publicHint: 'Po krátkej kontrole sa zobrazí s tvojím menom pri tomto riadku.',
        privateHint: 'Túto myšlienku vidíš iba ty.',
        spoilerHint: 'Skryje myšlienku, kým čitateľ nedočíta knihu alebo si ju sám nezobrazí.',
        keepsJournal: 'Uložením si ponecháš riadok aj myšlienku vo svojom zápisníku.',
        editInReview: 'Úprava sa kontroluje',
        editNotApproved: 'Úprava nebola schválená',
        approvedStays: 'Počas kontroly úpravy zostáva schválená myšlienka verejná.',
        duplicateThought:
          'Pri tomto riadku už máš verejnú myšlienku. Otvor ju v zápisníku a uprav ju.',
        viewJournal: 'Otvoriť v zápisníku',
        openLine: 'Otvoriť riadok a jeho myšlienky',
        likeThought: 'Páči sa mi myšlienka, {{formattedCount}} srdiečok',
        unlikeThought: 'Odobrať srdiečko myšlienke, {{formattedCount}} srdiečok',
      },
      quoteModeration: {
        declineEditHint: 'Zamietnutím tejto úpravy zostane aktuálne verejné znenie zachované.',
        pair: 'Citát a myšlienka',
        approveBoth: 'Schváliť citát aj myšlienku',
        approveThought: 'Schváliť myšlienku',
        approveQuoteDeclineThought: 'Schváliť citát · zamietnuť myšlienku',
        keepQuoteDeclineThought: 'Ponechať citát · zamietnuť myšlienku',
        declineThoughtOnly: 'Zamietnuť myšlienku · citát nemeniť',
        quoteAlreadyApproved: 'Citát je už schválený',
        skip: 'Zatiaľ preskočiť →',
        returnSkipped: 'Vrátiť preskočené',
        skippedNotice: 'Preskočené počas tejto návštevy. Rozhodnutie sa neuložilo.',
        allSkipped: 'Načítané položky sú odložené. Načítaj ďalšie alebo sa vráť k preskočeným.',
        quoteOnlyNotice: 'Citát je schválený; myšlienka zamietnutá.',

        nav: 'Citáty a myšlienky',
        moderation: 'Moderovanie',
        intro: 'Zdieľané citáty, citáty z katalógu a myšlienky čitateľov na jednom mieste.',
        shared: 'Žiadosť o zdieľanie citátu',
        wording: 'Citát z katalógu',
        thought: 'Verejná myšlienka',
        refresh: 'Obnoviť',
        loading: 'Načítava sa…',
        loadingItems: 'Načítavajú sa položky na kontrolu…',
        waiting: 'čaká',
        matching: 'zodpovedá filtrom',
        content: 'Obsah',
        all: 'Všetok obsah',
        sharedFilter: 'Zdieľané citáty',
        wordingFilter: 'Citáty z katalógu',
        thoughtFilter: 'Myšlienky',
        status: 'Stav',
        pending: 'Čaká na schválenie',
        approvedFilter: 'Schválené citáty z katalógu',
        hiddenFilter: 'Skryté citáty z katalógu',
        search: 'Hľadať',
        searchPlaceholder: 'Názov knihy alebo text',
        clear: 'Vymazať všetky filtre',
        readerHint:
          'Filter čitateľov sa vzťahuje na zdieľané citáty a myšlienky. Prispievatelia do katalógu zostávajú anonymní.',
        empty: 'Nič nečaká na schválenie',
        noMatch: 'Žiadne položky nezodpovedajú filtrom',
        emptyHint: 'Sú tu všetky citáty a myšlienky čakajúce na schválenie.',
        filterHint: 'Zmeň alebo vymaž filtre a zobraz ďalšie položky.',
        more: 'Načítať ďalšie',
        of: 'z',
        loadError: 'Položky na kontrolu sa nepodarilo načítať. Skús obnoviť stránku.',
        approvedNotice: 'Schválené. Zoznam bol obnovený.',
        hiddenNotice: 'Citát je skrytý. Zoznam bol obnovený.',
        declinedNotice: 'Zamietnuté. Zoznam bol obnovený.',
        conflict: 'Táto položka sa počas kontroly zmenila. Pred rozhodnutím obnov zoznam.',
        decisionError: 'Rozhodnutie sa nepodarilo uložiť. Skús to znova.',
        saves: 'uložení čitateľmi',
        awaiting: 'Čaká na schválenie',
        approved: 'Schválené',
        hidden: 'Skryté',
        published: 'Aktuálne verejné',
        edit: 'Odoslaná úprava',
        spoilers: 'Obsahuje spoilery',
        hide: 'Skryť citát',
        decline: 'Zamietnuť',
        approve: 'Schváliť',
        thoughtLabel: 'Myšlienka',
        quoteLabel: 'Citát',
      },
      lineModeration: {
        nav: 'Riadky a myšlienky',
        title: 'Citáty a kontrola myšlienok',
        wordings: 'Citáty z katalógu',
        thoughts: 'Verejné myšlienky',
        intro:
          'Každý verejný citát vyžaduje schválenie znenia moderátorom. Počas uzavretej bety stačí na oprávnenosť jedno uloženie, takže schválením citátu z katalógu sa citát zverejní. Náhľad knihy uprednostňuje obľúbené schválené citáty. Verejné myšlienky a ich úpravy sa schvaľujú samostatne.',
        loading: 'Načítava sa…',
        empty: 'Na kontrolu nič nečaká.',
        refresh: 'Obnoviť',
        more: 'Načítať ďalšie',
        approve: 'Schváliť',
        allowPublication: 'Povoliť zverejnenie',
        hide: 'Skryť citát',
        notPublic: 'Nezverejnené',
        reject: 'Zamietnuť',
        spoilers: 'Hovorí o neskorších častiach',
        published: 'Teraz verejné',
        draft: 'Odoslaný text',
        error: 'Zoznam sa nepodarilo načítať alebo aktualizovať. Skús to znova.',
        conflict: 'Záznam sa zmenil. Obnov zoznam a skontroluj najnovšiu verziu.',
      },
      quoteSharing: {
        title: 'Zdieľať citát',
        ink: 'Atrament',
        paper: 'Papier',
        teal: 'Tyrkysová',
        includeThought: 'Pridať moju myšlienku',
        showCover: 'Zobraziť obálku',
        showName: 'Pridať moje krstné meno: {{name}}',
        myThought: 'Moja myšlienka',
        kept: 'Uložené v Belore',
        keptBy: 'Uložené v Belore · {{name}}',
        save: 'Uložiť obrázok',
        saved: 'Obrázok uložený.',
        unavailable: 'Zdieľanie na tomto zariadení nie je dostupné. Obrázok si môžeš uložiť.',
        failed: 'Obrázok sa nepodarilo exportovať. Skús to znova.',
        tooLong:
          'Tento citát je príliš dlhý na jeden obrázok. Zdieľaj kratší citát alebo vypni svoju myšlienku.',
        preview: 'Náhľad obrázka s citátom: {{text}}',
      },
      journal: {
        deleteErrorTitle: 'Záznam sa nepodarilo odstrániť',
        editInReview: 'Úprava sa kontroluje',
        editNotApproved: 'Úprava nebola schválená',
        wholeBook: 'Celá kniha',
        public: 'Verejná',
        inReview: 'Posudzuje sa',
        notPublished: 'Nezverejnená',
        entryAccessibility: '{{kind}}, {{metadata}}: {{text}}',
        wordCount_one: '{{formattedCount}} slovo',
        wordCount_few: '{{formattedCount}} slová',
        wordCount_other: '{{formattedCount}} slov',
        edited: 'upravené {{date}}',
        thoughtLikes: 'Páči sa {{formattedCount}} čitateľom',
        likeLine: 'Páči sa mi citát. Počet srdiečok: {{formattedCount}}',
        unlikeLine: 'Odobrať srdiečko citátu. Počet srdiečok: {{formattedCount}}',

        shelves: {
          all: 'Všetko',
          currently_reading: 'Čítaš',
          to_be_read: 'TBR',
          already_read: 'Prečítané',
          did_not_finish: 'DNF',
        },
        customPrivacy:
          'Citáty a myšlienky k vlastným knihám zostávajú súkromné a do katalógu sa nezapočítavajú.',
        removeThoughtConfirm: 'Odstrániť túto myšlienku? Citát zostane v zápisníku.',
        saveDraft: 'Uložiť môj text',
        savedThought: 'Uložená verzia',
        noSavedThought: 'Myšlienka bola odstránená inde.',
        clearSearch: 'Vymazať hľadanie v zápisníku',

        title: 'Zápisník',
        books: 'Knihy',
        all: 'Všetko',
        quotes: 'Citáty',
        notes: 'Poznámky',
        reviews: 'Recenzie',
        filter: 'Filter',
        search: 'Hľadať v zápisníku',
        add: 'Pridať citát alebo poznámku',
        saveQuote: 'Uložiť citát',
        writeNote: 'Napísať poznámku',
        emptyTitle: 'Miesto pre tvoje myšlienky',
        emptyBody: 'Ulož si citát alebo napíš poznámku ku knihe. Nájdeš tu aj svoje recenzie.',
        noBooksTitle: 'Začni knihou',
        noBooksBody:
          'Pridaj si prvú knihu. Potom si tu môžeš uchovať citáty, poznámky aj recenzie.',
        findBook: 'Nájsť knihu',
        checkingBooks: 'Načítavame tvoje knihy…',
        booksError: 'Tvoje knihy sa nepodarilo načítať.',
        clearFilters: 'Zrušiť filtre',
        filterEmptyTitle: 'Nič sa nenašlo',
        filterEmptyBody: 'Skús iné vyhľadávanie alebo zmeň filtre.',
        thought: 'Tvoja myšlienka',
        private: 'Súkromná',
        addThought: '+ Pridať myšlienku',
        editThought: 'Upraviť myšlienku',
        saveThought: 'Uložiť myšlienku',
        removeThought: 'Odstrániť myšlienku',
        thoughtPlaceholder: 'Čo ti tento citát pripomenul?',
        saved: 'Uložili si: {{formattedCount}}',
        openQuote: 'Otvoriť citát: {{text}}',
        order: 'Zmeniť poradie zápisníka',
        error: 'Zápisník sa nepodarilo načítať.',
        thoughtError: 'Myšlienka sa neuložila. Tvoj koncept zostáva tu.',
        conflict: 'Táto myšlienka sa zmenila inde. Pred ďalším pokusom načítaj uloženú verziu.',
        reload: 'Načítať uloženú verziu',
        privacy:
          'Citáty, ktoré si uložíš z kníh z katalógu, sa počítajú anonymne. Keď moderátor schváli znenie citátu, ktorý si uložíš, slová z knihy sa môžu objaviť na jej stránke bez tvojho mena či myšlienky. Tvoje myšlienky zostávajú súkromné, pokiaľ sa ich nerozhodneš zverejniť. Poznámky sú vždy súkromné.',
        privacyTitle: 'Tvoje uložené citáty',
        privacyShort: 'Uložené citáty sa počítajú anonymne.',
        customPrivacyShort: 'Citáty z vlastných kníh zostávajú súkromné.',
        loading: 'Načítava sa zápisník…',
        retry: 'Skúsiť znova',
        filterTitle: 'Filtrovať zápisník',
        bookFilterGroups: {
          currently_reading: 'Práve čítam',
          already_read: 'Prečítané',
          other: 'Ostatné knihy',
        },
        added: 'Pridané',
        clear: 'Zrušiť filtre',
        newestShort: 'Najnovšie',
        oldestShort: 'Najstaršie',
        orderToggle: 'Poradie: {{order}}. Ťukni pre zmenu',
        kindSelected: '{{kind}}, vybraté. Ťukni a zobrazí sa všetko',
        removeFilter: 'Zrušiť filter: {{label}}',
        openBookAction: 'Otvoriť knihu',
        reviewAccessibility: 'Recenzia, {{rating}}, {{status}}, {{date}}: {{text}}',
        reviewSpoilers: 'Obsahuje spoilery · ostatní ju uvidia skrytú',
        yourReview: 'TVOJA RECENZIA',
        yourRating: 'HODNOTENIE',
        addReview: '+ Pridať recenziu',
        moreFilters: 'Ďalšie filtre: {{count}}. Otvoriť filter',
        moderatorNote: 'Poznámka moderátora',
      },

      achievementContentLanguage: 'sk',
      modularLibrary: {
        ...modularLibraryCopy.sk,
        ...modularLibraryScreenCopy.sk,
        illustratedTitle: 'Ilustrovaná knižnica',
      },
      modularLibraryBookPreview: modularLibraryBookPreviewCopy.sk,
      modularLibraryCollectible: modularLibraryCollectibleCopy.sk,
      modularLibrarySceneItems: modularLibrarySceneItemCopy.sk,
      modularLibraryArrange: modularLibraryArrangementCopy.sk,
      modularLibraryEditor: modularLibraryEditorCopy.sk,
      readingGoal: readingGoalCopy.sk,
      board: boardCopy.sk,
      bookShare: bookShareCopy.sk,
      bookFinish: bookFinishCopy.sk,
      shareImage: shareImageCopy.sk,
      boardAdmin: boardAdminCopy.sk,
      catalogueSync: catalogueSyncCopy.sk,
      progressCelebration: progressCelebrationTranslations.sk,
      libraryResources: {
        catalogueFailed: 'Dostupnosť ilustrácií sa nepodarilo overiť. Skús to znova.',
        checkingAvailability: 'Overuje sa dostupnosť ilustrácií…',
        downloading: 'Sťahuje sa ilustrácia na používanie offline…',
        downloadFailed: 'Zberateľský predmet ti zostáva. Ilustráciu sa nepodarilo stiahnuť.',
        unavailable:
          'Tento zberateľský predmet vlastníš. Jeho ilustrácia ešte nie je dostupná na stiahnutie.',
        retry: 'Skúsiť stiahnuť znova',
      },
      productionLibrary: {
        loadFailed:
          'Knižnicu sa nepodarilo načítať. Skús to znova, aby tvoje uložené usporiadanie zostalo v bezpečí.',
        starterCollection: 'Úvodná zbierka',
        testingCollection: 'Testovacia zbierka',
        included: 'Súčasť tvojej knižnice',
        emptyTitle: 'Miesto pre tvoje knihy',
        emptyBody:
          'Pridaj knihu, ktorú práve čítaš, už máš prečítanú alebo si ju chceš prečítať. Vytvor si vlastnú knižnicu.',
        emptyAddBook: 'Pridať prvú knihu',
      },
      selection: {
        choose: 'Vybrať možnosť',
        openHint: 'Otvorí možnosti v spodnom paneli.',
        search: 'Hľadať v možnostiach',
        clearSearch: 'Vymazať vyhľadávanie možností',
        noResults: 'Žiadne zodpovedajúce možnosti. Skús iné vyhľadávanie.',
        selectedCount_one: '{{count}} vybraná',
        selectedCount_few: '{{count}} vybrané',
        selectedCount_other: '{{count}} vybraných',
        done: 'Hotovo',
        limit: 'Dosiahnutý limit výberu. Zruš výber možnosti a vyber inú.',
      },
      bookLanguages: {
        en: 'Angličtina',
        sk: 'Slovenčina',
        cs: 'Čeština',
        de: 'Nemčina',
        pl: 'Poľština',
        hu: 'Maďarčina',
        fr: 'Francúzština',
        es: 'Španielčina',
        sv: 'Švédčina',
      },
      readingCompletion: {
        save: 'Označiť ako prečítané',
        unknown: 'Nepamätám si',
        title: 'Kedy si knihu dočítal/a?',
        now: 'Teraz',
        today: 'Dnes',
        earlier: 'V minulosti',
        earlierHint: 'Stačí rok.',
        fieldsHint: 'Vyplň len to, čo si pamätáš.',
        dateEntry: 'Alebo zadaj dátum',
        optionalFieldsHint: 'Stačí rok. Mesiac a deň sú voliteľné.',
        yearPlaceholder: 'RRRR',
        dayLabel: 'Deň',
        monthOptionalLabel: 'Mesiac (voliteľné)',
        dayOptionalLabel: 'Deň (voliteľné)',
        optionalValue: 'Nepamätám si',
        unknownYear: 'Nepamätám si rok',
        calendar: 'Vybrať z kalendára',
        dateNotSelected: 'Vyber „Dnes“, zadaj rok alebo vyber „Nepamätám si“.',
        restoreDate: 'Zadať dátum, ktorý si pamätáš',
        monthRequiresYear: 'Najprv zadaj rok.',
        dayRequiresMonth: 'Najprv vyber mesiac.',
        year: 'Iba rok',
        month: 'Rok a mesiac',
        date: 'Presný dátum',
        precision: 'Čo si pamätáš?',
        choiceHint: 'Vyber podľa toho, čo si pamätáš. Presný dátum nepotrebuješ.',
        nowHint:
          'Uložíme dnešný dátum dočítania. Pri rozčítanej knihe sa zostávajúce strany započítajú do dnešného cieľa.',
        pastHint: 'Zaznamenáme čítanie z minulosti bez pridania strán do dnešného cieľa.',
        unknownHint:
          'Kniha zostane prečítaná bez dátumu. Nepriradíme ju ku konkrétnemu roku ani k dnešným stranám.',
        yearLabel: 'Rok',
        monthLabel: 'Mesiac',
        yearInvalid: 'Zadaj štvorciferný rok, najneskôr aktuálny.',
        monthInvalid: 'Vyber rok a mesiac, najneskôr aktuálny mesiac.',
        dateInvalid: 'Vyber platný dátum, najneskôr dnešný.',
        summary: 'Prečítané: {{period}}',
        yearOnlyTitle: 'Mesiac nebol zaznamenaný',
        yearOnlyBody:
          'Tieto knihy sa počítajú do tohto roka bez priradenia ku konkrétnemu mesiacu.',
      },
      customBooks: {
        title: 'Pridať vlastnú knihu',
        addBook: 'Pridať knihu',
        pageTitle: 'Pridať knihu',
        pagePrivacy: 'Iba vo vašej knižnici',
        privacyDisclaimer:
          'Súkromná. Katalógové údaje sa zdieľajú iba po vašom odoslaní a schválení moderátorom.',
        authorsSearch: 'Hľadať autora alebo zadať nové meno',
        authorsPlaceholder: 'Hľadať alebo pridať autora',
        subtitlePlaceholder: 'Pridať podnázov',
        yearPlaceholder: 'Rok',
        libraryCategoriesHint: 'Vaše štítky. Neovplyvňujú žánrové úspechy.',
        authorsIntro: 'Vyhľadajte autora v katalógu alebo pridajte nové meno.',
        authorsEmpty: 'Žiadni zodpovedajúci autori. Meno môžete pridať nižšie.',
        chooseDate: 'Vybrať dátum',
        datePickerHint: 'Vyberte deň dočítania, najneskôr dnes.',
        titleRequired: 'Názov knihy je povinný.',
        authorLabel: 'Autor',
        addAnotherAuthor: 'Pridať ďalšieho autora',
        enterAuthor: 'Zadať autora',
        optional: 'Voliteľné',
        chooseCategories: 'Vybrať kategórie',
        isbnPlaceholder: 'ISBN-10 alebo ISBN-13',
        categoriesIntro: 'Vyberte všetky, ktoré sa hodia.',
        categoriesPrivacy: 'Súkromné štítky · nezapočítavajú sa do žánrových úspechov',
        categoriesSearch: 'Hľadať kategórie',
        categoriesSelected: 'Vybrané: {{count}}',
        categoriesClear: 'Vymazať',
        categoriesDone: 'Hotovo',
        categoriesEmpty: 'Žiadne zodpovedajúce kategórie. Skúste iný výraz.',
        missingTitle: 'Chýba v katalógu?',
        entryPrivacy: 'Pridaj ju súkromne. Neskôr môžeš odoslať jej katalógové údaje na kontrolu.',
        entryHint: 'Nemôžeš nájsť svoju knihu alebo správne vydanie?',
        privacy:
          'Pridajte knihu do osobnej knižnice. Katalógové údaje zostanú súkromné, kým ich neodošlete a moderátor ich neschváli.',
        signInRequired: 'Prihláste sa a uložte vlastnú knihu do svojej knižnice.',
        signIn: 'Prejsť na prihlásenie',
        bookTitle: 'Názov knihy',
        authors: 'Autori',
        authorHint:
          'Vyberte návrh z katalógu alebo pridajte meno. Autori sa zobrazia v poradí pridania.',
        authorName: 'Meno autora',
        addAuthor: 'Pridať „{{name}}“',
        removeAuthor: 'Odstrániť {{name}}',
        moveAuthorEarlier: 'Posunúť {{name}} vyššie',
        searchingAuthors: 'Hľadáme autorov v katalógu…',
        authorSearchFailed: 'Návrhy autorov nie sú dostupné. Meno môžete pridať aj sami.',
        edition: 'Podrobnosti vydania',
        isbn: 'ISBN',
        isbnHint: 'Nepovinné. Zadajte ISBN uvedené v tomto vydaní, ak ho má.',
        language: 'Jazyk',
        unknown: 'Neznámy',
        otherLanguage: 'Iný jazyk',
        languageCode: 'Kód jazyka',
        languageCodeHint: 'Zadajte dvojpísmenový kód jazyka, napríklad „uk“ pre ukrajinčinu.',
        pages: 'Počet strán',
        pagesHint:
          'Nepovinné. Bez celkového počtu môžete zapisovať strany a knihu sami označiť za prečítanú.',
        binding: 'Formát',
        bindings: {
          unknown: 'Neuvedený',
          paperback: 'Mäkká väzba',
          hardcover: 'Tvrdá väzba',
          ebook: 'Elektronická kniha',
          other: 'Iný',
        },
        genres: 'Osobné kategórie',
        genresHint:
          'Vyberte všetky vhodné kategórie. Tieto osobné štítky sa nezapočítavajú do žánrových úspechov.',
        moreDetails: 'Ďalšie údaje o vydaní',
        subtitle: 'Podnázov',
        publisher: 'Vydavateľ',
        publishedYear: 'Rok vydania',
        reading: 'Vo vašej knižnici',
        status: 'Polica podľa čítania',
        statuses: {
          to_be_read: 'Na prečítanie',
          currently_reading: 'Práve čítam',
          already_read: 'Prečítané',
          did_not_finish: 'Nedočítané',
        },
        startsAtBeginning: 'Čítanie začne na strane 0. Po pridaní knihy môžete stranu upraviť.',
        knowFinishDate: 'Viem, kedy som ju dočítal/a',
        unknownFinishDateHint:
          'Nechajte vypnuté, ak dátum nepoznáte. Knihu bez dátumu nezapočítame do ročného cieľa.',
        finishedOn: 'Dátum dočítania',
        dateHint: 'Použite formát rok-mesiac-deň. Vyberte skutočný dátum najneskôr dnes.',
        datePlaceholder: 'RRRR-MM-DD',
        save: 'Pridať do mojej knižnice',
        retrySave: 'Zopakovať rovnaké uloženie',
        optionalHint:
          'ISBN a údaje o vydaní sú nepovinné. Pridajte autora alebo vyberte „Neznámy autor“.',
        openMatch: 'Otvoriť knihu z katalógu',
        editIsbn: 'Upraviť ISBN',
        shelfDestination: 'Súkromná · pridá sa na policu {{shelf}}',
        validation: {
          title: 'Zadajte názov s najviac 1 000 znakmi.',
          subtitle: 'Podnázov môže mať najviac 2 000 znakov.',
          publisher: 'Názov vydavateľa môže mať najviac 1 000 znakov.',
          authors:
            'Pridajte aspoň jedného autora alebo vyberte „Neznámy autor“. Môžete pridať najviac 16 autorov.',
          authorsPending: 'Pridajte meno z poľa autora alebo ho pred uložením vymažte.',
          isbn: 'Skontrolujte ISBN. Použite platné 10- alebo 13-miestne ISBN knihy alebo pole nechajte prázdne.',
          language: 'Zadajte dvojpísmenový kód jazyka alebo vyberte „Neznámy“.',
          pages: 'Zadajte celý počet strán od 1 do 10 000 alebo pole nechajte prázdne.',
          year: 'Zadajte celý rok vydania od 1 do 9999 alebo pole nechajte prázdne.',
          date: 'Vyberte dátum dočítania, najneskôr dnes, alebo označte dátum za neznámy.',
        },
        errors: {
          catalogueMatchShort: 'Toto vydanie už je v katalógu.',
          catalogueMatch:
            'Toto ISBN už je v katalógu. Otvorte vydanie, skontrolujte ho a pridajte do knižnice.',
          conflict: 'Tieto údaje sa nepodarilo uložiť spolu. Skontrolujte ich a skúste to znova.',
          invalid:
            'Niektoré údaje o knihe sa nepodarilo prijať. Skontrolujte polia a skúste to znova.',
          unavailable:
            'Ukladanie je dočasne nedostupné. Údaje zostávajú tu; keď sa služba obnoví, zopakujte rovnaké uloženie.',
          uncertain:
            'Nepodarilo sa potvrdiť uloženie knihy. Zopakujte rovnaké uloženie, aby sa nepridala dvakrát.',
          signIn: 'Prihláste sa znova a uložte knihu do svojej knižnice.',
        },
      },
      libraryBookPreview: {
        title: 'Náhľad knihy',
        inMyBooks: 'V mojich knihách',
        openDetails: 'Otvoriť detail knihy',
        currentPage: 'Strana {{currentPage}}',
        percent: '{{progress}} %',
        accessibility: {
          open: 'Zobraziť náhľad knihy {{title}}',
          close: 'Zavrieť náhľad knihy',
          details: 'Otvoriť detail knihy {{title}}',
        },
      },
      accessibility: {
        addBookToShelf: 'Pridať knihu {{title}} do čitateľskej kategórie',
        archiveFilter: '{{label}}, {{count}} kníh',
        archiveFilter_one: '{{label}}, {{count}} kniha',
        archiveFilter_few: '{{label}}, {{count}} knihy',
        archiveFilter_many: '{{label}}, {{count}} kníh',
        adjustProgress: 'Upraviť hodnotu progresu o {{amount}}',
        backToMyBooks: 'Späť na Moje knihy',
        changeBookShelf: 'Zmeniť kategóriu knihy {{title}}, aktuálne {{shelf}}',
        clearBookSearch: 'Vymazať hľadanie kníh',
        closeBookBarcodeScanner: 'Zavrieť skener čiarového kódu knihy',
        closeChamberActions: 'Zavrieť akcie komnaty',
        closeCompletion: 'Zavrieť oslavu dokončenia',
        closeProgressEditor: 'Zavrieť úpravu progresu',
        categorySheetHandle: 'Úchyt panela čitateľských kategórií',
        categorySheetHandleHint: 'Potiahni nadol alebo aktivuj na zavretie čitateľských kategórií',
        emptyFirstReward: 'Prvá odmena: Strážca strán. Začni výberom knihy.',
        emptyWeeklyGoal: 'Týždenný cieľ. Začína sa knihou.',
        filterSheetHandle: 'Úchyt panela filtrov vyhľadávania',
        filterSheetHandleHint: 'Potiahni nadol alebo aktivuj na zavretie filtrov vyhľadávania',
        progressSheetHandle: 'Úchyt panela progresu',
        progressSheetHandleHint: 'Potiahni nadol alebo aktivuj na zavretie panela progresu',
        openBookByAuthor: 'Otvoriť {{title}} od {{authors}}',
        openBookProgress: 'Otvoriť {{title}}, strana {{currentPage}}',
        openBookProgressWithTotal: 'Otvoriť {{title}}, strana {{currentPage}} z {{pageCount}}',
        progressEditorHint: 'Otvorí ovládanie na zmenu aktuálnej strany',
        pageRuler: 'Aktuálna strana {{currentPage}} z {{pageCount}}',
        percentSlider: 'Čitateľský progres, {{progress}} percent',
        progressValueInputPages: 'Aktuálna strana',
        progressValueInputPercent: 'Percentuálny progres',
        scanBookBarcode: 'Naskenovať čiarový kód knihy',
        prototypeGoal:
          '{{completed}} z {{target}} čitateľských relácií splnených pre prototyp týždenného cieľa',
        prototypeGoal_one:
          '{{completed}} z {{target}} čitateľskej relácie splnených pre prototyp týždenného cieľa',
        prototypeGoal_few:
          '{{completed}} z {{target}} čitateľských relácií splnených pre prototyp týždenného cieľa',
        prototypeGoal_many:
          '{{completed}} z {{target}} čitateľských relácií splnených pre prototyp týždenného cieľa',
        prototypeReward: 'Ďalšia odmena prototypu: Strážca strán, zostáva {{count}} relácií',
        prototypeReward_one: 'Ďalšia odmena prototypu: Strážca strán, zostáva {{count}} relácia',
        prototypeReward_few: 'Ďalšia odmena prototypu: Strážca strán, zostávajú {{count}} relácie',
        prototypeReward_many: 'Ďalšia odmena prototypu: Strážca strán, zostáva {{count}} relácií',
        searchByTitleOrAuthor: 'Hľadať podľa názvu, autora alebo ISBN',
        setProgressPercent: 'Nastaviť progres na {{value}} percent',
        searchMyBooks: 'Hľadať v mojich knihách',
        showChamberActions: 'Zobraziť akcie komnaty',
        showTbrList: 'Zobraziť zoznam TBR',
        showTbrListHint: 'Otvorí tvoje knihy v TBR',
        startNextBook: 'Začať čítať {{title}}',
        updateProgress: 'Upraviť progres knihy {{title}}, prečítané {{progress}} %',
        updateProgressUnknownTotal:
          'Upraviť progres knihy {{title}}, celkový počet strán nie je známy',
      },
      actions: {
        add: 'Pridať',
        addBook: 'Pridať knihu',
        addToTbr: 'Pridať do TBR',
        adding: 'Pridáva sa...',
        back: 'Späť',
        browseBooks: 'Prehľadávať knihy',
        cancel: 'Zrušiť',
        chooseLater: 'Vyberiem neskôr',
        close: 'Zavrieť',
        continueApple: 'Pokračovať cez Apple',
        continueAuthKit: 'Pokračovať cez AuthKit',
        continueGoogle: 'Pokračovať cez Google',
        details: 'Detaily',
        find: 'Nájsť',
        findBooks: 'Nájsť knihy',
        finishBook: 'Dokončiť knihu',
        more: 'Ďalšie akcie',
        retry: 'Skúsiť znova',
        remove: 'Odstrániť',
        saveProgress: 'Uložiť progres',
        saving: 'Ukladá sa...',
        search: 'Hľadať',
        share: 'Zdieľať',
        signOut: 'Odhlásiť sa',
        signInEmail: 'Prihlásiť sa e-mailom',
        signingOut: 'Odhlasovanie...',
        startReading: 'Začať čítať',
        trackProgress: 'Sledovať progres',
      },
      auth: {
        callbackFailed: 'Prihlásenie sa nepodarilo dokončiť',
        callbackReturning: 'Dokončuje sa zabezpečené prihlásenie...',
        callbackTryAgain: 'Vrátiť sa a skúsiť znova',
        errors: {
          authorizationGrantInvalid:
            'Poskytovateľ identity nedokončil prihlásenie. Vráť sa a začni nový pokus.',
          callbackMissingCode: 'V spätnom volaní AuthKit chýba kód alebo PKCE stav',
          callbackStateInvalid: 'Stav spätného volania AuthKit je neplatný alebo vypršal',
          remoteSignOutUnconfirmed: 'Vzdialené odhlásenie WorkOS nebolo potvrdené',
          sessionExpired: 'Relácia WorkOS vypršala',
          signInRequired: `Pred volaním ${APP_NAME} API sa prihlás`,
        },
        proof: {
          apiUserCreated: 'Vytvorí sa po overení API',
          appOwnedUser: 'Používateľ aplikácie',
          authenticated: 'Overené',
          body: 'Táto ohraničená obrazovka overuje natívny WorkOS PKCE tok, uloženú reláciu a autentifikované volanie cez Nest API.',
          eyebrow: 'READ TRACKER · OVERENIE AUTH',
          nestMe: 'Nest /me',
          notAuthenticated: 'Neoverené',
          redirect: 'Presmerovanie: {{uri}}',
          title: 'Dôveryhodné dvere do knižnice.',
          verified: 'Overené',
          verifyNestApi: 'Overiť Nest API',
          waiting: 'Čaká sa',
          workosSession: 'WorkOS relácia',
        },
      },
      dev: {
        typographyStandoff: {
          body: 'V každej ukážke sú rovnaké produktové úlohy, veľkosti a paleta. Porovnaj rytmus nadpisu, veľké číslice, príponu progresu a stredoeurópsku diakritiku.',
          candidateLabel: 'Typografická ukážka {{name}}',
          current: 'Súčasné',
          currentPage: 'Aktuálna strana',
          derived: 'Strana 146 zo 416',
          eyebrow: 'TYPOGRAFICKÉ LABORATÓRIUM · ŠESŤ PÍSIEM',
          languageProof: 'Kŕdeľ ďatľov učí koňa žrať kôru.',
          loadError: 'Porovnávacie písma sa nepodarilo načítať.',
          loading: 'Pripravujem porovnávacie písma...',
          modalTitle: 'Upraviť čitateľský progres',
          pageSuffix: 'zo 416 strán',
          title: 'Šesť serifov vstupuje do knižnice.',
        },
      },
      catalogue: {
        authorSuggestions: 'Autori',
        authorSuggestionsLoading: 'Hľadáme autorov…',
        authorSuggestionsFailed: 'Návrhy nie sú dostupné. Stále môžete vyhľadávať podľa mena.',
        authorSuggestionsEmpty: 'Zatiaľ žiadne návrhy. Stále môžete vyhľadávať podľa mena.',
        aboutBook: 'O knihe',
        obalkyFeedback: {
          title: 'Hodnotenia a recenzie z Obálky knih',
          averageAccessibility: 'Hodnotenie Obálky knih: {{value}} z 5',
          ratingInfo:
            'Toto hodnotenie na škále do 5 hviezdičiek pochádza od čitateľov Obálky knih. Dostupné úryvky z recenzií nájdete pod popisom knihy. Hodnotenia a recenzie Belore sú uvedené samostatne. Údaje poskytovateľa sa ukladajú do vyrovnávacej pamäte a ich obnovenie môže trvať až 24 hodín.',
          votes_one: '{{count}} hodnotenie',
          votes_few: '{{count}} hodnotenia',
          votes_many: '{{count}} hodnotení',
          votes_other: '{{count}} hodnotení',
          excerpts: 'Úryvky z recenzií',
          openSource: 'Otvoriť na obalkyknih.cz',
        },
        coverCredit: {
          prefix: 'Obálka z',
          accessibilityHint: 'Otvorí túto knihu na obalkyknih.cz.',
        },
        allGenres: 'Všetky žánre',
        account: 'KATALÓG ÚČTU',
        addToShelf: 'Pridať do kategórie',
        addedToTbr: 'Kniha {{title}} bola pridaná do TBR.',
        allResults: 'Všetky',
        anyLanguage: 'Všetky',
        assignmentFailed: 'Knihu sa nepodarilo presunúť. Skús to znova.',
        assignedToShelf: 'Kniha {{title}} je teraz v kategórii {{shelf}}.',
        availableResults: 'Nepridané',
        bookLanguages: 'Jazyky kníh',
        detailEyebrow: 'DETAIL KNIHY',
        detailLoading: 'Načítavam podrobnosti o knihe…',
        detailUnavailable: 'Podrobnosti o knihe sa nepodarilo načítať. Skús to znova.',
        detailUnavailableTitle: 'Knihu sa nepodarilo otvoriť',
        detailUnavailableBody: 'Podrobnosti o knihe teraz nie sú dostupné. O chvíľu to skús znova.',
        detailRefreshFailedTitle: 'Knihu sa nepodarilo aktualizovať',
        detailRefreshFailedBody:
          'Načítané podrobnosti si môžeš ďalej prezerať. Skús ich aktualizovať znova.',
        partialDetail: {
          body: 'Toto vydanie sa teraz nepodarilo načítať. Informácie z katalógu vyššie nemusia byť úplné.',
          retry: 'Skúsiť znova',
          source: 'Zobraziť zdroj katalógu',
          title: 'Úplné podrobnosti nie sú dostupné',
        },
        publicPreview: {
          addAction: 'Pridať do mojej knižnice',
          addBody: 'Vyber, do ktorej kategórie kniha patrí.',
          addTitle: 'Ulož si túto knihu',
          footerBody: 'Ukladaj knihy, sleduj čítanie a zdieľaj recenzie.',
          footerTitle: 'Pokračuj v Belore',
          openInAppAction: 'Otvoriť v Belore',
          openInAppFailed: 'Belore sa nepodarilo otvoriť. Môžeš pokračovať v čítaní tu.',
          openInAppTitle: 'Máš už Belore v tomto zariadení?',
          signInAction: 'Prihlásiť sa do Belore',
          signInBody: 'Vyber si spôsob prihlásenia.',
          signInTitle: 'Prihlásiť sa do Belore',
          shareNotAvailable: 'Toto vydanie zatiaľ nie je dostupné na verejné zdieľanie.',
        },
        editionsLoadError: 'Ďalšie vydania sa nepodarilo načítať. Skús to znova.',
        isbnCopy: {
          action: 'Kopírovať ISBN',
          copied: 'Skopírované do schránky',
          failed: 'Nepodarilo sa skopírovať. Podržte ISBN a skopírujte ho.',
        },
        metadataPageCount: 'Strany',
        editionLanguage: 'Jazyk vydania: {{language}}',
        editionLanguageUnknown: 'Jazyk vydania nie je známy',
        editionUnavailable: 'Toto vydanie sa zatiaľ nedá otvoriť v aplikácii.',
        genreAndSubjectLabel: 'Žánre a témy z katalógu',
        loadMoreEditions: 'Načítať ďalšie vydania',
        loadingEditions: 'Načítava sa…',
        retryEditions: 'Skúsiť znova',
        noMatchingEditionsLoaded: 'Zatiaľ nie sú načítané žiadne zodpovedajúce vydania.',
        noOtherEditions: 'K tejto knihe zatiaľ nepoznáme ďalšie vydania.',
        noOtherEditionsInLanguages: 'Žiadne ďalšie vydania nezodpovedajú vybraným jazykom kníh.',
        openInOpenLibrary: 'Otvoriť v Open Library',
        otherEditions: 'Ďalšie vydania',
        moveReadBookTitle: 'Presunúť prečítanú knihu?',
        moveReadBookBody:
          'Táto kniha je už označená ako prečítaná. Chceš ju presunúť do kategórie „{{shelf}}“?',
        moveReadBookAction: 'Presunúť knihu',
        rereadProgressHint: 'Priebeh čítania začne znova od začiatku.',
        removeFromLibrary: 'Odstrániť z mojej knižnice',
        removeFromLibraryBody:
          'Tvoj čitateľský progres, osobné hodnotenie, recenzia, poznámky a citáty k tomuto vydaniu sa vymažú. Kniha zostane v spoločnom katalógu.',
        removePrivateBookFromLibraryBody:
          'Tvoj čitateľský progres, osobné hodnotenie, recenzia, poznámky a citáty k tejto súkromnej knihe sa vymažú.',
        removeFromLibraryFailed: 'Knihu sa nepodarilo odstrániť z tvojej knižnice. Skús to znova.',
        removeFromLibraryTitle: 'Odstrániť {{title}} z tvojej knižnice?',
        showMoreEditions: 'Zobraziť všetky {{count}}',
        genres: {
          adventure: 'Dobrodružné',
          biography_memoir: 'Biografie a memoáre',
          childrens: 'Detská literatúra',
          comics_graphic_novels: 'Komiksy a grafické romány',
          fantasy: 'Fantasy',
          historical_fiction: 'Historická próza',
          history: 'História',
          horror: 'Horor',
          literary_fiction: 'Literárna fikcia',
          mystery_thriller: 'Detektívky a trilery',
          nonfiction: 'Literatúra faktu',
          poetry: 'Poézia',
          romance: 'Romantika',
          science_fiction: 'Vedecká fantastika',
          young_adult: 'Pre mládež',
        },
        markdownAccessibility: {
          blockquote: 'Bloková citácia',
          bulletPoint: 'Odrážka',
          headings: 'Nadpisy',
          images: 'Obrázky',
          links: 'Odkazy',
          math: 'Matematický výraz: {latex}',
          nestedBlockquote: 'Vnorená bloková citácia',
          nestedBulletPoint: 'Vnorená odrážka',
          nestedOrderedItem: 'Vnorená položka zoznamu {n}',
          orderedItem: 'Položka zoznamu {n}',
          tableRow: 'Riadok {n}: {content}',
        },
        externalCandidateUnavailable:
          'Túto externú knihu sa zatiaľ nepodarilo uložiť. Skús to znova.',
        applyFilters: 'Zobraziť výsledky',
        activeFilterCount: '{{count}} aktívnych filtrov',
        activeFilterCount_one: '{{count}} aktívny filter',
        activeFilterCount_few: '{{count}} aktívne filtre',
        filterPanel: 'Spresniť hľadanie',
        filters: 'Filtre',
        filtersAccessibilityLabel: 'Filtre, aktívne: {{count}}',
        findTitle: 'Nájsť knihy',
        recentSearches: 'Nedávne hľadania',
        clearRecentSearches: 'Vymazať',
        clearRecentSearchesLabel: 'Vymazať nedávne hľadania',
        repeatRecentSearch: 'Hľadať znova {{query}}',
        scanTip:
          'Máš knihu v ruke? Naskenuj čiarový kód na zadnej strane obálky a otvor presne toto vydanie.',
        idleHint: 'Hľadaj v katalógu podľa názvu, autora alebo ISBN.',
        findInvitation: 'Hľadaj knihu podľa názvu,\nautora alebo ISBN.',
        findFocusedTitle: 'Ktorú knihu\nhľadáš?',
        findFocusedHint: 'Stačí názov, autor\nalebo ISBN.',
        findSearchPlaceholder: 'Názov, autor alebo ISBN',
        idleTitle: 'Nájdi svoju knihu',
        idleBody: 'Hľadaj podľa názvu, autora alebo ISBN, alebo si pridaj vlastnú knihu.',
        startSearch: 'Hľadať knihy',
        resultsTitle: 'Výsledky',
        searchingResults: 'Hľadám…',
        bookLanguagesLabel: 'Jazyky kníh',
        changeBookLanguages: 'Jazyky kníh: {{languages}}. Zmeniť',
        searchAllFields: 'Hľadať vo všetkých poliach',
        keepOneLanguage: 'Pred odstránením tohto jazyka vyber ďalší.',
        languages: 'Jazyky',
        languageSelectorAccessibilityLabel: '{{languages}}, vybrané: {{count}}',
        resetFilters: 'Obnoviť',
        searchIn: 'Hľadať v',
        searchScope: {
          all: 'Všetko',
          author: 'Autor',
          isbn: 'ISBN',
          title: 'Názov',
        },
        linkCopied: 'Odkaz na knihu bol skopírovaný.',
        moveToShelf: 'Presunúť do kategórie',
        noResults: 'Žiadne zodpovedajúce knihy. Skús iný názov, autora alebo ISBN.',
        emptyTitle: 'Nenašli sa žiadne knihy',
        emptyHintAll: 'Skontroluj pravopis alebo skús iný názov, autora či ISBN.',
        emptyHintTitle:
          'Podľa názvu sa nič nenašlo. Skontroluj pravopis alebo skús autora či ISBN.',
        emptyHintAuthor:
          'Podľa autora sa nič nenašlo. Skontroluj pravopis alebo skús názov či ISBN.',
        emptyHintIsbn:
          'K tomuto ISBN sme nenašli žiadne vydanie. Skontroluj číslo alebo hľadaj podľa názvu či autora.',
        emptySearchedLanguages: 'Hľadané v jazykoch: {{languages}}.',
        changeLanguages: 'Zmeniť jazyky',
        nowOnReadingDesk: 'Kniha {{title}} je teraz na tvojom čitateľskom stole.',
        pageCount: '{{count}} strán',
        pageCount_one: '{{count}} strana',
        pageCount_few: '{{count}} strany',
        pageCount_many: '{{count}} strán',
        pageCountUnknown: 'Počet strán nie je známy',
        preview: 'NÁHĽADOVÝ KATALÓG',
        resultCount: 'Načítaných kníh: {{count}}',
        resultCount_one: 'Načítaná kniha: {{count}}',
        resultCount_few: 'Načítané knihy: {{count}}',
        resultCount_many: 'Načítaných kníh: {{count}}',
        returnToFind: 'Vráť sa do hľadania a vyber iný výsledok.',
        savedResults: 'Moje knihy',
        showLess: 'Zobraziť menej',
        showMore: 'Zobraziť viac',
        shareFailed: 'Odkaz na knihu sa nepodarilo zdieľať.',
        shareMessage: '{{title}}\n{{url}}',
        shareText: `Otvor {{title}} v ${APP_NAME}.`,
        shareUnavailable: 'Pred zdieľaním z natívnej zostavy nastav EXPO_PUBLIC_WEB_URL.',
        scanner: {
          allowCamera: 'Povoliť fotoaparát',
          errors: {
            camera: 'Fotoaparát sa nepodarilo spustiť. Zavri skener a skús to znova.',
            invalid:
              'Tento čiarový kód nie je platné ISBN knihy. Skús pôvodný kód na zadnej strane obálky.',
            lookup: 'Katalóg kníh nie je dostupný. Skontroluj pripojenie a skenuj znova.',
            not_found:
              'Pre toto ISBN sa nenašlo žiadne vydanie. Skús iný kód alebo zadaj ISBN ručne.',
          },
          instructions: 'Umiestni čiarový kód ISBN zo zadnej strany knihy do rámčeka.',
          lookingUp: 'Hľadám toto vydanie...',
          manualHint: 'Ak sa kód nedá načítať, zavri skener a zadaj ISBN ručne.',
          openSettings: 'Otvoriť nastavenia',
          permissionBody:
            'Fotoaparát sa používa iba na načítanie čiarového kódu ISBN. Žiadna fotografia sa neuloží.',
          permissionTitle: 'Povoľ prístup k fotoaparátu',
          title: 'Skenovať knihu',
        },
        pagination: {
          filtered:
            'Na tejto stránke nie sú zodpovedajúce vydania. Načítaj ďalšie a pokračuj v hľadaní.',
          more: 'Načítať ďalšie knihy',
          moreWider: 'Načítať ďalšie zo širšieho katalógu',
          loading: 'Načítava sa…',
          retry: 'Skúsiť načítať znova',
          failed: 'Ďalšie knihy sa nepodarilo načítať. Tvoje výsledky zostali zachované.',
          refine:
            'Hľadáš konkrétne vydanie? Spresni názov, autora či jazyk alebo hľadaj podľa ISBN.',
          limit:
            'Dosiahli sme limit vyhľadávania v katalógu. Spresni hľadanie a nájdi ďalšie zhody.',
        },
        searchWider: 'Prehľadať širší katalóg',
        titleOrAuthor: 'Hľadať názov, autora alebo ISBN',
        scopedSearchPlaceholder: {
          title: 'Hľadať podľa názvu',
          author: 'Hľadať podľa autora',
          isbn: 'Hľadať podľa ISBN',
        },
        widerSearchHint: 'Nevidíš správne vydanie? Skús rozšírený knižný katalóg.',
        widerSearchIncluded: 'Prehľadal sa aj širší katalóg.',
      },
      chamber: {
        allChallengesComplete: 'VŠETKY VÝZVY SPLNENÉ',
        allChallengesCompleteShort: 'SPLNENÉ',
        badgeReward: 'Odznak',
        chooseBookToBegin: 'Začni výberom knihy',
        chooseCurrentRead: 'Vyber aktuálne čítanie',
        currentReading: 'AKTUÁLNE ČÍTAŠ',
        earnedRewards: 'ZÍSKANÉ ODMENY',
        earnedRewardsShort: 'ZBIERKA',
        emptyCurrentBody: 'Začni knihu z Mojich kníh a položí sa na čitateľský stôl.',
        firstReward: 'PRVÁ ODMENA',
        followedChallenge: 'SLEDOVANÁ VÝZVA',
        followedChallengeShort: 'SLEDOVANÁ VÝZVA',
        journeyLoading: 'Načítavam tvoju cestu',
        journeyUnavailable: 'CESTA NIE JE DOSTUPNÁ',
        nextReward: 'ĎALŠIA ODMENA',
        nextRewardShort: 'ODMENA',
        noCurrentBook: 'ŽIADNA AKTUÁLNA KNIHA',
        openChallengeHint: 'Otvorí detail tejto výzvy.',
        openCollection: 'Pozrieť získané odmeny',
        openCollectionHint: 'Otvorí tvoju zbierku odmien.',
        openJourney: 'Objaviť výzvy',
        openJourneyHint: 'Otvorí obrazovku Cesta.',
        openTbr: 'Otvoriť TBR',
        pageKeeper: 'Strážca strán',
        readingChamber: 'Čitateľská komnata',
        readingRhythmCompact:
          '{{daysCurrent}}/{{daysTarget}} dní · {{weeksCurrent}}/{{weeksTarget}} týž.',
        recommendedChallenge: 'NAJBLIŽŠIA VÝZVA',
        recommendedChallengeShort: 'NAJBLIŽŠIA VÝZVA',
        rewardDistance: 'zostáva {{count}} relácií',
        rewardDistance_one: 'zostáva {{count}} relácia',
        rewardDistance_few: 'zostávajú {{count}} relácie',
        rewardDistance_many: 'zostáva {{count}} relácií',
        retryJourney: 'Ťukni a skús ju načítať znova',
        retryJourneyHint: 'Znova načíta progres tvojej cesty.',
        sourceAccountSynced: 'ÚČET SYNCHRONIZOVANÝ',
        sourcePreviewCollection: 'NÁHĽADOVÁ ZBIERKA',
        sourceSyncingLibrary: 'SYNCHRONIZUJEM KNIŽNICU',
        syncPausedPreview: 'Synchronizácia pozastavená - dostupné sú náhľadové dáta.',
        syncPausedSaved: 'Synchronizácia pozastavená - uložený progres je dostupný.',
        themePackReward: 'Vzhľad knižnice',
        yourCollection: 'Tvoja zbierka',
        weeklyGoal: 'TÝŽDENNÝ CIEĽ',
        weeklyGoalEmpty: 'Začína sa knihou',
      },
      libraryThemes: {
        active: 'AKTÍVNA',
        assetPending: 'Produkčný raster tejto komnaty sa ešte pripravuje.',
        previewUnavailable: 'Náhľad nie je k dispozícii',
        change: 'Zmeniť',
        classic: {
          description: 'Pôvodná orechová skriňa Belore, teplé výstavné svetlo a mosadzné detaily.',
          title: 'Klasická knižnica',
          unlockHint: 'Je súčasťou každej knižnice.',
        },
        downloadAndUse: 'Stiahnuť a použiť',
        downloadRequired: 'Bezpečne sa stiahne, keď si ju vyberieš.',
        remoteDownloadSavedChoice: 'VYBRANÁ',
        remoteDownloadInProgress: 'Pripravujem túto tému…',
        remoteDownloadFailed: 'Tému sa nepodarilo stiahnuť. Aktuálny vzhľad sa nezmenil.',
        remoteDownloadUnavailable: 'Túto tému máš v zbierke. Jej súbory ešte nie sú dostupné.',
        remoteDownloadRetry: 'Skúsiť stiahnuť znova',
        editorLabel: 'BALÍK TÉMY',
        errors: {
          'asset-missing': 'Táto téma ešte nie je dostupná v tejto verzii appky.',
          conflict: 'Aktívna téma sa zmenila v inom zariadení. Skús to znova.',
          invalid: 'Odpoveď s témami nebola platná. Zostáva aktívna tvoja predchádzajúca téma.',
          unavailable: 'Tému sa nepodarilo zmeniť. Skús to znova.',
          unowned: 'Tento balík témy ešte nemáš odomknutý.',
        },
        eyebrow: 'ZBERATEĽSKÉ TÉMY KNIŽNICE',
        intro:
          'Balíky tém menia materiály skrine a autorské svetlo bez presúvania kníh, collectibles alebo rozloženia políc.',
        loadFailed: 'Tvoju zbierku tém sa nepodarilo obnoviť. Skús to znova.',
        locked: 'ZAMKNUTÁ',
        menuLabel: 'Témy knižnice',
        openHint: 'Otvorí tvoju zbierku balíkov tém knižnice',
        minimalWarmGrey: {
          description:
            'Police natreté teplou sivou farbou, so zaoblenými hranami v ilustrovanom štýle Belore.',
          title: 'Teplý sivý minimalizmus',
          unlockHint: 'Odomkneš za päť schválených verejných recenzií rôznych kníh.',
        },
        minimalBlack: {
          description:
            'Police s lešteným čiernym lakom a jemnými odleskami, v ktorých vyniknú tvoje knihy.',
          title: 'Čierny minimalizmus',
          unlockHint:
            'Odomkneš za hodnotenie piatich rôznych kníh. Počítajú sa aj súkromné hodnotenia.',
        },
        orbitalArchive: {
          description: 'Grafitové police, chladné svetlo a rám tabletu okolo práve čítanej knihy.',
          title: 'Orbitálny archív',
          unlockHint: 'Odmena za splnenie čitateľskej výzvy.',
        },
        verdantScriptorium: {
          description: 'Dymový dub, lesnozelené niky a bronzový magický kruh pod tvojou knihou.',
          title: 'Zelené skriptórium',
          unlockHint: 'Odmena za splnenie čitateľskej výzvy.',
        },
        owned: 'VLASTNÍŠ',
        title: 'Témy knižnice',
        useTheme: 'Použiť túto tému',
        webUnsupported: 'Témy zatiaľ nie sú podporované vo webových prehliadačoch.',
      },
      cabinetSyncChoice: {
        body: 'Knižnica v tomto zariadení je usporiadaná inak než verzia uložená v tvojom účte. Vyber, ktoré rozloženie sa má zdieľať medzi zariadeniami.',
        chooseLater: 'Rozhodnúť neskôr',
        cloud: 'Cloudové rozloženie',
        cloudHint: 'Aktuálne zdieľané cez tvoj účet',
        consequence:
          'Voľba tohto zariadenia nahradí cloudové usporiadanie. Voľba cloudu nahradí uložené usporiadanie v tomto zariadení.',
        deviceHint: 'Uložené iba v tomto zariadení',
        error: 'Toto rozloženie sa nepodarilo vybrať. Nič sme nenahradili; skús to znova.',
        eyebrow: 'PRVÁ SYNCHRONIZÁCIA',
        itemCount: '{{count}} umiestnených predmetov',
        itemCount_one: '{{count}} umiestnený predmet',
        itemCount_few: '{{count}} umiestnené predmety',
        itemCount_many: '{{count}} umiestnených predmetov',
        laterNote:
          'Úpravy zostanú pozastavené, kým si nevyberieš. Pri rozhodnutí neskôr sa žiadne rozloženie nevymaže.',
        saveConflictBody:
          'Cloudová knižnica sa zmenila po otvorení editora. Vyber, či chceš uložiť usporiadanie z tohto zariadenia alebo načítať najnovšie cloudové rozloženie.',
        saveConflictEyebrow: 'KONFLIKT PRI UKLADANÍ',
        saveConflictLaterNote:
          'Neuložené zmeny zostanú otvorené. Kým si nevyberieš, nič sa nenahradí.',
        saveConflictTitle: 'Knižnica sa zmenila v inom zariadení',
        thisDevice: 'Toto zariadenie',
        title: 'Našli sme dve rozloženia knižnice',
        useCloud: 'Použiť cloudové rozloženie',
        useDevice: 'Použiť rozloženie z tohto zariadenia',
        unsavedDeviceHint: 'Tvoje aktuálne neuložené usporiadanie',
      },
      cabinetEditor: {
        applyDisplay: 'Použiť na nástenke',
        applyPlatformSpot: 'Použiť na platforme',
        alreadyPlaced: 'V inej polici',
        applyShelf: 'Použiť v polici',
        arrange: 'Usporiadať',
        bookPlacementNotice:
          'Nedostupné knihy sú už vystavené, rezervované ako práve čítané alebo označené DNF. Knihy zo všetkých ostatných kategórií sem môžeš umiestniť.',
        chooseEveryPosition: 'Vyber predmet pre každú pozíciu.',
        chooseLayout: 'Vyber rozloženie',
        chooseShelf: 'VYBER POLICU, MIESTO NA PLATFORME ALEBO NÁSTENKU',
        clearDisplay: 'Vyprázdniť nástenku',
        clearPlatformSpot: 'Vyprázdniť miesto',
        clearShelf: 'Vyprázdniť policu',
        collectibles: 'Collectibles',
        currentBook: 'Práve čítaš',
        currentReserved: 'Aktuálne čítanie — rezervované',
        didNotFinishUnavailable: 'DNF — nedostupné',
        discard: 'Zahodiť zmeny',
        discardBody: 'Neuložené rozloženie políc sa stratí.',
        discardTitle: 'Odísť bez uloženia?',
        errors: {
          conflict: 'Knižnica sa zmenila inde. Zavri editor, znova ho otvor a skús to opäť.',
          saveFailed: 'Rozloženie políc sa nepodarilo uložiť. Skús to znova.',
        },
        keepEditing: 'Pokračovať v úprave',
        layoutDoesNotUseTab: 'Toto rozloženie nepoužíva predmety z tejto zbierky.',
        loadFailed: 'Editor políc nemohol načítať tvoju knižnicu.',
        loading: 'Pripravujem tvoje police…',
        myBooks: 'Moje knihy',
        movesFromAnotherDisplay: 'Presunie sa z iného miesta',
        noBooks: 'Nenašli sa žiadne dostupné knihy.',
        noCollectibles: 'Pre tvoje police zatiaľ nie sú dostupné žiadne collectibles.',
        open: 'Upraviť knižnicu',
        openHint: 'Otvorí úpravy témy knižnice, vystavených kníh a zberateľských predmetov',
        recipes: {
          bookObject: 'Kniha + predmet',
          compactAccessory: 'Kompaktný doplnok',
          collectible: 'Collectible',
          empty: 'Prázdna',
          fiveBadges: 'Päť odznakov',
          faceOut: 'Obálkou dopredu',
          longAccessory: 'Pozdĺžny doplnok',
          objectBook: 'Predmet + kniha',
          oneBadge: 'Jeden odznak',
          threeBadges: 'Tri odznaky',
          threeSpines: 'Tri chrbty',
          twoCollectibles: 'Dva predmety',
          twoCovers: 'Dve obálky',
          twoSpines: 'Dva chrbty',
        },
        redo: 'Znova',
        reloadLatest: 'Načítať najnovšie',
        save: 'Uložiť',
        searchPlaceholder: 'Hľadať v tvojej knižnici',
        selectedDisplay: 'VYBRANÁ PLOCHA',
        selectedPlatformSpot: 'VYBRANÉ MIESTO NA PLATFORME',
        selectedShelf: 'VYBRANÁ POLICA',
        shelves: {
          badgeBoard: 'Nástenka na odznaky',
          centerMiddle: 'Polica aktuálneho čítania',
          centerUpper: 'Stredná horná polica',
          leftLower: 'Ľavá spodná polica',
          leftMiddle: 'Ľavá stredná polica',
          leftUpper: 'Ľavá horná polica',
          platformLeft: 'Ľavé miesto na platforme',
          platformRight: 'Pravé miesto na platforme',
          rightLower: 'Pravá spodná polica',
          rightMiddle: 'Pravá stredná polica',
          rightUpper: 'Pravá horná polica',
        },
        title: 'Usporiadať knižnicu',
        undo: 'Späť',
        badges: {
          bookExplorer: 'Knižný objaviteľ',
          firstFinish: 'Prvá dočítaná kniha',
          hundredPages: 'Sto strán',
          nightReader: 'Nočný čitateľ',
          sevenDayStreak: 'Sedemdňová séria',
        },
      },
      completion: {
        dnf: {
          eyebrow: 'NEDOČÍTANÉ',
          stoppedAt: 'Zastavené na strane {{currentPage}} z {{pageCount}} · {{progress}}',
          stoppedAtPage: 'Zastavené na strane {{currentPage}}',
          returnToReading: 'Vrátiť medzi čítané',
          returnFailed: 'Knihu sa nepodarilo vrátiť medzi čítané. Skús to znova.',
          body: 'Kniha je teraz medzi Nedočítanými.',
          reflectionTitle: 'Pár slov, ak chceš',
          reflectionBody:
            'Ak chceš, môžeš ostatným čitateľom napísať, prečo kniha zostala nedočítaná. Hodnotenie aj recenzia sú nepovinné.',
          save: 'Uložiť a zavrieť',
          skip: 'Zatiaľ zavrieť',
          discardBody:
            'Neuložené hodnotenie alebo recenzia sa stratí. Kniha zostane medzi Nedočítanými.',
          discard: 'Zahodiť a zavrieť',
        },
        body: 'Ďalšia prečítaná kniha. Vychutnaj si tento okamih.',
        chooseNext: 'Čo budeš čítať ďalej?',
        chooseNextBody: 'Vyber si knihu z TBR a položíme ju na čitateľský stôl.',
        continueToNext: 'Pokračovať k ďalšej knihe',
        discardAndContinue: 'Zahodiť koncept a pokračovať',
        discardBody:
          'Neuložené hodnotenie alebo koncept recenzie sa stratí. Kniha zostane medzi Prečítanými.',
        discardTitle: 'Odísť bez dokončenia dojmu?',
        eyebrow: 'KNIHA DOKONČENÁ',
        keepEditing: 'Pokračovať v úprave',
        milestoneLabel: 'ČITATEĽSKÝ MÍĽNIK',
        milestoneValue: '+1 prečítaná kniha',
        noTbr: 'Tvoje TBR je prázdne. Keď budeš pripravený, nájdi si ďalšiu knihu v katalógu.',
        pagesRead: 'Prečítaných {{count}} strán',
        reflectionBody:
          'Aká bola kniha? Pridaj hodnotenie a podeľ sa v recenzii o to, čo v tebe zostalo. Oboje je nepovinné a vždy sa k tomu môžeš vrátiť neskôr.',
        reflectionTitle: 'Podeľ sa o svoje dojmy',
        saveAndContinue: 'Uložiť dojem a pokračovať',
        saveAndClose: 'Uložiť a zavrieť',
        closeForNow: 'Zatiaľ zavrieť',
        discardAndClose: 'Zahodiť a zavrieť',
        skipReflection: 'Teraz preskočiť · vybrať ďalšiu knihu',
        startFailed: 'Ďalšiu knihu sa nepodarilo začať čítať. Skús to znova.',
        title: 'Kniha {{title}} je dočítaná',
      },
      annotations: {
        editor: {
          add: {
            note: 'Pridať poznámku',
            quote: 'Uložiť citát',
          },
          bodyAccessibilityLabel: {
            note: 'Text poznámky',
            quote: 'Text citátu',
          },
          bodyLabel: {
            note: 'Tvoja poznámka',
            quote: 'Citovaný úryvok',
          },
          characterCount: '{{count}} / {{limit}} znakov',
          close: 'Zavrieť editor citátu alebo poznámky',
          confirmDelete: {
            note: 'Odstrániť poznámku',
            quote: 'Odstrániť citát',
          },
          delete: {
            note: 'Odstrániť poznámku',
            quote: 'Odstrániť citát',
          },
          deleteBody: {
            note: 'Túto súkromnú poznámku po odstránení nebude možné obnoviť.',
            quote: 'Tento uložený citát po odstránení nebude možné obnoviť.',
          },
          deleteTitle: {
            note: 'Odstrániť túto poznámku?',
            quote: 'Odstrániť tento citát?',
          },
          discard: 'Zahodiť zmeny',
          discardBody: 'Neuložené zmeny sa stratia.',
          discardTitle: 'Odísť bez dokončenia záznamu?',
          edit: {
            note: 'Upraviť poznámku',
            quote: 'Upraviť citát',
          },
          handle: 'Úchyt editora citátu a poznámky',
          handleHint: 'Potiahni nadol alebo aktivuj na zatvorenie editora',
          keepEditing: 'Pokračovať v úprave',
          open: {
            notes: 'Všetky poznámky',
            quotes: 'Všetky citáty',
          },
          pageAccessibilityLabel: 'Číslo strany',
          pageHint: 'Nepovinné',
          pageLabel: 'Strana',
          placeholder: {
            note: 'Čo si chceš zapamätať?',
            quote: 'Napíš alebo vlož úryvok…',
          },
          save: 'Uložiť záznam',
        },
        errors: {
          conflict: 'Tento záznam sa zmenil inde. Zavri ho, znova otvor a skús to opäť.',
          delete: 'Záznam sa nepodarilo odstrániť. Skús to znova.',
          save: 'Záznam sa nepodarilo uložiť. Skús to znova.',
        },
        journal: {
          createLabel: { quote: 'Pridať citát', note: 'Pridať poznámku' },
          add: {
            note: '+ Poznámka',
            quote: '+ Citát',
          },
          addAccessibilityLabel: {
            note: 'Pridať súkromnú poznámku',
            quote: 'Uložiť citát',
          },
          open: {
            notes: 'Všetky poznámky',
            quotes: 'Všetky citáty',
          },
          edit: 'Upraviť',
          editAccessibilityLabel: {
            note: 'Upraviť poznámku {{position}}: {{details}}',
            quote: 'Upraviť citát {{position}}: {{details}}',
          },
          empty: 'K tejto knihe zatiaľ nemáš citáty ani poznámky.',
          emptyBook: 'Ulož si citát zo strany alebo si zapíš, čo ti kniha pripomenula.',
          emptyCurrent: 'Ulož si úryvok alebo myšlienku, kým je čerstvá.',
          eyebrow: 'ČITATEĽSKÝ DENNÍK',
          intro: 'Ulož si ku knihe úryvky, ku ktorým sa chceš vrátiť, a súkromné poznámky.',
          loadError: 'Tvoje citáty a poznámky sa nepodarilo načítať.',
          loading: 'Otváram tvoj čitateľský denník…',
          loadMore: 'Načítať staršie záznamy',
          loadMoreError: 'Staršie záznamy sa nepodarilo načítať.',
          page: 'Strana {{pageNumber}}',
          retryLoadMore: 'Skúsiť načítať staršie záznamy znova',
          title: 'Citáty a poznámky',
        },
        kind: {
          note: 'POZNÁMKA',
          quote: 'CITÁT',
          review: 'RECENZIA',
        },
        quick: {
          sectionTitle: 'Zachytiť z tejto knihy',
          addAccessibilityLabel: {
            note: 'Pridať súkromnú poznámku ku knihe {{title}}',
            quote: 'Uložiť citát z knihy {{title}}',
          },
          label: {
            note: 'Poznámka',
            quote: 'Citát',
          },
        },
        validation: {
          body_required: 'Pred uložením napíš text.',
          body_too_long: 'Záznam musí mať menej ako {{limit}} znakov.',
          page_invalid: 'Zadaj celé číslo strany od 1 do 10 000 000.',
        },
      },
      quotes: {
        title: 'Moje citáty',
        shortTitle: 'Citáty',
        passageCount: '{{formattedCount}} úryvkov',
        passageCount_one: '{{formattedCount}} úryvok',
        passageCount_few: '{{formattedCount}} úryvky',
        passageCount_many: '{{formattedCount}} úryvkov',
        passageCount_other: '{{formattedCount}} úryvkov',
        searchAccessibility: 'Hľadať v citátoch, knihách alebo autoroch',
        searchPlaceholder: 'Hľadať citáty, knihy alebo autorov',
        capturedOn: 'Zachytené {{date}}',
        pageSuffix: '  ·  s. {{pageNumber}}',
        readerCount: 'Tento citát zachytilo {{formattedCount}} čitateľov',
        readerCount_one: 'Tento citát zachytil {{formattedCount}} čitateľ',
        readerCount_few: 'Tento citát zachytili {{formattedCount}} čitatelia',
        readerCount_many: 'Tento citát zachytilo {{formattedCount}} čitateľov',
        readerCount_other: 'Tento citát zachytilo {{formattedCount}} čitateľov',
        privateHint: 'Tento citát vidíš iba ty',
        pendingHint: 'Prebieha kontrola zverejnenia',
        rejectedHint: 'Tento citát nebol schválený na zverejnenie',
        loading: 'Načítavam tvoje citáty',
        loadingMore: 'Načítavam staršie citáty…',
        actions: {
          add: 'Pridať',
          addAccessibility: 'Pridať citát',
          clearSearch: 'Vymazať hľadanie v citátoch',
          like: 'Páči sa mi tento citát',
          makePrivateAccessibility: 'Nastaviť citát ako súkromný',
          openAccessibility: 'Otvoriť Moje citáty',
          openBook: 'Otvoriť knihu {{title}}',
          publish: 'Zverejniť',
          publishAccessibility: 'Zverejniť tento citát',
          unlike: 'Odobrať označenie Páči sa mi',
        },
        status: {
          private: 'SÚKROMNÉ',
          pending: 'ČAKÁ NA KONTROLU',
          rejected: 'NEZVEREJNENÉ',
          published: 'ZVEREJNENÉ',
        },
        filters: {
          date: {
            label: 'Pridané',
            choose: 'Dátum zachytenia',
            any: 'Kedykoľvek',
            '30_days': 'Posledných 30 dní',
            year: 'Tento rok',
          },
          book: {
            label: 'Kniha',
            choose: 'Vybrať knihy',
            all: 'Všetko',
          },
          author: {
            label: 'Autor',
            choose: 'Vybrať autorov',
            all: 'Všetko',
          },
        },
        sort: {
          choose: 'Zoradiť citáty',
          newest: 'Najnovšie prvé',
          oldest: 'Najstaršie prvé',
          changeAccessibility: 'Zmeniť poradie citátov',
        },
        bookPicker: {
          title: 'Z ktorej knihy pochádza?',
          intro: 'Pred uložením úryvku vyber knihu.',
          selectAccessibility: 'Uložiť citát z knihy {{title}}',
        },
        errors: {
          update: 'Citát sa nepodarilo aktualizovať. Skús to znova.',
        },
        states: {
          signedOut: {
            title: 'Tvoje citáty zostanú pri tvojom účte',
            body: 'Prihlás sa, aby si mohol vidieť a ukladať úryvky zo svojich kníh.',
          },
          error: {
            title: 'Tvoje citáty sa nepodarilo načítať',
            body: 'Uložené úryvky sú v bezpečí. Skús ich otvoriť znova.',
            action: 'Skúsiť znova',
          },
          noResults: {
            title: 'Žiadne úryvky nezodpovedajú výberu',
            body: 'Skús inú frázu alebo zruš aktuálne filtre.',
            action: 'Zrušiť filtre',
          },
          empty: {
            title: 'Úryvok, ku ktorému sa chceš vrátiť?',
            body: 'Ulož si prvý citát spolu s jeho knihou a autorom.',
            action: 'Pridať citát',
          },
        },
      },
      notes: {
        title: 'Moje poznámky',
        shortTitle: 'Poznámky',
        noteCount: '{{formattedCount}} poznámok',
        noteCount_one: '{{formattedCount}} poznámka',
        noteCount_few: '{{formattedCount}} poznámky',
        noteCount_many: '{{formattedCount}} poznámok',
        noteCount_other: '{{formattedCount}} poznámok',
        searchAccessibility: 'Hľadať v poznámkach, knihách alebo autoroch',
        searchPlaceholder: 'Hľadať poznámky, knihy alebo autorov',
        addedOn: 'Pridané {{date}}',
        pageSuffix: '  ·  s. {{pageNumber}}',
        loading: 'Načítavam tvoje poznámky',
        loadingMore: 'Načítavam staršie poznámky…',
        actions: {
          add: 'Pridať',
          addAccessibility: 'Pridať poznámku',
          clearSearch: 'Vymazať hľadanie v poznámkach',
          openAccessibility: 'Otvoriť Moje poznámky',
          openBook: 'Otvoriť knihu {{title}}',
        },
        status: {
          private: 'SÚKROMNÁ POZNÁMKA',
        },
        filters: {
          date: {
            label: 'Pridané',
            choose: 'Dátum pridania',
            any: 'Kedykoľvek',
            '30_days': 'Posledných 30 dní',
            year: 'Tento rok',
          },
          book: {
            label: 'Kniha',
            choose: 'Vybrať knihy',
            all: 'Všetko',
          },
          author: {
            label: 'Autor',
            choose: 'Vybrať autorov',
            all: 'Všetko',
          },
        },
        sort: {
          choose: 'Zoradiť poznámky',
          newest: 'Najnovšie prvé',
          oldest: 'Najstaršie prvé',
          changeAccessibility: 'Zmeniť poradie poznámok',
        },
        bookPicker: {
          title: 'Ku ktorej knihe patrí?',
          intro: 'Pred uložením poznámky vyber knihu.',
          selectAccessibility: 'Pridať poznámku ku knihe {{title}}',
        },
        states: {
          signedOut: {
            title: 'Tvoje poznámky zostanú pri tvojom účte',
            body: 'Prihlás sa, aby si videl a ukladal súkromné poznámky ku knihám.',
          },
          error: {
            title: 'Tvoje poznámky sa nepodarilo načítať',
            body: 'Tvoje súkromné poznámky sú v bezpečí. Skús ich otvoriť znova.',
            action: 'Skúsiť znova',
          },
          versionMismatch: {
            title: 'Poznámky sú dočasne nedostupné',
            body: 'Tvoje poznámky sú v bezpečí. Skús to znova o chvíľu.',
            action: 'Skúsiť znova',
          },
          noResults: {
            title: 'Žiadne poznámky nezodpovedajú výberu',
            body: 'Skús inú frázu alebo zruš aktuálne filtre.',
            action: 'Zrušiť filtre',
          },
          empty: {
            title: 'Niečo, čo si chceš zapamätať?',
            body: 'Ulož si prvú súkromnú poznámku spolu s jej knihou a autorom.',
            action: 'Pridať poznámku',
          },
        },
      },
      feedback: {
        community: {
          averageAccessibility: 'Priemerné hodnotenie {{value}} z 5',
          beloreSource: 'KOMUNITA BELORE',
          empty: 'Zatiaľ tu nie sú žiadne schválené verejné recenzie.',
          eyebrow: 'RECENZIE ČITATEĽOV',
          loadError:
            'Recenzie čitateľov sa nepodarilo načítať. Tvoj súkromný dojem zostáva nedotknutý.',
          loading: 'Načítavajú sa recenzie čitateľov…',
          ownReviewAbove: 'Tvoja zverejnená recenzia je zobrazená vyššie v časti Tvoj dojem.',
          openLibraryAccessibility: 'Otvoriť túto knihu v Open Library',
          openLibraryAverageAccessibility: 'Priemerné hodnotenie v Open Library {{value}} z 5',
          openLibraryEmpty: 'V Open Library zatiaľ nie sú hodnotenia.',
          openLibraryEmptyShort: 'Zatiaľ bez hodnotení',
          openLibraryLoading: 'Načítavajú sa hodnotenia z Open Library…',
          totalRatingCount: 'Celkom {{count}} hodnotení',
          totalRatingCount_one: 'Celkom {{count}} hodnotenie',
          totalRatingCount_few: 'Celkom {{count}} hodnotenia',
          totalRatingCount_many: 'Celkom {{count}} hodnotení',
          ratingInfoTitle: 'O tomto hodnotení',
          ratingInfoOpenLibrary:
            'Tento priemer na škále do 5 hviezdičiek pochádza iba od čitateľov Open Library. Hodnotenia a recenzie čitateľov Belore sú vedené samostatne a nie sú zahrnuté v tomto skóre. Údaje Open Library sa ukladajú do vyrovnávacej pamäte, preto sa najnovšie hodnotenia ešte nemusia zobraziť.',
          openLibraryNote: 'Uložené oddelene od hodnotení a recenzií čitateľov Belore.',
          openLibraryRatingCount: '{{count}} hodnotení v Open Library',
          openLibraryRatingCount_one: '{{count}} hodnotenie v Open Library',
          openLibraryRatingCount_few: '{{count}} hodnotenia v Open Library',
          openLibraryRatingCount_many: '{{count}} hodnotení v Open Library',
          openLibrarySource: 'HODNOTENIE OPEN LIBRARY',
          publishedOn: 'Zverejnené {{date}}',
          ratingCount: '{{count}} hodnotení',
          ratingCount_one: '{{count}} hodnotenie',
          ratingCount_few: '{{count}} hodnotenia',
          ratingCount_many: '{{count}} hodnotení',
          readerReview: 'Recenzia čitateľa',
          revealSpoiler: 'Zobraziť recenziu',
          reviewCount: '{{count}} verejných recenzií',
          reviewCount_one: '{{count}} verejná recenzia',
          reviewCount_few: '{{count}} verejné recenzie',
          reviewCount_many: '{{count}} verejných recenzií',
          spoilerHidden: 'Táto recenzia obsahuje spoilery.',
          title: 'Čo hovoria čitatelia',
        },
        discardChanges: 'Zahodiť zmeny',
        eyebrow: 'OSOBNÝ DOJEM',
        intro: 'Hodnotenie a recenzia sa ukladajú samostatne. Pridaj jedno alebo oboje.',
        loadErrorBody:
          'Neotvorili sme prázdny editor, pretože by mohol prepísať už uložený dojem ku knihe.',
        loadErrorTitle: 'Tvoj dojem sa nepodarilo načítať',
        notSaved: 'Neuložené',
        saveChanges: 'Uložiť dojem',
        readPrompt: {
          addAction: 'Pridať medzi Prečítané a pokračovať',
          eyebrow: 'PREDTÝM, AKO PRIDÁŠ DOJEM',
          finishDateNote:
            'Presunutím sa kniha označí ako dnes dočítaná. Dátum môžeš neskôr upraviť.',
          keepAction: 'Ponechať v {{shelf}} a pokračovať',
          moveAction: 'Presunúť medzi Prečítané a pokračovať',
          trackedBody:
            'Hodnotenie a recenziu si otvoril pri knihe, ktorá je stále v {{shelf}}. Pred pokračovaním vyber, kam patrí.',
          title: 'Presunúť knihu medzi Prečítané?',
          untrackedBody:
            'Hodnotenie a recenzia potrebujú miesto v tvojej knižnici. Pred pokračovaním pridaj knihu medzi Prečítané.',
        },
        retryLoad: 'Skúsiť načítať znova',
        summary: {
          edit: 'Upraviť dojem',
          emptyBody:
            'Ulož si súkromné hodnotenie, napíš recenziu alebo ju zverejni pre ostatných čitateľov.',
          private: 'Súkromné',
          ratingLabel: 'TVOJE HODNOTENIE',
          rejectedBadge: 'POTREBUJE ÚPRAVU',
          rejectedBody:
            'Uprav recenziu a znova ju ulož. Opravená verzia sa opäť odošle na kontrolu.',
          rejectedTitle: 'Moderátor zanechal poznámku k tvojej verejnej recenzii',
          reviewLabel: 'TVOJA RECENZIA',
          spoilers: 'Obsahuje spoilery',
          start: 'Ohodnotiť alebo recenzovať',
          value: '{{value}} / 5',
        },
        rating: {
          clear: 'Vymazať hodnotenie',
          confirmRemove: 'Odstrániť hodnotenie',
          controlAccessibilityLabel: 'Vyber svoje hodnotenie',
          hint: 'Ťukni na hviezdu. Opätovným ťuknutím na vybranú hviezdu nastavíš pol boda.',
          keep: 'Ponechať hodnotenie',
          compactLabel: 'Hodnotenie',
          label: 'Tvoje hodnotenie',
          notRated: 'Bez hodnotenia',
          remove: 'Odstrániť hodnotenie',
          removeConfirmationBody: 'Tvoja napísaná recenzia zostane bez zmeny.',
          removeConfirmationTitle: 'Odstrániť toto hodnotenie?',
          save: 'Uložiť hodnotenie',
          sectionAccessibilityLabel: 'Osobné hodnotenie',
          update: 'Upraviť hodnotenie',
          value: '{{value}} z 5',
          zeroAccessibilityLabel: 'Ohodnotiť knihu nulou z piatich hviezdičiek',
          pickerLabel: 'Vybrať presné hodnotenie',
          confirmPicker: 'Hotovo',
          compactValue: '{{value}} / 5',
        },
        review: {
          confirmDelete: 'Odstrániť recenziu',
          containsSpoilers: 'Táto recenzia obsahuje spoilery',
          delete: 'Odstrániť recenziu',
          deleteConfirmationBody:
            'Tvoje hodnotenie zostane zachované. Odstránenú recenziu nebude možné obnoviť.',
          deleteConfirmationTitle: 'Odstrániť túto recenziu?',
          inputAccessibilityLabel: 'Tvoja napísaná recenzia',
          keep: 'Ponechať recenziu',
          compactLabel: 'Recenzia',
          label: 'Tvoja recenzia',
          optional: 'nepovinné',
          placeholder: 'Čo v tebe zostalo po poslednej strane?',
          privateNote: 'Túto recenziu vidíš iba ty.',
          publish: 'Zverejniť túto recenziu pre ostatných čitateľov',
          publishBody:
            'Predvolene zapnuté. Pri schválenej recenzii sa zobrazí tvoje verejné čitateľské meno. Ak ju chceš ponechať súkromnú, zruš zaškrtnutie.',
          save: 'Uložiť recenziu',
          sectionAccessibilityLabel: 'Osobná napísaná recenzia',
          update: 'Upraviť recenziu',
        },
        title: 'Tvoj dojem',
        unsavedClose: 'Pred zatvorením ulož zmeny alebo vyber Zahodiť zmeny.',
        unavailable: 'Ak chceš uložiť hodnotenie alebo recenziu ku knihe, prihlás sa.',
      },
      recovery: {
        title: 'Niečo sa pokazilo',
        body: 'Vyčistili sme dáta, ktoré mal Belore uložené v telefóne. Tvoje knihy, citáty a poznámky sú v bezpečí v tvojom účte.',
        reload: 'Načítať Belore znova',
      },
      common: {
        retry: 'Skúsiť znova',
        all: 'Všetko',
        allBooks: 'Všetky knihy',
        bookCount: '{{count}} kníh',
        bookCount_one: '{{count}} kniha',
        bookCount_few: '{{count}} knihy',
        bookCount_many: '{{count}} kníh',
        brand: APP_NAME.toUpperCase(),
        currentlyReading: 'Aktuálne čítaš',
        currentlyReadingShort: 'Čítaš',
        currentlyReadingTitle: 'Aktuálne čítaš',
        didNotFinish: 'Nedokončené',
        didNotFinishTitle: 'Nedokončené',
        dnf: 'DNF',
        finishedBooks: 'Prečítané knihy',
        inTbr: 'V TBR',
        ofCount: 'z {{count}}',
        pageCurrent: 'Strana {{currentPage}}',
        pageCurrentOfTotal: 'Strana {{currentPage}} z {{pageCount}}',
        pageCurrentUnknownTotal: 'Strana {{currentPage}} z neznámeho počtu',
        read: 'Prečítané',
        searchResults: 'Výsledky hľadania',
        tbr: 'TBR',
        toBeRead: 'Chcem čítať',
      },
      achievements: {
        categories: {
          journey: {
            body: 'Čítanie, ktoré aplikácia sledovala v čase.',
            title: 'Čitateľská cesta',
          },
          library: {
            body: 'Tvoja celoživotná knižnica vrátane kníh prečítaných pred registráciou.',
            title: 'Osobná knižnica',
          },
          reflection: {
            body: 'Hodnotenia a recenzie, ktoré zachytávajú tvoje čitateľské dojmy.',
            title: 'Reflexia',
          },
        },
        eyebrow: 'TVOJA ČITATEĽSKÁ CESTA',
        intro:
          'Objavuj samostatné čitateľské výzvy, sleduj potvrdený progres a pozri si odznaky či vzhľady knižnice, ktoré môžeš získať.',
        items: {
          afterword: {
            body: 'Dokonči sledovanú knihu a potom ju ohodnoť.',
            title: 'Doslov',
          },
          another_story: {
            body: 'Dokonči tri rôzne diela sledované od čítania až po koniec.',
            title: 'Ďalší príbeh',
          },
          between_bookmarks: {
            body: 'Zaznamenaj progres tej istej knihy počas troch rôznych dní.',
            title: 'Medzi záložkami',
          },
          cover_to_cover: {
            body: 'Dokonči knihu, ktorú si začal a posúval v aplikácii.',
            title: 'Od obálky po obálku',
          },
          first_bookmark: {
            body: 'Zaznamenaj svoj prvý posun v čítaní.',
            title: 'Prvá záložka',
          },
          first_impression: {
            body: 'Ohodnoť prvú prečítanú knihu, aj keď patrí do tvojej histórie.',
            title: 'Prvý dojem',
          },
          first_read: {
            body: 'Zaznamenaj prvú prečítanú knihu vrátane minulého čítania.',
            title: 'Prvý príbeh',
          },
          first_shelf: {
            body: 'Pridaj prvú knihu do knižnice.',
            title: 'Prvá polička',
          },
          five_worlds: {
            body: 'Prečítaj knihy z piatich rôznych žánrov.',
            title: 'Päť svetov',
          },
          five_public_reviews: {
            body: 'Zverejni recenzie piatich rôznych kníh. Počítajú sa iba schválené verejné recenzie.',
            title: 'Päť recenzií',
          },
          five_ratings: {
            body: 'Ohodnoť päť rôznych kníh. Počítajú sa aj súkromné hodnotenia; knihy nemusíš mať dočítané.',
            title: 'Päť hodnotení',
          },
          personal_archive: {
            body: 'Zaznamenaj desať rôznych prečítaných diel.',
            title: 'Osobný archív',
          },
          reading_rhythm: {
            body: 'Čítaj počas siedmich rôznych dní rozložených aspoň do troch týždňov.',
            title: 'Čitateľský rytmus',
          },
          second_genre: {
            body: 'Prečítaj knihy z dvoch žánrov a objav nové územie.',
            title: 'Nové územie',
          },
        },
        loadFailed: 'Tvoje achievementy sa nepodarilo načítať.',
        locked: 'Odznak',
        milestone: 'Míľnik',
        ritual: {
          body: 'Vráť sa k jednej knihe počas troch dní, dokonči ju a pridaj hodnotenie. Všetky tri kroky musia patriť k tomu istému čítaniu.',
          eyebrow: 'ODMENA · VZHĽAD KNIŽNICE',
          progress: 'Dokončené {{current}} z {{target}} krokov rituálu',
          title: 'Čitateľský rituál',
          unlocked: 'Výzva splnená · Vzhľad je dostupný',
        },
        title: 'Achievementy',
        unlocked: 'Splnené',
      },
      journey: {
        readingCalendar: readingCalendarSk,
        loading: 'Načítava sa tvoja Cesta',
        refreshFailed:
          'Zobrazuje sa naposledy uložená Cesta. Aktuálny progres sa nepodarilo načítať.',
        versionMismatch: {
          body: 'Aplikácia a server ešte dokončujú aktualizáciu. O chvíľu Belore znova načítaj.',
          cachedBody:
            'Kým aplikácia a server dokončia aktualizáciu, zobrazuje sa uložený progres. O chvíľu Belore znova načítaj.',
          title: 'Belore sa práve aktualizuje',
        },
        accessibility: {
          instrumentHint: 'Otvorí detail tohto achievementu.',
          nextGoal: 'Ďalší achievement: {{title}}',
          pager: 'Vybrané achievementy',
          pagerItem: 'Zobraziť achievement {{position}} z {{count}}',
          pagerPosition: 'Achievement {{position}} z {{count}}',
          previousGoal: 'Predchádzajúci achievement: {{title}}',
        },
        hero: {
          betweenBookmarksRemaining_few: 'Ešte {{count}} dni s tou istou knihou.',
          betweenBookmarksRemaining_one: 'Ešte jeden deň s tou istou knihou.',
          betweenBookmarksRemaining_other: 'Ešte {{count}} dní s tou istou knihou.',
          readingRhythm: '7 dní · aspoň 3 týždne',
          readingRitual: 'Tri kroky v rámci jedného čítania.',
        },
        actions: {
          collection: 'Zbierka',
          details: 'Detail výzvy',
          discover: 'Objaviť výzvy',
        },
        focused: {
          newAttemptRequired: 'Začni nové čítanie knihy a splň všetky tri kroky v rámci neho.',
          rateBook: 'Ohodnotiť knihu',
          ritualSteps: {
            post_finish_rating: 'Ohodnotiť po dočítaní',
            three_activity_days: 'Čítať počas troch dní',
            tracked_finish: 'Dočítať knihu',
          },
        },
        allComplete: {
          body: 'Prezri si zbierku alebo sa vráť, keď pribudnú nové výzvy.',
          title: 'Všetky úvodné výzvy sú splnené',
        },
        annualGoal: {
          accessibilityProgress: 'Čitateľský cieľ na rok {{year}}, {{current}} z {{target}} kníh',
          bookAccessibility: '{{title}}, dokončená {{date}}',
          booksCounted: 'Započítané knihy',
          booksCountedBody_one: '{{count}} rôzne dielo dokončené v tomto kalendárnom roku.',
          booksCountedBody_few: '{{count}} rôzne diela dokončené v tomto kalendárnom roku.',
          booksCountedBody_many: '{{count}} rôznych diel dokončených v tomto kalendárnom roku.',
          booksCountedBody_other: '{{count}} rôznych diel dokončených v tomto kalendárnom roku.',
          completed: 'Splnené',
          completedBody:
            'Dosiahol si tohtoročný cieľ. Každá ďalšia dokončená kniha zostane zapísaná nižšie.',
          completedOn: 'Dokončené {{date}}',
          completedTitle: 'Cieľ je splnený',
          compactProgress: '{{current}} / {{target}}',
          compactSet: 'Nastaviť cieľ',
          create: 'Nastaviť ročný cieľ',
          detailProgress: '{{current}} z {{target}} kníh',
          detailRemaining_one: 'Do cieľa zostáva jedna kniha.',
          detailRemaining_few: 'Do cieľa zostávajú {{count}} knihy.',
          detailRemaining_many: 'Do cieľa zostáva {{count}} kníh.',
          detailRemaining_other: 'Do cieľa zostáva {{count}} kníh.',
          detailTitle: 'Čitateľský cieľ {{year}}',
          edit: 'Upraviť cieľ',
          editor: {
            body: 'Vyber si osobný cieľ. Je to pomôcka na plánovanie, nie achievement ani podmienka na collectible.',
            create: 'Nastaviť cieľ',
            createTitle: 'Vyber si cieľ',
            decrement: 'Znížiť ročný cieľ kníh',
            editTitle: 'Zmeniť cieľ',
            eyebrow: '{{year}} · OSOBNÝ CIEĽ',
            hint: 'Zadaj celé číslo od 1 do 999.',
            increment: 'Zvýšiť ročný cieľ kníh',
            invalid: 'Vyber celé číslo od 1 do 999.',
            label: 'Počet kníh na dokončenie',
            preciseEntry: 'Ťukni pre zadanie presného čísla',
            preciseEntryHint: 'Otvorí číselnú klávesnicu na priame zadanie.',
            preciseEntryLabel: 'Zadať presný ročný cieľ, aktuálne {{count}}',
            rulerAccessibility_one: 'Ročný čitateľský cieľ, {{count}} kniha',
            rulerAccessibility_few: 'Ročný čitateľský cieľ, {{count}} knihy',
            rulerAccessibility_many: 'Ročný čitateľský cieľ, {{count}} kníh',
            rulerAccessibility_other: 'Ročný čitateľský cieľ, {{count}} kníh',
            rulerHint:
              'Potiahni doľava alebo doprava po jednej knihe. Ťuknutím na hodnotu ju zadáš priamo.',
            save: 'Uložiť cieľ',
            value_one: '{{count}} kniha',
            value_few: '{{count}} knihy',
            value_many: '{{count}} kníh',
            value_other: '{{count}} kníh',
          },
          emptyBody:
            'Vyber si, koľko rôznych kníh chceš dokončiť v tomto kalendárnom roku. Cieľ môžeš neskôr zmeniť.',
          emptyTitle: 'Nastav si čitateľský cieľ na rok {{year}}',
          eyebrow: '{{year}} · OSOBNÝ CIEĽ',
          historyEmptyBody: 'Pre tento kalendárny rok nie je uložený osobný cieľ.',
          historyEmptyTitle: 'Na rok {{year}} nebol nastavený cieľ',
          keep: 'Ponechať cieľ',
          loadFailed: 'Ročný cieľ sa nepodarilo načítať. Skús to znova.',
          loadFailedTitle: 'Tvoj ročný cieľ nie je dostupný',
          loading: 'Načítava sa ročný cieľ',
          noBooksBody: 'Dokonči knihu v roku {{year}} a automaticky sa objaví tu.',
          noBooksTitle: 'Zatiaľ žiadna dokončená kniha',
          openBookHint: 'Otvorí detail tejto knihy.',
          openHint: 'Otvorí ročný cieľ a knihy, ktoré sa doň započítali.',
          pace: {
            complete: 'Cieľ je už splnený, takže žiadne ďalšie tempo nie je potrebné.',
            label: 'Tempo do cieľa',
            remaining_one:
              'Do konca roka {{year}} zostáva {{count}} deň; zvyšok cieľa zodpovedá priemeru približne {{rate}} knihy za mesiac.',
            remaining_few:
              'Do konca roka {{year}} zostávajú {{count}} dni; zvyšok cieľa zodpovedá priemeru približne {{rate}} knihy za mesiac.',
            remaining_many:
              'Do konca roka {{year}} zostáva {{count}} dní; zvyšok cieľa zodpovedá priemeru približne {{rate}} knihy za mesiac.',
            remaining_other:
              'Do konca roka {{year}} zostáva {{count}} dní; zvyšok cieľa zodpovedá priemeru približne {{rate}} knihy za mesiac.',
          },
          progress: '{{current}} / {{target}} kníh',
          refreshFailed:
            'Zobrazuje sa naposledy uložený cieľ. Aktuálny progres sa nepodarilo načítať.',
          remaining_one: 'zostáva {{count}}',
          remaining_few: 'zostávajú {{count}}',
          remaining_many: 'zostáva {{count}}',
          remaining_other: 'zostáva {{count}}',
          remove: 'Odstrániť ročný cieľ',
          removeBody: 'Tvoje knihy aj čitateľská história zostanú bez zmeny.',
          removeConfirm: 'Odstrániť cieľ',
          removeFailed: 'Ročný cieľ sa nepodarilo odstrániť. Skús to znova.',
          removeTitle: 'Odstrániť cieľ na rok {{year}}?',
          retryHint: 'Znova načíta tvoj ročný čitateľský cieľ.',
          saveFailed: 'Ročný cieľ sa nepodarilo uložiť. Skús to znova.',
          set: 'Nastaviť cieľ na rok {{year}}',
          title: 'Ročný cieľ',
        },
        catalogue: {
          filters: {
            all: 'Všetky',
            library: 'Knižnica',
            reading: 'Čítanie',
            reflection: 'Reflexia',
          },
          status: {
            completed: 'Splnené',
            inProgress: 'Rozpracované',
            notStarted: 'Nezačaté',
            unavailable: 'Stav nie je dostupný',
          },
          intro: 'Výzvy, ktoré splníš čítaním. Väčšina prinesie odznak alebo nový vzhľad knižnice.',
          filtersLabel: 'Zobraziť výzvy',
          stillOpen: 'Ešte otvorené',
          left: 'Zostáva {{count}}',
          complete: 'Splnené',
          completeCount: '{{completed}} z {{total}}',
          earned: 'Získané {{date}}',
          earnedUnknown: 'Dátum získania nie je dostupný',
          noReward: 'Bez odmeny',
          everyAchievement: 'Všetky výzvy',
          allComplete: 'Všetkých {{count}} splnených',
          dateRange: 'Získané od {{first}} do {{last}}',
          dateRangeUnknown: 'Všetky čitateľské výzvy splnené',
          rewardGrid: 'Tvoje splnené výzvy',
          share: 'Zdieľať',
          shareTitle: '{{name}} má splnených všetkých {{count}}',
          shareAnonymous: 'Všetkých {{count}} splnených',
          shareEyebrow: 'Čitateľské výzvy',
          shareFooter: 'Čitateľský život uchovaný v Belore',
          shareFailed: 'Obrázok sa nepodarilo zdieľať. Skús to znova.',
          shareUnavailable: 'Zdieľanie obrázkov nie je na tomto zariadení dostupné.',
          shareNeedsBuild: 'Zdieľanie obrázkov vyžaduje novú vývojovú zostavu.',
          shareNeedsUpdate: 'Ak chceš zdieľať obrázky výziev, aktualizuj aplikáciu.',
          title: 'Objaviť výzvy',
        },
        celebration: achievementCelebrationTranslations.sk,
        categories: {
          library: 'Knižnica',
          reading: 'Čítanie',
          reflection: 'Reflexia',
        },
        collection: {
          inUse: 'Používa sa',
          preview: 'Ukážka',
          inUseCount: 'Používa sa: {{count}}',
          onShelfCount: 'Na poličke: {{count}}',
          onBoardCount: 'Na nástenke: {{count}}',
          onShelf: 'Na poličke',
          notPlaced: 'Zatiaľ neumiestnené',
          onBoard: 'Na tvojej nástenke',
          notOnBoard: 'Zatiaľ nie je na tvojej nástenke',
          moreToEarn: 'Ďalšie odmeny',
          putOnBoard: 'Pridať na nástenku',
          editBoard: 'Upraviť nástenku',
          boardFull: 'Nástenka je plná. Uprav ju a uvoľni miesto.',
          seeAchievement: 'Ako si odznak získal/a',
          earnedDate: 'Odznak · získaný {{date}}',
          useAppearance: 'Použiť tento vzhľad',
          appearanceBody:
            'Zmení knižnice a čitateľský stojan. Knihy a predmety zostanú na svojom mieste.',
          currentAppearance: 'Aktuálny vzhľad',
          replacesAppearance: 'Nahradí {{name}} · kedykoľvek sa môžeš vrátiť',
          placePiece: 'Umiestniť na poličku',
          movePiece: 'Presunúť na inú poličku',
          usePot: 'Použiť s rastlinou',
          placementUnknown: 'Umiestnenie sa nepodarilo overiť',
          saveFailed: 'Zmenu sa nepodarilo uložiť. Skús to znova.',

          badge: 'Odznak',
          badges: 'Odznaky',
          discoverAction: 'Objaviť výzvy',
          emptyBody:
            'Plň výzvy a získané odznaky, predmety na poličku či vzhľady knižnice sa objavia tu.',
          emptyTitle: 'Tvoja zbierka zatiaľ čaká',
          filterEmptyBody: 'Zatiaľ tu nemáš odmenu tohto typu. Ostatné odmeny zostávajú v zbierke.',
          filterEmptyTitle: 'Zatiaľ bez odmeny tohto typu',
          filters: {
            all: 'Všetko',
            badges: 'Odznaky',
            objects: 'Predmety',
            themes: 'Vzhľady',
          },
          filtersLabel: 'Filtrovať zbierku podľa typu odmeny',
          inspectTheme: 'Pozrieť vzhľad',
          intro: 'Všetko tu si získal/a čítaním. Ťukni na odmenu a použi ju vo svojej knižnici.',
          items: {
            artDecoBookmark: 'Art Deco záložka',
            artDecoReadingLamp: 'Art Deco čitateľská lampa',
            oxbloodVessel: 'Vínovočervená váza',
            readingSocietyMedallion: 'Medailón čitateľského spolku',
            soapstoneOwl: 'Mastenecová sova',
          },
          shelfPiece: 'Predmet na poličku',
          shelfPieces: 'Predmety',
          shelfPiecesBody:
            'Väčšie predmety si zachovávajú svoju skutočnú vizuálnu váhu; menšie odznaky zostávajú samostatnou zbierkou.',
          loading: 'Načítava sa tvoja zbierka',
          showAll: 'Zobraziť všetko',
          source: {
            achievementsCached:
              'Zobrazujú sa uložené odznaky. Nové odmeny za výzvy sa nepodarilo overiť.',
            achievementsUnavailable:
              'Odznaky sa nepodarilo overiť. Ostatné typy odmien môžu byť stále dostupné nižšie.',
            objectsInvalid:
              'Vlastníctvo predmetov na poličku sa nepodarilo overiť, preto sa nezobrazujú ako pravdivo prázdna zbierka.',
            objectsUnavailable:
              'Predmety na poličku sa nepodarilo načítať. Ostatné overené odmeny zostávajú zobrazené nižšie.',
            partialTitle: 'Zbierka nemusí byť úplná',
            themesCached: 'Kým je zdroj oprávnení offline, zobrazujú sa uložené vzhľady knižnice.',
            themesUnavailable:
              'Vzhľady knižnice sa nepodarilo načítať. Ostatné overené odmeny zostávajú zobrazené nižšie.',
            unavailableBody:
              'Nepodarilo sa overiť žiadny zdroj odmien. Skús znova načítať svoju overenú zbierku.',
            unavailableTitle: 'Zbierka nie je dostupná',
          },
          themePack: 'Vzhľad knižnice',
          themes: 'Vzhľady',
          title: 'Zbierka',
        },
        context: {
          followed: 'Sledovaná výzva',
          near: 'Na dosah',
          recommended: 'Odporúčané',
        },
        detail: {
          challenge: 'Výzva',
          followShort: 'Sledovať',
          followingShort: 'Sleduješ',
          yourReading: 'Tvoje čítanie',
          steps: 'Kroky',
          chooseBook: 'Vybrať knihu',
          changeBook: 'Zmeniť knihu',
          chooseBookBody: 'Vyber si knihu pre túto výzvu.',
          selectionFailed: 'Knihu sa nepodarilo zmeniť. Skús to znova.',
          noBooks: 'Začni pridaním knihy do knižnice.',
          sameBook1: 'Tento krok patrí k tejto knihe',
          sameBook2: 'Oba kroky patria k tejto knihe',
          sameBook3: 'Všetky tri kroky patria k tejto knihe',
          sameBook4: 'Všetky štyri kroky patria k tejto knihe',
          sameBookMany: 'Všetkých {{count}} krokov patrí k tejto knihe',
          starsEnough: 'stačia hviezdičky',
          ratingValue: '{{rating}} z 5 hviezdičiek',
          rewardUnlocked: 'Odmena · odomknutá',
          unlocksByRating: 'Odomkne sa hodnotením',
          unlocksWhenComplete: 'Odomkne sa splnením krokov',
          yoursNow: 'Teraz je tvoj',
          preview: 'Ukážka',
          lastStep: 'Posledný krok k odmene',
          nextStep: 'Tvoj ďalší krok',
          rateBook: 'Ohodnotiť knihu',
          readBook: 'Pokračovať v čítaní',
          finishBook: 'Dokončiť knihu',
          startBook: 'Začať čítať',
          applyTheme: 'Použiť vzhľad',
          themeApplied: 'Vzhľad je použitý',
          themeCaption: 'Kedykoľvek ho zmeníš v úprave knižnice',
          actionFailed: 'Čítanie sa nepodarilo uložiť. Skús to znova.',
          themeFailed: 'Vzhľad sa nepodarilo použiť. Skús to znova.',
          stepAccessibility: '{{step}}, {{status}}',
          dateListEnd: '{{start}} a {{end}}',
          finishedOn: '{{weekday}} {{date}}',
          weekdays: {
            '0': 'v nedeľu',
            '1': 'v pondelok',
            '2': 'v utorok',
            '3': 'v stredu',
            '4': 'vo štvrtok',
            '5': 'v piatok',
            '6': 'v sobotu',
          },
          earnedOn: 'Splnené {{date}}',
          follow: 'Sledovať výzvu',
          followOutcomes: {
            followed: 'Výzvu teraz nájdeš medzi sledovanými.',
            limit_reached: 'Môžeš sledovať najviac tri výzvy. Najprv jednu prestaň sledovať.',
            unavailable: 'Túto výzvu momentálne nemožno sledovať.',
            unfollowed: 'Výzvu už nesleduješ.',
          },
          noReward: 'Míľnik · Bez zberateľskej odmeny',
          notFound: 'Túto výzvu sa nepodarilo nájsť.',
          progress: 'Progres',
          reward: 'Odmena',
          rewardUnavailable: 'Podrobnosti odmeny nie sú dostupné',
          title: 'Detail výzvy',
          unfollow: 'Prestať sledovať',
        },
        feedback: {
          actions: {
            continue: 'Pokračovať',
            done: 'Hotovo',
            revealReward: 'Odhaliť odmenu',
            viewCollection: 'Pozrieť v zbierke',
            viewThemes: 'Pozrieť vzhľady',
          },
          confirmation: {
            archive: 'VÝZVA SPLNENÁ',
            tracked: 'VÝZVA SPLNENÁ',
          },
          result: {
            alreadyOwned: 'Odmenu už máš v zbierke',
            badgeAdded: 'Pridané do zbierky',
            milestone: 'MÍĽNIK SPLNENÝ',
            themeAdded: 'Vzhľad pridaný',
            unavailable: 'Výzva je splnená. Jej odmena je dočasne nedostupná.',
          },
          reward: {
            badge: 'NOVÝ ODZNAK',
            theme: 'NOVÝ VZHĽAD KNIŽNICE',
          },
          progress: {
            compactValue: '{{current}} / {{target}}',
            dismissAccessibility: 'Zavrieť upozornenie o pokroku',
            eyebrow: 'POKROK NA CESTE',
            headline: 'O krok bližšie',
            multiple: 'Posunulo sa {{count}} výziev',
            multiple_one: '{{count}} výzva napreduje',
            multiple_few: '{{count}} výzvy napredujú',
            multiple_many: '{{count}} výziev napreduje',
            multiple_other: '{{count}} výziev napreduje',
            multipleBody: 'Otvor Cestu a pozri si potvrdený pokrok.',
            more_one: '+{{count}} ďalšia výzva',
            more_few: '+{{count}} ďalšie výzvy',
            more_many: '+{{count}} ďalších výziev',
            more_other: '+{{count}} ďalších výziev',
            motivationMany: 'Pokračuj — odmena je na dosah',
            motivationOne: 'Už len jeden krok k odmene',
            openMultiple: 'Pozrieť cestu',
            openSingle: 'Pozrieť výzvu',
            single: '+{{delta}} {{unit}} · teraz {{current}} z {{target}}',
          },
          summary: {
            archiveBody: 'Tvoja zapísaná história posunula tieto výzvy.',
            archiveTitle: 'Tvoja cesta sa posunula',
            importBody: 'Čitateľskú históriu sme zapísali bez reťaze jednotlivých odhalení.',
            importTitle: 'História je zapísaná',
            remainingBody: 'Tieto výzvy splnila tá istá akcia.',
            remainingTitle: 'A ešte niečo',
          },
        },
        groups: {
          library_discovery: 'Objavovanie',
          library_foundations: 'Základy knižnice',
          reading_completion: 'Dokončovanie',
          reading_habits: 'Návyky',
          reading_ritual: 'Čitateľský rituál',
          reflection_notes: 'Zápisky',
        },
        progress: {
          betweenBookmarks: {
            active: 'Aktuálne čítanie',
            activeWithBest:
              'Aktuálne čítanie · doterajší najlepší pokus {{best}} z {{target}} {{unit}}',
            bestValue: 'Doterajší najlepší pokus · {{current}} z {{target}} {{unit}}',
            newAttemptRequired: 'Ak chceš pokračovať, začni nové čítanie knihy.',
          },
          dimensions: {
            days: 'Dni čítania',
            weeks: 'Aktívne týždne',
          },
          readingRhythm:
            '{{daysCurrent}}/{{daysTarget}} dní · {{weeksCurrent}}/{{weeksTarget}} týždne',
          ritualStepState: '{{step}}: {{state}}',
          ritualSteps: {
            post_finish_rating: {
              long: 'Hodnotenie po dočítaní',
              short: 'Hodnotenie',
            },
            three_activity_days: {
              long: 'Tri dni aktivity',
              short: '3 dni',
            },
            tracked_finish: {
              long: 'Dokončenie knihy',
              short: 'Dokončiť',
            },
          },
          stepComplete: 'Splnené',
          stepIncomplete: 'Zostáva',
          unavailable: 'Progres nie je dostupný',
          units: {
            books: 'kníh',
            count: 'položiek',
            weeks: 'týždňov',
            days: 'dní',
            genres: 'žánrov',
            ratings: 'hodnotení',
            reviews: 'recenzií',
            sessions: 'relácií',
            steps: 'krokov',
            works: 'diel',
          },
          value: '{{current}} z {{target}} {{unit}}',
        },
        rewards: {
          badge: 'Odmena · Odznak',
          themePack: 'Odmena · Vzhľad knižnice',
        },
        summary: {
          completed: '{{completed}} z {{total}} splnené',
        },
        unavailable: {
          body: 'Kým sa progres obnoví, celý katalóg nájdeš v Objaviť výzvy.',
          title: 'Progres cesty nie je dostupný',
        },
      },
      dataErrors: {
        bookNotFound: 'Kniha sa nenašla',
        bookRelationNotFound: 'Vzťah ku knihe sa nenašiel',
        noCurrentReading: 'Nie je vybraná žiadna aktuálne čítaná kniha',
        requestFailed: 'Knižnica nemohla dokončiť túto požiadavku.',
      },
      legal: {
        title: 'Právne informácie',
        terms: 'Podmienky používania',
        privacy: 'Ochrana súkromia',
        opensBrowser: 'Otvorí web Belore v prehliadači.',
        openError:
          'Stránku sa nepodarilo otvoriť. Skús to znova alebo otvor {{url}} v prehliadači.',
      },
      accountDeletion: {
        action: 'Vymazať účet',
        hint: 'Natrvalo vymaže tvoj účet Belore a osobné údaje o čítaní.',
        title: 'Vymazať tvoj účet?',
        body: 'Tvoj profil, knižnica, história čítania, poznámky, uložené citáty a recenzie sa natrvalo odstránia. Tento krok sa nedá vrátiť späť.',
        catalogueNote:
          'Schválené príspevky do spoločného knižného katalógu môžu zostať bez prepojenia na tvoj účet.',
        acknowledgement: 'Rozumiem, že vymazanie je trvalé.',
        confirm: 'Natrvalo vymazať účet',
        deleting: 'Vymazávame tvoj účet… Nechaj túto obrazovku otvorenú.',
        retry: 'Zopakovať vymazanie',
        error:
          'Nepodarilo sa potvrdiť dokončenie vymazania. Skús to znova a overíme a dokončíme požiadavku.',
        unavailable: 'Vymazanie účtu je dočasne nedostupné. Skús to neskôr.',
        retryRequired:
          'Vymazanie účtu sa začalo a prístup je zablokovaný. Odstraňovanie sa automaticky dokončuje. Skús to znova a overíme stav. Zatvorením obrazovky sa vymazanie nezruší.',
      },
      profileRedesign: {
        photoLibraryPermission: 'Belore používa tvoju galériu na výber profilovej fotky.',
        usePhoto: 'Použiť fotku',
        account: 'Účet',
        save: 'Uložiť',
        username: 'Používateľské meno',
        name: 'Meno',
        email: 'E-mail',
        library: 'Knihy',
        app: 'Aplikácia',
        privacyTerms: 'Súkromie a podmienky',
        languages: 'Jazyky',
        quotesPrivacy: 'Citáty a súkromie',
        developer: 'Vývoj · len vývojárske buildy',
        changePhoto: 'Zmeniť fotku',
        emailHint:
          'Tvoje používateľské meno patrí na Belore iba tebe. Zobrazuje sa nad tvojím menom a pri myšlienkach, ktoré zverejníš. Tvoj e-mail sa nikdy nezobrazuje.',
        usernameHint:
          '3 až 30 znakov: malé a veľké písmená (A–Z), čísla, bodky a podčiarkovníky. Zobrazuje sa nad tvojím menom a pri verejných myšlienkach.',
        available: '@{{username}} je voľné',
        taken: 'Toto používateľské meno je už obsadené',
        invalid:
          'Použi 3–30 malých alebo veľkých písmen (A–Z), číslic, bodiek alebo podčiarkovníkov',
        reserved: 'Toto používateľské meno je vyhradené',
        checking: 'Overujem dostupnosť…',
        checkError: 'Dostupnosť sa nepodarilo overiť. Skús to znova.',
        clear: 'Vymazať používateľské meno',
        saveError: 'Zmeny sa nepodarilo uložiť. Skús to znova.',
        appLanguage: 'Jazyk aplikácie',
        readingCount: 'Jazyky, v ktorých čítam · {{count}}',
        contentCount: 'Obsah a odporúčania · {{count}}',
        addLanguages: 'Pridať alebo odstrániť jazyky',
        readingHint: 'Iba predvolené jazykové filtre v Hľadaní. Vyber si 1 až 12.',
        contentHint:
          'Jazyky citátov, recenzií a odporúčaní na Nástenke. Stránky kníh zahŕňajú aj jazyk vydania. Vyber si 1 až 12.',
        selected: 'Vybrané · {{count}} z 12',
        allLanguages: 'Všetky jazyky',
        takePhoto: 'Odfotiť',
        choosePhoto: 'Vybrať z galérie',
        providerPhoto: 'Použiť fotku z prihlásenia',
        removePhoto: 'Odstrániť fotku',
        initials: 'Namiesto nej iniciály',
        photoHint: 'Štvorcová, zobrazená v malom. Uchovávame iba tvoju najnovšiu fotku.',
        photoError: 'Fotku sa nepodarilo aktualizovať. Skús to znova.',
        photoPermission:
          'Ak chceš odfotiť fotku, povoľ prístup ku kamere v nastaveniach zariadenia.',
        photoHidden: 'Tvoju predchádzajúcu fotku skryl moderátor. Môžeš si vybrať inú.',
        completionBody: 'Doplň svoje meno a jedinečné používateľské meno.',
        privacyIntro: 'Čo môžu ostatní čitatelia vidieť z toho, čo si uchovávaš v Belore.',
        privacySave: 'Uloženia sa počítajú bez tvojho mena',
        privacySaveBody:
          'Každý citát, ktorý si uložíš z knihy v katalógu, pridá k danému riadku jedného čitateľa.',
        privacyPopular: 'Uložené riadky sa môžu objaviť na stránke knihy',
        privacyPopularBody:
          'Keď moderátor schváli ich znenie. Iba slová knihy, nikdy tvoje meno ani myšlienka.',
        privacyThought: 'Tvoje myšlienky zostávajú súkromné',
        privacyThoughtBody:
          'Kým sa nerozhodneš niektorú zverejniť. Verejné myšlienky sa pred zobrazením kontrolujú.',
        privacyNotes: 'Poznámky sú vždy súkromné',
        privacyCustom: 'Knihy, ktoré si pridáš sám, sa nikdy nezapočítavajú',
        you: 'Ty',
        searchLanguages: 'Hľadať jazyky',
        privacyMore: 'Viac v <privacy>Zásadách ochrany súkromia</privacy>.',
        nameHint: 'Meno zobrazené na tvojom profile.',
        version: 'Belore {{version}}',
      },
      profile: {
        readingLanguages: 'Jazyky, v ktorých čítam',
        readingLanguagesHint: 'Iba predvolené jazykové filtre v Hľadaní. Vyber si 1 až 12.',
        contentLanguages: 'Jazyky obsahu',
        contentLanguagesError:
          'Jazyky obsahu sa nepodarilo uložiť alebo načítať. Ťukni a skús znova.',
        readingLanguagesError: 'Jazyky sa nepodarilo uložiť alebo načítať. Ťukni a skús znova.',

        blenderLibrary: 'Živá knižnica',
        blenderLibraryAccessibilityHint:
          'Prepína medzi živou vrstvenou orechovou knižnicou a pôvodnou grafikou knižnice.',
        blenderLibraryAccessibilityLabel: 'Živá knižnica',
        blenderLibraryBody:
          'Použi živú orechovú komnatu s knihami, relikviami a autorským svetlom. Vypnutím sa vrátiš k pôvodnej grafike knižnice.',
        body: 'Spravuj svoj účet a spoj celú čitateľskú históriu v jednej knižnici.',
        completion: {
          body: 'Vyber meno, ktoré čitatelia uvidia pri tvojich verejných recenziách. Pred vstupom do knižnice ho ulož.',
          continue: 'Pokračovať do knižnice',
          errors: {
            required: 'Ak chceš pokračovať, zadaj meno.',
            too_long: 'Použi najviac 120 znakov.',
          },
          eyebrow: 'EŠTE JEDEN DETAIL',
          loadErrorBody:
            'K tvojmu čitateľskému profilu sa teraz nevieme dostať. Skús to o chvíľu znova.',
          loadErrorEyebrow: 'MALÁ ATRAMENTOVÁ NEHODA',
          retrying: 'Skúšame znova…',
          loadErrorTitle: 'Profil sa nepodarilo načítať',
          loadingBody: 'Kontrolujeme, či je tvoj čitateľský profil pripravený.',
          loadingTitle: 'Kontrolujeme profil…',
          nameHint:
            'Použi svoje skutočné meno alebo pseudonym. Toto meno sa verejne zobrazí pri schválených recenziách.',
          nameLabel: 'Verejné meno čitateľa',
          namePlaceholder: 'Tvoje meno alebo pseudonym',
          saveError: 'Profil sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.',
          signedInAs: 'Prihlásený účet: {{email}}',
          signOutError: 'Odhlásenie sa nepodarilo dokončiť. Skús to znova.',
          title: 'Dokonči svoj profil',
          useAnotherAccount: 'Použiť iný účet',
        },
        goodreadsImportAccessibilityHint: 'Otvorí sprievodcu a náhľad CSV súboru z Goodreads.',
        goodreadsImportBody: 'Knihy, poličky, hodnotenia a recenzie.',
        goodreadsImportTitle: 'Importovať z Goodreads',
        dataExport: {
          title: 'Stiahnuť moje dáta',
          body: 'Knižnica pre iné aplikácie, citáty a poznámky ako text a úplná záloha.',
          chooseFormat: 'Vyber si, čo chceš stiahnuť.',
          formats: {
            backup: {
              title: 'Úplná záloha Belore',
              body: 'Všetko, čo si ukladáš: úplná záloha, knižnica v CSV, citáty a poznámky v jednom ZIP súbore.',
            },
            goodreads: {
              title: 'Knižnica pre iné aplikácie',
              body: 'Knihy, poličky, hodnotenia a recenzie v CSV súbore vo formáte Goodreads.',
            },
            notebook: {
              title: 'Citáty a poznámky',
              body: 'Tvoje zápisky ako text v Markdown formáte, rozdelené podľa kníh v jednom ZIP súbore.',
            },
          },
          preparing: 'Pripravujeme tvoje dáta…',
          retry: 'Skúsiť stiahnuť znova',
          successNative: 'Tvoj export je pripravený na uloženie alebo zdieľanie.',
          successWeb: 'Sťahovanie sa začalo.',
          errors: {
            download: 'Dáta sa nepodarilo stiahnuť. Skontroluj pripojenie a skús to znova.',
            rateLimit: 'Dáta si môžeš stiahnuť raz za minútu. Chvíľu počkaj a skús to znova.',
            sharingUnavailable: 'Zdieľanie na tomto zariadení nie je dostupné. Skús to znova.',
          },
        },
        illustratedLibraryPrototype: 'Prototyp ilustrovanej knižnice',
        illustratedLibraryPrototypeHint: 'Otvorí samostatnú ukážku ilustrovanej knižnice.',
        language: 'Jazyk aplikácie',
        languageBody: `Použi jazyk zariadenia alebo vyber jazyk pre ${APP_NAME}.`,
        languageSelectorAccessibilityHint: 'Otvorí zoznam podporovaných jazykov.',
        languageSelectorAccessibilityLabel: 'Jazyk aplikácie: {{language}}',
        developerLabs: {
          textRecognition: 'Laboratórium rozpoznávania textu',
          textRecognitionAccessibilityHint:
            'Porovná Apple Vision a Google ML Kit na odfotenej strane.',
          title: 'Vývojárske laboratóriá',
        },
        journeyTesting: {
          body: 'Pozri si Journey feedback bez zmeny knižnice alebo získaných odmien.',
          completionAccessibilityHint:
            'Zobrazí animovanú success obrazovku dokončeného achievementu.',
          completionAction: 'Otestovať dokončenie achievementu',
          title: 'Testovanie Journey',
        },
        languages: {
          czech: 'Čeština',
          english: 'English',
          slovak: 'Slovenčina',
        },
        libraryTools: 'Nástroje knižnice',
        libraryToolsBody: `Prenes existujúcu čitateľskú históriu do ${APP_NAME}.`,
        readerFallback: `Čitateľ ${APP_NAME}`,
        settings: 'Nastavenia aplikácie',
        settingsBody: `Vyber, ako má ${APP_NAME} vyzerať a reagovať v tomto zariadení.`,
        soundEffectsBody: 'Zvuky pri pokroku v Ceste.',
        soundEffectsTitle: 'Zvukové efekty',
        hapticsTitle: 'Hmatová odozva',
        hapticsBody: 'Jemná odozva pri výbere a uložení.',
        hapticsSaveFailed: 'Nastavenie platí teraz. Nepodarilo sa ho uložiť pre ďalšiu návštevu.',
        systemDefault: 'Podľa systému',
        openHint: 'Otvorí tvoje knihy a nastavenia.',
        title: 'Tvoj profil',
        settingsTitle: 'Nastavenia',
        settingsHint: 'Otvorí nastavenia aplikácie a účtu.',
        booksPrivate: 'Tvoje knihy a poličky vidíš len ty',
      },
      goodreadsImport: {
        unmatched: {
          title: 'Knihy bez zhody v katalógu',
          body: 'Vyber knihy, ktoré chceš ponechať ako vlastné knihy v knižnici. Použijeme údaje a históriu čítania z exportu Goodreads. Tieto knihy aj ich recenzie zostanú súkromné.',
          selected: 'Vybrané: {{selected}} z {{total}}',
          selectAll: 'Vybrať všetky',
          deselectAll: 'Zrušiť výber',
          summary:
            'Na import: {{count}} · Vlastné knihy: {{selected}} · Nevybrané: {{skipped}} · Konflikty: {{conflicts}}',
          skipped: 'Preskočené',
          conflicts: 'Konflikty',
        },
        backToProfile: 'Späť na profil',
        body: `Goodreads pripraví katalóg. ${APP_NAME} ho najprv prečíta v tomto zariadení, ukáže zhrnutie a potom ti umožní priradiť vlastné poličky stavov čítania.`,
        changeFile: 'Zmeniť',
        chooseCsv: 'CSV už mám',
        chooseDestination: `Vyber cieľ v ${APP_NAME}`,
        completed: {
          body: 'Import sa bezpečne dokončil. {{changed}} z {{count}} kníh zmenilo tvoju knižnicu.',
          created: 'Pridané nové knihy',
          openLibrary: 'Otvoriť Moje knihy',
          ratings: 'Importované hodnotenia',
          reviews: 'Importované recenzie',
          summaryTitle: 'Výsledok importu',
          title: 'Tvoja knižnica z Goodreads je importovaná',
          unchanged: 'Knihy bez potrebnej zmeny',
          updated: 'Aktualizované existujúce knihy',
        },
        errors: {
          empty_file: 'Vybraný súbor je prázdny.',
          file_too_large: 'Vyber CSV súbor z Goodreads menší ako 10 MB.',
          malformed_csv: 'CSV súbor je poškodený alebo obsahuje neukončenú hodnotu v úvodzovkách.',
          missing_headers: 'Tento súbor nevyzerá ako export knižnice z Goodreads.',
          no_books: 'Export z Goodreads neobsahuje žiadne knihy.',
          not_csv: 'Vyber .csv súbor stiahnutý z Goodreads.',
          open_failed: 'Stránku exportu Goodreads sa nepodarilo otvoriť.',
          read_failed: 'Súbor sa nepodarilo prečítať. Skús ho stiahnuť znova.',
          title: 'Náhľad súboru sa nepodaril',
        },
        importErrors: {
          apply_failed: 'Nič sa neimportovalo iba čiastočne. Skús import spustiť znova.',
          community_terms_required:
            'Na verejné recenzie treba prijať aktualizované podmienky používania. Zatiaľ ich importuj ako súkromné – pri ďalšom otvorení ťa Belore požiada o súhlas.',
          public_posting_restricted:
            'Verejné príspevky teraz nie sú pre tvoj účet dostupné. Recenzie importuj ako súkromné.',
          feature_unavailable:
            'Import z Goodreads je na serveri dočasne nedostupný. Tvoja knižnica sa nezmenila.',
          plan_changed:
            'Tvoja knižnica sa zmenila alebo platnosť plánu vypršala. Vráť sa ku kontrole a priprav nový plán.',
          prepare_failed:
            'Server nedokázal pripraviť tento import. CSV zostalo iba v tomto zariadení; skús to znova.',
          server_shelves_unresolved:
            'Server našiel vlastnú poličku stavu čítania, ktorá ešte potrebuje cieľ. Skontroluj priradenie poličiek a skús to znova.',
          title: 'V importe sa nedá pokračovať',
        },
        fileSizeKb: '{{count}} kB',
        finishReview: 'Dokončiť kontrolu',
        handoffBody:
          'Táto obrazovka pripravila iba overený plán. Žiadne knihy, hodnotenia, recenzie ani voľby sa neuložili ani neuplatnili.',
        handoffTitle: 'Import zatiaľ nie je aktívny',
        kicker: 'Katalóg zvonka',
        instructions: {
          chooseBody: 'Vráť sa sem a vyber stiahnutý súbor goodreads_library_export.csv.',
          chooseTitle: 'Vyber stiahnutý CSV súbor',
          exportBody:
            'Prejdi priamo na https://goodreads.com/review/import alebo v Goodreads otvor My Books → Import/Export. Zvoľ Export Library a počkaj, kým Goodreads pripraví súbor.',
          exportTitle: 'Exportuj svoju knižnicu',
          openBody: `Prihlás sa priamo na Goodreads. ${APP_NAME} si nikdy nepýta tvoje heslo do Goodreads.`,
          openTitle: 'Otvor Goodreads',
        },
        mappingBody:
          'Známe stavy čítania z Goodreads priradíme automaticky. Vlastné kategórie zostanú oddelené od stavu čítania.',
        mappingTitle: 'Skontroluj poličky',
        metrics: {
          books: 'Nájdené knihy',
          ratings: 'Hodnotenia',
          reviews: 'Recenzie',
        },
        noExclusiveShelf: 'Bez hlavnej poličky',
        openGoodreads: 'Otvoriť export Goodreads',
        privateBody:
          'Súbor sa najprv prečíta iba v tomto zariadení. Pred každou zmenou uvidíš zhrnutie a reprezentatívne ukážky.',
        privateTitle: 'Tvoje heslo zostáva na Goodreads',
        progress: {
          accessibility: 'Fáza {{current}} z {{total}}',
          export: 'Export',
          ready: 'Pripravené',
          review: 'Kontrola',
        },
        readyPlan: {
          body: 'Server overil {{count}} kníh: {{matched}} zodpovedá katalógu a {{create}} vytvoríme.',
          body_one:
            'Server overil {{count}} knihu: {{matched}} zodpovedá katalógu a {{create}} vytvoríme.',
          body_few:
            'Server overil {{count}} knihy: {{matched}} zodpovedajú katalógu a {{create}} vytvoríme.',
          body_many:
            'Server overil {{count}} kníh: {{matched}} zodpovedá katalógu a {{create}} vytvoríme.',
          importBooks: 'Importovať {{count}} kníh',
          importBooks_one: 'Importovať {{count}} knihu',
          importBooks_few: 'Importovať {{count}} knihy',
          importBooks_many: 'Importovať {{count}} kníh',
          noEffect:
            'V knižnici sa zatiaľ nič nezmenilo. Importovať knihy uplatní celý plán atómovo—ak zlyhá, neuloží sa žiadny čiastočný import.',
          ratingsExcluded: 'Hodnotenia: {{count}} hodnotení zostane mimo komunitných odporúčaní.',
          ratingsIncluded:
            'Hodnotenia: súhlas zahŕňa {{count}} hodnotení pre budúce komunitné odporúčania.',
          reviewsExcluded: 'Recenzie: {{count}} recenzií zostane nezverejnených.',
          reviewsIncluded:
            'Recenzie: súhlas zahŕňa {{count}} recenzií pre budúce zverejnenie na verejných stránkach kníh.',
          title: 'Voľby v tomto pláne',
        },
        readyBody:
          'Kontrola {{count}} kníh je dokončená. Toto je iba plán; v tvojej knižnici sa nič nezmenilo.',
        readyBody_one:
          'Kontrola {{count}} knihy je dokončená. Toto je iba plán; v tvojej knižnici sa nič nezmenilo.',
        readyBody_few:
          'Kontrola {{count}} kníh je dokončená. Toto je iba plán; v tvojej knižnici sa nič nezmenilo.',
        readyBody_many:
          'Kontrola {{count}} kníh je dokončená. Toto je iba plán; v tvojej knižnici sa nič nezmenilo.',
        readyTitle: 'Plán importu je pripravený',
        reviewPlan: {
          allShelvesBody:
            'Známe stavy čítania sa priradili automaticky a každá vlastná polička stavu už má cieľ. Ostatné kategórie Goodreads zostávajú oddelené nižšie.',
          allShelvesTitle: 'Stavy čítania sú priradené',
          booksAndHistory: 'Knihy a história čítania',
          bookDetails: 'Údaje o knihách',
          bookSamplesShown: 'Zobrazená je ukážka {{shown}} z {{total}} kníh.',
          booksSummary: '{{reading}} Čítaš · {{tbr}} TBR · {{read}} Prečítané · {{dnf}} DNF',
          changeFileHint: 'Vyber iný CSV súbor z Goodreads a nahraď tento náhľad.',
          choose: 'Vybrať',
          continue: 'Skontrolovať údaje a súhlasy',
          categorySamplesShown: 'Zobrazených je {{shown}} z {{total}} štítkov kategórií.',
          customCategories: 'Kategórie Goodreads',
          customCategoryNone: 'Nenašli sa žiadne vlastné kategórie Goodreads.',
          customCategoryPolicy:
            'Kategórie zostávajú oddelené od stavu čítania; nezmeníme ich hromadne na Prečítané. Plán ich zaznamená pre budúcu podporu kategórií.',
          customCategorySummary: '{{count}} štítkov v {{assignments}} priradeniach ku knihám',
          customShelvesBody:
            'Pre každú vlastnú exkluzívnu poličku vyber najbližší stav čítania. Jej pôvodný názov z Goodreads zostane v náhľade.',
          dateAdded: 'Dátum pridania',
          dateRead: 'Dátum prečítania',
          detailsBody:
            'Otvor ľubovoľnú časť, pozri si ukážku a rozhodni, ktoré súhlasy chceš zahrnúť do plánu.',
          detailsTitle: 'Skontroluj plán importu',
          fileChecked: 'CSV skontrolované v tomto zariadení',
          formats: 'Formáty kníh',
          groupedBody:
            'Veľké knižnice zostávajú zhrnuté v prehľadných častiach. Podrobná kontrola ukáže ukážky a presné počty.',
          included: 'Zahrnuté',
          invalidRatings: '{{count}} neplatných hodnotení sme označili ako bez hodnotenia.',
          invalidRatings_one: '{{count}} neplatné hodnotenie sme označili ako bez hodnotenia.',
          invalidRatings_few: '{{count}} neplatné hodnotenia sme označili ako bez hodnotenia.',
          invalidRatings_many: '{{count}} neplatných hodnotení sme označili ako bez hodnotenia.',
          invalidReadCounts: 'Počty prečítaní v {{count}} riadkoch sme ignorovali.',
          invalidReadCounts_one: 'Počet prečítaní v {{count}} riadku sme ignorovali.',
          invalidReadCounts_few: 'Počty prečítaní v {{count}} riadkoch sme ignorovali.',
          invalidReadCounts_many: 'Počty prečítaní v {{count}} riadkoch sme ignorovali.',
          duplicateBookIds: '{{count}} duplicitných Goodreads ID kníh sme ignorovali.',
          duplicateBookIds_one: '{{count}} duplicitné Goodreads ID knihy sme ignorovali.',
          duplicateBookIds_few: '{{count}} duplicitné Goodreads ID kníh sme ignorovali.',
          duplicateBookIds_many: '{{count}} duplicitných Goodreads ID kníh sme ignorovali.',
          isbn10: 'ISBN-10',
          isbn13: 'ISBN-13',
          metadataSummary: 'ISBN, vydania, vydavatelia, dátumy a počty prečítaní',
          noRatingsConsent: 'Tento súbor neobsahuje žiadne platné hodnotenia.',
          noReviewsConsent: 'Tento súbor neobsahuje žiadne písané recenzie.',
          nothingChanged: 'Toto je lokálny náhľad. V tvojej knižnici sa nič nezmenilo.',
          noWrittenReviews: 'Tento súbor neobsahuje žiadne písané recenzie.',
          overviewBody:
            'Veľkú knižnicu sme zhrnuli do niekoľkých prehľadných častí. V tvojej knižnici sa nič nezmenilo.',
          overviewTitle: 'Toto sme našli',
          planOnly:
            'CSV sa odošle a server pripraví bezpečný plán. Tvoja knižnica sa zmení až po potvrdení Importovať knihy na ďalšej obrazovke.',
          preparePlan: 'Pripraviť plán importu',
          publishers: 'Vydavatelia',
          qualityTitle: 'Niektoré riadky si vyžadujú pozornosť',
          ratingsConsentBody: `Povoliť, aby týchto {{count}} hodnotení zlepšovalo odporúčania naprieč ${APP_NAME}. Hodnotenia sa nezverejnia ako recenzie.`,
          ratingsConsentTitle: 'Použiť moje hodnotenia pre komunitné odporúčania',
          ratingsSummary: '{{rated}} hodnotených · {{unrated}} bez platného hodnotenia',
          readCountSummary:
            'Zaznamenané prečítania: {{reads}} · Knihy prečítané viackrát: {{rereads}}',
          resolveToContinue: 'Priraď {{count}} vlastných poličiek stavov a pokračuj',
          resolveToContinue_one: 'Priraď túto vlastnú poličku stavu a pokračuj',
          resolveToContinue_few: 'Priraď {{count}} vlastné poličky stavov a pokračuj',
          resolveToContinue_many: 'Priraď {{count}} vlastných poličiek stavov a pokračuj',
          reviewSamplesShown: 'Zobrazená je ukážka {{shown}} z {{total}} recenzií.',
          reviewsConsentBody: `Povoliť, aby sa týchto {{count}} recenzií zobrazilo na verejných stránkach kníh s mojím profilom ${APP_NAME}.`,
          reviewsConsentTitle: 'Zverejniť moje písané recenzie na stránkach kníh',
          reviewsSummary: '{{count}} písaných recenzií',
          reviewsSummary_one: '{{count}} písaná recenzia',
          reviewsSummary_few: '{{count}} písané recenzie',
          reviewsSummary_many: '{{count}} písaných recenzií',
          sharingBody:
            'Vyber, ako sa môžu použiť importované hodnotenia a recenzie. Obe povolenia môžeš pred importom vypnúť.',
          sharingFootnote:
            'Tieto voľby sa zaznamenajú spolu s importom a neskôr ich bude možné zmeniť.',
          sharingTitle: 'Súhlasy, ktoré chceš zahrnúť',
          shelvesNeedChoice: '{{count}} vlastných poličiek stavov potrebuje cieľ',
          shelvesNeedChoice_one: 'Jedna vlastná polička stavu potrebuje cieľ',
          shelvesNeedChoice_few: '{{count}} vlastné poličky stavov potrebujú cieľ',
          shelvesNeedChoice_many: '{{count}} vlastných poličiek stavov potrebuje cieľ',
          statusDestination: `Stav čítania v ${APP_NAME}`,
          skippedRows: '{{count}} nepoužiteľných riadkov nezahrnieme do plánu.',
          skippedRows_one: '{{count}} nepoužiteľný riadok nezahrnieme do plánu.',
          skippedRows_few: '{{count}} nepoužiteľné riadky nezahrnieme do plánu.',
          skippedRows_many: '{{count}} nepoužiteľných riadkov nezahrnieme do plánu.',
          starRating: '{{count}} hviezdičiek',
          starRating_one: '{{count}} hviezdička',
          starRating_few: '{{count}} hviezdičky',
          starRating_many: '{{count}} hviezdičiek',
          unrated: 'Bez hodnotenia',
          unratedExplanation: '{{count}} kníh nemá platné hodnotenie z Goodreads.',
          unratedExplanation_one: '{{count}} kniha nemá platné hodnotenie z Goodreads.',
          unratedExplanation_few: '{{count}} knihy nemajú platné hodnotenie z Goodreads.',
          unratedExplanation_many: '{{count}} kníh nemá platné hodnotenie z Goodreads.',
          whatWillImport: 'Čo obsahuje tento plán',
          writtenReviews: 'Písané recenzie',
        },
        reviewAgain: 'Skontrolovať priradenie poličiek',
        reviewBody: 'Skontroluj nájdené údaje a rozhodni, kam patria vlastné poličky Goodreads.',
        reviewTitle: 'Kontrola pred importom',
        status: {
          currently_reading: 'Čítaš',
          did_not_finish: 'DNF',
          read: 'Prečítané',
          to_be_read: 'TBR',
          unclassified: 'Vyber cieľ',
        },
        title: 'Prenes knihy cez prah',
        toolbarTitle: 'Import z Goodreads',
        unresolved: 'Ak chceš pokračovať, vyber cieľ pre {{count}} vlastných poličiek.',
        unresolved_one: 'Ak chceš pokračovať, vyber cieľ pre túto vlastnú poličku.',
        unresolved_few: 'Ak chceš pokračovať, vyber cieľ pre {{count}} vlastné poličky.',
        unresolved_many: 'Ak chceš pokračovať, vyber cieľ pre {{count}} vlastných poličiek.',
      },
      author: {
        biography: 'Životopis',
        accessibility: {
          openProfile: 'Otvoriť profil autora {{name}}',
          openSource: 'Otvoriť zdroj profilu',
          photo: 'Portrét autora {{name}}',
        },
        booksBy: 'Knihy od autora',
        booksRating: '{{value}} · {{count}} hodnotení kníh tohto autora v Open Library',
        booksRating_one: '{{value}} · {{count}} hodnotenie kníh tohto autora v Open Library',
        booksRating_few: '{{value}} · {{count}} hodnotenia kníh tohto autora v Open Library',
        booksRating_many: '{{value}} · {{count}} hodnotení kníh tohto autora v Open Library',
        booksRatingAccessibility: 'Knihy tohto autora majú v Open Library priemer {{value}} z 5',
        emptyBooks: 'Pre tohto autora zatiaľ nie sú dostupné knihy s ISBN.',
        emptyQuotes: 'Od tohto autora zatiaľ nemáš uložený citát.',
        loadError: 'Profil autora sa nepodarilo načítať. Skús to znova.',
        loading: 'Hľadám autora {{name}}…',
        loadingQuotes: 'Hľadám tvoje citáty…',
        openLibraryBorn: 'Autor · Narodený {{date}}',
        openLibraryProfile: 'Profil autora',
        seeMoreInOpenLibrary: 'Zobraziť viac v Open Library',
        shareFailed: 'Profil autora sa nepodarilo zdieľať.',
        shareMessage: '{{name}}\n{{url}}',
        title: 'Autor',
        unknownName: 'Neznámy autor',
        viewAll: 'Zobraziť všetko',
        website: 'Webstránka',
        yourQuotes: 'Tvoje citáty',
      },
      launch: {
        illustrated: {
          tagline: 'Vytvorte si vlastnú knižnicu.',
          preparing: 'Pripravujeme vašu knižnicu…',
          restoring: 'Obnovujeme vašu knižnicu…',
          signingIn: 'Prihlasujeme vás…',
          slow: 'Trvá to o niečo dlhšie.',
          loadError: 'Knižnicu sa nepodarilo načítať.',
          retry: 'Skúsiť znova',
          otherAccount: 'Použiť iný účet',
        },
        authenticated: {
          body: 'Tvoja relácia je pripravená. Čitateľská niť odomyká dvere.',
          title: 'Západka ťa spoznala',
        },
        authorizing: {
          body: 'Dokonči bezpečné prihlásenie. Dvere sa otvoria, keď sa vrátiš.',
          title: 'Bezpečné prihlásenie je otvorené',
        },
        booting: {
          body: 'Čitateľská niť kontroluje uloženú reláciu.',
          title: 'Obnovujem tvoju knižnicu...',
        },
        entered: {
          body: 'Tvoja čitateľská komnata je pripravená.',
          title: 'Knižnica otvorená',
        },
        entering: {
          body: 'Presúvaš sa do svojho aktuálneho priestoru na čítanie.',
          title: 'Vstupuješ do knižnice...',
        },
        error: {
          fallbackBody: 'Skús bezpečné prihlásenie znova.',
          title: 'Prah sa nepodarilo otvoriť',
        },
        opening: {
          body: 'Čitateľská komnata je pripravená za prahom.',
          title: 'Otváram tvoju knižnicu...',
        },
        sceneLoading: 'Pripravujem celú scénu tvojej knižnice…',
        signOutRemoteUnconfirmed:
          'Na tomto zariadení si odhlásený, ale vzdialené odhlásenie sa nepodarilo potvrdiť.',
      },
      menu: {
        allBooks: 'Všetky knihy',
        findBook: 'Nájsť knihu',
      },
      tabs: {
        library: 'Knižnica',
        myBooks: 'Moje knihy',
        find: 'Nájsť',
        myJourney: 'Cesta',
        journal: 'Zápisník',
        board: 'Nástenka',
        profile: 'Profil',
      },
      myBooks: {
        loading: 'Načítavajú sa tvoje knihy…',
        loadErrorTitle: 'Knihy sa nepodarilo načítať',
        loadErrorBody: 'Skontroluj pripojenie a o chvíľu to skús znova.',
        refreshErrorTitle: 'Knihy sa nepodarilo aktualizovať',
        refreshErrorBody: 'Zobrazené knihy si môžeš ďalej prezerať. O chvíľu to skús znova.',
        chooseNextRead: 'Vyber ďalšie čítanie',
        currentReading: 'Aktuálne čítaš',
        emptyNoMatches: 'Žiadne zhodné knihy',
        emptyShelf: 'Táto polica je prázdna',
        findPlace: 'Nájdi knihu a daj jej miesto vo svojej knižnici.',
        finishedEmpty: 'Prečítané knihy sa stanú súčasťou tejto police.',
        finishedOn: 'Dočítané {{date}}',
        openTbrToBegin: 'Otvor TBR policu a začni.',
        readShelf: 'Nedávno prečítané',
        searchCatalogue: 'Hľadať v katalógu',
        searchPlaceholder: 'Hľadať v mojich knihách',
        shelfWaiting: 'Knihy, ktoré na teba čakajú, sa zhromaždia tu.',
        title: 'Moje knihy',
        tryAnother: 'Skús iný názov alebo autora.',
        viewAll: 'Zobraziť všetko {{count}}',
      },
      personalPageCount: {
        sheetRange: 'Zadajte celé číslo od {{minimum}} do {{maximum}}.',
        add: 'Pridať počet strán',
        belowCurrentPage: 'Celkový počet nemôže byť nižší než aktuálna strana ({{currentPage}}).',
        change: 'Zmeniť počet strán',
        inputLabel: 'Celkový počet strán tvojho výtlačku',
        invalid: 'Zadaj celé číslo od 1 do {{maximum}}.',
        placeholder: 'Počet strán',
        privateExplanation:
          'Hodnota sa uloží iba k tvojej knihe a nezmení spoločný záznam vydania.',
        required: 'Zadaj celkový počet strán.',
        save: 'Uložiť počet',
        saveFailed: 'Počet strán sa nepodarilo uložiť. Skús to znova.',
        title: 'Tvoj počet strán',
        update: 'Upraviť počet',
      },
      progress: {
        approximatePages: '≈ {{currentPage}} strán',
        completeLabel: 'prečítané',
        percentValue: '{{progress}} %',
        percentModeCompact: '%',
        completionNote: 'Uloženie 100 % dokončí knihu a presunie ju medzi Prečítané.',
        editorBody: 'Zadaj stranu, ktorú si dosiahol, alebo percento prečítanej knihy.',
        invalidPage: 'Zadaj celé číslo strany od 0 do {{pageCount}}.',
        invalidPageWithoutTotal: 'Zadaj celé číslo strany 0 alebo viac.',
        invalidPercent: 'Zadaj celé percento od 0 do 100.',
        ofPages: 'z {{pageCount}} strán',
        pageInputLabel: 'Aktuálna strana',
        pageUnit: 'strana',
        pageOfTotal: 'Strana {{currentPage}} z {{pageCount}}',
        pageTotalPrefix: 'z',
        pageTotalValue: '{{pageCount}} strán',
        pageRulerHint: 'Ťuknutím na číslo upravíš hodnotu.',
        pagesMode: 'Strany',
        pagesUnit: 'strán',
        percentComplete: 'Prečítané {{progress}} %',
        percentInputLabel: 'Aktuálny progres',
        percentMode: 'Percentá',
        percentSliderHint: 'Ťuknutím na číslo upravíš hodnotu.',
        percentUnit: 'percento',
        resolvedPage: 'Strana {{currentPage}} z {{pageCount}} - {{progress}} %',
        saveFailed: 'Progres sa nepodarilo uložiť. Skús to znova.',
        beingSaved: 'Ukladá sa…',
        savedOffline: 'Uložené v telefóne · odošleme po pripojení',
        syncingOffline: 'Odosielame uložený progres…',
        saveFailedRestored:
          'Progres sa nepodarilo uložiť. Obnovili sme tvoj predchádzajúci progres.',
        updateTitle: 'Upraviť progres',
        moreActions: 'Ďalšie možnosti pre túto knihu',
        openBookDetails: 'Otvoriť detail knihy',
        share: 'Zdieľať',
        openBookDetailsFor: 'Otvoriť detail knihy {{title}}',
        markDidNotFinish: 'Označiť ako nedočítané',
        markDidNotFinishFailed: 'Knihu sa nepodarilo presunúť medzi nedočítané. Skús to znova.',
        unsaved: {
          title: 'Uložiť nový progres?',
          body: 'Zmena progresu ešte nie je uložená.',
          invalidBody: 'Zmena progresu nie je platná, preto ju nemožno uložiť.',
          save: 'Uložiť',
          discard: 'Zahodiť',
          cancel: 'Zrušiť',
          webSave: 'OK ju uloží. Zrušiť ti dovolí ju zahodiť alebo pokračovať v úprave.',
          webDiscard: 'Zahodiť neuloženú zmenu progresu?',
          returnToEditing: 'Späť do úpravy',
          webReturn: 'Vrátiť sa k úprave? Zrušiť zmenu zahodí.',
        },
        unknownTotalHint:
          'Toto vydanie nemá známy počet strán. Zadaj aktuálnu stranu priamo alebo nižšie doplň počet strán svojho výtlačku.',
        valueRequired: 'Zadaj svoj čitateľský progres.',
      },
    },
  },
  cs: {
    translation: {
      catalogueContributions: catalogueContributionsCopy.cs,
      community: communityCopy.cs,
      textLanguage: textLanguageCopy.cs,
      countFormat: countFormatCopy.cs,
      perspectiveLibrary: perspectiveLibraryCopy.cs,
      noteEditor: {
        fullScreen: 'Celá obrazovka',
        quickHint: 'Delší myšlenky? Otevři celou obrazovku a piš dál.',
        wholeBook: 'O celé knize',
        wholeBookHint: 'Bez stránky — na stránce knihy se zobrazí jako první.',
        titlePlaceholder: 'Název (nepovinné)',
        titleLabel: 'Název poznámky',
        placeholder: 'Piš dál…',
        bodyLabel: 'Tvoje poznámka',
        format: {
          bold: 'Tučné',
          italic: 'Kurzíva',
          underline: 'Podtržené',
          strikeThrough: 'Přeškrtnuté',
          unorderedList: 'Odrážkový seznam',
          orderedList: 'Číslovaný seznam',
        },
        insertQuote: 'Vložit citát',
        insert: 'Vložit',
        scanNew: 'Skenovat nový',
        fromBook: 'Z knihy {{book}}',
        noQuotes: 'Tady se zobrazí tvoje uložené citáty z této knihy.',
        selectQuote: 'Vybrat citát: {{body}}',
        noPage: 'Bez stránky',
        quoteRemoved: 'Citát byl odstraněn',
        openQuote: 'Otevřít citát',
        removeLink: 'Odstranit odkaz',
        hideKeyboard: 'Skrýt klávesnici',
        formatting: 'Formátování',
        newPlaceholder: 'Napiš poznámku…',
        page: 's. {{page}}',
        privateShort: 'Soukromé',
        keepWriting: 'Pokračovat v psaní',
        yourNotes: 'Tvoje poznámky',
        yourQuotes: 'Tvoje citáty',
        openJournal: 'Otevřít v zápisníku',
        journalTitle: 'Zápisník',
        openJournalShort: 'Otevřít',
        notesCount_one: '{{count}} poznámka',
        notesCount_few: '{{count}} poznámky',
        notesCount_many: '{{count}} poznámky',
        notesCount_other: '{{count}} poznámek',
        quotesCount_one: '{{count}} citát',
        quotesCount_few: '{{count}} citáty',
        quotesCount_many: '{{count}} citátu',
        quotesCount_other: '{{count}} citátů',
        words_one: '{{count}} slovo',
        words_few: '{{count}} slova',
        words_many: '{{count}} slova',
        words_other: '{{count}} slov',
        edited: 'upraveno {{date}}',
        minutes: '{{count}} min',
        private: 'Soukromá poznámka',
        untitled: 'Poznámka bez názvu',
        conflictTitle: 'Tato poznámka se změnila jinde',
        conflictBody:
          'Tvůj koncept i uložená verze jsou tady. Ponech obě jako samostatné poznámky nebo použij uloženou verzi.',
        yourDraft: 'Tvůj koncept',
        savedVersion: 'Uložená verze',
        keepBoth: 'Ponechat obě poznámky',
        useSaved: 'Použít uloženou verzi',
        reviewVersions: 'Porovnat verze',
        drafts: 'Poznámky čekající na uložení',
        resumeDraft: 'Pokračovat: {{title}}',
        draftHint: 'Uloženo v tomto zařízení',
        status: {
          saved: 'Uloženo',
          dirty: 'Neuložené změny',
          saving: 'Ukládá se…',
          failed: 'Neuloženo · Zkusit znovu',
          storageFailed: 'Neuloženo',
          conflict: 'Vyber verzi',
          invalid: 'Zkontroluj poznámku',
        },
        message: {
          captureFailed:
            'Editor nedokázal načíst poslední změny. Nech poznámku otevřenou a zkus to znovu.',
          failed: 'Neuloženo. Tvůj koncept je v tomto zařízení. Po připojení to zkus znovu.',
          storageFailed:
            'Zařízení nedokázalo uchovat poslední změny. Nech poznámku otevřenou a zkus to znovu.',
          conflict:
            'Jinde byla uložena novější verze. Tvůj koncept je v bezpečí; vyber verzi, kterou chceš ponechat.',
          invalid:
            'Přidej text, název nebo citát. Poznámka může mít nejvýše 20 000 znaků a název 200 znaků.',
        },
      },
      capture: {
        scan: 'Naskenovat stránku',
        cameraPermission: 'Povol Belore skenovat čárové kódy knih a fotit úryvky.',
        fromBook: 'Z knihy',
        typePassage: 'Nebo sem napiš citát…',
        scanAgain: 'Skenovat znovu',
        checkWords: 'Naskenováno ze stránky · zkontroluj text',
        fromProgress: 'Z tvého pokroku',
        foundOnPage: 'Nalezeno na stránce',
        pageOptional: 'Nepovinná stránka',
        optional: 'Nepovinné',
        thoughtPlaceholder: 'Proč ti utkvěl v paměti?',
        save: 'Uložit citát',
        retrySave: 'Zkusit uložit znovu',
        saveUncertain:
          'Uložení se nepodařilo potvrdit. Návrh zůstává tady. Zopakuj stejné uložení, aby nevznikla kopie.',
        choose: 'Vyber text',
        takePhoto: 'Vyfotit',
        retake: 'Vyfotit znovu',
        typeInstead: 'Napsat citát',
        retryPhoto: 'Přečíst znovu',
        usePassage: 'Použít tento text',
        useAll: 'Použít celý text',
        reading: 'Čtu stránku…',
        selectIOS: 'Podrž slovo a táhni úchyty. Nebo použij celý text a potom ho uprav.',
        selectAndroid: 'Klepni na slovo, dvakrát na větu nebo táhni úchyty.',
        pageFound: 'Nalezena stránka {{page}}',
        photoPrivacy: 'Text se čte v telefonu. Fotka se neuchovává.',
        unavailable:
          'Skenování v této verzi nebo na tomto zařízení není dostupné. Citát můžeš napsat nebo vložit.',
        cameraReason: 'Povol fotoaparát pro skenování stránky knihy.',
        allowCamera: 'Povolit fotoaparát',
        openSettings: 'Otevřít nastavení',
        scanFailed: 'Fotku se nepodařilo přečíst. Zkus lepší světlo nebo citát napiš.',
        noText: 'Text nebyl nalezen. Vyfoť stránku znovu nebo citát napiš.',
        typeQuoteInstead: 'Raději citát napíšu',
        headerSave: 'Uložit',
        privacyShow: 'Ukáže, jak se tvoje citáty počítají',
        privacyHide: 'Skryje vysvětlení',
        liveTextTitle: 'Text nebyl nalezen',
        liveTextBody:
          'Pokud je stránka na fotce ostrá, je nejspíš vypnutý Živý text. Belore s ním čte stránku přímo v telefonu.',
        liveTextSteps: 'Zapni Živý text',
        liveTextSettings: 'Nastavení',
        liveTextGeneral: 'Obecné',
        liveTextRegion: 'Jazyk a oblast',
        liveTextToggle: 'Živý text',
        liveTextReturn: 'Pak se vrať sem a fotku přečteme znovu.',
        cameraFailed: 'Fotoaparát se nespustil. Zavři skener a zkus to znovu.',
        flashOn: 'Zapnout blesk',
        flashOff: 'Vypnout blesk',
        pagePhoto: 'Vyfocená stránka knihy',
        selectionStart: 'Začátek výběru',
        selectionEnd: 'Konec výběru',
      },
      journalReturn: {
        latestNote: 'Tvoje poslední poznámka',
        edited: 'upraveno {{date}}',
        keepWriting: 'Psát dál',
        allNotes: 'Všechny ({{count}}) ›',
        allNotesAccessibility: 'Všechny poznámky ke knize, {{count}}',

        lastSaved: 'Naposledy sis z této knihy uložil/a',
        page: 's. {{page}}',
        kept: 'Co ti zůstalo',
        step: '{{current}} z {{total}}',
        summary: '{{quotes}} a {{notes}} během čtení.',
        quoteCount_one: '{{count}} citát',
        quoteCount_few: '{{count}} citáty',
        quoteCount_other: '{{count}} citátů',
        noteCount_one: '{{count}} poznámka',
        noteCount_few: '{{count}} poznámky',
        noteCount_other: '{{count}} poznámek',
        empty: 'Zatím nic uloženého. Před další knihou si přidej citát nebo poznámku.',
        loadFailed: 'Uložené záznamy se nepodařilo načíst. Zkus to znovu nebo pokračuj.',
        shareFavourite: 'Sdílet oblíbený citát',
        chooseQuote: 'Vyber citát ke sdílení.',
        addOne: 'Přidat ještě jeden',
        continue: 'Pokračovat',
        next: 'Dále',
        more: 'Načíst další záznamy',
      },
      libraryReaders: {
        title: 'Čtenáři této knihy',
        accessible: 'Čtenáři této knihy. Uložení: {{readers}}. Srdíčka citátu: {{likes}}.',
        thoughtAccessible: '{{name}}: {{thought}}. Srdíčka myšlenky: {{likes}}.',
      },
      lines: {
        title: 'Řádky z této knihy',
        intro:
          'Citáty schválené moderátory se schválenými veřejnými myšlenkami. Řádky za tvou stránkou zůstávají skryté.',
        mostSaved: 'Nejvíce uložené',
        mostLiked: 'Nejvíce srdíček',
        save: 'Uložit',
        saved: 'Uloženo',
        thoughts_one: '{{formattedCount}} myšlenka',
        thoughts_few: '{{formattedCount}} myšlenky',
        thoughts_other: '{{formattedCount}} myšlenek',
        allThoughts: 'Všechny myšlenky ({{formattedCount}})',
        empty: 'Zatím tu nejsou veřejné řádky. Ulož si citát, který ti utkvěl v paměti.',
        error: 'Tyto řádky se nepodařilo načíst.',
        mutationError: 'Změnu se nepodařilo uložit. Zkus to znovu.',
        more: 'Zobrazit další',
        viewAll: 'Zobrazit všechny citáty',
        allTitle: 'Citáty z knihy',
        previous: 'Předchozí',
        next: 'Další',
        position: 'Citát {{number}}',
        signIn: 'Přihlas se a zobraz citáty z této knihy.',
        beyond: 'Za tvou stránkou',
        laterThought: 'Mluví o pozdějších částech',
        page: 's. {{page}}',
        approximatePage: 'přibližně s. {{page}}',
        yourPage: 'jsi na s. {{page}}',
        unknownPage: 'Neznámá stránka',
        show: 'Klepnutím zobrazíš',
        reader: 'Čtenář',
        you: 'Ty',
        addThought: 'Přidat svou myšlenku',
        thoughtPrompt: 'Přidej svou myšlenku…',
        write: 'Napsat',
        makePublic: 'Zveřejnit mou myšlenku',
        publicHint: 'Po krátké kontrole se zobrazí s tvým jménem u tohoto řádku.',
        privateHint: 'Tuto myšlenku vidíš jen ty.',
        spoilerHint: 'Skryje myšlenku, dokud čtenář nedočte knihu nebo si ji sám nezobrazí.',
        keepsJournal: 'Uložením si ponecháš řádek i myšlenku ve svém zápisníku.',
        editInReview: 'Úprava se kontroluje',
        editNotApproved: 'Úprava nebyla schválena',
        approvedStays: 'Během kontroly úpravy zůstává schválená myšlenka veřejná.',
        duplicateThought:
          'U tohoto řádku už máš veřejnou myšlenku. Otevři ji v zápisníku a uprav ji.',
        viewJournal: 'Otevřít v zápisníku',
        openLine: 'Otevřít řádek a jeho myšlenky',
        likeThought: 'Líbí se mi myšlenka, {{formattedCount}} srdíček',
        unlikeThought: 'Odebrat srdíčko myšlence, {{formattedCount}} srdíček',
      },
      quoteModeration: {
        declineEditHint: 'Zamítnutím této úpravy zůstane aktuálně veřejné znění zachováno.',
        pair: 'Citát a myšlenka',
        approveBoth: 'Schválit citát i myšlenku',
        approveThought: 'Schválit myšlenku',
        approveQuoteDeclineThought: 'Schválit citát · zamítnout myšlenku',
        keepQuoteDeclineThought: 'Ponechat citát · zamítnout myšlenku',
        declineThoughtOnly: 'Zamítnout myšlenku · citát neměnit',
        quoteAlreadyApproved: 'Citát je již schválený',
        skip: 'Zatím přeskočit →',
        returnSkipped: 'Vrátit přeskočené',
        skippedNotice: 'Přeskočeno během této návštěvy. Rozhodnutí se neuložilo.',
        allSkipped: 'Načtené položky jsou odložené. Načti další nebo se vrať k přeskočeným.',
        quoteOnlyNotice: 'Citát je schválený; myšlenka zamítnutá.',

        nav: 'Citáty a myšlenky',
        moderation: 'Moderování',
        intro: 'Sdílené citáty, citáty z katalogu a myšlenky čtenářů na jednom místě.',
        shared: 'Žádost o sdílení citátu',
        wording: 'Citát z katalogu',
        thought: 'Veřejná myšlenka',
        refresh: 'Obnovit',
        loading: 'Načítání…',
        loadingItems: 'Načítání položek ke kontrole…',
        waiting: 'čeká',
        matching: 'odpovídá filtrům',
        content: 'Obsah',
        all: 'Veškerý obsah',
        sharedFilter: 'Sdílené citáty',
        wordingFilter: 'Citáty z katalogu',
        thoughtFilter: 'Myšlenky',
        status: 'Stav',
        pending: 'Čeká na schválení',
        approvedFilter: 'Schválené citáty z katalogu',
        hiddenFilter: 'Skryté citáty z katalogu',
        search: 'Hledat',
        searchPlaceholder: 'Název knihy nebo text',
        clear: 'Vymazat všechny filtry',
        readerHint:
          'Filtr čtenářů se vztahuje na sdílené citáty a myšlenky. Přispěvatelé do katalogu zůstávají anonymní.',
        empty: 'Nic nečeká na schválení',
        noMatch: 'Žádné položky neodpovídají filtrům',
        emptyHint: 'Jsou zde všechny citáty a myšlenky čekající na schválení.',
        filterHint: 'Změň nebo vymaž filtry a zobraz další položky.',
        more: 'Načíst další',
        of: 'z',
        loadError: 'Položky ke kontrole se nepodařilo načíst. Zkus obnovit stránku.',
        approvedNotice: 'Schváleno. Seznam byl obnoven.',
        hiddenNotice: 'Citát je skrytý. Seznam byl obnoven.',
        declinedNotice: 'Zamítnuto. Seznam byl obnoven.',
        conflict: 'Tato položka se během kontroly změnila. Před rozhodnutím obnov seznam.',
        decisionError: 'Rozhodnutí se nepodařilo uložit. Zkus to znovu.',
        saves: 'uložení čtenáři',
        awaiting: 'Čeká na schválení',
        approved: 'Schváleno',
        hidden: 'Skryto',
        published: 'Aktuálně veřejné',
        edit: 'Odeslaná úprava',
        spoilers: 'Obsahuje spoilery',
        hide: 'Skrýt citát',
        decline: 'Zamítnout',
        approve: 'Schválit',
        thoughtLabel: 'Myšlenka',
        quoteLabel: 'Citát',
      },
      lineModeration: {
        nav: 'Řádky a myšlenky',
        title: 'Citáty a kontrola myšlenek',
        wordings: 'Citáty z katalogu',
        thoughts: 'Veřejné myšlenky',
        intro:
          'Každý veřejný citát vyžaduje schválení znění moderátorem. Během uzavřené bety stačí ke způsobilosti jedno uložení, takže schválením citátu z katalogu se citát zveřejní. Náhled knihy upřednostňuje oblíbené schválené citáty. Veřejné myšlenky a jejich úpravy se schvalují samostatně.',
        loading: 'Načítá se…',
        empty: 'Na kontrolu nic nečeká.',
        refresh: 'Obnovit',
        more: 'Načíst další',
        approve: 'Schválit',
        allowPublication: 'Povolit zveřejnění',
        hide: 'Skrýt citát',
        notPublic: 'Nezveřejněno',
        reject: 'Zamítnout',
        spoilers: 'Mluví o pozdějších částech',
        published: 'Nyní veřejné',
        draft: 'Odeslaný text',
        error: 'Seznam se nepodařilo načíst nebo aktualizovat. Zkus to znovu.',
        conflict: 'Záznam se změnil. Obnov seznam a zkontroluj nejnovější verzi.',
      },
      quoteSharing: {
        title: 'Sdílet citát',
        ink: 'Inkoust',
        paper: 'Papír',
        teal: 'Tyrkysová',
        includeThought: 'Přidat mou myšlenku',
        showCover: 'Zobrazit obálku',
        showName: 'Přidat mé křestní jméno: {{name}}',
        myThought: 'Moje myšlenka',
        kept: 'Uloženo v Belore',
        keptBy: 'Uloženo v Belore · {{name}}',
        save: 'Uložit obrázek',
        saved: 'Obrázek uložen.',
        unavailable: 'Sdílení na tomto zařízení není dostupné. Obrázek si můžeš uložit.',
        failed: 'Obrázek se nepodařilo exportovat. Zkus to znovu.',
        tooLong:
          'Tento citát je příliš dlouhý na jeden obrázek. Sdílej kratší citát nebo vypni svou myšlenku.',
        preview: 'Náhled obrázku s citátem: {{text}}',
      },
      journal: {
        deleteErrorTitle: 'Záznam se nepodařilo odstranit',
        editInReview: 'Úprava se kontroluje',
        editNotApproved: 'Úprava nebyla schválena',
        wholeBook: 'Celá kniha',
        public: 'Veřejná',
        inReview: 'Posuzuje se',
        notPublished: 'Nezveřejněná',
        entryAccessibility: '{{kind}}, {{metadata}}: {{text}}',
        wordCount_one: '{{formattedCount}} slovo',
        wordCount_few: '{{formattedCount}} slova',
        wordCount_other: '{{formattedCount}} slov',
        edited: 'upraveno {{date}}',
        thoughtLikes: 'Líbí se {{formattedCount}} čtenářům',
        likeLine: 'Líbí se mi citát. Počet srdíček: {{formattedCount}}',
        unlikeLine: 'Odebrat srdíčko citátu. Počet srdíček: {{formattedCount}}',

        shelves: {
          all: 'Vše',
          currently_reading: 'Čteš',
          to_be_read: 'TBR',
          already_read: 'Přečteno',
          did_not_finish: 'DNF',
        },
        customPrivacy:
          'Citáty a myšlenky k vlastním knihám zůstávají soukromé a do katalogu se nezapočítávají.',
        removeThoughtConfirm: 'Odstranit tuto myšlenku? Citát zůstane v zápisníku.',
        saveDraft: 'Uložit můj text',
        savedThought: 'Uložená verze',
        noSavedThought: 'Myšlenka byla odstraněna jinde.',
        clearSearch: 'Vymazat hledání v zápisníku',

        title: 'Zápisník',
        books: 'Knihy',
        all: 'Vše',
        quotes: 'Citáty',
        notes: 'Poznámky',
        reviews: 'Recenze',
        filter: 'Filtr',
        search: 'Hledat v zápisníku',
        add: 'Přidat citát nebo poznámku',
        saveQuote: 'Uložit citát',
        writeNote: 'Napsat poznámku',
        emptyTitle: 'Místo pro tvé myšlenky',
        emptyBody: 'Ulož si citát nebo napiš poznámku ke knize. Najdeš tu i své recenze.',
        noBooksTitle: 'Začni knihou',
        noBooksBody: 'Přidej si první knihu. Potom si tu můžeš uchovat citáty, poznámky i recenze.',
        findBook: 'Najít knihu',
        checkingBooks: 'Načítáme tvé knihy…',
        booksError: 'Tvé knihy se nepodařilo načíst.',
        clearFilters: 'Zrušit filtry',
        filterEmptyTitle: 'Nic se nenašlo',
        filterEmptyBody: 'Zkus jiné hledání nebo změň filtry.',
        thought: 'Tvoje myšlenka',
        private: 'Soukromá',
        addThought: '+ Přidat myšlenku',
        editThought: 'Upravit myšlenku',
        saveThought: 'Uložit myšlenku',
        removeThought: 'Odstranit myšlenku',
        thoughtPlaceholder: 'Co ti tento citát připomněl?',
        saved: 'Uložili si: {{formattedCount}}',
        openQuote: 'Otevřít citát: {{text}}',
        order: 'Změnit pořadí zápisníku',
        error: 'Zápisník se nepodařilo načíst.',
        thoughtError: 'Myšlenka se neuložila. Tvůj koncept zůstává tady.',
        conflict: 'Tato myšlenka se změnila jinde. Před dalším pokusem načti uloženou verzi.',
        reload: 'Načíst uloženou verzi',
        privacy:
          'Citáty, které si uložíš z knih z katalogu, se počítají anonymně. Když moderátor schválí znění citátu, který si uložíš, slova z knihy se mohou objevit na její stránce bez tvého jména či myšlenky. Tvoje myšlenky zůstávají soukromé, pokud se je nerozhodneš zveřejnit. Poznámky jsou vždy soukromé.',
        privacyTitle: 'Tvoje uložené citáty',
        privacyShort: 'Uložené citáty se počítají anonymně.',
        customPrivacyShort: 'Citáty z vlastních knih zůstávají soukromé.',
        loading: 'Načítá se zápisník…',
        retry: 'Zkusit znovu',
        filterTitle: 'Filtrovat zápisník',
        bookFilterGroups: {
          currently_reading: 'Právě čtu',
          already_read: 'Přečtené',
          other: 'Ostatní knihy',
        },
        added: 'Přidané',
        clear: 'Zrušit filtry',
        newestShort: 'Nejnovější',
        oldestShort: 'Nejstarší',
        orderToggle: 'Pořadí: {{order}}. Klepni pro změnu',
        kindSelected: '{{kind}}, vybráno. Klepni a zobrazí se vše',
        removeFilter: 'Zrušit filtr: {{label}}',
        openBookAction: 'Otevřít knihu',
        reviewAccessibility: 'Recenze, {{rating}}, {{status}}, {{date}}: {{text}}',
        reviewSpoilers: 'Obsahuje spoilery · ostatní ji uvidí skrytou',
        yourReview: 'TVOJE RECENZE',
        yourRating: 'HODNOCENÍ',
        addReview: '+ Přidat recenzi',
        moreFilters: 'Další filtry: {{count}}. Otevřít filtr',
        moderatorNote: 'Poznámka moderátora',
      },

      achievementContentLanguage: 'cs',
      modularLibrary: {
        ...modularLibraryCopy.cs,
        ...modularLibraryScreenCopy.cs,
        illustratedTitle: 'Ilustrovaná knihovna',
      },
      modularLibraryBookPreview: modularLibraryBookPreviewCopy.cs,
      modularLibraryCollectible: modularLibraryCollectibleCopy.cs,
      modularLibrarySceneItems: modularLibrarySceneItemCopy.cs,
      modularLibraryArrange: modularLibraryArrangementCopy.cs,
      modularLibraryEditor: modularLibraryEditorCopy.cs,
      readingGoal: readingGoalCopy.cs,
      board: boardCopy.cs,
      bookShare: bookShareCopy.cs,
      bookFinish: bookFinishCopy.cs,
      shareImage: shareImageCopy.cs,
      boardAdmin: boardAdminCopy.cs,
      catalogueSync: catalogueSyncCopy.cs,
      progressCelebration: progressCelebrationTranslations.cs,
      libraryResources: {
        catalogueFailed: 'Dostupnost ilustrací se nepodařilo ověřit. Zkus to znovu.',
        checkingAvailability: 'Ověřuje se dostupnost ilustrací…',
        downloading: 'Stahuje se ilustrace pro použití offline…',
        downloadFailed: 'Sběratelský předmět ti zůstává. Ilustraci se nepodařilo stáhnout.',
        unavailable:
          'Tento sběratelský předmět vlastníš. Jeho ilustrace ještě není dostupná ke stažení.',
        retry: 'Zkusit stáhnout znovu',
      },
      productionLibrary: {
        loadFailed:
          'Knihovnu se nepodařilo načíst. Zkus to znovu, aby tvé uložené uspořádání zůstalo v bezpečí.',
        starterCollection: 'Úvodní sbírka',
        testingCollection: 'Testovací sbírka',
        included: 'Součást tvé knihovny',
        emptyTitle: 'Místo pro tvé knihy',
        emptyBody:
          'Přidej knihu, kterou právě čteš, už máš přečtenou nebo si ji chceš přečíst. Vytvoř si vlastní knihovnu.',
        emptyAddBook: 'Přidat první knihu',
      },
      selection: {
        choose: 'Vybrat možnost',
        openHint: 'Otevře možnosti ve spodním panelu.',
        search: 'Hledat v možnostech',
        clearSearch: 'Vymazat hledání možností',
        noResults: 'Žádné odpovídající možnosti. Zkus jiné hledání.',
        selectedCount_one: '{{count}} vybraná',
        selectedCount_few: '{{count}} vybrané',
        selectedCount_other: '{{count}} vybraných',
        done: 'Hotovo',
        limit: 'Dosažen limit výběru. Zruš výběr možnosti a vyber jinou.',
      },
      bookLanguages: {
        en: 'Angličtina',
        sk: 'Slovenština',
        cs: 'Čeština',
        de: 'Němčina',
        pl: 'Polština',
        hu: 'Maďarština',
        fr: 'Francouzština',
        es: 'Španělština',
        sv: 'Švédština',
      },
      readingCompletion: {
        save: 'Označit jako přečtené',
        unknown: 'Nepamatuji si',
        title: 'Kdy jsi knihu dočetl/a?',
        now: 'Teď',
        today: 'Dnes',
        earlier: 'Dříve',
        earlierHint: 'Stačí rok.',
        fieldsHint: 'Vyplň jen to, co si pamatuješ.',
        dateEntry: 'Nebo zadej datum',
        optionalFieldsHint: 'Stačí rok. Měsíc a den jsou volitelné.',
        yearPlaceholder: 'RRRR',
        dayLabel: 'Den',
        monthOptionalLabel: 'Měsíc (volitelné)',
        dayOptionalLabel: 'Den (volitelné)',
        optionalValue: 'Nepamatuji si',
        unknownYear: 'Nepamatuji si rok',
        calendar: 'Vybrat z kalendáře',
        dateNotSelected: 'Vyber „Dnes“, zadej rok nebo vyber „Nepamatuji si“.',
        restoreDate: 'Zadat datum, které si pamatuješ',
        monthRequiresYear: 'Nejprve zadej rok.',
        dayRequiresMonth: 'Nejprve vyber měsíc.',
        year: 'Pouze rok',
        month: 'Rok a měsíc',
        date: 'Přesné datum',
        precision: 'Co si pamatuješ?',
        choiceHint: 'Vyber podle toho, co si pamatuješ. Přesné datum nepotřebuješ.',
        nowHint:
          'Uložíme dnešní datum dočtení. U rozečtené knihy se zbývající strany započítají do dnešního cíle.',
        pastHint: 'Zaznamenáme čtení z minulosti bez přidání stran do dnešního cíle.',
        unknownHint:
          'Kniha zůstane přečtená bez data. Nepřiřadíme ji ke konkrétnímu roku ani k dnešním stranám.',
        yearLabel: 'Rok',
        monthLabel: 'Měsíc',
        yearInvalid: 'Zadej čtyřciferný rok, nejpozději aktuální.',
        monthInvalid: 'Vyber rok a měsíc, nejpozději aktuální měsíc.',
        dateInvalid: 'Vyber platné datum, nejpozději dnešní.',
        summary: 'Přečteno: {{period}}',
        yearOnlyTitle: 'Měsíc nebyl zaznamenán',
        yearOnlyBody: 'Tyto knihy se počítají do tohoto roku bez přiřazení ke konkrétnímu měsíci.',
      },
      customBooks: {
        title: 'Přidat vlastní knihu',
        addBook: 'Přidat knihu',
        pageTitle: 'Přidat knihu',
        pagePrivacy: 'Pouze ve vaší knihovně',
        privacyDisclaimer:
          'Soukromá. Katalogové údaje se sdílejí pouze po vašem odeslání a schválení moderátorem.',
        authorsSearch: 'Hledat autora nebo zadat nové jméno',
        authorsPlaceholder: 'Hledat nebo přidat autora',
        subtitlePlaceholder: 'Přidat podnázev',
        yearPlaceholder: 'Rok',
        libraryCategoriesHint: 'Vaše štítky. Neovlivňují žánrové úspěchy.',
        authorsIntro: 'Vyhledejte autora v katalogu nebo přidejte nové jméno.',
        authorsEmpty: 'Žádní odpovídající autoři. Jméno můžete přidat níže.',
        chooseDate: 'Vybrat datum',
        datePickerHint: 'Vyberte den dočtení, nejpozději dnes.',
        titleRequired: 'Název knihy je povinný.',
        authorLabel: 'Autor',
        addAnotherAuthor: 'Přidat dalšího autora',
        enterAuthor: 'Zadat autora',
        optional: 'Volitelné',
        chooseCategories: 'Vybrat kategorie',
        isbnPlaceholder: 'ISBN-10 nebo ISBN-13',
        categoriesIntro: 'Vyberte všechny, které se hodí.',
        categoriesPrivacy: 'Soukromé štítky · nezapočítávají se do žánrových úspěchů',
        categoriesSearch: 'Hledat kategorie',
        categoriesSelected: 'Vybráno: {{count}}',
        categoriesClear: 'Vymazat',
        categoriesDone: 'Hotovo',
        categoriesEmpty: 'Žádné odpovídající kategorie. Zkuste jiný výraz.',
        missingTitle: 'Chybí v katalogu?',
        entryPrivacy:
          'Přidej ji soukromě. Později můžeš odeslat její katalogové údaje ke kontrole.',
        entryHint: 'Nemůžeš najít svou knihu nebo správné vydání?',
        privacy:
          'Přidejte knihu do osobní knihovny. Katalogové údaje zůstanou soukromé, dokud je neodešlete a moderátor je neschválí.',
        signInRequired: 'Přihlaste se a uložte vlastní knihu do své knihovny.',
        signIn: 'Přejít k přihlášení',
        bookTitle: 'Název knihy',
        authors: 'Autoři',
        authorHint:
          'Vyberte návrh z katalogu nebo přidejte jméno. Autoři se zobrazí v pořadí přidání.',
        authorName: 'Jméno autora',
        addAuthor: 'Přidat „{{name}}“',
        removeAuthor: 'Odstranit {{name}}',
        moveAuthorEarlier: 'Posunout {{name}} výše',
        searchingAuthors: 'Hledáme autory v katalogu…',
        authorSearchFailed: 'Návrhy autorů nejsou dostupné. Jméno můžete přidat i sami.',
        edition: 'Podrobnosti vydání',
        isbn: 'ISBN',
        isbnHint: 'Nepovinné. Zadejte ISBN uvedené v tomto vydání, pokud ho má.',
        language: 'Jazyk',
        unknown: 'Neznámý',
        otherLanguage: 'Jiný jazyk',
        languageCode: 'Kód jazyka',
        languageCodeHint: 'Zadejte dvoupísmenný kód jazyka, například „uk“ pro ukrajinštinu.',
        pages: 'Počet stran',
        pagesHint:
          'Nepovinné. Bez celkového počtu můžete zapisovat strany a knihu sami označit za přečtenou.',
        binding: 'Formát',
        bindings: {
          unknown: 'Neuvedený',
          paperback: 'Měkká vazba',
          hardcover: 'Pevná vazba',
          ebook: 'Elektronická kniha',
          other: 'Jiný',
        },
        genres: 'Osobní kategorie',
        genresHint:
          'Vyberte všechny vhodné kategorie. Tyto osobní štítky se nezapočítávají do žánrových úspěchů.',
        moreDetails: 'Další údaje o vydání',
        subtitle: 'Podnázev',
        publisher: 'Vydavatel',
        publishedYear: 'Rok vydání',
        reading: 'Ve vaší knihovně',
        status: 'Police podle čtení',
        statuses: {
          to_be_read: 'K přečtení',
          currently_reading: 'Právě čtu',
          already_read: 'Přečtené',
          did_not_finish: 'Nedočtené',
        },
        startsAtBeginning: 'Čtení začne na straně 0. Po přidání knihy můžete stranu upravit.',
        knowFinishDate: 'Vím, kdy jsem ji dočetl/a',
        unknownFinishDateHint:
          'Nechte vypnuté, pokud datum neznáte. Knihu bez data nezapočítáme do ročního cíle.',
        finishedOn: 'Datum dočtení',
        dateHint: 'Použijte formát rok-měsíc-den. Vyberte skutečné datum nejpozději dnes.',
        datePlaceholder: 'RRRR-MM-DD',
        save: 'Přidat do mé knihovny',
        retrySave: 'Zopakovat stejné uložení',
        optionalHint:
          'ISBN a údaje o vydání jsou nepovinné. Přidejte autora nebo vyberte „Neznámý autor“.',
        openMatch: 'Otevřít knihu z katalogu',
        editIsbn: 'Upravit ISBN',
        shelfDestination: 'Soukromá · přidá se na polici {{shelf}}',
        validation: {
          title: 'Zadejte název s nejvýše 1 000 znaky.',
          subtitle: 'Podnázev může mít nejvýše 2 000 znaků.',
          publisher: 'Název vydavatele může mít nejvýše 1 000 znaků.',
          authors:
            'Přidejte alespoň jednoho autora nebo vyberte „Neznámý autor“. Můžete přidat nejvýše 16 autorů.',
          authorsPending: 'Přidejte jméno z pole autora nebo ho před uložením smažte.',
          isbn: 'Zkontrolujte ISBN. Použijte platné 10- nebo 13místné ISBN knihy nebo pole nechte prázdné.',
          language: 'Zadejte dvoupísmenný kód jazyka nebo vyberte „Neznámý“.',
          pages: 'Zadejte celý počet stran od 1 do 10 000 nebo pole nechte prázdné.',
          year: 'Zadejte celý rok vydání od 1 do 9999 nebo pole nechte prázdné.',
          date: 'Zadejte skutečné datum dočtení ve formátu RRRR-MM-DD, nejpozději dnes, nebo označte datum za neznámé.',
        },
        errors: {
          catalogueMatchShort: 'Toto vydání už je v katalogu.',
          catalogueMatch:
            'Toto ISBN už je v katalogu. Otevřete vydání, zkontrolujte ho a přidejte do knihovny.',
          conflict: 'Tyto údaje se nepodařilo uložit společně. Zkontrolujte je a zkuste to znovu.',
          invalid:
            'Některé údaje o knize se nepodařilo přijmout. Zkontrolujte pole a zkuste to znovu.',
          unavailable:
            'Ukládání je dočasně nedostupné. Údaje zůstávají zde; až se služba obnoví, zopakujte stejné uložení.',
          uncertain:
            'Nepodařilo se potvrdit uložení knihy. Zopakujte stejné uložení, aby se nepřidala dvakrát.',
          signIn: 'Přihlaste se znovu a uložte knihu do své knihovny.',
        },
      },
      libraryBookPreview: {
        title: 'Náhled knihy',
        inMyBooks: 'V mých knihách',
        openDetails: 'Otevřít detail knihy',
        currentPage: 'Strana {{currentPage}}',
        percent: '{{progress}} %',
        accessibility: {
          open: 'Zobrazit náhled knihy {{title}}',
          close: 'Zavřít náhled knihy',
          details: 'Otevřít detail knihy {{title}}',
        },
      },
      accessibility: {
        addBookToShelf: 'Přidat knihu {{title}} do čtenářské kategorie',
        archiveFilter: '{{label}}, {{count}} knih',
        archiveFilter_one: '{{label}}, {{count}} kniha',
        archiveFilter_few: '{{label}}, {{count}} knihy',
        archiveFilter_many: '{{label}}, {{count}} knih',
        adjustProgress: 'Upravit hodnotu pokroku o {{amount}}',
        backToMyBooks: 'Zpět na Moje knihy',
        changeBookShelf: 'Změnit kategorii knihy {{title}}, aktuálně {{shelf}}',
        clearBookSearch: 'Vymazat hledání knih',
        closeBookBarcodeScanner: 'Zavřít skener čárového kódu knihy',
        closeChamberActions: 'Zavřít akce komnaty',
        closeCompletion: 'Zavřít oslavu dokončení',
        closeProgressEditor: 'Zavřít úpravu pokroku',
        categorySheetHandle: 'Úchyt panelu čtenářských kategorií',
        categorySheetHandleHint: 'Táhni dolů nebo aktivuj pro zavření čtenářských kategorií',
        emptyFirstReward: 'První odměna: Strážce stránek. Začni výběrem knihy.',
        emptyWeeklyGoal: 'Týdenní cíl. Začíná knihou.',
        filterSheetHandle: 'Úchyt panelu filtrů vyhledávání',
        filterSheetHandleHint: 'Táhni dolů nebo aktivuj pro zavření filtrů vyhledávání',
        progressSheetHandle: 'Úchyt panelu pokroku',
        progressSheetHandleHint: 'Tažením dolů nebo aktivací zavři panel pokroku',
        openBookByAuthor: 'Otevřít {{title}} od {{authors}}',
        openBookProgress: 'Otevřít {{title}}, strana {{currentPage}}',
        openBookProgressWithTotal: 'Otevřít {{title}}, strana {{currentPage}} z {{pageCount}}',
        progressEditorHint: 'Otevře ovládání pro změnu aktuální strany',
        pageRuler: 'Aktuální strana {{currentPage}} z {{pageCount}}',
        percentSlider: 'Čtenářský pokrok, {{progress}} procent',
        progressValueInputPages: 'Aktuální strana',
        progressValueInputPercent: 'Procentuální pokrok',
        scanBookBarcode: 'Naskenovat čárový kód knihy',
        prototypeGoal:
          '{{completed}} z {{target}} čtenářských relací splněno pro prototyp týdenního cíle',
        prototypeGoal_one:
          '{{completed}} z {{target}} čtenářské relace splněno pro prototyp týdenního cíle',
        prototypeGoal_few:
          '{{completed}} z {{target}} čtenářských relací splněno pro prototyp týdenního cíle',
        prototypeGoal_many:
          '{{completed}} z {{target}} čtenářských relací splněno pro prototyp týdenního cíle',
        prototypeReward: 'Další odměna prototypu: Strážce stránek, zbývá {{count}} relací',
        prototypeReward_one: 'Další odměna prototypu: Strážce stránek, zbývá {{count}} relace',
        prototypeReward_few: 'Další odměna prototypu: Strážce stránek, zbývají {{count}} relace',
        prototypeReward_many: 'Další odměna prototypu: Strážce stránek, zbývá {{count}} relací',
        searchByTitleOrAuthor: 'Hledat podle názvu, autora nebo ISBN',
        setProgressPercent: 'Nastavit pokrok na {{value}} procent',
        searchMyBooks: 'Hledat v mých knihách',
        showChamberActions: 'Zobrazit akce komnaty',
        showTbrList: 'Zobrazit seznam TBR',
        showTbrListHint: 'Otevře tvoje knihy v TBR',
        startNextBook: 'Začít číst {{title}}',
        updateProgress: 'Upravit pokrok knihy {{title}}, přečteno {{progress}} %',
        updateProgressUnknownTotal:
          'Upravit pokrok knihy {{title}}, celkový počet stran není známý',
      },
      actions: {
        add: 'Přidat',
        addBook: 'Přidat knihu',
        addToTbr: 'Přidat do TBR',
        adding: 'Přidává se...',
        back: 'Zpět',
        browseBooks: 'Procházet knihy',
        cancel: 'Zrušit',
        chooseLater: 'Vyberu později',
        close: 'Zavřít',
        continueApple: 'Pokračovat přes Apple',
        continueAuthKit: 'Pokračovat přes AuthKit',
        continueGoogle: 'Pokračovat přes Google',
        details: 'Detaily',
        find: 'Najít',
        findBooks: 'Najít knihy',
        finishBook: 'Dokončit knihu',
        more: 'Další akce',
        retry: 'Zkusit znovu',
        remove: 'Odstranit',
        saveProgress: 'Uložit pokrok',
        saving: 'Ukládá se...',
        search: 'Hledat',
        share: 'Sdílet',
        signOut: 'Odhlásit se',
        signInEmail: 'Přihlásit se e-mailem',
        signingOut: 'Odhlašování...',
        startReading: 'Začít číst',
        trackProgress: 'Sledovat pokrok',
      },
      auth: {
        callbackFailed: 'Přihlášení se nepodařilo dokončit',
        callbackReturning: 'Dokončuje se zabezpečené přihlášení...',
        callbackTryAgain: 'Vrátit se a zkusit znovu',
        errors: {
          authorizationGrantInvalid:
            'Poskytovatel identity nedokončil přihlášení. Vrať se a začni nový pokus.',
          callbackMissingCode: 'Ve zpětném volání AuthKit chybí kód nebo PKCE stav',
          callbackStateInvalid: 'Stav zpětného volání AuthKit je neplatný nebo vypršel',
          remoteSignOutUnconfirmed: 'Vzdálené odhlášení WorkOS nebylo potvrzeno',
          sessionExpired: 'Relace WorkOS vypršela',
          signInRequired: `Před voláním ${APP_NAME} API se přihlas`,
        },
        proof: {
          apiUserCreated: 'Vytvoří se po ověření API',
          appOwnedUser: 'Uživatel aplikace',
          authenticated: 'Ověřeno',
          body: 'Tato ohraničená obrazovka ověřuje nativní WorkOS PKCE tok, uloženou relaci a autentizované volání přes Nest API.',
          eyebrow: 'READ TRACKER · OVĚŘENÍ AUTH',
          nestMe: 'Nest /me',
          notAuthenticated: 'Neověřeno',
          redirect: 'Přesměrování: {{uri}}',
          title: 'Důvěryhodné dveře do knihovny.',
          verified: 'Ověřeno',
          verifyNestApi: 'Ověřit Nest API',
          waiting: 'Čeká se',
          workosSession: 'WorkOS relace',
        },
      },
      dev: {
        typographyStandoff: {
          body: 'V každé ukázce jsou stejné produktové role, velikosti a paleta. Porovnej rytmus nadpisu, velké číslice, příponu pokroku a středoevropskou diakritiku.',
          candidateLabel: 'Typografická ukázka {{name}}',
          current: 'Současné',
          currentPage: 'Aktuální strana',
          derived: 'Strana 146 ze 416',
          eyebrow: 'TYPOGRAFICKÁ LABORATOŘ · ŠEST PÍSEM',
          languageProof: 'Příliš žluťoučký kůň úpěl ďábelské ódy.',
          loadError: 'Porovnávací písma se nepodařilo načíst.',
          loading: 'Připravuji porovnávací písma...',
          modalTitle: 'Upravit čtenářský pokrok',
          pageSuffix: 'ze 416 stran',
          title: 'Šest serifů vstupuje do knihovny.',
        },
      },
      catalogue: {
        authorSuggestions: 'Autoři',
        authorSuggestionsLoading: 'Hledáme autory…',
        authorSuggestionsFailed: 'Návrhy nejsou dostupné. Stále můžete hledat podle jména.',
        authorSuggestionsEmpty: 'Zatím žádné návrhy. Stále můžete hledat podle jména.',
        obalkyFeedback: {
          title: 'Hodnocení a recenze z Obálky knih',
          averageAccessibility: 'Hodnocení Obálky knih: {{value}} z 5',
          ratingInfo:
            'Toto hodnocení na škále do 5 hvězdiček pochází od čtenářů Obálky knih. Dostupné úryvky z recenzí najdete pod popisem knihy. Hodnocení a recenze Belore jsou uvedeny samostatně. Údaje poskytovatele se ukládají do mezipaměti a jejich obnovení může trvat až 24 hodin.',
          votes_one: '{{count}} hodnocení',
          votes_few: '{{count}} hodnocení',
          votes_many: '{{count}} hodnocení',
          votes_other: '{{count}} hodnocení',
          excerpts: 'Úryvky z recenzí',
          openSource: 'Otevřít na obalkyknih.cz',
        },
        coverCredit: {
          prefix: 'Obálka z',
          accessibilityHint: 'Otevře tuto knihu na obalkyknih.cz.',
        },
        account: 'KATALOG ÚČTU',
        addToShelf: 'Přidat do kategorie',
        addedToTbr: 'Kniha {{title}} byla přidána do TBR.',
        allResults: 'Všechny',
        anyLanguage: 'Všechny',
        assignmentFailed: 'Knihu se nepodařilo přesunout. Zkus to znovu.',
        assignedToShelf: 'Kniha {{title}} je teď v kategorii {{shelf}}.',
        availableResults: 'Nepřidané',
        bookLanguages: 'Jazyky knih',
        aboutBook: 'O knize',
        allGenres: 'Všechny žánry',
        detailEyebrow: 'DETAIL KNIHY',
        detailLoading: 'Načítám podrobnosti o knize…',
        detailUnavailable: 'Podrobnosti o knize se nepodařilo načíst. Zkus to znovu.',
        detailUnavailableTitle: 'Knihu se nepodařilo otevřít',
        detailUnavailableBody: 'Podrobnosti o knize teď nejsou dostupné. Za chvíli to zkus znovu.',
        detailRefreshFailedTitle: 'Knihu se nepodařilo aktualizovat',
        detailRefreshFailedBody:
          'Načtené podrobnosti si můžeš dál prohlížet. Zkus je aktualizovat znovu.',
        partialDetail: {
          body: 'Toto vydání se teď nepodařilo načíst. Informace z katalogu výše nemusí být úplné.',
          retry: 'Zkusit znovu',
          source: 'Zobrazit zdroj katalogu',
          title: 'Úplné podrobnosti nejsou dostupné',
        },
        publicPreview: {
          addAction: 'Přidat do mé knihovny',
          addBody: 'Vyber, do které kategorie kniha patří.',
          addTitle: 'Ulož si tuto knihu',
          footerBody: 'Ukládej knihy, sleduj čtení a sdílej recenze.',
          footerTitle: 'Pokračuj v Belore',
          openInAppAction: 'Otevřít v Belore',
          openInAppFailed: 'Belore se nepodařilo otevřít. Můžeš pokračovat ve čtení tady.',
          openInAppTitle: 'Máš už Belore v tomto zařízení?',
          signInAction: 'Přihlásit se do Belore',
          signInBody: 'Vyber si způsob přihlášení.',
          signInTitle: 'Přihlásit se do Belore',
          shareNotAvailable: 'Toto vydání zatím není dostupné pro veřejné sdílení.',
        },
        editionsLoadError: 'Další vydání se nepodařilo načíst. Zkus to znovu.',
        isbnCopy: {
          action: 'Kopírovat ISBN',
          copied: 'Zkopírováno do schránky',
          failed: 'Nepodařilo se zkopírovat. Podržte ISBN a zkopírujte ho.',
        },
        metadataPageCount: 'Strany',
        editionLanguage: 'Jazyk vydání: {{language}}',
        editionLanguageUnknown: 'Jazyk vydání není známý',
        editionUnavailable: 'Toto vydání zatím nelze otevřít v aplikaci.',
        genreAndSubjectLabel: 'Žánry a témata z katalogu',
        loadMoreEditions: 'Načíst další vydání',
        loadingEditions: 'Načítání…',
        retryEditions: 'Zkusit znovu',
        noMatchingEditionsLoaded: 'Zatím nejsou načtena žádná odpovídající vydání.',
        noOtherEditions: 'U této knihy zatím neznáme další vydání.',
        noOtherEditionsInLanguages: 'Žádná další vydání neodpovídají vybraným jazykům knih.',
        openInOpenLibrary: 'Otevřít v Open Library',
        otherEditions: 'Další vydání',
        moveReadBookTitle: 'Přesunout přečtenou knihu?',
        moveReadBookBody:
          'Tato kniha je už označená jako přečtená. Chceš ji přesunout do kategorie „{{shelf}}“?',
        moveReadBookAction: 'Přesunout knihu',
        rereadProgressHint: 'Průběh čtení začne znovu od začátku.',
        removeFromLibrary: 'Odstranit z mé knihovny',
        removeFromLibraryBody:
          'Tvůj čtenářský pokrok, osobní hodnocení, recenze, poznámky a citáty k tomuto vydání se smažou. Kniha zůstane ve společném katalogu.',
        removePrivateBookFromLibraryBody:
          'Tvůj čtenářský pokrok, osobní hodnocení, recenze, poznámky a citáty k této soukromé knize se smažou.',
        removeFromLibraryFailed: 'Knihu se nepodařilo odstranit z tvé knihovny. Zkus to znovu.',
        removeFromLibraryTitle: 'Odstranit {{title}} z tvé knihovny?',
        showMoreEditions: 'Zobrazit všech {{count}}',
        genres: {
          adventure: 'Dobrodružné',
          biography_memoir: 'Biografie a memoáry',
          childrens: 'Dětská literatura',
          comics_graphic_novels: 'Komiksy a grafické romány',
          fantasy: 'Fantasy',
          historical_fiction: 'Historická próza',
          history: 'Historie',
          horror: 'Horor',
          literary_fiction: 'Literární fikce',
          mystery_thriller: 'Detektivky a thrillery',
          nonfiction: 'Literatura faktu',
          poetry: 'Poezie',
          romance: 'Romantika',
          science_fiction: 'Vědecká fantastika',
          young_adult: 'Pro mládež',
        },
        markdownAccessibility: {
          blockquote: 'Bloková citace',
          bulletPoint: 'Odrážka',
          headings: 'Nadpisy',
          images: 'Obrázky',
          links: 'Odkazy',
          math: 'Matematický výraz: {latex}',
          nestedBlockquote: 'Vnořená bloková citace',
          nestedBulletPoint: 'Vnořená odrážka',
          nestedOrderedItem: 'Vnořená položka seznamu {n}',
          orderedItem: 'Položka seznamu {n}',
          tableRow: 'Řádek {n}: {content}',
        },
        externalCandidateUnavailable:
          'Tuto externí knihu se zatím nepodařilo uložit. Zkus to znovu.',
        applyFilters: 'Zobrazit výsledky',
        activeFilterCount: '{{count}} aktivních filtrů',
        activeFilterCount_one: '{{count}} aktivní filtr',
        activeFilterCount_few: '{{count}} aktivní filtry',
        filterPanel: 'Upřesnit hledání',
        filters: 'Filtry',
        filtersAccessibilityLabel: 'Filtry, aktivní: {{count}}',
        findTitle: 'Najít knihy',
        recentSearches: 'Nedávná hledání',
        clearRecentSearches: 'Vymazat',
        clearRecentSearchesLabel: 'Vymazat nedávná hledání',
        repeatRecentSearch: 'Hledat znovu {{query}}',
        scanTip:
          'Máš knihu v ruce? Naskenuj čárový kód na zadní straně obálky a otevři přesně toto vydání.',
        idleHint: 'Hledej v katalogu podle názvu, autora nebo ISBN.',
        findInvitation: 'Hledej knihu podle názvu,\nautora nebo ISBN.',
        findFocusedTitle: 'Kterou knihu\nhledáš?',
        findFocusedHint: 'Stačí název, autor\nnebo ISBN.',
        findSearchPlaceholder: 'Název, autor nebo ISBN',
        idleTitle: 'Najdi svou knihu',
        idleBody: 'Hledej podle názvu, autora nebo ISBN, nebo si přidej vlastní knihu.',
        startSearch: 'Hledat knihy',
        resultsTitle: 'Výsledky',
        searchingResults: 'Hledám…',
        bookLanguagesLabel: 'Jazyky knih',
        changeBookLanguages: 'Jazyky knih: {{languages}}. Změnit',
        searchAllFields: 'Hledat ve všech polích',
        keepOneLanguage: 'Před odebráním tohoto jazyka vyberte další.',
        languages: 'Jazyky',
        languageSelectorAccessibilityLabel: '{{languages}}, vybráno: {{count}}',
        resetFilters: 'Obnovit',
        searchIn: 'Hledat v',
        searchScope: {
          all: 'Vše',
          author: 'Autor',
          isbn: 'ISBN',
          title: 'Název',
        },
        linkCopied: 'Odkaz na knihu byl zkopírován.',
        moveToShelf: 'Přesunout do kategorie',
        noResults: 'Žádné odpovídající knihy. Zkus jiný název, autora nebo ISBN.',
        emptyTitle: 'Nenašly se žádné knihy',
        emptyHintAll: 'Zkontroluj pravopis nebo zkus jiný název, autora či ISBN.',
        emptyHintTitle: 'Podle názvu se nic nenašlo. Zkontroluj pravopis nebo zkus autora či ISBN.',
        emptyHintAuthor:
          'Podle autora se nic nenašlo. Zkontroluj pravopis nebo zkus název či ISBN.',
        emptyHintIsbn:
          'K tomuto ISBN jsme nenašli žádné vydání. Zkontroluj číslo nebo hledej podle názvu či autora.',
        emptySearchedLanguages: 'Hledáno v jazycích: {{languages}}.',
        changeLanguages: 'Změnit jazyky',
        nowOnReadingDesk: 'Kniha {{title}} je teď na tvém čtenářském stole.',
        pageCount: '{{count}} stran',
        pageCount_one: '{{count}} strana',
        pageCount_few: '{{count}} strany',
        pageCount_many: '{{count}} stran',
        pageCountUnknown: 'Počet stran není známý',
        preview: 'NÁHLEDOVÝ KATALOG',
        resultCount: 'Načtených knih: {{count}}',
        resultCount_one: 'Načtená kniha: {{count}}',
        resultCount_few: 'Načtené knihy: {{count}}',
        resultCount_many: 'Načtených knih: {{count}}',
        returnToFind: 'Vrať se do hledání a vyber jiný výsledek.',
        savedResults: 'Moje knihy',
        showLess: 'Zobrazit méně',
        showMore: 'Zobrazit více',
        shareFailed: 'Odkaz na knihu se nepodařilo sdílet.',
        shareMessage: '{{title}}\n{{url}}',
        shareText: `Otevři {{title}} v ${APP_NAME}.`,
        shareUnavailable: 'Před sdílením z nativního sestavení nastav EXPO_PUBLIC_WEB_URL.',
        scanner: {
          allowCamera: 'Povolit fotoaparát',
          errors: {
            camera: 'Fotoaparát se nepodařilo spustit. Zavři skener a zkus to znovu.',
            invalid:
              'Tento čárový kód není platné ISBN knihy. Zkus původní kód na zadní straně obálky.',
            lookup: 'Katalog knih není dostupný. Zkontroluj připojení a skenuj znovu.',
            not_found:
              'Pro toto ISBN se nenašlo žádné vydání. Zkus jiný kód nebo zadej ISBN ručně.',
          },
          instructions: 'Umísti čárový kód ISBN ze zadní strany knihy do rámečku.',
          lookingUp: 'Hledám toto vydání...',
          manualHint: 'Pokud kód nelze načíst, zavři skener a zadej ISBN ručně.',
          openSettings: 'Otevřít nastavení',
          permissionBody:
            'Fotoaparát se používá pouze ke čtení čárového kódu ISBN. Žádná fotografie se neuloží.',
          permissionTitle: 'Povol přístup k fotoaparátu',
          title: 'Skenovat knihu',
        },
        pagination: {
          filtered: 'Na této stránce nejsou odpovídající vydání. Načti další a pokračuj v hledání.',
          more: 'Načíst další knihy',
          moreWider: 'Načíst další ze širšího katalogu',
          loading: 'Načítání…',
          retry: 'Zkusit načíst znovu',
          failed: 'Další knihy se nepodařilo načíst. Tvé výsledky zůstaly zachovány.',
          refine: 'Hledáš konkrétní vydání? Upřesni název, autora či jazyk nebo hledej podle ISBN.',
          limit: 'Dosáhli jsme limitu vyhledávání v katalogu. Upřesni hledání a najdi další shody.',
        },
        searchWider: 'Prohledat širší katalog',
        titleOrAuthor: 'Hledat název, autora nebo ISBN',
        scopedSearchPlaceholder: {
          title: 'Hledat podle názvu',
          author: 'Hledat podle autora',
          isbn: 'Hledat podle ISBN',
        },
        widerSearchHint: 'Nevidíš správné vydání? Zkus rozšířený knižní katalog.',
        widerSearchIncluded: 'Prohledal se také širší katalog.',
      },
      chamber: {
        allChallengesComplete: 'VŠECHNY VÝZVY SPLNĚNY',
        allChallengesCompleteShort: 'SPLNĚNO',
        badgeReward: 'Odznak',
        chooseBookToBegin: 'Začni výběrem knihy',
        chooseCurrentRead: 'Vyber aktuální čtení',
        currentReading: 'AKTUÁLNĚ ČTEŠ',
        earnedRewards: 'ZÍSKANÉ ODMĚNY',
        earnedRewardsShort: 'SBÍRKA',
        emptyCurrentBody: 'Začni knihu z Mých knih a položí se na čtenářský stůl.',
        firstReward: 'PRVNÍ ODMĚNA',
        followedChallenge: 'SLEDOVANÁ VÝZVA',
        followedChallengeShort: 'SLEDOVANÁ VÝZVA',
        journeyLoading: 'Načítám tvoji cestu',
        journeyUnavailable: 'CESTA NENÍ DOSTUPNÁ',
        nextReward: 'DALŠÍ ODMĚNA',
        nextRewardShort: 'ODMĚNA',
        noCurrentBook: 'ŽÁDNÁ AKTUÁLNÍ KNIHA',
        openChallengeHint: 'Otevře detail této výzvy.',
        openCollection: 'Zobrazit získané odměny',
        openCollectionHint: 'Otevře tvoji sbírku odměn.',
        openJourney: 'Objevit výzvy',
        openJourneyHint: 'Otevře obrazovku Cesta.',
        openTbr: 'Otevřít TBR',
        pageKeeper: 'Strážce stránek',
        readingChamber: 'Čtenářská komnata',
        readingRhythmCompact:
          '{{daysCurrent}}/{{daysTarget}} dní · {{weeksCurrent}}/{{weeksTarget}} týd.',
        recommendedChallenge: 'NEJBLIŽŠÍ VÝZVA',
        recommendedChallengeShort: 'NEJBLIŽŠÍ VÝZVA',
        rewardDistance: 'zbývá {{count}} relací',
        rewardDistance_one: 'zbývá {{count}} relace',
        rewardDistance_few: 'zbývají {{count}} relace',
        rewardDistance_many: 'zbývá {{count}} relací',
        retryJourney: 'Klepni a zkus ji načíst znovu',
        retryJourneyHint: 'Znovu načte pokrok tvojí cesty.',
        sourceAccountSynced: 'ÚČET SYNCHRONIZOVÁN',
        sourcePreviewCollection: 'NÁHLEDOVÁ SBÍRKA',
        sourceSyncingLibrary: 'SYNCHRONIZUJI KNIHOVNU',
        syncPausedPreview: 'Synchronizace pozastavena - dostupná jsou náhledová data.',
        syncPausedSaved: 'Synchronizace pozastavena - uložený pokrok je dostupný.',
        themePackReward: 'Vzhled knihovny',
        yourCollection: 'Tvoje sbírka',
        weeklyGoal: 'TÝDENNÍ CÍL',
        weeklyGoalEmpty: 'Začíná knihou',
      },
      libraryThemes: {
        active: 'AKTIVNÍ',
        assetPending: 'Produkční raster této komnaty se ještě připravuje.',
        previewUnavailable: 'Náhled není k dispozici',
        change: 'Změnit',
        classic: {
          description: 'Původní ořechová skříň Belore, teplé výstavní světlo a mosazné detaily.',
          title: 'Klasická knihovna',
          unlockHint: 'Je součástí každé knihovny.',
        },
        downloadAndUse: 'Stáhnout a použít',
        downloadRequired: 'Bezpečně se stáhne, až si ho vybereš.',
        remoteDownloadSavedChoice: 'VYBRANÉ',
        remoteDownloadInProgress: 'Připravuji toto téma…',
        remoteDownloadFailed: 'Téma se nepodařilo stáhnout. Aktuální vzhled se nezměnil.',
        remoteDownloadUnavailable: 'Toto téma máš ve sbírce. Jeho soubory ještě nejsou dostupné.',
        remoteDownloadRetry: 'Zkusit stáhnout znovu',
        editorLabel: 'BALÍČEK TÉMATU',
        errors: {
          'asset-missing': 'Toto téma ještě není dostupné v této verzi aplikace.',
          conflict: 'Aktivní téma se změnilo na jiném zařízení. Zkus to znovu.',
          invalid: 'Odpověď s tématy nebyla platná. Zůstává aktivní tvoje předchozí téma.',
          unavailable: 'Téma se nepodařilo změnit. Zkus to znovu.',
          unowned: 'Tento balíček tématu ještě nemáš odemčený.',
        },
        eyebrow: 'SBĚRATELSKÁ TÉMATA KNIHOVNY',
        intro:
          'Balíčky témat mění materiály skříně a autorské světlo bez přesouvání knih, collectibles nebo rozložení polic.',
        loadFailed: 'Tvoji sbírku témat se nepodařilo obnovit. Zkus to znovu.',
        locked: 'ZAMKNUTÉ',
        menuLabel: 'Témata knihovny',
        openHint: 'Otevře tvoji sbírku balíčků témat knihovny',
        minimalWarmGrey: {
          description:
            'Police natřené teplou šedou barvou, se zaoblenými hranami v ilustrovaném stylu Belore.',
          title: 'Teplý šedý minimalismus',
          unlockHint: 'Odemkneš za pět schválených veřejných recenzí různých knih.',
        },
        minimalBlack: {
          description:
            'Police s leštěným černým lakem a jemnými odlesky, ve kterých vyniknou tvoje knihy.',
          title: 'Černý minimalismus',
          unlockHint: 'Odemkneš za hodnocení pěti různých knih. Počítají se i soukromá hodnocení.',
        },
        orbitalArchive: {
          description: 'Grafitové police, chladné světlo a rám tabletu kolem právě čtené knihy.',
          title: 'Orbitální archiv',
          unlockHint: 'Odměna za splnění čtenářské výzvy.',
        },
        verdantScriptorium: {
          description: 'Kouřový dub, lesně zelené niky a bronzový magický kruh pod tvojí knihou.',
          title: 'Zelené skriptorium',
          unlockHint: 'Odměna za splnění čtenářské výzvy.',
        },
        owned: 'VLASTNÍŠ',
        title: 'Témata knihovny',
        useTheme: 'Použít toto téma',
        webUnsupported: 'Témata zatím nejsou podporována ve webových prohlížečích.',
      },
      cabinetSyncChoice: {
        body: 'Knihovna v tomto zařízení je uspořádaná jinak než verze uložená v tvém účtu. Vyber, které rozložení se má sdílet mezi zařízeními.',
        chooseLater: 'Rozhodnout později',
        cloud: 'Cloudové rozložení',
        cloudHint: 'Aktuálně sdílené přes tvůj účet',
        consequence:
          'Volba tohoto zařízení nahradí cloudové uspořádání. Volba cloudu nahradí uložené uspořádání v tomto zařízení.',
        deviceHint: 'Uložené jen v tomto zařízení',
        error: 'Toto rozložení se nepodařilo vybrat. Nic jsme nenahradili; zkus to znovu.',
        eyebrow: 'PRVNÍ SYNCHRONIZACE',
        itemCount: '{{count}} umístěných předmětů',
        itemCount_one: '{{count}} umístěný předmět',
        itemCount_few: '{{count}} umístěné předměty',
        itemCount_many: '{{count}} umístěných předmětů',
        laterNote:
          'Úpravy zůstanou pozastavené, dokud si nevybereš. Při rozhodnutí později se žádné rozložení nesmaže.',
        saveConflictBody:
          'Cloudová knihovna se změnila po otevření editoru. Vyber, zda chceš uložit uspořádání z tohoto zařízení, nebo načíst nejnovější cloudové rozložení.',
        saveConflictEyebrow: 'KONFLIKT PŘI UKLÁDÁNÍ',
        saveConflictLaterNote:
          'Neuložené změny zůstanou otevřené. Dokud si nevybereš, nic se nenahradí.',
        saveConflictTitle: 'Knihovna se změnila na jiném zařízení',
        thisDevice: 'Toto zařízení',
        title: 'Našli jsme dvě rozložení knihovny',
        useCloud: 'Použít cloudové rozložení',
        useDevice: 'Použít rozložení z tohoto zařízení',
        unsavedDeviceHint: 'Tvoje aktuální neuložené uspořádání',
      },
      cabinetEditor: {
        applyDisplay: 'Použít na nástěnce',
        applyPlatformSpot: 'Použít na platformě',
        alreadyPlaced: 'V jiné polici',
        applyShelf: 'Použít v polici',
        arrange: 'Uspořádat',
        bookPlacementNotice:
          'Nedostupné knihy jsou už vystavené, rezervované jako právě čtené nebo označené DNF. Knihy ze všech ostatních kategorií sem můžeš umístit.',
        chooseEveryPosition: 'Vyber předmět pro každou pozici.',
        chooseLayout: 'Vyber rozložení',
        chooseShelf: 'VYBER POLICI, MÍSTO NA PLATFORMĚ NEBO NÁSTĚNKU',
        clearDisplay: 'Vyprázdnit nástěnku',
        clearPlatformSpot: 'Vyprázdnit místo',
        clearShelf: 'Vyprázdnit polici',
        collectibles: 'Collectibles',
        currentBook: 'Právě čteš',
        currentReserved: 'Aktuální čtení — rezervováno',
        didNotFinishUnavailable: 'DNF — nedostupné',
        discard: 'Zahodit změny',
        discardBody: 'Neuložené rozložení polic se ztratí.',
        discardTitle: 'Odejít bez uložení?',
        errors: {
          conflict: 'Knihovna se změnila jinde. Zavři editor, znovu ho otevři a zkus to znovu.',
          saveFailed: 'Rozložení polic se nepodařilo uložit. Zkus to znovu.',
        },
        keepEditing: 'Pokračovat v úpravě',
        layoutDoesNotUseTab: 'Toto rozložení nepoužívá předměty z této sbírky.',
        loadFailed: 'Editor polic nemohl načíst tvoji knihovnu.',
        loading: 'Připravuji tvoje police…',
        myBooks: 'Moje knihy',
        movesFromAnotherDisplay: 'Přesune se z jiného místa',
        noBooks: 'Nebyly nalezeny žádné dostupné knihy.',
        noCollectibles: 'Pro tvoje police zatím nejsou dostupné žádné collectibles.',
        open: 'Upravit knihovnu',
        openHint: 'Otevře úpravy tématu knihovny, vystavených knih a sběratelských předmětů',
        recipes: {
          bookObject: 'Kniha + předmět',
          compactAccessory: 'Kompaktní doplněk',
          collectible: 'Collectible',
          empty: 'Prázdná',
          fiveBadges: 'Pět odznaků',
          faceOut: 'Obálkou dopředu',
          longAccessory: 'Podélný doplněk',
          objectBook: 'Předmět + kniha',
          oneBadge: 'Jeden odznak',
          threeBadges: 'Tři odznaky',
          threeSpines: 'Tři hřbety',
          twoCollectibles: 'Dva předměty',
          twoCovers: 'Dvě obálky',
          twoSpines: 'Dva hřbety',
        },
        redo: 'Znovu',
        reloadLatest: 'Načíst nejnovější',
        save: 'Uložit',
        searchPlaceholder: 'Hledat v tvojí knihovně',
        selectedDisplay: 'VYBRANÁ PLOCHA',
        selectedPlatformSpot: 'VYBRANÉ MÍSTO NA PLATFORMĚ',
        selectedShelf: 'VYBRANÁ POLICE',
        shelves: {
          badgeBoard: 'Nástěnka na odznaky',
          centerMiddle: 'Police aktuálního čtení',
          centerUpper: 'Střední horní police',
          leftLower: 'Levá spodní police',
          leftMiddle: 'Levá střední police',
          leftUpper: 'Levá horní police',
          platformLeft: 'Levé místo na platformě',
          platformRight: 'Pravé místo na platformě',
          rightLower: 'Pravá spodní police',
          rightMiddle: 'Pravá střední police',
          rightUpper: 'Pravá horní police',
        },
        title: 'Uspořádat knihovnu',
        undo: 'Zpět',
        badges: {
          bookExplorer: 'Knižní objevitel',
          firstFinish: 'První dočtená kniha',
          hundredPages: 'Sto stran',
          nightReader: 'Noční čtenář',
          sevenDayStreak: 'Sedmidenní série',
        },
      },
      completion: {
        dnf: {
          eyebrow: 'NEDOČTENO',
          stoppedAt: 'Zastaveno na straně {{currentPage}} z {{pageCount}} · {{progress}}',
          stoppedAtPage: 'Zastaveno na straně {{currentPage}}',
          returnToReading: 'Vrátit mezi čtené',
          returnFailed: 'Knihu se nepodařilo vrátit mezi čtené. Zkus to znovu.',
          body: 'Kniha je teď mezi Nedočtenými.',
          reflectionTitle: 'Pár slov, pokud chceš',
          reflectionBody:
            'Pokud chceš, můžeš ostatním čtenářům napsat, proč kniha zůstala nedočtená. Hodnocení i recenze jsou nepovinné.',
          save: 'Uložit a zavřít',
          skip: 'Zatím zavřít',
          discardBody:
            'Neuložené hodnocení nebo recenze se ztratí. Kniha zůstane mezi Nedočtenými.',
          discard: 'Zahodit a zavřít',
        },
        body: 'Další přečtená kniha. Vychutnej si tenhle okamžik.',
        chooseNext: 'Co budeš číst dál?',
        chooseNextBody: 'Vyber si knihu z TBR a položíme ji na čtenářský stůl.',
        continueToNext: 'Pokračovat k další knize',
        discardAndContinue: 'Zahodit koncept a pokračovat',
        discardBody:
          'Neuložené hodnocení nebo koncept recenze se ztratí. Kniha zůstane mezi Přečtenými.',
        discardTitle: 'Odejít bez dokončení dojmu?',
        eyebrow: 'KNIHA DOKONČENA',
        keepEditing: 'Pokračovat v úpravě',
        milestoneLabel: 'ČTENÁŘSKÝ MILNÍK',
        milestoneValue: '+1 přečtená kniha',
        noTbr: 'Tvoje TBR je prázdné. Až budeš připravený, najdi si další knihu v katalogu.',
        pagesRead: 'Přečteno {{count}} stran',
        reflectionBody:
          'Jaká byla kniha? Přidej hodnocení a poděl se v recenzi o to, co v tobě zůstalo. Obojí je nepovinné a vždy se k tomu můžeš vrátit později.',
        reflectionTitle: 'Poděl se o své dojmy',
        saveAndContinue: 'Uložit dojem a pokračovat',
        saveAndClose: 'Uložit a zavřít',
        closeForNow: 'Zatím zavřít',
        discardAndClose: 'Zahodit a zavřít',
        skipReflection: 'Teď přeskočit · vybrat další knihu',
        startFailed: 'Další knihu se nepodařilo začít číst. Zkus to znovu.',
        title: 'Kniha {{title}} je dočtená',
      },
      annotations: {
        editor: {
          add: {
            note: 'Přidat poznámku',
            quote: 'Uložit citát',
          },
          bodyAccessibilityLabel: {
            note: 'Text poznámky',
            quote: 'Text citátu',
          },
          bodyLabel: {
            note: 'Tvoje poznámka',
            quote: 'Citovaný úryvek',
          },
          characterCount: '{{count}} / {{limit}} znaků',
          close: 'Zavřít editor citátu nebo poznámky',
          confirmDelete: {
            note: 'Odstranit poznámku',
            quote: 'Odstranit citát',
          },
          delete: {
            note: 'Odstranit poznámku',
            quote: 'Odstranit citát',
          },
          deleteBody: {
            note: 'Tuto soukromou poznámku po odstranění nebude možné obnovit.',
            quote: 'Tento uložený citát po odstranění nebude možné obnovit.',
          },
          deleteTitle: {
            note: 'Odstranit tuto poznámku?',
            quote: 'Odstranit tento citát?',
          },
          discard: 'Zahodit změny',
          discardBody: 'Neuložené změny se ztratí.',
          discardTitle: 'Odejít bez dokončení záznamu?',
          edit: {
            note: 'Upravit poznámku',
            quote: 'Upravit citát',
          },
          handle: 'Úchyt editoru citátu a poznámky',
          handleHint: 'Táhni dolů nebo aktivuj pro zavření editoru',
          keepEditing: 'Pokračovat v úpravě',
          open: {
            notes: 'Všechny poznámky',
            quotes: 'Všechny citáty',
          },
          pageAccessibilityLabel: 'Číslo stránky',
          pageHint: 'Nepovinné',
          pageLabel: 'Stránka',
          placeholder: {
            note: 'Co si chceš zapamatovat?',
            quote: 'Napiš nebo vlož úryvek…',
          },
          save: 'Uložit záznam',
        },
        errors: {
          conflict: 'Tento záznam se změnil jinde. Zavři ho, znovu otevři a zkus to opět.',
          delete: 'Záznam se nepodařilo odstranit. Zkus to znovu.',
          save: 'Záznam se nepodařilo uložit. Zkus to znovu.',
        },
        journal: {
          createLabel: { quote: 'Přidat citát', note: 'Přidat poznámku' },
          add: {
            note: '+ Poznámka',
            quote: '+ Citát',
          },
          addAccessibilityLabel: {
            note: 'Přidat soukromou poznámku',
            quote: 'Uložit citát',
          },
          open: {
            notes: 'Všechny poznámky',
            quotes: 'Všechny citáty',
          },
          edit: 'Upravit',
          editAccessibilityLabel: {
            note: 'Upravit poznámku {{position}}: {{details}}',
            quote: 'Upravit citát {{position}}: {{details}}',
          },
          empty: 'K této knize zatím nemáš citáty ani poznámky.',
          emptyBook: 'Ulož si citát ze stránky nebo si zapiš, co ti kniha připomněla.',
          emptyCurrent: 'Ulož si úryvek nebo myšlenku, dokud je čerstvá.',
          eyebrow: 'ČTENÁŘSKÝ DENÍK',
          intro: 'Ulož si ke knize úryvky, ke kterým se chceš vrátit, a soukromé poznámky.',
          loadError: 'Tvoje citáty a poznámky se nepodařilo načíst.',
          loading: 'Otevírám tvůj čtenářský deník…',
          loadMore: 'Načíst starší záznamy',
          loadMoreError: 'Starší záznamy se nepodařilo načíst.',
          page: 'Stránka {{pageNumber}}',
          retryLoadMore: 'Zkusit načíst starší záznamy znovu',
          title: 'Citáty a poznámky',
        },
        kind: {
          note: 'POZNÁMKA',
          quote: 'CITÁT',
          review: 'RECENZE',
        },
        quick: {
          sectionTitle: 'Zachytit z této knihy',
          addAccessibilityLabel: {
            note: 'Přidat soukromou poznámku ke knize {{title}}',
            quote: 'Uložit citát z knihy {{title}}',
          },
          label: {
            note: 'Poznámka',
            quote: 'Citát',
          },
        },
        validation: {
          body_required: 'Před uložením napiš text.',
          body_too_long: 'Záznam musí mít méně než {{limit}} znaků.',
          page_invalid: 'Zadej celé číslo stránky od 1 do 10 000 000.',
        },
      },
      quotes: {
        title: 'Moje citáty',
        shortTitle: 'Citáty',
        passageCount: '{{formattedCount}} úryvků',
        passageCount_one: '{{formattedCount}} úryvek',
        passageCount_few: '{{formattedCount}} úryvky',
        passageCount_many: '{{formattedCount}} úryvků',
        passageCount_other: '{{formattedCount}} úryvků',
        searchAccessibility: 'Hledat v citátech, knihách nebo autorech',
        searchPlaceholder: 'Hledat citáty, knihy nebo autory',
        capturedOn: 'Zachyceno {{date}}',
        pageSuffix: '  ·  s. {{pageNumber}}',
        readerCount: 'Tento citát zachytilo {{formattedCount}} čtenářů',
        readerCount_one: 'Tento citát zachytil {{formattedCount}} čtenář',
        readerCount_few: 'Tento citát zachytili {{formattedCount}} čtenáři',
        readerCount_many: 'Tento citát zachytilo {{formattedCount}} čtenářů',
        readerCount_other: 'Tento citát zachytilo {{formattedCount}} čtenářů',
        privateHint: 'Tento citát vidíš jen ty',
        pendingHint: 'Probíhá kontrola zveřejnění',
        rejectedHint: 'Tento citát nebyl schválen ke zveřejnění',
        loading: 'Načítám tvoje citáty',
        loadingMore: 'Načítám starší citáty…',
        actions: {
          add: 'Přidat',
          addAccessibility: 'Přidat citát',
          clearSearch: 'Vymazat hledání v citátech',
          like: 'To se mi líbí',
          makePrivateAccessibility: 'Nastavit citát jako soukromý',
          openAccessibility: 'Otevřít Moje citáty',
          openBook: 'Otevřít knihu {{title}}',
          publish: 'Zveřejnit',
          publishAccessibility: 'Zveřejnit tento citát',
          unlike: 'Odebrat označení To se mi líbí',
        },
        status: {
          private: 'SOUKROMÉ',
          pending: 'ČEKÁ NA KONTROLU',
          rejected: 'NEZVEŘEJNĚNO',
          published: 'ZVEŘEJNĚNO',
        },
        filters: {
          date: {
            label: 'Přidáno',
            choose: 'Datum zachycení',
            any: 'Kdykoli',
            '30_days': 'Posledních 30 dní',
            year: 'Tento rok',
          },
          book: {
            label: 'Kniha',
            choose: 'Vybrat knihy',
            all: 'Vše',
          },
          author: {
            label: 'Autor',
            choose: 'Vybrat autory',
            all: 'Vše',
          },
        },
        sort: {
          choose: 'Seřadit citáty',
          newest: 'Nejnovější první',
          oldest: 'Nejstarší první',
          changeAccessibility: 'Změnit pořadí citátů',
        },
        bookPicker: {
          title: 'Z které knihy pochází?',
          intro: 'Před uložením úryvku vyber knihu.',
          selectAccessibility: 'Uložit citát z knihy {{title}}',
        },
        errors: {
          update: 'Citát se nepodařilo aktualizovat. Zkus to znovu.',
        },
        states: {
          signedOut: {
            title: 'Tvoje citáty zůstanou u tvého účtu',
            body: 'Přihlas se, abys mohl vidět a ukládat úryvky ze svých knih.',
          },
          error: {
            title: 'Tvoje citáty se nepodařilo načíst',
            body: 'Uložené úryvky jsou v bezpečí. Zkus je otevřít znovu.',
            action: 'Zkusit znovu',
          },
          noResults: {
            title: 'Žádné úryvky neodpovídají výběru',
            body: 'Zkus jinou frázi nebo zruš aktuální filtry.',
            action: 'Zrušit filtry',
          },
          empty: {
            title: 'Úryvek, ke kterému se chceš vrátit?',
            body: 'Ulož si první citát spolu s jeho knihou a autorem.',
            action: 'Přidat citát',
          },
        },
      },
      notes: {
        title: 'Moje poznámky',
        shortTitle: 'Poznámky',
        noteCount: '{{formattedCount}} poznámek',
        noteCount_one: '{{formattedCount}} poznámka',
        noteCount_few: '{{formattedCount}} poznámky',
        noteCount_many: '{{formattedCount}} poznámek',
        noteCount_other: '{{formattedCount}} poznámek',
        searchAccessibility: 'Hledat v poznámkách, knihách nebo autorech',
        searchPlaceholder: 'Hledat poznámky, knihy nebo autory',
        addedOn: 'Přidáno {{date}}',
        pageSuffix: '  ·  s. {{pageNumber}}',
        loading: 'Načítám tvoje poznámky',
        loadingMore: 'Načítám starší poznámky…',
        actions: {
          add: 'Přidat',
          addAccessibility: 'Přidat poznámku',
          clearSearch: 'Vymazat hledání v poznámkách',
          openAccessibility: 'Otevřít Moje poznámky',
          openBook: 'Otevřít knihu {{title}}',
        },
        status: {
          private: 'SOUKROMÁ POZNÁMKA',
        },
        filters: {
          date: {
            label: 'Přidáno',
            choose: 'Datum přidání',
            any: 'Kdykoli',
            '30_days': 'Posledních 30 dní',
            year: 'Tento rok',
          },
          book: {
            label: 'Kniha',
            choose: 'Vybrat knihy',
            all: 'Vše',
          },
          author: {
            label: 'Autor',
            choose: 'Vybrat autory',
            all: 'Vše',
          },
        },
        sort: {
          choose: 'Seřadit poznámky',
          newest: 'Nejnovější první',
          oldest: 'Nejstarší první',
          changeAccessibility: 'Změnit pořadí poznámek',
        },
        bookPicker: {
          title: 'Ke které knize patří?',
          intro: 'Před uložením poznámky vyber knihu.',
          selectAccessibility: 'Přidat poznámku ke knize {{title}}',
        },
        states: {
          signedOut: {
            title: 'Tvoje poznámky zůstanou u tvého účtu',
            body: 'Přihlas se, abys viděl a ukládal soukromé poznámky ke knihám.',
          },
          error: {
            title: 'Tvoje poznámky se nepodařilo načíst',
            body: 'Tvoje soukromé poznámky jsou v bezpečí. Zkus je otevřít znovu.',
            action: 'Zkusit znovu',
          },
          versionMismatch: {
            title: 'Poznámky jsou dočasně nedostupné',
            body: 'Tvoje poznámky jsou v bezpečí. Zkus to znovu za chvíli.',
            action: 'Zkusit znovu',
          },
          noResults: {
            title: 'Žádné poznámky neodpovídají výběru',
            body: 'Zkus jinou frázi nebo zruš aktuální filtry.',
            action: 'Zrušit filtry',
          },
          empty: {
            title: 'Něco, co si chceš zapamatovat?',
            body: 'Ulož si první soukromou poznámku spolu s její knihou a autorem.',
            action: 'Přidat poznámku',
          },
        },
      },
      feedback: {
        community: {
          averageAccessibility: 'Průměrné hodnocení {{value}} z 5',
          beloreSource: 'KOMUNITA BELORE',
          empty: 'Zatím zde nejsou žádné schválené veřejné recenze.',
          eyebrow: 'RECENZE ČTENÁŘŮ',
          loadError: 'Recenze čtenářů se nepodařilo načíst. Tvůj soukromý dojem zůstává nedotčený.',
          loading: 'Načítají se recenze čtenářů…',
          ownReviewAbove: 'Tvoje zveřejněná recenze je zobrazena výše v části Tvůj dojem.',
          openLibraryAccessibility: 'Otevřít tuto knihu v Open Library',
          openLibraryAverageAccessibility: 'Průměrné hodnocení v Open Library {{value}} z 5',
          openLibraryEmpty: 'V Open Library zatím nejsou hodnocení.',
          openLibraryEmptyShort: 'Zatím bez hodnocení',
          openLibraryLoading: 'Načítají se hodnocení z Open Library…',
          totalRatingCount: 'Celkem {{count}} hodnocení',
          totalRatingCount_one: 'Celkem {{count}} hodnocení',
          totalRatingCount_few: 'Celkem {{count}} hodnocení',
          totalRatingCount_many: 'Celkem {{count}} hodnocení',
          ratingInfoTitle: 'O tomto hodnocení',
          ratingInfoOpenLibrary:
            'Tento průměr na škále do 5 hvězdiček pochází pouze od čtenářů Open Library. Hodnocení a recenze čtenářů Belore jsou vedeny samostatně a nejsou zahrnuty v tomto skóre. Údaje Open Library se ukládají do mezipaměti, proto se nejnovější hodnocení ještě nemusí zobrazit.',
          openLibraryNote: 'Uloženo odděleně od hodnocení a recenzí čtenářů Belore.',
          openLibraryRatingCount: '{{count}} hodnocení v Open Library',
          openLibraryRatingCount_one: '{{count}} hodnocení v Open Library',
          openLibraryRatingCount_few: '{{count}} hodnocení v Open Library',
          openLibraryRatingCount_many: '{{count}} hodnocení v Open Library',
          openLibrarySource: 'HODNOCENÍ OPEN LIBRARY',
          publishedOn: 'Zveřejněno {{date}}',
          ratingCount: '{{count}} hodnocení',
          ratingCount_one: '{{count}} hodnocení',
          ratingCount_few: '{{count}} hodnocení',
          ratingCount_many: '{{count}} hodnocení',
          readerReview: 'Recenze čtenáře',
          revealSpoiler: 'Zobrazit recenzi',
          reviewCount: '{{count}} veřejných recenzí',
          reviewCount_one: '{{count}} veřejná recenze',
          reviewCount_few: '{{count}} veřejné recenze',
          reviewCount_many: '{{count}} veřejných recenzí',
          spoilerHidden: 'Tato recenze obsahuje spoilery.',
          title: 'Co říkají čtenáři',
        },
        discardChanges: 'Zahodit změny',
        eyebrow: 'OSOBNÍ DOJEM',
        intro: 'Hodnocení a recenze se ukládají samostatně. Přidej jedno nebo obojí.',
        loadErrorBody:
          'Neotevřeli jsme prázdný editor, protože by mohl přepsat už uložený dojem ke knize.',
        loadErrorTitle: 'Tvůj dojem se nepodařilo načíst',
        notSaved: 'Neuloženo',
        saveChanges: 'Uložit dojem',
        readPrompt: {
          addAction: 'Přidat mezi Přečtené a pokračovat',
          eyebrow: 'NEŽ PŘIDÁŠ DOJEM',
          finishDateNote:
            'Přesunutím se kniha označí jako dnes dočtená. Datum můžeš později upravit.',
          keepAction: 'Ponechat v {{shelf}} a pokračovat',
          moveAction: 'Přesunout mezi Přečtené a pokračovat',
          trackedBody:
            'Hodnocení a recenzi jsi otevřel u knihy, která je stále v {{shelf}}. Než budeš pokračovat, vyber, kam patří.',
          title: 'Přesunout knihu mezi Přečtené?',
          untrackedBody:
            'Hodnocení a recenze potřebují místo ve tvé knihovně. Než budeš pokračovat, přidej knihu mezi Přečtené.',
        },
        retryLoad: 'Zkusit načíst znovu',
        summary: {
          edit: 'Upravit dojem',
          emptyBody:
            'Ulož si soukromé hodnocení, napiš recenzi nebo ji zveřejni pro ostatní čtenáře.',
          private: 'Soukromé',
          ratingLabel: 'TVOJE HODNOCENÍ',
          rejectedBadge: 'POTŘEBUJE ÚPRAVU',
          rejectedBody: 'Uprav recenzi a znovu ji ulož. Opravená verze se opět odešle ke kontrole.',
          rejectedTitle: 'Moderátor zanechal poznámku k tvé veřejné recenzi',
          reviewLabel: 'TVOJE RECENZE',
          spoilers: 'Obsahuje spoilery',
          start: 'Ohodnotit nebo recenzovat',
          value: '{{value}} / 5',
        },
        rating: {
          clear: 'Vymazat hodnocení',
          confirmRemove: 'Odstranit hodnocení',
          controlAccessibilityLabel: 'Vyber své hodnocení',
          hint: 'Klepni na hvězdu. Opětovným klepnutím na vybranou hvězdu nastavíš půl bodu.',
          keep: 'Ponechat hodnocení',
          compactLabel: 'Hodnocení',
          label: 'Tvoje hodnocení',
          notRated: 'Bez hodnocení',
          remove: 'Odstranit hodnocení',
          removeConfirmationBody: 'Tvoje napsaná recenze zůstane beze změny.',
          removeConfirmationTitle: 'Odstranit toto hodnocení?',
          save: 'Uložit hodnocení',
          sectionAccessibilityLabel: 'Osobní hodnocení',
          update: 'Upravit hodnocení',
          value: '{{value}} z 5',
          zeroAccessibilityLabel: 'Ohodnotit knihu nulou z pěti hvězdiček',
          pickerLabel: 'Vybrat přesné hodnocení',
          confirmPicker: 'Hotovo',
          compactValue: '{{value}} / 5',
        },
        review: {
          confirmDelete: 'Odstranit recenzi',
          containsSpoilers: 'Tato recenze obsahuje spoilery',
          delete: 'Odstranit recenzi',
          deleteConfirmationBody:
            'Tvoje hodnocení zůstane zachované. Odstraněnou recenzi nebude možné obnovit.',
          deleteConfirmationTitle: 'Odstranit tuto recenzi?',
          inputAccessibilityLabel: 'Tvoje napsaná recenze',
          keep: 'Ponechat recenzi',
          compactLabel: 'Recenze',
          label: 'Tvoje recenze',
          optional: 'nepovinné',
          placeholder: 'Co v tobě zůstalo po poslední stránce?',
          privateNote: 'Tuto recenzi vidíš pouze ty.',
          publish: 'Zveřejnit tuto recenzi pro ostatní čtenáře',
          publishBody:
            'Ve výchozím stavu zapnuto. U schválené recenze se zobrazí tvoje veřejné čtenářské jméno. Pokud ji chceš ponechat soukromou, zruš zaškrtnutí.',
          save: 'Uložit recenzi',
          sectionAccessibilityLabel: 'Osobní napsaná recenze',
          update: 'Upravit recenzi',
        },
        title: 'Tvůj dojem',
        unsavedClose: 'Před zavřením ulož změny nebo vyber Zahodit změny.',
        unavailable: 'Pokud chceš uložit hodnocení nebo recenzi ke knize, přihlas se.',
      },
      recovery: {
        title: 'Něco se pokazilo',
        body: 'Vyčistili jsme data, která měl Belore uložená v telefonu. Tvoje knihy, citáty a poznámky jsou v bezpečí ve tvém účtu.',
        reload: 'Načíst Belore znovu',
      },
      common: {
        retry: 'Zkusit znovu',
        all: 'Vše',
        allBooks: 'Všechny knihy',
        bookCount: '{{count}} knih',
        bookCount_one: '{{count}} kniha',
        bookCount_few: '{{count}} knihy',
        bookCount_many: '{{count}} knih',
        brand: APP_NAME.toUpperCase(),
        currentlyReading: 'Aktuálně čteš',
        currentlyReadingShort: 'Čteš',
        currentlyReadingTitle: 'Aktuálně čteš',
        didNotFinish: 'Nedokončeno',
        didNotFinishTitle: 'Nedokončeno',
        dnf: 'DNF',
        finishedBooks: 'Přečtené knihy',
        inTbr: 'V TBR',
        ofCount: 'z {{count}}',
        pageCurrent: 'Strana {{currentPage}}',
        pageCurrentOfTotal: 'Strana {{currentPage}} z {{pageCount}}',
        pageCurrentUnknownTotal: 'Strana {{currentPage}} z neznámého počtu',
        read: 'Přečteno',
        searchResults: 'Výsledky hledání',
        tbr: 'TBR',
        toBeRead: 'Chci číst',
      },
      achievements: {
        categories: {
          journey: {
            body: 'Čtení, které aplikace sledovala v čase.',
            title: 'Čtenářská cesta',
          },
          library: {
            body: 'Tvoje celoživotní knihovna včetně knih přečtených před registrací.',
            title: 'Osobní knihovna',
          },
          reflection: {
            body: 'Hodnocení a recenze, které zachycují tvoje čtenářské dojmy.',
            title: 'Reflexe',
          },
        },
        eyebrow: 'TVOJE ČTENÁŘSKÁ CESTA',
        intro:
          'Objevuj samostatné čtenářské výzvy, sleduj potvrzený postup a prohlédni si odznaky či vzhledy knihovny, které můžeš získat.',
        items: {
          afterword: {
            body: 'Dokonči sledovanou knihu a potom ji ohodnoť.',
            title: 'Doslov',
          },
          another_story: {
            body: 'Dokonči tři různá díla sledovaná od čtení až do konce.',
            title: 'Další příběh',
          },
          between_bookmarks: {
            body: 'Zaznamenej postup stejné knihy během tří různých dnů.',
            title: 'Mezi záložkami',
          },
          cover_to_cover: {
            body: 'Dokonči knihu, kterou jsi začal a posouval v aplikaci.',
            title: 'Od obálky k obálce',
          },
          first_bookmark: {
            body: 'Zaznamenej svůj první posun ve čtení.',
            title: 'První záložka',
          },
          first_impression: {
            body: 'Ohodnoť první přečtenou knihu, i když patří do tvé historie.',
            title: 'První dojem',
          },
          first_read: {
            body: 'Zaznamenej první přečtenou knihu včetně minulého čtení.',
            title: 'První příběh',
          },
          first_shelf: {
            body: 'Přidej první knihu do knihovny.',
            title: 'První police',
          },
          five_worlds: {
            body: 'Přečti knihy z pěti různých žánrů.',
            title: 'Pět světů',
          },
          five_public_reviews: {
            body: 'Zveřejni recenze pěti různých knih. Počítají se pouze schválené veřejné recenze.',
            title: 'Pět recenzí',
          },
          five_ratings: {
            body: 'Ohodnoť pět různých knih. Počítají se i soukromá hodnocení; knihy nemusíš mít dočtené.',
            title: 'Pět hodnocení',
          },
          personal_archive: {
            body: 'Zaznamenej deset různých přečtených děl.',
            title: 'Osobní archiv',
          },
          reading_rhythm: {
            body: 'Čti během sedmi různých dnů rozložených alespoň do tří týdnů.',
            title: 'Čtenářský rytmus',
          },
          second_genre: {
            body: 'Přečti knihy ze dvou žánrů a objev nové území.',
            title: 'Nové území',
          },
        },
        loadFailed: 'Tvoje achievementy se nepodařilo načíst.',
        locked: 'Odznak',
        milestone: 'Milník',
        ritual: {
          body: 'Vrať se k jedné knize během tří dnů, dokonči ji a přidej hodnocení. Všechny tři kroky musí patřit ke stejnému čtení.',
          eyebrow: 'ODMĚNA · VZHLED KNIHOVNY',
          progress: 'Dokončeno {{current}} ze {{target}} kroků rituálu',
          title: 'Čtenářský rituál',
          unlocked: 'Výzva splněna · Vzhled je dostupný',
        },
        title: 'Achievementy',
        unlocked: 'Splněno',
      },
      journey: {
        readingCalendar: readingCalendarCs,
        loading: 'Načítá se tvoje Cesta',
        refreshFailed:
          'Zobrazuje se naposledy uložená Cesta. Aktuální postup se nepodařilo načíst.',
        versionMismatch: {
          body: 'Aplikace a server ještě dokončují aktualizaci. Za chvíli Belore znovu načti.',
          cachedBody:
            'Dokud aplikace a server dokončují aktualizaci, zobrazuje se uložený postup. Za chvíli Belore znovu načti.',
          title: 'Belore se právě aktualizuje',
        },
        accessibility: {
          instrumentHint: 'Otevře detail tohoto achievementu.',
          nextGoal: 'Další achievement: {{title}}',
          pager: 'Vybrané achievementy',
          pagerItem: 'Zobrazit achievement {{position}} z {{count}}',
          pagerPosition: 'Achievement {{position}} z {{count}}',
          previousGoal: 'Předchozí achievement: {{title}}',
        },
        hero: {
          betweenBookmarksRemaining_few: 'Ještě {{count}} dny se stejnou knihou.',
          betweenBookmarksRemaining_one: 'Ještě jeden den se stejnou knihou.',
          betweenBookmarksRemaining_other: 'Ještě {{count}} dní se stejnou knihou.',
          readingRhythm: '7 dní · alespoň 3 týdny',
          readingRitual: 'Tři kroky v rámci jednoho čtení.',
        },
        actions: {
          collection: 'Sbírka',
          details: 'Detail výzvy',
          discover: 'Objevit výzvy',
        },
        focused: {
          newAttemptRequired: 'Začni nové čtení knihy a splň všechny tři kroky v jeho rámci.',
          rateBook: 'Ohodnotit knihu',
          ritualSteps: {
            post_finish_rating: 'Ohodnotit po dočtení',
            three_activity_days: 'Číst během tří dnů',
            tracked_finish: 'Dočíst knihu',
          },
        },
        allComplete: {
          body: 'Prohlédni si sbírku nebo se vrať, až přibudou nové výzvy.',
          title: 'Všechny úvodní výzvy jsou splněné',
        },
        annualGoal: {
          accessibilityProgress: 'Čtenářský cíl na rok {{year}}, {{current}} z {{target}} knih',
          bookAccessibility: '{{title}}, dokončena {{date}}',
          booksCounted: 'Započítané knihy',
          booksCountedBody_one: '{{count}} různé dílo dokončené v tomto kalendářním roce.',
          booksCountedBody_few: '{{count}} různá díla dokončená v tomto kalendářním roce.',
          booksCountedBody_many: '{{count}} různých děl dokončených v tomto kalendářním roce.',
          booksCountedBody_other: '{{count}} různých děl dokončených v tomto kalendářním roce.',
          completed: 'Splněno',
          completedBody:
            'Dosáhl jsi letošního cíle. Každá další dokončená kniha zůstane zapsaná níže.',
          completedOn: 'Dokončeno {{date}}',
          completedTitle: 'Cíl je splněný',
          compactProgress: '{{current}} / {{target}}',
          compactSet: 'Nastavit cíl',
          create: 'Nastavit roční cíl',
          detailProgress: '{{current}} z {{target}} knih',
          detailRemaining_one: 'Do cíle zbývá jedna kniha.',
          detailRemaining_few: 'Do cíle zbývají {{count}} knihy.',
          detailRemaining_many: 'Do cíle zbývá {{count}} knih.',
          detailRemaining_other: 'Do cíle zbývá {{count}} knih.',
          detailTitle: 'Čtenářský cíl {{year}}',
          edit: 'Upravit cíl',
          editor: {
            body: 'Vyber si osobní cíl. Je to pomůcka pro plánování, ne achievement ani podmínka pro collectible.',
            create: 'Nastavit cíl',
            createTitle: 'Vyber si cíl',
            decrement: 'Snížit roční cíl knih',
            editTitle: 'Změnit cíl',
            eyebrow: '{{year}} · OSOBNÍ CÍL',
            hint: 'Zadej celé číslo od 1 do 999.',
            increment: 'Zvýšit roční cíl knih',
            invalid: 'Vyber celé číslo od 1 do 999.',
            label: 'Počet knih k dokončení',
            preciseEntry: 'Klepni pro zadání přesného čísla',
            preciseEntryHint: 'Otevře číselnou klávesnici pro přímé zadání.',
            preciseEntryLabel: 'Zadat přesný roční cíl, aktuálně {{count}}',
            rulerAccessibility_one: 'Roční čtenářský cíl, {{count}} kniha',
            rulerAccessibility_few: 'Roční čtenářský cíl, {{count}} knihy',
            rulerAccessibility_many: 'Roční čtenářský cíl, {{count}} knih',
            rulerAccessibility_other: 'Roční čtenářský cíl, {{count}} knih',
            rulerHint:
              'Táhni doleva nebo doprava po jedné knize. Klepnutím na hodnotu ji zadáš přímo.',
            save: 'Uložit cíl',
            value_one: '{{count}} kniha',
            value_few: '{{count}} knihy',
            value_many: '{{count}} knih',
            value_other: '{{count}} knih',
          },
          emptyBody:
            'Vyber si, kolik různých knih chceš dokončit v tomto kalendářním roce. Cíl můžeš později změnit.',
          emptyTitle: 'Nastav si čtenářský cíl na rok {{year}}',
          eyebrow: '{{year}} · OSOBNÍ CÍL',
          historyEmptyBody: 'Pro tento kalendářní rok není uložený osobní cíl.',
          historyEmptyTitle: 'Pro rok {{year}} nebyl nastavený cíl',
          keep: 'Ponechat cíl',
          loadFailed: 'Roční cíl se nepodařilo načíst. Zkus to znovu.',
          loadFailedTitle: 'Tvůj roční cíl není dostupný',
          loading: 'Načítá se roční cíl',
          noBooksBody: 'Dokonči knihu v roce {{year}} a automaticky se objeví tady.',
          noBooksTitle: 'Zatím žádná dokončená kniha',
          openBookHint: 'Otevře detail této knihy.',
          openHint: 'Otevře roční cíl a knihy, které se do něj započítaly.',
          pace: {
            complete: 'Cíl je už splněný, takže žádné další tempo není potřeba.',
            label: 'Tempo k cíli',
            remaining_one:
              'Do konce roku {{year}} zbývá {{count}} den; zbytek cíle odpovídá průměru přibližně {{rate}} knihy za měsíc.',
            remaining_few:
              'Do konce roku {{year}} zbývají {{count}} dny; zbytek cíle odpovídá průměru přibližně {{rate}} knihy za měsíc.',
            remaining_many:
              'Do konce roku {{year}} zbývá {{count}} dní; zbytek cíle odpovídá průměru přibližně {{rate}} knihy za měsíc.',
            remaining_other:
              'Do konce roku {{year}} zbývá {{count}} dní; zbytek cíle odpovídá průměru přibližně {{rate}} knihy za měsíc.',
          },
          progress: '{{current}} / {{target}} knih',
          refreshFailed:
            'Zobrazuje se naposledy uložený cíl. Aktuální postup se nepodařilo načíst.',
          remaining_one: 'zbývá {{count}}',
          remaining_few: 'zbývají {{count}}',
          remaining_many: 'zbývá {{count}}',
          remaining_other: 'zbývá {{count}}',
          remove: 'Odstranit roční cíl',
          removeBody: 'Tvoje knihy i čtenářská historie zůstanou beze změny.',
          removeConfirm: 'Odstranit cíl',
          removeFailed: 'Roční cíl se nepodařilo odstranit. Zkus to znovu.',
          removeTitle: 'Odstranit cíl na rok {{year}}?',
          retryHint: 'Znovu načte tvůj roční čtenářský cíl.',
          saveFailed: 'Roční cíl se nepodařilo uložit. Zkus to znovu.',
          set: 'Nastavit cíl na rok {{year}}',
          title: 'Roční cíl',
        },
        catalogue: {
          filters: {
            all: 'Všechny',
            library: 'Knihovna',
            reading: 'Čtení',
            reflection: 'Reflexe',
          },
          status: {
            completed: 'Splněno',
            inProgress: 'Rozpracováno',
            notStarted: 'Nezačato',
            unavailable: 'Stav není dostupný',
          },
          intro: 'Výzvy, které splníš čtením. Většina přinese odznak nebo nový vzhled knihovny.',
          filtersLabel: 'Zobrazit výzvy',
          stillOpen: 'Ještě otevřené',
          left: 'Zbývá {{count}}',
          complete: 'Splněné',
          completeCount: '{{completed}} z {{total}}',
          earned: 'Získáno {{date}}',
          earnedUnknown: 'Datum získání není dostupné',
          noReward: 'Bez odměny',
          everyAchievement: 'Všechny výzvy',
          allComplete: 'Všech {{count}} splněno',
          dateRange: 'Získáno od {{first}} do {{last}}',
          dateRangeUnknown: 'Všechny čtenářské výzvy splněny',
          rewardGrid: 'Tvoje splněné výzvy',
          share: 'Sdílet',
          shareTitle: '{{name}} má splněno všech {{count}}',
          shareAnonymous: 'Všech {{count}} splněno',
          shareEyebrow: 'Čtenářské výzvy',
          shareFooter: 'Čtenářský život uchovaný v Belore',
          shareFailed: 'Obrázek se nepodařilo sdílet. Zkus to znovu.',
          shareUnavailable: 'Sdílení obrázků není na tomto zařízení dostupné.',
          shareNeedsBuild: 'Sdílení obrázků vyžaduje nové vývojové sestavení.',
          shareNeedsUpdate: 'Pro sdílení obrázků výzev aktualizuj aplikaci.',
          title: 'Objevit výzvy',
        },
        celebration: achievementCelebrationTranslations.cs,
        categories: {
          library: 'Knihovna',
          reading: 'Čtení',
          reflection: 'Reflexe',
        },
        collection: {
          inUse: 'Používá se',
          preview: 'Náhled',
          inUseCount: 'Používá se: {{count}}',
          onShelfCount: 'Na polici: {{count}}',
          onBoardCount: 'Na nástěnce: {{count}}',
          onShelf: 'Na polici',
          notPlaced: 'Zatím neumístěno',
          onBoard: 'Na tvé nástěnce',
          notOnBoard: 'Zatím není na tvé nástěnce',
          moreToEarn: 'Další odměny',
          putOnBoard: 'Přidat na nástěnku',
          editBoard: 'Upravit nástěnku',
          boardFull: 'Nástěnka je plná. Uprav ji a uvolni místo.',
          seeAchievement: 'Jak jsi odznak získal/a',
          earnedDate: 'Odznak · získaný {{date}}',
          useAppearance: 'Použít tento vzhled',
          appearanceBody:
            'Změní knihovny a čtenářský stojan. Knihy a předměty zůstanou na svém místě.',
          currentAppearance: 'Aktuální vzhled',
          replacesAppearance: 'Nahradí {{name}} · kdykoli se můžeš vrátit',
          placePiece: 'Umístit na polici',
          movePiece: 'Přesunout na jinou polici',
          usePot: 'Použít s rostlinou',
          placementUnknown: 'Umístění se nepodařilo ověřit',
          saveFailed: 'Změnu se nepodařilo uložit. Zkus to znovu.',

          badge: 'Odznak',
          badges: 'Odznaky',
          discoverAction: 'Objevit výzvy',
          emptyBody:
            'Plň výzvy a získané odznaky, předměty na polici či vzhledy knihovny se objeví tady.',
          emptyTitle: 'Tvoje sbírka zatím čeká',
          filterEmptyBody: 'Zatím tu nemáš odměnu tohoto typu. Ostatní odměny zůstávají ve sbírce.',
          filterEmptyTitle: 'Zatím bez odměny tohoto typu',
          filters: {
            all: 'Vše',
            badges: 'Odznaky',
            objects: 'Předměty',
            themes: 'Vzhledy',
          },
          filtersLabel: 'Filtrovat sbírku podle typu odměny',
          inspectTheme: 'Prohlédnout vzhled',
          intro: 'Vše tady jsi získal/a čtením. Klepni na odměnu a použij ji ve své knihovně.',
          items: {
            artDecoBookmark: 'Art Deco záložka',
            artDecoReadingLamp: 'Art Deco čtenářská lampa',
            oxbloodVessel: 'Vínově červená váza',
            readingSocietyMedallion: 'Medailon čtenářského spolku',
            soapstoneOwl: 'Mastencová sova',
          },
          shelfPiece: 'Předmět na polici',
          shelfPieces: 'Předměty',
          shelfPiecesBody:
            'Větší předměty si zachovávají skutečnou vizuální váhu; menší odznaky zůstávají samostatnou sbírkou.',
          loading: 'Načítá se tvoje sbírka',
          showAll: 'Zobrazit vše',
          source: {
            achievementsCached:
              'Zobrazují se uložené odznaky. Nové odměny za výzvy se nepodařilo ověřit.',
            achievementsUnavailable:
              'Odznaky se nepodařilo ověřit. Ostatní typy odměn mohou být stále dostupné níže.',
            objectsInvalid:
              'Vlastnictví předmětů na polici se nepodařilo ověřit, proto se nezobrazují jako pravdivě prázdná sbírka.',
            objectsUnavailable:
              'Předměty na polici se nepodařilo načíst. Ostatní ověřené odměny zůstávají zobrazené níže.',
            partialTitle: 'Sbírka nemusí být úplná',
            themesCached:
              'Dokud je zdroj oprávnění offline, zobrazují se uložené vzhledy knihovny.',
            themesUnavailable:
              'Vzhledy knihovny se nepodařilo načíst. Ostatní ověřené odměny zůstávají zobrazené níže.',
            unavailableBody:
              'Nepodařilo se ověřit žádný zdroj odměn. Zkus znovu načíst svoji ověřenou sbírku.',
            unavailableTitle: 'Sbírka není dostupná',
          },
          themePack: 'Vzhled knihovny',
          themes: 'Vzhledy',
          title: 'Sbírka',
        },
        context: {
          followed: 'Sledovaná výzva',
          near: 'Na dosah',
          recommended: 'Doporučeno',
        },
        detail: {
          challenge: 'Výzva',
          followShort: 'Sledovat',
          followingShort: 'Sleduješ',
          yourReading: 'Tvé čtení',
          steps: 'Kroky',
          chooseBook: 'Vybrat knihu',
          changeBook: 'Změnit knihu',
          chooseBookBody: 'Vyber si knihu pro tuto výzvu.',
          selectionFailed: 'Knihu se nepodařilo změnit. Zkus to znovu.',
          noBooks: 'Začni přidáním knihy do knihovny.',
          sameBook1: 'Tento krok patří k této knize',
          sameBook2: 'Oba kroky patří k této knize',
          sameBook3: 'Všechny tři kroky patří k této knize',
          sameBook4: 'Všechny čtyři kroky patří k této knize',
          sameBookMany: 'Všech {{count}} kroků patří k této knize',
          starsEnough: 'stačí hvězdičky',
          ratingValue: '{{rating}} z 5 hvězdiček',
          rewardUnlocked: 'Odměna · odemčená',
          unlocksByRating: 'Odemkne se hodnocením',
          unlocksWhenComplete: 'Odemkne se splněním kroků',
          yoursNow: 'Teď je tvůj',
          preview: 'Ukázka',
          lastStep: 'Poslední krok k odměně',
          nextStep: 'Tvůj další krok',
          rateBook: 'Ohodnotit knihu',
          readBook: 'Pokračovat ve čtení',
          finishBook: 'Dokončit knihu',
          startBook: 'Začít číst',
          applyTheme: 'Použít vzhled',
          themeApplied: 'Vzhled je použitý',
          themeCaption: 'Kdykoli ho změníš v úpravě knihovny',
          actionFailed: 'Čtení se nepodařilo uložit. Zkus to znovu.',
          themeFailed: 'Vzhled se nepodařilo použít. Zkus to znovu.',
          stepAccessibility: '{{step}}, {{status}}',
          dateListEnd: '{{start}} a {{end}}',
          finishedOn: '{{weekday}} {{date}}',
          weekdays: {
            '0': 'v neděli',
            '1': 'v pondělí',
            '2': 'v úterý',
            '3': 've středu',
            '4': 've čtvrtek',
            '5': 'v pátek',
            '6': 'v sobotu',
          },
          earnedOn: 'Splněno {{date}}',
          follow: 'Sledovat výzvu',
          followOutcomes: {
            followed: 'Výzvu teď najdeš mezi sledovanými.',
            limit_reached: 'Můžeš sledovat nejvýše tři výzvy. Nejdřív jednu přestaň sledovat.',
            unavailable: 'Tuto výzvu teď nelze sledovat.',
            unfollowed: 'Výzvu už nesleduješ.',
          },
          noReward: 'Milník · Bez sběratelské odměny',
          notFound: 'Tuto výzvu se nepodařilo najít.',
          progress: 'Postup',
          reward: 'Odměna',
          rewardUnavailable: 'Podrobnosti odměny nejsou dostupné',
          title: 'Detail výzvy',
          unfollow: 'Přestat sledovat',
        },
        feedback: {
          actions: {
            continue: 'Pokračovat',
            done: 'Hotovo',
            revealReward: 'Odhalit odměnu',
            viewCollection: 'Prohlédnout ve sbírce',
            viewThemes: 'Prohlédnout vzhledy',
          },
          confirmation: {
            archive: 'VÝZVA SPLNĚNA',
            tracked: 'VÝZVA SPLNĚNA',
          },
          result: {
            alreadyOwned: 'Odměnu už máš ve sbírce',
            badgeAdded: 'Přidáno do sbírky',
            milestone: 'MILNÍK SPLNĚN',
            themeAdded: 'Vzhled přidán',
            unavailable: 'Výzva je splněna. Její odměna je dočasně nedostupná.',
          },
          reward: {
            badge: 'NOVÝ ODZNAK',
            theme: 'NOVÝ VZHLED KNIHOVNY',
          },
          progress: {
            compactValue: '{{current}} / {{target}}',
            dismissAccessibility: 'Zavřít upozornění na postup',
            eyebrow: 'POKROK NA CESTĚ',
            headline: 'O krok blíž',
            multiple: 'Posunulo se {{count}} výzev',
            multiple_one: '{{count}} výzva postupuje',
            multiple_few: '{{count}} výzvy postupují',
            multiple_many: '{{count}} výzev postupuje',
            multiple_other: '{{count}} výzev postupuje',
            multipleBody: 'Otevři Cestu a prohlédni si potvrzený pokrok.',
            more_one: '+{{count}} další výzva',
            more_few: '+{{count}} další výzvy',
            more_many: '+{{count}} dalších výzev',
            more_other: '+{{count}} dalších výzev',
            motivationMany: 'Pokračuj — odměna je na dosah',
            motivationOne: 'Už jen jeden krok k odměně',
            openMultiple: 'Prohlédnout cestu',
            openSingle: 'Prohlédnout výzvu',
            single: '+{{delta}} {{unit}} · nyní {{current}} z {{target}}',
          },
          summary: {
            archiveBody: 'Tvoje zapsaná historie posunula tyto výzvy.',
            archiveTitle: 'Tvoje cesta se posunula',
            importBody: 'Čtenářskou historii jsme zapsali bez řetězce jednotlivých odhalení.',
            importTitle: 'Historie je zapsaná',
            remainingBody: 'Tyto výzvy splnila stejná akce.',
            remainingTitle: 'A ještě něco',
          },
        },
        groups: {
          library_discovery: 'Objevování',
          library_foundations: 'Základy knihovny',
          reading_completion: 'Dokončování',
          reading_habits: 'Návyky',
          reading_ritual: 'Čtenářský rituál',
          reflection_notes: 'Zápisky',
        },
        progress: {
          betweenBookmarks: {
            active: 'Aktuální čtení',
            activeWithBest:
              'Aktuální čtení · dosavadní nejlepší pokus {{best}} z {{target}} {{unit}}',
            bestValue: 'Dosavadní nejlepší pokus · {{current}} z {{target}} {{unit}}',
            newAttemptRequired: 'Pokud chceš pokračovat, začni nové čtení knihy.',
          },
          dimensions: {
            days: 'Dny čtení',
            weeks: 'Aktivní týdny',
          },
          readingRhythm:
            '{{daysCurrent}}/{{daysTarget}} dní · {{weeksCurrent}}/{{weeksTarget}} týdny',
          ritualStepState: '{{step}}: {{state}}',
          ritualSteps: {
            post_finish_rating: {
              long: 'Hodnocení po dočtení',
              short: 'Hodnocení',
            },
            three_activity_days: {
              long: 'Tři dny aktivity',
              short: '3 dny',
            },
            tracked_finish: {
              long: 'Dokončení knihy',
              short: 'Dokončit',
            },
          },
          stepComplete: 'Splněno',
          stepIncomplete: 'Zbývá',
          unavailable: 'Postup není dostupný',
          units: {
            books: 'knih',
            count: 'položek',
            weeks: 'týdnů',
            days: 'dnů',
            genres: 'žánrů',
            ratings: 'hodnocení',
            reviews: 'recenzí',
            sessions: 'relací',
            steps: 'kroků',
            works: 'děl',
          },
          value: '{{current}} z {{target}} {{unit}}',
        },
        rewards: {
          badge: 'Odměna · Odznak',
          themePack: 'Odměna · Vzhled knihovny',
        },
        summary: {
          completed: '{{completed}} z {{total}} splněno',
        },
        unavailable: {
          body: 'Než se postup obnoví, celý katalog najdeš v Objevit výzvy.',
          title: 'Postup cesty není dostupný',
        },
      },
      dataErrors: {
        bookNotFound: 'Kniha nebyla nalezena',
        bookRelationNotFound: 'Vztah ke knize nebyl nalezen',
        noCurrentReading: 'Není vybraná žádná aktuálně čtená kniha',
        requestFailed: 'Knihovna nemohla dokončit tento požadavek.',
      },
      legal: {
        title: 'Právní informace',
        terms: 'Podmínky používání',
        privacy: 'Ochrana soukromí',
        opensBrowser: 'Otevře web Belore v prohlížeči.',
        openError: 'Stránku se nepodařilo otevřít. Zkus to znovu nebo otevři {{url}} v prohlížeči.',
      },
      accountDeletion: {
        action: 'Smazat účet',
        hint: 'Trvale smaže tvůj účet Belore a osobní údaje o čtení.',
        title: 'Smazat tvůj účet?',
        body: 'Tvůj profil, knihovna, historie čtení, poznámky, uložené citáty a recenze budou trvale odstraněny. Tento krok nelze vrátit zpět.',
        catalogueNote:
          'Schválené příspěvky do společného knižního katalogu mohou zůstat bez propojení s tvým účtem.',
        acknowledgement: 'Rozumím, že smazání je trvalé.',
        confirm: 'Trvale smazat účet',
        deleting: 'Mažeme tvůj účet… Nech tuto obrazovku otevřenou.',
        retry: 'Zopakovat smazání',
        error:
          'Nepodařilo se potvrdit dokončení smazání. Zkus to znovu a ověříme a dokončíme požadavek.',
        unavailable: 'Smazání účtu je dočasně nedostupné. Zkus to později.',
        retryRequired:
          'Mazání účtu začalo a přístup je zablokován. Odstraňování se automaticky dokončuje. Zkus to znovu a ověříme stav. Zavřením obrazovky se mazání nezruší.',
      },
      profileRedesign: {
        photoLibraryPermission: 'Belore používá tvou galerii k výběru profilové fotky.',
        usePhoto: 'Použít fotku',
        account: 'Účet',
        save: 'Uložit',
        username: 'Uživatelské jméno',
        name: 'Jméno',
        email: 'E-mail',
        library: 'Knihy',
        app: 'Aplikace',
        privacyTerms: 'Soukromí a podmínky',
        languages: 'Jazyky',
        quotesPrivacy: 'Citáty a soukromí',
        developer: 'Vývoj · jen vývojářské buildy',
        changePhoto: 'Změnit fotku',
        emailHint:
          'Tvoje uživatelské jméno patří na Belore jen tobě. Zobrazuje se nad tvým jménem a u myšlenek, které zveřejníš. Tvůj e-mail se nikdy nezobrazuje.',
        usernameHint:
          '3 až 30 znaků: malá a velká písmena (A–Z), čísla, tečky a podtržítka. Zobrazuje se nad tvým jménem a u veřejných myšlenek.',
        available: '@{{username}} je volné',
        taken: 'Toto uživatelské jméno je již obsazené',
        invalid: 'Použij 3–30 malých nebo velkých písmen (A–Z), číslic, teček nebo podtržítek',
        reserved: 'Toto uživatelské jméno je vyhrazené',
        checking: 'Ověřuji dostupnost…',
        checkError: 'Dostupnost se nepodařilo ověřit. Zkus to znovu.',
        clear: 'Vymazat uživatelské jméno',
        saveError: 'Změny se nepodařilo uložit. Zkus to znovu.',
        appLanguage: 'Jazyk aplikace',
        readingCount: 'Jazyky, ve kterých čtu · {{count}}',
        contentCount: 'Obsah a doporučení · {{count}}',
        addLanguages: 'Přidat nebo odebrat jazyky',
        readingHint: 'Pouze výchozí jazykové filtry v Hledání. Vyber si 1 až 12.',
        contentHint:
          'Jazyky citátů, recenzí a doporučení na Nástěnce. Stránky knih zahrnují i jazyk vydání. Vyber si 1 až 12.',
        selected: 'Vybrané · {{count}} z 12',
        allLanguages: 'Všechny jazyky',
        takePhoto: 'Vyfotit',
        choosePhoto: 'Vybrat z galerie',
        providerPhoto: 'Použít fotku z přihlášení',
        removePhoto: 'Odstranit fotku',
        initials: 'Místo ní iniciály',
        photoHint: 'Čtvercová, zobrazená v malém. Uchováváme jen tvoji nejnovější fotku.',
        photoError: 'Fotku se nepodařilo aktualizovat. Zkus to znovu.',
        photoPermission:
          'Pokud chceš pořídit fotku, povol přístup k fotoaparátu v nastavení zařízení.',
        photoHidden: 'Tvou předchozí fotku skryl moderátor. Můžeš si vybrat jinou.',
        completionBody: 'Doplň své jméno a jedinečné uživatelské jméno.',
        privacyIntro: 'Co mohou ostatní čtenáři vidět z toho, co si uchováváš v Belore.',
        privacySave: 'Uložení se počítají bez tvého jména',
        privacySaveBody:
          'Každý citát, který si uložíš z knihy v katalogu, přidá k danému řádku jednoho čtenáře.',
        privacyPopular: 'Uložené řádky se mohou objevit na stránce knihy',
        privacyPopularBody:
          'Když moderátor schválí jejich znění. Jen slova knihy, nikdy tvoje jméno ani myšlenka.',
        privacyThought: 'Tvoje myšlenky zůstávají soukromé',
        privacyThoughtBody:
          'Dokud se nerozhodneš některou zveřejnit. Veřejné myšlenky se před zobrazením kontrolují.',
        privacyNotes: 'Poznámky jsou vždy soukromé',
        privacyCustom: 'Knihy, které si přidáš sám, se nikdy nezapočítávají',
        you: 'Ty',
        searchLanguages: 'Hledat jazyky',
        privacyMore: 'Více v <privacy>Zásadách ochrany soukromí</privacy>.',
        nameHint: 'Jméno zobrazené na tvém profilu.',
        version: 'Belore {{version}}',
      },
      profile: {
        readingLanguages: 'Jazyky, ve kterých čtu',
        readingLanguagesHint: 'Pouze výchozí jazykové filtry v Hledání. Vyber si 1 až 12.',
        contentLanguages: 'Jazyky obsahu',
        contentLanguagesError:
          'Jazyky obsahu se nepodařilo uložit nebo načíst. Klepni a zkus to znovu.',
        readingLanguagesError: 'Jazyky se nepodařilo uložit nebo načíst. Klepni a zkus to znovu.',

        blenderLibrary: 'Živá knihovna',
        blenderLibraryAccessibilityHint:
          'Přepíná mezi živou vrstvenou ořechovou knihovnou a původní grafikou knihovny.',
        blenderLibraryAccessibilityLabel: 'Živá knihovna',
        blenderLibraryBody:
          'Použij živou ořechovou komnatu s knihami, relikviemi a autorským světlem. Vypnutím se vrátíš k původní grafice knihovny.',
        body: 'Spravuj svůj účet a spoj celou čtenářskou historii v jedné knihovně.',
        completion: {
          body: 'Vyber jméno, které čtenáři uvidí u tvých veřejných recenzí. Před vstupem do knihovny ho ulož.',
          continue: 'Pokračovat do knihovny',
          errors: {
            required: 'Pokud chceš pokračovat, zadej jméno.',
            too_long: 'Použij nejvýše 120 znaků.',
          },
          eyebrow: 'JEŠTĚ JEDEN DETAIL',
          loadErrorBody:
            'Ke tvému čtenářskému profilu se teď nemůžeme dostat. Zkus to za chvíli znovu.',
          loadErrorEyebrow: 'MALÁ INKOUSTOVÁ NEHODA',
          retrying: 'Zkoušíme znovu…',
          loadErrorTitle: 'Profil se nepodařilo načíst',
          loadingBody: 'Kontrolujeme, zda je tvůj čtenářský profil připravený.',
          loadingTitle: 'Kontrolujeme profil…',
          nameHint:
            'Použij své skutečné jméno nebo pseudonym. Toto jméno se veřejně zobrazí u schválených recenzí.',
          nameLabel: 'Veřejné jméno čtenáře',
          namePlaceholder: 'Tvoje jméno nebo pseudonym',
          saveError: 'Profil se nepodařilo uložit. Zkontroluj připojení a zkus to znovu.',
          signedInAs: 'Přihlášený účet: {{email}}',
          signOutError: 'Odhlášení se nepodařilo dokončit. Zkus to znovu.',
          title: 'Dokonči svůj profil',
          useAnotherAccount: 'Použít jiný účet',
        },
        goodreadsImportAccessibilityHint: 'Otevře průvodce a náhled CSV souboru z Goodreads.',
        goodreadsImportBody: 'Knihy, police, hodnocení a recenze.',
        goodreadsImportTitle: 'Importovat z Goodreads',
        dataExport: {
          title: 'Stáhnout moje data',
          body: 'Knihovna pro jiné aplikace, citáty a poznámky jako text a úplná záloha.',
          chooseFormat: 'Vyber si, co chceš stáhnout.',
          formats: {
            backup: {
              title: 'Úplná záloha Belore',
              body: 'Všechno, co si ukládáš: úplná záloha, knihovna v CSV, citáty a poznámky v jednom ZIP souboru.',
            },
            goodreads: {
              title: 'Knihovna pro jiné aplikace',
              body: 'Knihy, police, hodnocení a recenze v CSV souboru ve formátu Goodreads.',
            },
            notebook: {
              title: 'Citáty a poznámky',
              body: 'Tvoje zápisky jako text ve formátu Markdown, rozdělené podle knih v jednom ZIP souboru.',
            },
          },
          preparing: 'Připravujeme tvoje data…',
          retry: 'Zkusit stáhnout znovu',
          successNative: 'Tvůj export je připravený k uložení nebo sdílení.',
          successWeb: 'Stahování začalo.',
          errors: {
            download: 'Data se nepodařilo stáhnout. Zkontroluj připojení a zkus to znovu.',
            rateLimit: 'Data si můžeš stáhnout jednou za minutu. Chvíli počkej a zkus to znovu.',
            sharingUnavailable: 'Sdílení na tomto zařízení není dostupné. Zkus to znovu.',
          },
        },
        illustratedLibraryPrototype: 'Prototyp ilustrované knihovny',
        illustratedLibraryPrototypeHint: 'Otevře samostatnou ukázku ilustrované knihovny.',
        language: 'Jazyk aplikace',
        languageBody: `Použij jazyk zařízení nebo vyber jazyk pro ${APP_NAME}.`,
        languageSelectorAccessibilityHint: 'Otevře seznam podporovaných jazyků.',
        languageSelectorAccessibilityLabel: 'Jazyk aplikace: {{language}}',
        developerLabs: {
          textRecognition: 'Laboratoř rozpoznávání textu',
          textRecognitionAccessibilityHint:
            'Porovná Apple Vision a Google ML Kit na vyfocené stránce.',
          title: 'Vývojářské laboratoře',
        },
        journeyTesting: {
          body: 'Prohlédni si Journey feedback bez změny knihovny nebo získaných odměn.',
          completionAccessibilityHint:
            'Zobrazí animovanou success obrazovku dokončeného achievementu.',
          completionAction: 'Otestovat dokončení achievementu',
          title: 'Testování Journey',
        },
        languages: {
          czech: 'Čeština',
          english: 'English',
          slovak: 'Slovenčina',
        },
        libraryTools: 'Nástroje knihovny',
        libraryToolsBody: `Přenes existující čtenářskou historii do ${APP_NAME}.`,
        readerFallback: `Čtenář ${APP_NAME}`,
        settings: 'Nastavení aplikace',
        settingsBody: `Vyber, jak má ${APP_NAME} vypadat a reagovat na tomto zařízení.`,
        soundEffectsBody: 'Zvuky při pokroku na Cestě.',
        soundEffectsTitle: 'Zvukové efekty',
        hapticsTitle: 'Hmatová odezva',
        hapticsBody: 'Jemná odezva při výběru a uložení.',
        hapticsSaveFailed: 'Nastavení platí nyní. Nepodařilo se ho uložit pro příští návštěvu.',
        systemDefault: 'Podle systému',
        openHint: 'Otevře tvoje knihy a nastavení.',
        title: 'Tvůj profil',
        settingsTitle: 'Nastavení',
        settingsHint: 'Otevře nastavení aplikace a účtu.',
        booksPrivate: 'Své knihy a poličky vidíš jen ty',
      },
      goodreadsImport: {
        unmatched: {
          title: 'Knihy bez shody v katalogu',
          body: 'Vyber knihy, které chceš ponechat jako vlastní knihy v knihovně. Použijeme údaje a historii čtení z exportu Goodreads. Tyto knihy i jejich recenze zůstanou soukromé.',
          selected: 'Vybráno: {{selected}} z {{total}}',
          selectAll: 'Vybrat všechny',
          deselectAll: 'Zrušit výběr',
          summary:
            'K importu: {{count}} · Vlastní knihy: {{selected}} · Nevybrané: {{skipped}} · Konflikty: {{conflicts}}',
          skipped: 'Přeskočeno',
          conflicts: 'Konflikty',
        },
        backToProfile: 'Zpět na profil',
        body: `Goodreads připraví katalog. ${APP_NAME} ho nejprve přečte v tomto zařízení, ukáže souhrn a potom ti umožní přiřadit vlastní police stavů čtení.`,
        changeFile: 'Změnit',
        chooseCsv: 'CSV už mám',
        chooseDestination: `Vyber cíl v ${APP_NAME}`,
        completed: {
          body: 'Import se bezpečně dokončil. {{changed}} z {{count}} knih změnilo tvoji knihovnu.',
          created: 'Přidané nové knihy',
          openLibrary: 'Otevřít Moje knihy',
          ratings: 'Importovaná hodnocení',
          reviews: 'Importované recenze',
          summaryTitle: 'Výsledek importu',
          title: 'Tvoje knihovna z Goodreads je importovaná',
          unchanged: 'Knihy bez potřebné změny',
          updated: 'Aktualizované existující knihy',
        },
        errors: {
          empty_file: 'Vybraný soubor je prázdný.',
          file_too_large: 'Vyber CSV soubor z Goodreads menší než 10 MB.',
          malformed_csv: 'CSV soubor je poškozený nebo obsahuje neukončenou hodnotu v uvozovkách.',
          missing_headers: 'Tento soubor nevypadá jako export knihovny z Goodreads.',
          no_books: 'Export z Goodreads neobsahuje žádné knihy.',
          not_csv: 'Vyber .csv soubor stažený z Goodreads.',
          open_failed: 'Stránku exportu Goodreads se nepodařilo otevřít.',
          read_failed: 'Soubor se nepodařilo přečíst. Zkus ho stáhnout znovu.',
          title: 'Náhled souboru se nepodařil',
        },
        importErrors: {
          apply_failed: 'Nic se neimportovalo jen částečně. Zkus import spustit znovu.',
          community_terms_required:
            'Pro veřejné recenze je potřeba přijmout aktualizované podmínky používání. Zatím je importuj jako soukromé – při dalším otevření tě Belore požádá o souhlas.',
          public_posting_restricted:
            'Veřejné příspěvky teď pro tvůj účet nejsou dostupné. Recenze importuj jako soukromé.',
          feature_unavailable:
            'Import z Goodreads je na serveru dočasně nedostupný. Tvoje knihovna se nezměnila.',
          plan_changed:
            'Tvoje knihovna se změnila nebo platnost plánu vypršela. Vrať se ke kontrole a připrav nový plán.',
          prepare_failed:
            'Server nedokázal připravit tento import. CSV zůstalo jen v tomto zařízení; zkus to znovu.',
          server_shelves_unresolved:
            'Server našel vlastní polici stavu čtení, která ještě potřebuje cíl. Zkontroluj přiřazení polic a zkus to znovu.',
          title: 'V importu nelze pokračovat',
        },
        fileSizeKb: '{{count}} kB',
        finishReview: 'Dokončit kontrolu',
        handoffBody:
          'Tato obrazovka připravila pouze ověřený plán. Žádné knihy, hodnocení, recenze ani volby se neuložily ani nepoužily.',
        handoffTitle: 'Import zatím není aktivní',
        kicker: 'Katalog zvenčí',
        instructions: {
          chooseBody: 'Vrať se sem a vyber stažený soubor goodreads_library_export.csv.',
          chooseTitle: 'Vyber stažený CSV soubor',
          exportBody:
            'Přejdi přímo na https://goodreads.com/review/import nebo v Goodreads otevři My Books → Import/Export. Zvol Export Library a počkej, než Goodreads připraví soubor.',
          exportTitle: 'Exportuj svou knihovnu',
          openBody: `Přihlas se přímo na Goodreads. ${APP_NAME} si nikdy neříká o tvoje heslo do Goodreads.`,
          openTitle: 'Otevři Goodreads',
        },
        mappingBody:
          'Známé stavy čtení z Goodreads přiřadíme automaticky. Vlastní kategorie zůstanou oddělené od stavu čtení.',
        mappingTitle: 'Zkontroluj police',
        metrics: {
          books: 'Nalezené knihy',
          ratings: 'Hodnocení',
          reviews: 'Recenze',
        },
        noExclusiveShelf: 'Bez hlavní police',
        openGoodreads: 'Otevřít export Goodreads',
        privateBody:
          'Soubor se nejprve přečte pouze v tomto zařízení. Před každou změnou uvidíš souhrn a reprezentativní ukázky.',
        privateTitle: 'Tvoje heslo zůstává na Goodreads',
        progress: {
          accessibility: 'Fáze {{current}} z {{total}}',
          export: 'Export',
          ready: 'Připraveno',
          review: 'Kontrola',
        },
        readyPlan: {
          body: 'Server ověřil {{count}} knih: {{matched}} odpovídá katalogu a {{create}} vytvoříme.',
          body_one:
            'Server ověřil {{count}} knihu: {{matched}} odpovídá katalogu a {{create}} vytvoříme.',
          body_few:
            'Server ověřil {{count}} knihy: {{matched}} odpovídají katalogu a {{create}} vytvoříme.',
          body_many:
            'Server ověřil {{count}} knih: {{matched}} odpovídá katalogu a {{create}} vytvoříme.',
          importBooks: 'Importovat {{count}} knih',
          importBooks_one: 'Importovat {{count}} knihu',
          importBooks_few: 'Importovat {{count}} knihy',
          importBooks_many: 'Importovat {{count}} knih',
          noEffect:
            'V knihovně se zatím nic nezměnilo. Importovat knihy použije celý plán atomicky—pokud selže, žádný částečný import se neuloží.',
          ratingsExcluded: 'Hodnocení: {{count}} hodnocení zůstane mimo komunitní doporučení.',
          ratingsIncluded:
            'Hodnocení: souhlas zahrnuje {{count}} hodnocení pro budoucí komunitní doporučení.',
          reviewsExcluded: 'Recenze: {{count}} recenzí zůstane nezveřejněných.',
          reviewsIncluded:
            'Recenze: souhlas zahrnuje {{count}} recenzí pro budoucí zveřejnění na veřejných stránkách knih.',
          title: 'Volby v tomto plánu',
        },
        readyBody:
          'Kontrola {{count}} knih je dokončená. Toto je pouze plán; v tvojí knihovně se nic nezměnilo.',
        readyBody_one:
          'Kontrola {{count}} knihy je dokončená. Toto je pouze plán; v tvojí knihovně se nic nezměnilo.',
        readyBody_few:
          'Kontrola {{count}} knih je dokončená. Toto je pouze plán; v tvojí knihovně se nic nezměnilo.',
        readyBody_many:
          'Kontrola {{count}} knih je dokončená. Toto je pouze plán; v tvojí knihovně se nic nezměnilo.',
        readyTitle: 'Plán importu je připravený',
        reviewPlan: {
          allShelvesBody:
            'Známé stavy čtení se přiřadily automaticky a každá vlastní police stavu už má cíl. Ostatní kategorie Goodreads zůstávají oddělené níže.',
          allShelvesTitle: 'Stavy čtení jsou přiřazené',
          booksAndHistory: 'Knihy a historie čtení',
          bookDetails: 'Údaje o knihách',
          bookSamplesShown: 'Zobrazuje se ukázka {{shown}} z {{total}} knih.',
          booksSummary: '{{reading}} Čteš · {{tbr}} TBR · {{read}} Přečteno · {{dnf}} DNF',
          changeFileHint: 'Vyber jiný CSV soubor z Goodreads a nahraď tento náhled.',
          choose: 'Vybrat',
          continue: 'Zkontrolovat údaje a souhlasy',
          categorySamplesShown: 'Zobrazuje se {{shown}} z {{total}} štítků kategorií.',
          customCategories: 'Kategorie Goodreads',
          customCategoryNone: 'Nebyly nalezeny žádné vlastní kategorie Goodreads.',
          customCategoryPolicy:
            'Kategorie zůstávají oddělené od stavu čtení; nezměníme je hromadně na Přečteno. Plán je zaznamená pro budoucí podporu kategorií.',
          customCategorySummary: '{{count}} štítků v {{assignments}} přiřazeních ke knihám',
          customShelvesBody:
            'Pro každou vlastní exkluzivní polici vyber nejbližší stav čtení. Její původní název z Goodreads zůstane v náhledu.',
          dateAdded: 'Datum přidání',
          dateRead: 'Datum přečtení',
          detailsBody:
            'Otevři libovolnou část, prohlédni si ukázku a rozhodni, které souhlasy chceš zahrnout do plánu.',
          detailsTitle: 'Zkontroluj plán importu',
          fileChecked: 'CSV zkontrolováno v tomto zařízení',
          formats: 'Formáty knih',
          groupedBody:
            'Velké knihovny zůstávají shrnuté v přehledných částech. Podrobná kontrola ukáže ukázky a přesné počty.',
          included: 'Zahrnuto',
          invalidRatings: '{{count}} neplatných hodnocení jsme označili jako bez hodnocení.',
          invalidRatings_one: '{{count}} neplatné hodnocení jsme označili jako bez hodnocení.',
          invalidRatings_few: '{{count}} neplatná hodnocení jsme označili jako bez hodnocení.',
          invalidRatings_many: '{{count}} neplatných hodnocení jsme označili jako bez hodnocení.',
          invalidReadCounts: 'Počty přečtení v {{count}} řádcích jsme ignorovali.',
          invalidReadCounts_one: 'Počet přečtení v {{count}} řádku jsme ignorovali.',
          invalidReadCounts_few: 'Počty přečtení v {{count}} řádcích jsme ignorovali.',
          invalidReadCounts_many: 'Počty přečtení v {{count}} řádcích jsme ignorovali.',
          duplicateBookIds: '{{count}} duplicitních Goodreads ID knih jsme ignorovali.',
          duplicateBookIds_one: '{{count}} duplicitní Goodreads ID knihy jsme ignorovali.',
          duplicateBookIds_few: '{{count}} duplicitní Goodreads ID knih jsme ignorovali.',
          duplicateBookIds_many: '{{count}} duplicitních Goodreads ID knih jsme ignorovali.',
          isbn10: 'ISBN-10',
          isbn13: 'ISBN-13',
          metadataSummary: 'ISBN, vydání, vydavatelé, data a počty přečtení',
          noRatingsConsent: 'Tento soubor neobsahuje žádná platná hodnocení.',
          noReviewsConsent: 'Tento soubor neobsahuje žádné psané recenze.',
          nothingChanged: 'Toto je místní náhled. V tvojí knihovně se nic nezměnilo.',
          noWrittenReviews: 'Tento soubor neobsahuje žádné psané recenze.',
          overviewBody:
            'Velkou knihovnu jsme shrnuli do několika přehledných částí. V tvojí knihovně se nic nezměnilo.',
          overviewTitle: 'Toto jsme našli',
          planOnly:
            'CSV se odešle a server připraví bezpečný plán. Tvoje knihovna se změní až po potvrzení Importovat knihy na další obrazovce.',
          preparePlan: 'Připravit plán importu',
          publishers: 'Vydavatelé',
          qualityTitle: 'Některé řádky vyžadují pozornost',
          ratingsConsentBody: `Povolit, aby těchto {{count}} hodnocení zlepšovalo doporučení napříč ${APP_NAME}. Hodnocení se nezveřejní jako recenze.`,
          ratingsConsentTitle: 'Použít moje hodnocení pro komunitní doporučení',
          ratingsSummary: '{{rated}} hodnocených · {{unrated}} bez platného hodnocení',
          readCountSummary:
            'Zaznamenaná přečtení: {{reads}} · Knihy přečtené vícekrát: {{rereads}}',
          resolveToContinue: 'Přiřaď {{count}} vlastních polic stavů a pokračuj',
          resolveToContinue_one: 'Přiřaď tuto vlastní polici stavu a pokračuj',
          resolveToContinue_few: 'Přiřaď {{count}} vlastní police stavů a pokračuj',
          resolveToContinue_many: 'Přiřaď {{count}} vlastních polic stavů a pokračuj',
          reviewSamplesShown: 'Zobrazuje se ukázka {{shown}} z {{total}} recenzí.',
          reviewsConsentBody: `Povolit, aby se těchto {{count}} recenzí zobrazilo na veřejných stránkách knih s mým profilem ${APP_NAME}.`,
          reviewsConsentTitle: 'Zveřejnit moje psané recenze na stránkách knih',
          reviewsSummary: '{{count}} psaných recenzí',
          reviewsSummary_one: '{{count}} psaná recenze',
          reviewsSummary_few: '{{count}} psané recenze',
          reviewsSummary_many: '{{count}} psaných recenzí',
          sharingBody:
            'Vyber, jak lze použít importovaná hodnocení a recenze. Obě oprávnění můžeš před importem vypnout.',
          sharingFootnote:
            'Tyto volby se zaznamenají spolu s importem a později je bude možné změnit.',
          sharingTitle: 'Souhlasy, které chceš zahrnout',
          shelvesNeedChoice: '{{count}} vlastních polic stavů potřebuje cíl',
          shelvesNeedChoice_one: 'Jedna vlastní police stavu potřebuje cíl',
          shelvesNeedChoice_few: '{{count}} vlastní police stavů potřebují cíl',
          shelvesNeedChoice_many: '{{count}} vlastních polic stavů potřebuje cíl',
          statusDestination: `Stav čtení v ${APP_NAME}`,
          skippedRows: '{{count}} nepoužitelných řádků nezahrneme do plánu.',
          skippedRows_one: '{{count}} nepoužitelný řádek nezahrneme do plánu.',
          skippedRows_few: '{{count}} nepoužitelné řádky nezahrneme do plánu.',
          skippedRows_many: '{{count}} nepoužitelných řádků nezahrneme do plánu.',
          starRating: '{{count}} hvězdiček',
          starRating_one: '{{count}} hvězdička',
          starRating_few: '{{count}} hvězdičky',
          starRating_many: '{{count}} hvězdiček',
          unrated: 'Bez hodnocení',
          unratedExplanation: '{{count}} knih nemá platné hodnocení z Goodreads.',
          unratedExplanation_one: '{{count}} kniha nemá platné hodnocení z Goodreads.',
          unratedExplanation_few: '{{count}} knihy nemají platné hodnocení z Goodreads.',
          unratedExplanation_many: '{{count}} knih nemá platné hodnocení z Goodreads.',
          whatWillImport: 'Co obsahuje tento plán',
          writtenReviews: 'Psané recenze',
        },
        reviewAgain: 'Zkontrolovat přiřazení polic',
        reviewBody: 'Zkontroluj nalezené údaje a rozhodni, kam patří vlastní police Goodreads.',
        reviewTitle: 'Kontrola před importem',
        status: {
          currently_reading: 'Čteš',
          did_not_finish: 'DNF',
          read: 'Přečteno',
          to_be_read: 'TBR',
          unclassified: 'Vyber cíl',
        },
        title: 'Přenes knihy přes práh',
        toolbarTitle: 'Import z Goodreads',
        unresolved: 'Pokud chceš pokračovat, vyber cíl pro {{count}} vlastních polic.',
        unresolved_one: 'Pokud chceš pokračovat, vyber cíl pro tuto vlastní polici.',
        unresolved_few: 'Pokud chceš pokračovat, vyber cíl pro {{count}} vlastní police.',
        unresolved_many: 'Pokud chceš pokračovat, vyber cíl pro {{count}} vlastních polic.',
      },
      author: {
        biography: 'Životopis',
        accessibility: {
          openProfile: 'Otevřít profil autora {{name}}',
          openSource: 'Otevřít zdroj profilu',
          photo: 'Portrét autora {{name}}',
        },
        booksBy: 'Knihy od autora',
        booksRating: '{{value}} · {{count}} hodnocení knih tohoto autora v Open Library',
        booksRating_one: '{{value}} · {{count}} hodnocení knih tohoto autora v Open Library',
        booksRating_few: '{{value}} · {{count}} hodnocení knih tohoto autora v Open Library',
        booksRating_many: '{{value}} · {{count}} hodnocení knih tohoto autora v Open Library',
        booksRatingAccessibility: 'Knihy tohoto autora mají v Open Library průměr {{value}} z 5',
        emptyBooks: 'Pro tohoto autora zatím nejsou dostupné knihy s ISBN.',
        emptyQuotes: 'Od tohoto autora zatím nemáš uložený citát.',
        loadError: 'Profil autora se nepodařilo načíst. Zkus to znovu.',
        loading: 'Hledám autora {{name}}…',
        loadingQuotes: 'Hledám tvoje citáty…',
        openLibraryBorn: 'Autor · Narozen {{date}}',
        openLibraryProfile: 'Profil autora',
        seeMoreInOpenLibrary: 'Zobrazit více v Open Library',
        shareFailed: 'Profil autora se nepodařilo sdílet.',
        shareMessage: '{{name}}\n{{url}}',
        title: 'Autor',
        unknownName: 'Neznámý autor',
        viewAll: 'Zobrazit vše',
        website: 'Webová stránka',
        yourQuotes: 'Tvoje citáty',
      },
      launch: {
        illustrated: {
          tagline: 'Vytvořte si vlastní knihovnu.',
          preparing: 'Připravujeme vaši knihovnu…',
          restoring: 'Obnovujeme vaši knihovnu…',
          signingIn: 'Přihlašujeme vás…',
          slow: 'Trvá to o něco déle.',
          loadError: 'Knihovnu se nepodařilo načíst.',
          retry: 'Zkusit znovu',
          otherAccount: 'Použít jiný účet',
        },
        authenticated: {
          body: 'Tvoje relace je připravená. Čtenářská nit odemyká dveře.',
          title: 'Západka tě poznala',
        },
        authorizing: {
          body: 'Dokonči bezpečné přihlášení. Dveře se otevřou, až se vrátíš.',
          title: 'Bezpečné přihlášení je otevřené',
        },
        booting: {
          body: 'Čtenářská nit kontroluje uloženou relaci.',
          title: 'Obnovuji tvoji knihovnu...',
        },
        entered: {
          body: 'Tvoje čtenářská komnata je připravená.',
          title: 'Knihovna otevřena',
        },
        entering: {
          body: 'Přesouváš se do svého aktuálního prostoru pro čtení.',
          title: 'Vstupuješ do knihovny...',
        },
        error: {
          fallbackBody: 'Zkus bezpečné přihlášení znovu.',
          title: 'Práh se nepodařilo otevřít',
        },
        opening: {
          body: 'Čtenářská komnata je připravená za prahem.',
          title: 'Otevírám tvoji knihovnu...',
        },
        sceneLoading: 'Připravuji celou scénu tvojí knihovny…',
        signOutRemoteUnconfirmed:
          'Na tomto zařízení jsi odhlášený, ale vzdálené odhlášení se nepodařilo potvrdit.',
      },
      menu: {
        allBooks: 'Všechny knihy',
        findBook: 'Najít knihu',
      },
      tabs: {
        library: 'Knihovna',
        myBooks: 'Moje knihy',
        find: 'Najít',
        myJourney: 'Cesta',
        journal: 'Zápisník',
        board: 'Nástěnka',
        profile: 'Profil',
      },
      myBooks: {
        loading: 'Načítají se tvoje knihy…',
        loadErrorTitle: 'Knihy se nepodařilo načíst',
        loadErrorBody: 'Zkontroluj připojení a za chvíli to zkus znovu.',
        refreshErrorTitle: 'Knihy se nepodařilo aktualizovat',
        refreshErrorBody: 'Zobrazené knihy si můžeš dál prohlížet. Za chvíli to zkus znovu.',
        chooseNextRead: 'Vyber další čtení',
        currentReading: 'Aktuálně čteš',
        emptyNoMatches: 'Žádné odpovídající knihy',
        emptyShelf: 'Tahle police je prázdná',
        findPlace: 'Najdi knihu a dej jí místo ve své knihovně.',
        finishedEmpty: 'Přečtené knihy se stanou součástí této police.',
        finishedOn: 'Dočteno {{date}}',
        openTbrToBegin: 'Otevři TBR polici a začni.',
        readShelf: 'Nedávno přečtené',
        searchCatalogue: 'Hledat v katalogu',
        searchPlaceholder: 'Hledat v mých knihách',
        shelfWaiting: 'Knihy, které na tebe čekají, se shromáždí tady.',
        title: 'Moje knihy',
        tryAnother: 'Zkus jiný název nebo autora.',
        viewAll: 'Zobrazit vše {{count}}',
      },
      personalPageCount: {
        sheetRange: 'Zadejte celé číslo od {{minimum}} do {{maximum}}.',
        add: 'Přidat počet stran',
        belowCurrentPage: 'Celkový počet nemůže být nižší než aktuální strana ({{currentPage}}).',
        change: 'Změnit počet stran',
        inputLabel: 'Celkový počet stran tvého výtisku',
        invalid: 'Zadej celé číslo od 1 do {{maximum}}.',
        placeholder: 'Počet stran',
        privateExplanation: 'Hodnota se uloží jen k tvé knize a nezmění společný záznam vydání.',
        required: 'Zadej celkový počet stran.',
        save: 'Uložit počet',
        saveFailed: 'Počet stran se nepodařilo uložit. Zkus to znovu.',
        title: 'Tvůj počet stran',
        update: 'Upravit počet',
      },
      progress: {
        approximatePages: '≈ {{currentPage}} stran',
        completeLabel: 'přečteno',
        percentValue: '{{progress}} %',
        percentModeCompact: '%',
        completionNote: 'Uložení 100 % dokončí knihu a přesune ji mezi Přečtené.',
        editorBody: 'Zadej stranu, na kterou ses dostal, nebo procento přečtené knihy.',
        invalidPage: 'Zadej celé číslo strany od 0 do {{pageCount}}.',
        invalidPageWithoutTotal: 'Zadej celé číslo strany 0 nebo více.',
        invalidPercent: 'Zadej celé procento od 0 do 100.',
        ofPages: 'z {{pageCount}} stran',
        pageInputLabel: 'Aktuální strana',
        pageUnit: 'strana',
        pageOfTotal: 'Strana {{currentPage}} z {{pageCount}}',
        pageTotalPrefix: 'z',
        pageTotalValue: '{{pageCount}} stran',
        pageRulerHint: 'Klepnutím na číslo upravíš hodnotu.',
        pagesMode: 'Strany',
        pagesUnit: 'stran',
        percentComplete: 'Přečteno {{progress}} %',
        percentInputLabel: 'Aktuální pokrok',
        percentMode: 'Procenta',
        percentSliderHint: 'Klepnutím na číslo upravíš hodnotu.',
        percentUnit: 'procento',
        resolvedPage: 'Strana {{currentPage}} z {{pageCount}} - {{progress}} %',
        saveFailed: 'Pokrok se nepodařilo uložit. Zkus to znovu.',
        beingSaved: 'Ukládá se…',
        savedOffline: 'Uloženo v telefonu · odešleme po připojení',
        syncingOffline: 'Odesíláme uložený pokrok…',
        saveFailedRestored: 'Pokrok se nepodařilo uložit. Obnovili jsme tvůj předchozí pokrok.',
        updateTitle: 'Upravit pokrok',
        moreActions: 'Další možnosti pro tuto knihu',
        openBookDetails: 'Otevřít detail knihy',
        share: 'Sdílet',
        openBookDetailsFor: 'Otevřít detail knihy {{title}}',
        markDidNotFinish: 'Označit jako nedočtené',
        markDidNotFinishFailed: 'Knihu se nepodařilo přesunout mezi nedočtené. Zkus to znovu.',
        unsaved: {
          title: 'Uložit nový pokrok?',
          body: 'Změna pokroku ještě není uložená.',
          invalidBody: 'Změna pokroku není platná, proto ji nelze uložit.',
          save: 'Uložit',
          discard: 'Zahodit',
          cancel: 'Zrušit',
          webSave: 'OK ji uloží. Zrušit ti dovolí ji zahodit nebo pokračovat v úpravě.',
          webDiscard: 'Zahodit neuloženou změnu pokroku?',
          returnToEditing: 'Zpět k úpravě',
          webReturn: 'Vrátit se k úpravě? Zrušit změnu zahodí.',
        },
        unknownTotalHint:
          'Toto vydání nemá známý počet stran. Zadej aktuální stranu přímo nebo níže doplň počet stran svého výtisku.',
        valueRequired: 'Zadej svůj čtenářský pokrok.',
      },
    },
  },
} as const;
